import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { MapContainer, TileLayer, Marker, Tooltip, Popup, Polyline, Circle, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { priceColor } from "../utils/priceColor";
import { stationIcon } from "../utils/stationIcon";
import { TILE_LAYER_ATTRIBUTION, useTileLayerUrl } from "../mapTiles";
import UserMenu from "../components/common/UserMenu";
import DoubleBookModal from "./dispatcher/DoubleBookModal";
import RouteMonitorModal from "../components/RouteMonitorModal";
import AnnouncementBanner from "../components/common/AnnouncementBanner";
import DatePicker from "react-datepicker";
import {
  Search, Phone, Gauge, Clock, Fuel, MapPin, Eye, EyeOff,
  AlertTriangle, User, Users, UserX, Navigation, ShieldHalf, ArrowLeft,
  TrendingUp, Check, Truck as TruckIcon, Layers, ChevronUp, ChevronDown,
  Maximize2, Minimize2, Play, Square, Focus, Bell, X, Plus, VenetianMask,
  Sun, Moon, PackageOpen, Palmtree,
} from "lucide-react";
import { useRouteVisibility, PICKUP_ROUTE_KEY } from "../hooks/useRouteVisibility";
import { useAlerts } from "../hooks/useAlerts";
import {
  partitionStopIntel, candidateForLoad, deriveStopState, humanEvidence, atFacilityLabel, formatDwell, facilityTzForLeg,
  computeAutoExpand, stopIntelHeaderLabel,
} from "./dispatcher/stopIntel";
import { buildRouteOptionsPayload, attachTripPlans } from "./dispatcher/routeOptionsPayload";
import { getImpersonationState, stopImpersonation } from "../utils/impersonation";

// This page is a standalone, chrome-free "Operational Center" (its own
// header, never nested in the DispatcherLayout shell) -- every value below
// is a CSS custom-property override, applied only within this page's own
// root div (see styles.page's usage), not at :root. The entire ~500-line
// styles object at the bottom of this file, plus nearly every inline style
// in the component, already reads through var(--bg)/var(--card)/var(--ink)/
// etc. -- overriding just these variables reskins the whole page dark
// without touching that styles object or the rest of the app (Fleet
// Dashboard, Reports, etc. stay on the existing light theme, since they
// read the real :root values in index.css, untouched here).
const DARK_THEME_VARS = {
  "--bg": "#0a0f1a",
  "--ink": "#eef2f6",
  "--ink-2": "#c7d0da",
  "--muted": "#7c8aa0",
  "--line": "#232f45",

  "--card": "#111a2c",
  "--card-2": "#18233a",

  // Brighter than the app-wide --brand (#0d7d6f) on purpose -- this page's
  // own dark background needs more contrast than the light shell's white
  // cards do for the same hue to read clearly. --brand-dark absorbs the
  // old value so anything using it for a "pressed/darker" variant still
  // makes sense relative to the new --brand.
  "--brand": "#14a08d",
  "--brand-dark": "#0d7d6f",
  "--brand-2": "#22a6c9",
  "--brand-wash": "rgba(20,160,141,0.16)",

  "--accent": "#14a08d",
  "--danger": "#f87171",
  "--warn": "#fbbf24",
  "--warn-wash": "rgba(217,119,6,0.18)",
  "--good-wash": "rgba(34,197,94,0.14)",
  "--bad-wash": "rgba(220,38,38,0.16)",

  "--shadow-xs": "0 1px 2px rgba(0,0,0,0.35)",
  "--shadow-sm": "0 1px 3px rgba(0,0,0,0.4)",
  "--shadow-md": "0 4px 14px rgba(0,0,0,0.45)",
  "--shadow-lg": "0 10px 30px rgba(0,0,0,0.5)",
  "--shadow-xl": "0 24px 64px rgba(0,0,0,0.55)",
};

// The real light theme -- same values as :root in index.css (not a fresh
// design), so switching back to light here looks identical to every other
// page in the app rather than a slightly-off approximation.
const LIGHT_THEME_VARS = {
  "--bg": "#f7faf9",
  "--ink": "#0f1a1c",
  "--ink-2": "#1e2e30",
  "--muted": "#5c6d6f",
  "--line": "#dfe7e5",

  "--card": "#ffffff",
  "--card-2": "#f1f5f4",

  "--brand": "#0d7d6f",
  "--brand-dark": "#085f54",
  "--brand-2": "#0891b2",
  "--brand-wash": "#e6f4f1",

  "--accent": "#0d7d6f",
  "--danger": "#dc2626",
  "--warn": "#d97706",
  "--warn-wash": "#fef3e2",
  "--good-wash": "#e9f8ee",
  "--bad-wash": "#fdecec",

  "--shadow-xs": "0 1px 2px rgba(9, 20, 19, 0.05)",
  "--shadow-sm": "0 1px 3px rgba(9, 20, 19, 0.07)",
  "--shadow-md": "0 4px 12px rgba(9, 20, 19, 0.08)",
  "--shadow-lg": "0 10px 28px rgba(9, 20, 19, 0.10)",
  "--shadow-xl": "0 24px 60px rgba(8, 95, 84, 0.18)",
};

// Persisted the same way DispatcherLayout remembers the sidebar's collapsed
// state -- a dispatcher who picks light shouldn't have it silently revert
// to dark on their next visit. Defaults to light now (was hardcoded dark).
const OP_CENTER_THEME_STORAGE_KEY = "op_center_theme";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
// The generic GET /loads/ list endpoint has no server-side ordering by
// default and no status filter -- with `limit=200`, once the table holds
// more than 200 rows (it's well past that), an OLD but still-active
// in_transit load can silently fall outside the fetched page and the truck
// it's assigned to reads as "Free" even though it's actively hauling
// something. The only thing this board actually needs from `loads` is
// "which loads currently occupy a truck," which is exactly in_transit --
// filtering server-side via /search/ for just that status keeps the
// fetched set small and correct regardless of how large the historical
// loads table grows.
const ACTIVE_LOADS_URL = `${apiBaseUrl}/search/loads/?filter_json=${encodeURIComponent(
  JSON.stringify({ conditions: [{ field: "status", operator: "is", value: "in_transit" }] })
)}&limit=2000`;
// Matches backend/trip_planning.py's OFF_ROUTE_THRESHOLD_MILES -- beyond
// this, a truck's live position is far enough from the saved route polyline
// that the live ETA re-check (which just projects onto the nearest point on
// that old route) shouldn't be trusted without a real "Refresh routes" call.
const OFF_ROUTE_THRESHOLD_MILES = 15.0;
// A little more generous than trip_progress_check.py's own 20-minute
// live-route reuse window -- the backend only updates
// live_route_computed_at when it genuinely recomputes, so this just needs
// to not go stale in the gap between this job's 5-minute poll cycles.
const LIVE_ROUTE_RECALC_STALE_MINUTES = 25.0;
const LOW_FUEL_PCT = 25;
// Stop intelligence (see the Stop Intelligence panel + stopIntel.js). The old
// client-side 1-mile / 8-minute reminder heuristic was removed in favor of the
// backend-authoritative 5-mile stop-detection state (arrival/dwell/departure
// columns), so Operational Center now shows ONE stop-state truth.
const LOW_HOS_HOURS = 1;
// Rough, client-side-only estimate used for list-level "at a glance" flags
// and the fleet summary strip -- deliberately separate from the real Cost
// Engine call (which only auto-fires for the one SELECTED truck) so browsing
// the fleet doesn't mean firing 20 API calls just to paint the list.
const EST_FUEL_PRICE_PER_GALLON = 3.85;
const EST_AVG_SPEED_MPH = 50;

const STOP_TYPE_COLOR = { pickup: "#5b9bd5", fuel: "#f2a33d", rest_zone: "#22c55e", fuel_and_rest: "#f2a33d", delivery: "#a78bfa" };
const STOP_TYPE_LABEL = { pickup: "Pickup", fuel: "Fuel stop", rest_zone: "Rest area (HOS)", fuel_and_rest: "Fuel + rest stop (combined)", delivery: "Delivery" };

const STATUS = {
  free: { color: "#e05d5d", label: "Free" },
  heading_to_pickup: { color: "#f2c94c", label: "Heading to pickup" },
  loaded: { color: "#4cae6d", label: "Loaded" },
};

// Single-select status segments shown as one joined pill row above the
// truck list -- "all" isn't in STATUS itself (it's not a real truck status,
// just the unfiltered view), so it's defined here with its own neutral color.
const STATUS_FILTER_KEYS = [
  { key: "all", label: "All", color: "var(--ink-2)" },
  { key: "free", label: "Free", color: STATUS.free.color },
  { key: "heading_to_pickup", label: "Pickup", color: STATUS.heading_to_pickup.color },
  { key: "loaded", label: "Loaded", color: STATUS.loaded.color },
];

// Secondary, single-select filters layered on top of the status segment --
// each maps straight to a flag already computed per-truck in `enriched`
// (fleetCounts[key] drives both the count shown and whether the chip
// renders at all, so an empty fleet never shows a dead "0 low fuel" chip).
// Replaced the old (At risk, Low fuel, Low HOS, Behind schedule, No driver)
// set per direct dispatcher feedback -- "At risk" was an opaque union
// nobody could explain at a glance, and "Low fuel" wasn't a useful
// dispatch-triage signal on its own. atRisk/lowFuel are still computed in
// `enriched` (the Route tab's red dot and the Fuel row's warn state still
// read them) -- only removed as filter chips here.
const EXTRA_FILTER_DEFS = [
  { key: "behindSchedule", label: "Behind schedule", icon: <AlertTriangle size={11} /> },
  { key: "emptyTruck", label: "Empty trucks", icon: <PackageOpen size={11} /> },
  { key: "lowHos", label: "Low HOS", icon: <Clock size={11} /> },
  { key: "resting", label: "Resting", icon: <Moon size={11} /> },
  { key: "noDriver", label: "No driver", icon: <User size={11} /> },
  { key: "noDispatcher", label: "No dispatcher", icon: <UserX size={11} /> },
  // Split into two per dispatcher feedback -- "On PTO" previously covered
  // BOTH a driver actively away right now and one merely scheduled for
  // later, which reads as wrong when a driver whose leave hasn't even
  // started yet shows up under a label that plainly means "away right
  // now." onPto is now current-only; upcomingPto is scheduled-but-not-
  // started. Labeled "Home time" not "PTO" -- these drivers aren't paid
  // time off, they're just home; keys are unchanged (still onPto/upcomingPto)
  // since that's what the rest of the code/backend already keys off of.
  { key: "onPto", label: "Home time", icon: <Palmtree size={11} /> },
  { key: "upcomingPto", label: "Upcoming home time", icon: <Palmtree size={11} /> },
];

// Sentinel used in the dispatcher-visibility Set to represent the "no dispatcher
// on file" (unassigned) truck pool, which has no real dispatcher id to key on.
const UNASSIGNED_KEY = "__unassigned__";

// Pull a US 5-digit zip out of the raw text a dispatcher typed into an
// origin/destination box (e.g. "62521" or "Decatur, IL 62521"), so we can
// PRESERVE the zip for reporting even though origin_location/destination_location
// keep showing the clean "City, ST" label. Returns null when no zip was typed
// (plain city name) -- never fabricates one. Zero geocoding / API calls.
function zipFromText(t) {
  if (!t) return null;
  const m = String(t).match(/\b(\d{5})(?:-\d{4})?\b/);
  return m ? m[1] : null;
}

// Assign-load forms require a US ZIP for origin + destination (no city-only
// entries) -- a ZIP geocodes to the city for display AND gives precise
// coordinates for pickup/delivery geofencing, so a dispatcher only needs to
// type the ZIP. Returns null when valid, else a human message naming the field.
function zipOnlyError(originText, destText) {
  const needsZip = [];
  if (!zipFromText(originText)) needsZip.push("origin");
  if (!zipFromText(destText)) needsZip.push("destination");
  if (!needsZip.length) return null;
  return `Enter a ZIP code for ${needsZip.join(" and ")} (e.g. 60601). City names aren't allowed here — a ZIP fills in the city automatically.`;
}

async function fetchRoadRoute(fromLat, fromLng, toLat, toLng) {
  const url = `https://router.project-osrm.org/route/v1/driving/${fromLng},${fromLat};${toLng},${toLat}?overview=full&geometries=geojson`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("OSRM request failed");
  const data = await res.json();
  if (!data.routes || !data.routes.length) throw new Error("No route returned");
  return data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
}

function authHeaders() {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
}

// Shared by Check Load + Log Broker Call's own lane-history lookups --
// resolves just a lat/lng for a free-text location, separate from (and
// much cheaper than) the real HERE routing match-truck-for-load does at
// submit time, so it can fire the moment a dispatcher tabs out of the
// origin/destination field instead of waiting for "Check this load."
function geocodeForLaneHistory(text) {
  if (!text || !text.trim()) return Promise.resolve(null);
  return axios
    .post(`${apiBaseUrl}/scripted/fleet/geocode-location`, { location: text }, authHeaders())
    .then((res) => ({ lat: res.data.lat, lng: res.data.lng }))
    .catch(() => null);
}

// Only fires once BOTH ends have a real lat/lng -- a lane search against
// just one resolved end would be meaningless (radius-matching every load
// that ever touched that one city, regardless of where it went).
function fetchLaneHistory(originGeo, destGeo, excludeLoadId, setter) {
  if (!originGeo || !destGeo) { setter(null); return; }
  setter({ loading: true });
  axios
    .post(`${apiBaseUrl}/scripted/fleet/lane-history`, {
      origin_lat: originGeo.lat, origin_lng: originGeo.lng,
      destination_lat: destGeo.lat, destination_lng: destGeo.lng,
      exclude_load_id: excludeLoadId || undefined, limit: 20,
    }, authHeaders())
    .then((res) => setter({ loading: false, ...res.data }))
    .catch(() => setter({ loading: false, error: "Couldn't load lane history" }));
}

// Backend datetimes are naive strings but represent true UTC (see
// CLAUDE.md / loads.pickup_datetime) -- `new Date("2026-08-08T19:00:00")`
// with no zone suffix gets silently parsed as the BROWSER's own local time
// by the JS spec, not UTC. Appending "Z" is the fix, and matters for more
// than display: isLate/pickupWaitHours compare against this value too, so
// a mis-parsed date was quietly producing wrong behind-schedule flags for
// any dispatcher not sitting in UTC+0.
function parseUtcNaive(isoLike) {
  if (!isoLike) return null;
  const iso = isoLike.endsWith("Z") || /[+-]\d\d:\d\d$/.test(isoLike) ? isoLike : `${isoLike}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Both the manual tab's datetime-local inputs and the AI-parsed pickup/
// delivery pair are always the SAME frame as each other (both raw
// facility-local digits, or both AI-resolved UTC -- never mixed), so a
// plain new Date() comparison is valid regardless of which frame it
// happens to be, without needing parseUtcNaive's own "assume UTC" fixup.
// Returns false (not blocking) if either side is missing/unparseable --
// the separate "required" gate already covers that case.
function isDeliveryBeforePickup(pickupLike, deliveryLike) {
  if (!pickupLike || !deliveryLike) return false;
  const pickup = new Date(pickupLike);
  const delivery = new Date(deliveryLike);
  if (Number.isNaN(pickup.getTime()) || Number.isNaN(delivery.getTime())) return false;
  return delivery.getTime() < pickup.getTime();
}

// Confirmed real: an AI free-text parse once emitted a 2-digit year (e.g.
// "0026-09-10" instead of "2026-09-10") straight into a load's
// pickup_datetime/delivery_datetime with nothing downstream ever
// questioning it -- it silently produced a "17,531,669 hours late"
// delivery-risk alert months later. Both AI-parse paths (Check Load's and
// Broker Call's own describe tab) share this same guard so neither can
// hand an out-of-range year to the manual tab's pre-fill or straight to
// POST /loads/. The backend also normalizes this defensively (see
// business rule on loads), but catching it here means the dispatcher sees
// a clear "re-enter this" message instead of a load silently getting a
// wrong-but-plausible-looking date.
function hasSaneOperationalYear(isoLike) {
  if (!isoLike) return true; // nothing to check -- the separate required/missing-field gates handle absence
  const d = new Date(isoLike);
  if (Number.isNaN(d.getTime())) return true; // not this guard's job -- a genuinely unparseable string fails elsewhere
  const year = d.getFullYear();
  const nowYear = new Date().getFullYear();
  return year >= nowYear - 1 && year <= nowYear + 3;
}

// Every status that means "this load stopped counting as a truck's active
// load" -- a dispatcher-cancelled load, plus the two lifecycle-cleanup
// statuses used to clear out stale Check Load exploration that never had a
// real pickup (cancelled_no_pickup) or had a pickup confirmation tap but
// was never actually carried (cancelled_stale_delivery). Kept as one list
// so a newly-added terminal status only needs to be added here once,
// mirroring backend/fleet_stats.py's CANCELLED_LOAD_STATUSES.
const TERMINAL_LOAD_STATUSES = ["delivered", "cancelled", "cancelled_no_pickup", "cancelled_stale_delivery"];
function isActiveLoadStatus(status) {
  return !TERMINAL_LOAD_STATUSES.includes(status);
}

// Fallback for a load booked with no explicit delivery deadline --
// "Delivery deadline" is an optional field in both the Check Load and
// Broker Call wizards, and a real broker call in particular often doesn't
// come with one. Without this, delivery_datetime stays permanently NULL,
// which silently makes the load invisible on the Fleet Load Board
// (LoadBoard.jsx's resolveCell requires BOTH pickup and delivery to place
// a booked-load block on any day) despite the load being real and
// in_transit -- confirmed live during load-board testing. Uses the chosen
// route's own simulated duration_hours added to pickup as a reasonable
// estimate -- the same number this app already shows elsewhere as the
// load's live ETA, just captured once at booking time instead of only
// ever existing as an on-the-fly calculation.
function addHoursToNaiveUtc(isoLike, hours) {
  const d = parseUtcNaive(isoLike);
  if (!d || !Number.isFinite(hours)) return null;
  const shifted = new Date(d.getTime() + hours * 3600 * 1000);
  return shifted.toISOString().replace(/\.\d{3}Z$/, "");
}

// current_trip_remaining_hours (match-truck-for-load) is hours-from-now,
// not a clock time -- this is the ONLY place that's turned into something
// a dispatcher can read. Deliberately just "when this truck arrives",
// never "when it's free for the new load" -- there's no unload-time
// buffer anywhere in the backend, so the caller must add its own caveat.
function formatCurrentTripArrival(remainingHours) {
  if (!Number.isFinite(remainingHours) || remainingHours <= 0) return null;
  const arrival = new Date(Date.now() + remainingHours * 3600 * 1000);
  return arrival.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

// Single source of truth for "which route is pre-selected before the
// dispatcher clicks anything" -- must match RouteLegendPanel's own
// cheapest-first sort exactly, since that sort is what the panel visually
// marks as selected by default. Previously each of the 4 route-fetch call
// sites (Check Load, Broker Call x3) defaulted chosenRouteType to
// best_match_route_options[0] -- whatever order the API happened to return,
// NOT necessarily the cheapest -- so the state driving what actually gets
// booked could silently disagree with which route the panel highlighted as
// primary. Used both to seed that state right after every fetch and as the
// booking-time fallback if state is somehow still unset.
function cheapestRouteType(routes) {
  if (!routes?.length) return null;
  return routes.reduce((best, r) => ((r.total_cost ?? Infinity) < (best.total_cost ?? Infinity) ? r : best)).route_type;
}

// Pickup/delivery appointment times are local to that specific facility,
// not the dispatcher's own timezone -- shows the real local clock time and
// zone abbreviation there when it's known (loads created via the broker-call
// flow store it), falling back to the browser's own zone for older loads
// that predate this.
function formatDateFacilityLocal(d, tzName) {
  if (!d) return null;
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit",
      timeZoneName: "short", timeZone: tzName || undefined,
    }).format(d);
  } catch {
    return d.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" });
  }
}

function formatFacilityLocal(isoLike, tzName) {
  return formatDateFacilityLocal(parseUtcNaive(isoLike), tzName);
}

// Time-only projection of the SAME canonical facility-local mechanism
// (Intl with timeZone: tzName) -- used by the compact Stop Intelligence
// appointment lines so "Appointment 11:00 AM CDT" reflects the FACILITY's
// clock, never the dispatcher's browser timezone. Falls back to the browser
// zone only when the facility tz is unknown (same as formatDateFacilityLocal).
function formatTimeFacilityLocal(isoLike, tzName) {
  const d = parseUtcNaive(isoLike);
  if (!d) return null;
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric", minute: "2-digit", timeZoneName: "short", timeZone: tzName || undefined,
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(d);
  }
}

// Wall-clock date/time of `date` AS SEEN IN `tzName`, as a "YYYY-MM-
// DDTHH:MM:00" string -- the shape /fleet/save-time-off's own start_local/
// end_local params expect. Only good for turning an already-known UTC
// instant into a display/round-trip string in one chosen zone; the reverse
// (typed local time -> UTC) is what save-time-off's own zoneinfo
// conversion is for, same split DriverCalendar.jsx uses.
function zonedInputParts(date, tzName) {
  if (!date) return null;
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tzName || undefined, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const parts = {};
  fmt.formatToParts(date).forEach((p) => { parts[p.type] = p.value; });
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour === "24" ? "00" : parts.hour}:${parts.minute}:00`;
}

// Inverse of zonedInputParts: a facility-local wall-clock string
// ("YYYY-MM-DDTHH:mm") interpreted in tzName -> the naive-UTC string that the
// loads.pickup_datetime / delivery_datetime columns store. Single-iteration
// offset correction, symmetric with zonedInputParts (both go through Intl in
// tzName), so a value round-trips: zonedInputParts(parseUtcNaive(x), tz) edited
// and passed back here reproduces x. Used when saving edited due dates.
function facilityLocalToUtcNaive(localStr, tzName) {
  if (!localStr) return null;
  const norm = localStr.length === 16 ? `${localStr}:00` : localStr;
  if (!tzName) return norm; // no facility tz -> treat the input as already UTC-naive
  const guessMs = Date.parse(`${norm}Z`);
  if (Number.isNaN(guessMs)) return norm;
  const shown = zonedInputParts(new Date(guessMs), tzName); // that instant's wall-clock in tzName
  const shownMs = Date.parse(`${shown}Z`);
  if (Number.isNaN(shownMs)) return norm;
  const utcMs = guessMs - (shownMs - guessMs); // subtract the tz offset at that instant
  return new Date(utcMs).toISOString().slice(0, 19); // "YYYY-MM-DDTHH:mm:ss" naive-UTC
}

// "Chicago, IL" -> "CHI, IL" -- compact enough for the truck list card's
// route line. Falls back to the raw string for anything that doesn't look
// like "City, ST" (a free-text or malformed location) rather than mangling
// it into something unreadable.
function abbreviateLocation(locationStr) {
  if (!locationStr) return "—";
  const parts = locationStr.split(",").map((s) => s.trim());
  if (parts.length < 2 || !parts[0]) return locationStr;
  return `${parts[0].slice(0, 3).toUpperCase()}, ${parts[1]}`;
}

// Date-only (no time) for the truck card's "Due" chip -- parseUtcNaive
// first, same as every other naive-UTC timestamp in this file, so this
// doesn't drift by the browser's own UTC offset the way plain
// `new Date(isoLike)` would (see formatDateFacilityLocal's own comment).
function formatDueDateShort(isoLike) {
  const d = parseUtcNaive(isoLike);
  if (!d) return null;
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}

// A driver at/near 0 drive-hours only has a knowable "back to full hours"
// time if they're actually resting -- off_duty/sleeper since hos_status_since
// starts the mandatory reset clock. A driver still 'driving' or 'on_duty'
// with low hours has no predictable reset time (nothing says when they'll
// stop), so this deliberately returns null rather than guessing.
const HOS_RESTING_DUTY_STATUSES = new Set(["offduty", "sleeper", "sleeperbed"]);
// Motive reports off_duty/sleeper (snake_case); Samsara reports
// offDuty/sleeperBed (camelCase) -- neither is normalized before landing on
// drivers.hos_duty_status, so an exact-string Set lookup silently missed
// every Samsara-synced driver's resting state. Strip case/separators before
// comparing so both providers' spellings resolve to the same bucket.
function isRestingDutyStatus(status) {
  return HOS_RESTING_DUTY_STATUSES.has(String(status || "").toLowerCase().replace(/[^a-z]/g, ""));
}

// Plain elapsed time since hos_status_since -- a confirmed FACT (this
// driver has genuinely been off_duty/sleeper this long, per Motive, held to
// a 3-consecutive-poll confirmation server-side so a single noisy read
// can't flip it), shown regardless of how many hours he has left. Never
// assumes he's taking a full mandatory reset -- with hours still in the
// bank there's no way to know if this is a quick coffee stop or the start
// of a real break, so this deliberately does NOT predict a "back at" time;
// see estimateHosReset for the separate, clearly-speculative prediction
// that only applies once he's actually maxed out and has to reset.
function restingElapsed(driver, nowMs) {
  if (!driver || !isRestingDutyStatus(driver.hos_duty_status)) return null;
  const since = parseUtcNaive(driver.hos_status_since);
  if (!since) return null;
  const minutes = Math.max(0, Math.round((nowMs - since.getTime()) / 60000));
  return { since, hours: Math.floor(minutes / 60), minutes: minutes % 60 };
}

function estimateHosReset(driver) {
  if (!driver || driver.hos_hours_remaining == null || Number(driver.hos_hours_remaining) >= 1) return null;
  if (!isRestingDutyStatus(driver.hos_duty_status)) return null;
  const since = parseUtcNaive(driver.hos_status_since);
  if (!since) return null;
  // Always assume the normal 10-hour daily reset, never the 34-hour cycle
  // restart -- the 60/70-hour cycle is a rolling 8-day window, so a low
  // cycle-hours number doesn't reliably mean the driver is really stuck at
  // the cap; it can just as easily mean hours are about to free up on their
  // own as an old day rolls off. Same reasoning as backend/hos_timing.py.
  const resetHours = 10;
  const availableAt = new Date(since.getTime() + resetHours * 60 * 60 * 1000);
  const hoursUntil = (availableAt.getTime() - Date.now()) / 3600000;
  return { availableAt, resetHours, isPast: hoursUntil <= 0, hoursUntil };
}

// Motive's recap.date values are plain "YYYY-MM-DD" (no time) -- parse and
// format as UTC so the weekday label can't shift a day off from whatever
// the calendar date actually was, the way `new Date("2026-08-07")` +
// local-timezone formatting sometimes does.
function shortWeekday(dateStr) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: "UTC" }).format(d);
}

function todayIsoUtc() {
  return new Date().toISOString().slice(0, 10);
}

// "0.2h" reads as a typo more than a duration -- for anything under an
// hour, minutes are the more honest unit (12m, not a rounded-to-one-decimal
// fraction of an hour that implies more precision than it has).
function formatHm(hours) {
  const totalMinutes = Math.round((hours || 0) * 60);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function haversineMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Cheap remaining-trip estimate for a truck's active load -- mirrors the
// Cost Engine's math (fuel/mpg, driver pay by pay_type, fixed+variable cost)
// closely enough for list-level flags and the fleet strip, but is NOT a
// substitute for the real per-truck Cost Engine call shown in the detail panel.
function estimateLoadEconomics(truck, driver, load, status) {
  if (!load || load.destination_lat == null || load.destination_lng == null) return null;
  const destLat = Number(load.destination_lat);
  const destLng = Number(load.destination_lng);
  let distanceMiles;
  if (status === "heading_to_pickup" && load.origin_lat != null) {
    const toOrigin = haversineMiles(Number(truck.current_lat), Number(truck.current_lng), Number(load.origin_lat), Number(load.origin_lng));
    const toDest = haversineMiles(Number(load.origin_lat), Number(load.origin_lng), destLat, destLng);
    distanceMiles = (toOrigin + toDest) * 1.2;
  } else {
    distanceMiles = haversineMiles(Number(truck.current_lat), Number(truck.current_lng), destLat, destLng) * 1.2;
  }
  const tripHours = distanceMiles / EST_AVG_SPEED_MPH;

  const payType = driver?.pay_type || "per_mile";
  let driverCost;
  if (payType === "hourly") driverCost = tripHours * (Number(driver?.pay_rate_hourly) || 28);
  else if (payType === "fixed_daily") driverCost = Math.ceil(tripHours / 24) * (Number(driver?.pay_rate_fixed_daily) || 220);
  else driverCost = distanceMiles * (Number(driver?.pay_rate_per_mile) || 0.55);

  const mpg = Number(truck.mpg_avg) || 6.5;
  const fuelCost = (distanceMiles / mpg) * EST_FUEL_PRICE_PER_GALLON;
  const otherCost = distanceMiles * (Number(truck.variable_cost_per_mile) || 0.15) + (Number(truck.fixed_cost_per_day) || 0) * (tripHours / 24);
  const revenue = Number(load.rate_offered) || 0;
  const profit = revenue - fuelCost - driverCost - otherCost;

  let behindSchedule = false;
  if (load.delivery_datetime) {
    const eta = new Date(Date.now() + tripHours * 3600 * 1000);
    // delivery_datetime is a naive-UTC string with no offset -- new Date()
    // on that parses it as browser-local, silently shifting the deadline
    // by the browser's UTC offset and under/over-flagging "behind schedule"
    // depending on timezone. parseUtcNaive is the correct parser the rest
    // of this file already uses for the same shape of timestamp.
    const deliveryDeadline = parseUtcNaive(load.delivery_datetime);
    behindSchedule = deliveryDeadline != null && eta > deliveryDeadline;
  }

  return { distanceMiles, tripHours, profit, behindSchedule };
}

// Maps a match-truck-for-load route candidate (+ its plan-full-trip result)
// into the shape save-route-plan/route_options expects. NOT a 1:1 field
// rename -- match-truck-for-load's candidates use different names for two
// of these (`driving_hours` not `duration_hours`, `profit_if_offered` not
// `contribution_profit`) because at quote time there's no agreed rate yet,
// just a floor/counter -- and tolls come back as a flat `toll_stops` list,
// not the `{toll_count, toll_names}` shape route_options stores. Passing
// `rateForProfit` explicitly (rather than trusting opt.profit_if_offered,
// which is frozen to whatever rate happened to be typed in when the route
// was first priced) lets a caller recompute profit against the actual
// final agreed rate at assignment time, not just the quote-time guess.
// buildRouteOptionsPayload + attachTripPlans moved to ./dispatcher/routeOptionsPayload
// (imported above) so the GM3A geometry/trip_plan binding invariant is unit-tested
// in isolation. Behavior is unchanged except the GM3A fix (plan bound by position,
// never recovered via the reused route_type key).

// load_intake_agent's only tool is match_truck_for_load -- its own tool
// call is now returned inline as tool_calls on the /agents/.../run response
// (previously only persisted server-side), a REAL, already-paid-for
// fleet-wide (or, when the request included truck_id, truck-scoped) search.
// Reusing it here means the broker-call/check-load wizards don't pay for a
// second, identical real-HERE search just to populate their own UI.
// Returns null (never throws) if the agent didn't call the tool, or the
// call itself errored -- callers must fall back to firing their own
// match-truck-for-load request in that case, exactly like before this
// reuse existed, so a rare tool-skipping hiccup just costs what it always
// used to instead of breaking the wizard.
function matchToolResultFromAgentRun(resData) {
  const calls = (resData?.tool_calls || []).filter(
    (c) => c.tool === "match_truck_for_load" && c.result && !c.error
  );
  if (!calls.length) return null;
  return calls[calls.length - 1].result;
}

// Mirrors match_truck_for_load's own negotiation formulas exactly (same
// constant names/values as the scripted API) so the broker-call wizard's
// "Broker offered" field can recalculate profit/counter live, per keystroke,
// without a round-trip -- total_cost/distance/driving_hours are truck-route
// facts that don't change when the offer changes, only these derived
// numbers do. If those constants ever move in the backend script, mirror
// the change here too.
const NEGOTIATION_MINIMUM_MARGIN_PCT = 0.15;
const NEGOTIATION_TARGET_MARGIN_PCT = 0.25;
const NEGOTIATION_COUNTER_ABOVE_OFFER_PCT = 0.05;

// totalCost is CASH-basis only (matches match-truck-for-load's own
// total_cost field) -- profit, floor, and suggestedCounter are ALL cash-
// basis, wait_cost has zero effect on any of them. Deliberately reverted
// from briefly including wait_cost in floor/suggestedCounter -- that
// produced unrealistic asks in practice (confirmed real: a $2200 Chicago->
// Boston offer got countered at ~$4400, roughly double, purely from a
// large wait estimate). wait_cost is a real but volatile, edge-case-prone
// number (depends on live HOS state and exact pickup timing) that a
// dispatcher should see and judge for themselves, not something the
// negotiating math should silently double the ask over. Keep this in sync
// with build_candidate's own margin math if it ever changes there.
function computeNegotiation(totalCost, distanceMiles, drivingHours, offeredRate) {
  const floor = totalCost * (1 + NEGOTIATION_MINIMUM_MARGIN_PCT);
  const costBasedCounter = totalCost * (1 + NEGOTIATION_TARGET_MARGIN_PCT);
  const hasOffer = offeredRate != null && !Number.isNaN(offeredRate);
  const suggestedCounter = hasOffer && offeredRate >= costBasedCounter
    ? offeredRate * (1 + NEGOTIATION_COUNTER_ABOVE_OFFER_PCT)
    : costBasedCounter;
  const profit = hasOffer ? offeredRate - totalCost : null;
  return {
    floor,
    suggestedCounter,
    profit,
    profitPerMile: profit != null && distanceMiles ? profit / distanceMiles : null,
    profitPerHour: profit != null && drivingHours ? profit / drivingHours : null,
  };
}

// A templated, non-LLM summary built straight from match_truck_for_load's
// own candidate fields -- shown for every candidate in the wizard's
// Analysis step. The agent's real narrative (brokerCallResult.narrative)
// only ever describes its single top pick, and re-calling the LLM for
// every candidate the dispatcher clicks through would be slow and costly;
// this covers the same load-bearing facts (deadhead, HOS reset, returning
// driver, wait, deadline risk) instantly for all of them.
function candidateNarrative(c) {
  const parts = [];
  const modeText = c.search_mode === "idle" ? "an idle truck" : "a truck finishing a nearby load";
  parts.push(`${c.driver_name || "This driver"} is driving ${modeText} with ${c.deadhead_miles ?? "?"} deadhead miles to reach pickup.`);
  if (c.is_returning_driver) {
    parts.push(`Motive shows no one live in the cab right now -- ${c.driver_name || "this driver"} is resting but is the last driver assigned to this truck and returns before pickup.`);
  }
  if (c.on_time_off) {
    const until = c.time_off_until_local || "an unknown time";
    parts.push(`${c.driver_name || "This driver"} is on scheduled time off right now, back ${until}${c.time_off_note ? ` (${c.time_off_note})` : ""} -- these numbers assume the trip starts once they're back.`);
  }
  if (c.used_dispatcher_availability_override) {
    parts.push(`Using the dispatcher-set "back at" note for ${c.driver_name || "this driver"} instead of their live HOS status.`);
  }
  if (c.needs_hos_reset) {
    parts.push(`Needs a ${c.hos_reset_hours}h HOS reset before this trip is legal${c.hos_reset_is_cycle_restart ? " (cycle hours are tight, but the rolling window means a normal break should still clear it)" : ""}.`);
  }
  if (c.pickup_timing_uncertain) {
    parts.push(c.pickup_timing_note);
  }
  if (c.wait_hours > 0) {
    parts.push(c.search_mode === "loaded"
      ? `Expect at least ${c.wait_hours}h of wait time before the pickup appointment -- more if unload runs long on the current load.`
      : `Expect ${c.wait_hours}h of wait time before the pickup appointment.`);
  }
  if (!c.meets_deadline) {
    parts.push(`Heads up -- this truck does not make the delivery deadline on the current route.`);
  }
  if (c.time_off_conflict_warning) {
    parts.push(c.time_off_conflict_warning);
  }
  return parts.join(" ");
}

// The step-1 ranked list used to show distance/driving-hours only, which
// makes a genuinely-better truck look worse at a glance -- a truck 40mi
// away with a 17h pickup wait can lose real money sitting idle, while one
// 500mi away with only a 4h wait comes out ahead once that wait is priced
// in (wait_hours * (fixed_cost_per_day/24 + driver hourly rate), already
// folded into total_cost/profit server-side -- this just makes the SAME
// number's reasoning legible instead of a black box). Grounded entirely in
// real fields the candidate already returns, no invented causal claims.
function matchWhyLine(c, neg, isTop, topNeg) {
  const bits = [];
  if (c.wait_hours > 0) {
    bits.push(c.search_mode === "loaded"
      ? `${c.wait_hours}h+ pickup wait (~$${Math.round(c.wait_cost)}+)`
      : `${c.wait_hours}h pickup wait (~$${Math.round(c.wait_cost)})`);
  }
  if (c.needs_hos_reset) bits.push(`${c.hos_reset_hours}h HOS reset${c.hos_reset_is_cycle_restart ? " (cycle-tight)" : ""}`);
  const factors = bits.length ? bits.join(" + ") : "no wait, no HOS reset";
  // late_for_pickup_hours and wait_hours are mutually exclusive (a truck is
  // either early-and-waiting or late, never both) -- called out separately
  // since "no wait, no HOS reset" would otherwise read as good news for a
  // truck that's actually missing the pickup appointment entirely.
  const lateNote = c.late_for_pickup_hours > 0
    ? ` Heads up: this truck would arrive ${c.late_for_pickup_hours}h AFTER the requested pickup time.`
    : "";
  // Time off isn't priced into deadhead/wait/reset the way HOS is -- a
  // driver on active time off already has it baked into effective_now (so
  // the deadhead/wait numbers above already account for it), but delivery
  // running into a driver's UPCOMING time off is purely advisory and easy
  // to miss buried in the numbers, so it's called out explicitly rather
  // than folded into "factors".
  const timeOffNote = c.on_time_off
    ? ` ${c.driver_name || "Driver"} is currently on time off, back ${c.time_off_until_local || "later"}.`
    : c.time_off_conflict_warning
      ? ` ${c.time_off_conflict_warning}`
      : "";
  // Distinct from lateNote (a real problem the numbers already reflect) --
  // this is about the CONFIDENCE behind those numbers: the driver hasn't
  // actually started the reset this trip needs before pickup yet, so the
  // timing is a reasonable assumption, not a confirmed fact. Worth a call
  // even when everything else on the card looks fine.
  const timingUncertainNote = c.pickup_timing_uncertain ? ` ${c.pickup_timing_note}` : "";
  if (isTop) {
    return `Wins on total cost -- ${c.deadhead_miles}mi deadhead, ${factors}.${lateNote}${timeOffNote}${timingUncertainNote}`;
  }
  if (neg.profit != null && topNeg.profit != null) {
    const gap = Math.round(topNeg.profit - neg.profit);
    return `$${gap.toLocaleString()} less profit than the top pick -- ${c.deadhead_miles}mi deadhead, ${factors}.${lateNote}${timeOffNote}${timingUncertainNote}`;
  }
  return `${c.deadhead_miles}mi deadhead, ${factors}.${lateNote}${timeOffNote}${timingUncertainNote}`;
}

function truckStatus(load, truck) {
  if (!load) return "free";
  // Dispatcher-confirmed pickup (or, once it exists, a driver-app
  // confirmation through the same /fleet/confirm-pickup endpoint) is the
  // ONLY thing that flips a load to "loaded" -- a truck sits at
  // "heading_to_pickup" no matter how close it gets to the shipper, since
  // GPS proximity alone doesn't mean freight is actually on the trailer.
  // Previously this fell back to a live-GPS-proximity guess once a truck
  // got within PICKUP_PROXIMITY_MILES, which is exactly the bug reported:
  // dispatchers saw "Loaded" hours before they'd actually confirmed
  // anything left the dock.
  if (load.actual_pickup_at) return "loaded";
  return "heading_to_pickup";
}

function truckIcon(label, statusColor, isSelected) {
  const ringColor = isSelected ? "#0d7d6f" : "#0b1020";
  const ringWidth = isSelected ? 2.5 : 1.2;
  const scale = isSelected ? 1.15 : 1;
  const html = `
    <div style="display:flex;flex-direction:column;align-items:center;pointer-events:none;transform:scale(${scale});transform-origin:center bottom;">
      <div style="
        background:${isSelected ? "#0d7d6f" : "#12151b"};
        color:${isSelected ? "#0b1020" : "#e5e7eb"};
        border:1px solid ${isSelected ? "#0d7d6f" : "rgba(255,255,255,0.3)"};
        font-size:14px; font-weight:800; padding:4px 11px; border-radius:999px;
        white-space:nowrap; margin-bottom:4px; box-shadow:0 2px 8px rgba(0,0,0,.55);
        letter-spacing:.01em;
      ">${label}</div>
      <svg width="56" height="56" viewBox="0 0 26 26" style="filter:drop-shadow(0 2px 5px rgba(0,0,0,.5));">
        <rect x="1" y="9" width="14" height="8" rx="1.5" fill="${statusColor}" stroke="${ringColor}" stroke-width="${ringWidth}"/>
        <path d="M15 11 h6 l3 4 v2 h-9 z" fill="${statusColor}" stroke="${ringColor}" stroke-width="${ringWidth}"/>
        <rect x="16.5" y="12" width="3.5" height="3" fill="#dff1ea" opacity="0.85"/>
        <circle cx="6" cy="18" r="2.3" fill="#0b0d12" stroke="#e5e7eb" stroke-width="0.8"/>
        <circle cx="20" cy="18" r="2.3" fill="#0b0d12" stroke="#e5e7eb" stroke-width="0.8"/>
      </svg>
    </div>`;
  return L.divIcon({ html, className: "", iconSize: [130, 88], iconAnchor: [65, 72] });
}

// Same silhouette as truckIcon, but dashed/translucent and violet -- "this
// is where the truck WILL be, not where it is." For a truck already
// chained onto a second load (see selectedTruckLoads' own sort/comment),
// its live GPS position is wherever it happens to be finishing the FIRST
// load -- often hundreds of miles from where the second load's own
// deadhead leg actually starts. Without a distinct marker for that, the
// second load's map just showed one solid truck icon sitting in what
// looked like the middle of nowhere relative to its own route, with
// nothing explaining why. A soft pulse (SVG SMIL, no external stylesheet
// needed) is the one deliberate motion accent on this map -- reads as
// "projected," not "live," at a glance.
function ghostTruckIcon(label, sublabel) {
  // First pass used a near-transparent (14% opacity) label background --
  // reads as "barely there" against light map tiles instead of "distinct
  // from the live truck." Same solid-dark label treatment truckIcon's own
  // pill already uses (so it's just as readable against ANY map color),
  // with the "projected, not live" cue carried entirely by the dashed
  // violet border + dashed truck silhouette instead of by transparency.
  const html = `
    <div style="display:flex;flex-direction:column;align-items:center;pointer-events:none;">
      <div style="
        background:#241a44; color:#ede9fe;
        border:1.5px dashed #a78bfa;
        font-size:12.5px; font-weight:800; padding:4px 11px; border-radius:999px;
        white-space:nowrap; margin-bottom:3px; letter-spacing:.01em;
        box-shadow:0 2px 8px rgba(0,0,0,.5);
      ">${label}</div>
      ${sublabel ? `<div style="
        background:#1a1430; color:#c4b5fd; font-size:10.5px; font-weight:700;
        padding:2px 9px; border-radius:999px; white-space:nowrap; margin-bottom:5px;
        border:1px solid rgba(167,139,250,0.55); box-shadow:0 2px 6px rgba(0,0,0,.45);
      ">${sublabel}</div>` : ""}
      <svg width="50" height="50" viewBox="0 0 26 26" style="filter:drop-shadow(0 2px 5px rgba(0,0,0,.55));">
        <g fill="none" stroke="#a78bfa" stroke-width="1.4" stroke-dasharray="2.2 2">
          <circle cx="13" cy="13" r="11" stroke-dasharray="2.5 2.8" opacity="0.6">
            <animate attributeName="r" values="10;12;10" dur="2.6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2.6s" repeatCount="indefinite" />
          </circle>
        </g>
        <rect x="1" y="9" width="14" height="8" rx="1.5" fill="rgba(139,92,246,0.4)" stroke="#c4b5fd" stroke-width="1.3" stroke-dasharray="2 1.4"/>
        <path d="M15 11 h6 l3 4 v2 h-9 z" fill="rgba(139,92,246,0.4)" stroke="#c4b5fd" stroke-width="1.3" stroke-dasharray="2 1.4"/>
        <rect x="16.5" y="12" width="3.5" height="3" fill="#ede9fe" opacity="0.7"/>
        <circle cx="6" cy="18" r="2.2" fill="#1a1430" stroke="#c4b5fd" stroke-width="1.1"/>
        <circle cx="20" cy="18" r="2.2" fill="#1a1430" stroke="#c4b5fd" stroke-width="1.1"/>
      </svg>
    </div>`;
  return L.divIcon({ html, className: "", iconSize: [140, 92], iconAnchor: [70, 76] });
}

// Keeps the map centered on the SELECTED truck, including as it moves --
// but NEVER resets zoom on a live-position poll tick (that read as "it
// zooms out" every ~15s). truckId decides the *kind* of recenter: a real
// switch (or the first-ever selection) gets a full flyTo at the dispatcher's
// current zoom (or the default zoom on first paint); a position update for
// the SAME truck just pans to keep it centered, zoom untouched either way.
// Leaflet markers stop click propagation by default, so this only ever
// fires for a genuine background click, never a click that landed on a
// truck marker.
function MapClickDeselect({ onDeselect }) {
  useMapEvents({ click: () => onDeselect() });
  return null;
}

function MapFlyTo({ lat, lng, zoom, truckId }) {
  const map = useMap();
  const lastTruckIdRef = useRef(null);
  const hasFlownRef = useRef(false);
  useEffect(() => {
    if (lat == null || lng == null) return;
    const isNewTruck = truckId !== lastTruckIdRef.current;
    lastTruckIdRef.current = truckId;
    if (isNewTruck) {
      // First-ever selection gets the default zoom for a nice fly-in; every
      // later switch keeps whatever zoom the dispatcher is already at.
      const targetZoom = hasFlownRef.current ? map.getZoom() : zoom;
      hasFlownRef.current = true;
      map.flyTo([lat, lng], targetZoom, { duration: 0.8 });
    } else {
      map.panTo([lat, lng], { animate: true, duration: 0.8 });
    }
  }, [lat, lng, zoom, truckId, map]);
  return null;
}

// Fits the map to an arbitrary set of points -- used to bring a whole route
// (or several, for the Next Trips "snake" preview) into view together, since
// those points can span much more of the map than a single route's own bounds.
// fitKey identifies "what's being looked at" (truck+load+route selection),
// deliberately excluding raw truck position -- routeSegments/routeViewPoints
// get a fresh points array on every ~15s live-location poll even when
// nothing the dispatcher actually chose has changed, and re-fitting bounds
// on every poll tick reads as the map randomly zooming out while watching.
function MapFitToPoints({ points, fitKey }) {
  const map = useMap();
  const lastKeyRef = useRef(null);
  useEffect(() => {
    if (!points || points.length < 2) return;
    if (fitKey !== undefined && fitKey === lastKeyRef.current) return;
    lastKeyRef.current = fitKey;
    map.fitBounds(L.latLngBounds(points), { padding: [60, 60], maxZoom: 7 });
  }, [points, fitKey, map]);
  return null;
}

// Leaflet sizes its canvas from the container's dimensions at mount time --
// resizing that container later (e.g. the broker-call wizard's map
// maximize toggle) leaves Leaflet drawing at the OLD size until something
// tells it to remeasure. The timeout gives the CSS height change a paint
// cycle to actually land before invalidateSize reads the new box.
function MapInvalidateSize({ trigger }) {
  const map = useMap();
  useEffect(() => {
    const id = setTimeout(() => map.invalidateSize(), 60);
    return () => clearTimeout(id);
  }, [trigger, map]);
  return null;
}

// Color assignment, show/hide state, "isolate one route," and the
// Christmas-lights reveal cycle all live in useRouteVisibility now (see
// src/hooks/useRouteVisibility.js) -- shared by the Route tab, Log Broker
// Call, and Check Load flows instead of three independent copies.

// Small controls bar reused everywhere a set of routes is toggleable:
// "isolate pickup route" (the general "show only this one" from the hook,
// applied to the pickup leg specifically) and the play/stop control for
// the sequential reveal. `hasPickup` hides the isolate-pickup button in
// contexts that don't draw a separate pickup/deadhead leg.
function RouteControls({ visibility, hasPickup = true, cycleMs = 3000 }) {
  const pickupIsolated = visibility.hidden.size > 0 && !visibility.isHidden(PICKUP_ROUTE_KEY)
    && [...visibility.hidden].every((k) => k !== PICKUP_ROUTE_KEY);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {hasPickup && (
        <button
          onClick={() => (pickupIsolated ? visibility.showAll() : visibility.isolate(PICKUP_ROUTE_KEY))}
          title={pickupIsolated ? "Show all routes" : "Show only the pickup route"}
          style={{
            display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600,
            padding: "5px 9px", borderRadius: 7, cursor: "pointer",
            border: `1px solid ${pickupIsolated ? "var(--brand)" : "var(--line)"}`,
            background: pickupIsolated ? "var(--brand)1a" : "var(--card)",
            color: pickupIsolated ? "var(--brand)" : "var(--ink-2)",
          }}
        >
          <Focus size={12} /> {pickupIsolated ? "Showing pickup only" : "Pickup route only"}
        </button>
      )}
      <button
        onClick={() => visibility.toggleCycle(cycleMs)}
        title={visibility.cycling ? "Stop cycling through routes" : "Show each route in turn, one at a time"}
        style={{
          display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600,
          padding: "5px 9px", borderRadius: 7, cursor: "pointer",
          border: `1px solid ${visibility.cycling ? "var(--brand)" : "var(--line)"}`,
          background: visibility.cycling ? "var(--brand)1a" : "var(--card)",
          color: visibility.cycling ? "var(--brand)" : "var(--ink-2)",
        }}
      >
        {visibility.cycling ? <Square size={11} /> : <Play size={11} />} {visibility.cycling ? "Stop" : "Cycle routes"}
      </button>
    </div>
  );
}

function tollIcon(color) {
  const html = `<div style="
    width:16px;height:16px;background:${color};border:2px solid #0b1020;
    border-radius:50%;box-shadow:0 0 0 3px ${color}55, 0 2px 6px rgba(0,0,0,.6);
    display:flex;align-items:center;justify-content:center;
    color:#0b1020;font-size:10px;font-weight:900;line-height:1;
  ">$</div>`;
  return L.divIcon({ html, className: "", iconSize: [16, 16], iconAnchor: [8, 8] });
}

function restAreaIcon() {
  const html = `<div style="
    width:16px;height:16px;background:#22c55e;border:1.5px solid #14532d;
    border-radius:4px;box-shadow:0 1px 3px rgba(0,0,0,.45);
    display:flex;align-items:center;justify-content:center;
    color:#052e16;font-size:10px;font-weight:800;line-height:1;
  ">P</div>`;
  return L.divIcon({ html, className: "", iconSize: [16, 16], iconAnchor: [8, 8] });
}

// Sits at the center of a route-plan rest-zone Circle -- the translucent
// circle alone reads as "an area of interest" but not obviously "stop and
// rest here for HOS," so this puts an unambiguous "REST" badge right on
// it, colored to match the route it belongs to (same swatch as the fuel
// pump icons and the route line itself).
function restZoneIcon(color) {
  const html = `<div style="
    background:${color}; color:#0b1020; font-size:9.5px; font-weight:800;
    letter-spacing:.03em; padding:3px 7px; border-radius:5px;
    border:1.5px solid #0b1020; box-shadow:0 2px 6px rgba(0,0,0,.5);
    white-space:nowrap;
  ">REST</div>`;
  return L.divIcon({ html, className: "", iconSize: [40, 18], iconAnchor: [20, 9] });
}

// A "fuel_and_rest" stop is always grounded at one real, exact station --
// unlike a plain rest_zone (a projected point somewhere along the route,
// radius_miles reflecting genuine uncertainty about exactly where the
// driver stops), it never carries a real radius_miles (see trip_planning.
// py: explicitly set to null once combined with a real station -- "no
// 'somewhere in this area' uncertainty left to show"). So this is
// deliberately a small FIXED decorative ring, not an uncertainty radius --
// just enough to visually read as "this stop is also a rest break," same
// as the REST badge does for a plain rest_zone, without implying the
// truck could stop anywhere in a wide area.
const COMBINED_STOP_RING_MILES = 1.2;

function pinIcon(color, label) {
  const html = `<div style="
    background:${color}; color:#0b1020; font-size:9.5px; font-weight:800;
    width:18px;height:18px;border-radius:50%;display:flex;align-items:center;justify-content:center;
    border:1.5px solid #0b1020; box-shadow:0 1px 3px rgba(0,0,0,.4);
  ">${label}</div>`;
  return L.divIcon({ html, className: "", iconSize: [18, 18], iconAnchor: [9, 9] });
}

export default function DispatcherBoard() {
  const tileUrl = useTileLayerUrl();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUserId = localStorage.getItem("id");
  const { isImpersonating, impersonatedUser } = getImpersonationState();
  const [theme, setTheme] = useState(() => localStorage.getItem(OP_CENTER_THEME_STORAGE_KEY) || "light");
  const toggleTheme = useCallback(() => {
    setTheme((t) => {
      const next = t === "dark" ? "light" : "dark";
      localStorage.setItem(OP_CENTER_THEME_STORAGE_KEY, next);
      return next;
    });
  }, []);
  const themeVars = theme === "dark" ? DARK_THEME_VARS : LIGHT_THEME_VARS;
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState({});
  const [loads, setLoads] = useState([]);
  // Real dispatcher-role users from /users/dispatchers -- see dispatcherOptions.
  const [dispatcherUsers, setDispatcherUsers] = useState([]);
  // Same counts the Alerts page's own header shows -- dispatchers live in
  // this Operational Center, not the DispatcherLayout shell (this page is
  // deliberately its own chrome, see the header below), so the bell needs
  // to exist here too, not just in that other shell's topbar.
  const { counts: alertCounts, alerts: openAlerts, reload: reloadAlerts } = useAlerts(apiBaseUrl, authHeaders);
  const [fuelStations, setFuelStations] = useState([]);
  // state_code -> $/gal diesel excise tax rate, so a station's hover
  // tooltip can break the real pump price into pre-tax price + tax instead
  // of only ever showing the all-in number.
  const [fuelTaxByState, setFuelTaxByState] = useState({});
  const [selectedTruckId, setSelectedTruckId] = useState(null);
  // Tank-capacity capture. Trucks start with NULL tank_capacity_gallons
  // ("unconfirmed" -- the fuel optimizer falls back to 200 until a real number
  // is entered). We prompt dispatch to get the true max gallons from the driver
  // at assign time, and allow editing from the Overview tab. Once saved (PUT
  // /trucks/{id} via the generic API), the next Show-routes recomputes fuel
  // stops with the real capacity.
  const [capacityPrompt, setCapacityPrompt] = useState(null);
  // Inline driver-phone editing (Overview). Motive rarely carries driver phones
  // (~4% populated) so they're hand-entered; Samsara does carry them. This lets
  // dispatch fix/add a number directly. Saved via PUT /drivers/{id}.
  const [editingDriverPhone, setEditingDriverPhone] = useState(false);
  const [driverPhoneDraft, setDriverPhoneDraft] = useState("");
  useEffect(() => {
    setHosDetailsOpen(false);
  }, [selectedTruckId]);

  // Real count of this truck's delivered loads today, for the Overview
  // tab's "Number of Trips Today" row -- delivered loads never show up in
  // `loads` (that state deliberately only holds in_transit rows, see
  // ACTIVE_LOADS_URL's own comment), so this is a small, separate,
  // on-demand fetch scoped to just the selected truck rather than pulling
  // every truck's delivery history into memory. No date filter in the
  // request itself (the filter DSL has no >= operator) -- limit=50 bounds
  // the request to a sane size (no real truck delivers more than that in
  // one day) and the actual "today" check happens client-side below.
  const [tripsToday, setTripsToday] = useState(null);
  useEffect(() => {
    if (!selectedTruckId) {
      setTripsToday(null);
      return;
    }
    let cancelled = false;
    const filterJson = encodeURIComponent(
      JSON.stringify({ conditions: [
        { field: "assigned_truck_id", operator: "is", value: selectedTruckId },
        { field: "status", operator: "is", value: "delivered" },
      ] })
    );
    axios
      .get(`${apiBaseUrl}/search/loads/?filter_json=${filterJson}&limit=50`, authHeaders())
      .then((res) => {
        if (cancelled) return;
        const rows = res.data.records || res.data || [];
        const today = todayIsoUtc();
        setTripsToday(rows.filter((l) => (l.actual_delivery_at || "").slice(0, 10) === today).length);
      })
      .catch(() => { if (!cancelled) setTripsToday(null); });
    return () => { cancelled = true; };
  }, [selectedTruckId]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showFuelStations, setShowFuelStations] = useState(false);
  const [focusTruckOnly, setFocusTruckOnly] = useState(false);
  // Which dispatchers' trucks show on the map -- null until initialized
  // (see the init effect below, once real trucks/dispatcher data is in).
  // Regular dispatchers default to just their own; admins/super_admins
  // default to everyone, since they're meant to see the whole fleet.
  const [visibleDispatcherIds, setVisibleDispatcherIds] = useState(null);
  const [truckStops, setTruckStops] = useState([]);
  const [restAreas, setRestAreas] = useState([]);
  const [showRestAreas, setShowRestAreas] = useState(false);
  const [driverTimeOff, setDriverTimeOff] = useState([]);
  // Map Layers (Focus truck/Fuel stations/Rest areas) and Routes are two
  // independent floating panels, not one shared scrolling box -- Layers is
  // a fixed, small set of toggles a dispatcher wants on hand for every
  // load, so it can't be gated behind however many routes Routes happens
  // to be showing today (confirmed real complaint: had to scroll past a
  // long route list just to reach Fuel stations/Rest areas). Each panel
  // opens by default and scrolls (if at all) independently of the other.
  const [mapLayersPanelOpen, setMapLayersPanelOpen] = useState(true);
  const [routesSectionOpen, setRoutesSectionOpen] = useState(true);
  // Collapsed by default -- a fleet with a real dispatcher roster (a dozen
  // plus, not the 2-3 example dispatchers this defaulted fine for) turned
  // the Map layers panel into a wall of per-dispatcher toggles before you
  // ever reached Fuel stations/Rest areas. Starts closed regardless of
  // fleet size for one consistent behavior; the (N) count in the header
  // and the "you"-tagged row mean there's rarely a need to open it.
  const [dispatcherListOpen, setDispatcherListOpen] = useState(false);
  const [routeMonitorOpen, setRouteMonitorOpen] = useState(false);

  // Drives the live "Resting Xh Ym" timer -- ticks once a minute so it
  // updates on its own without the dispatcher needing to refresh or
  // reselect the truck.
  const [clockNow, setClockNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setClockNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get(`${apiBaseUrl}/trucks/?limit=1000`, authHeaders()),
      axios.get(`${apiBaseUrl}/drivers/?limit=1000`, authHeaders()),
      axios.get(ACTIVE_LOADS_URL, authHeaders()),
      axios.get(`${apiBaseUrl}/fleet/latest-fuel-prices`, authHeaders()),  // current price per station (not the 39k-row history)
      axios.get(`${apiBaseUrl}/truck_stops/?limit=5000`, authHeaders()),
      axios.get(`${apiBaseUrl}/rest_areas/?limit=5000`, authHeaders()),
      axios.get(`${apiBaseUrl}/driver_time_off/?limit=500`, authHeaders()),
    ])
      .then(([truckRes, driverRes, loadRes, fuelRes, stopRes, restRes, timeOffRes]) => {
        const truckRows = (truckRes.data.records || truckRes.data || []).filter((t) => t.current_lat != null);
        const driverRows = driverRes.data.records || driverRes.data || [];
        const driverMap = {};
        driverRows.forEach((d) => (driverMap[d.id] = d));

        setTrucks(truckRows);
        setDrivers(driverMap);
        setLoads(loadRes.data.records || loadRes.data || []);
        setFuelStations(fuelRes.data.records || fuelRes.data || []);
        setTruckStops((stopRes.data.records || stopRes.data || []).filter((s) => s.lat != null));
        setRestAreas((restRes.data.records || restRes.data || []).filter((r) => r.lat != null));
        setDriverTimeOff(timeOffRes.data.records || timeOffRes.data || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));

    // Independent, best-effort -- a fuel station's price/tax breakdown is
    // a nice-to-have on the hover tooltip, not something that should ever
    // take the whole board down if this specific table isn't reachable
    // (e.g. an older ACL grant that hasn't picked up this table yet).
    axios
      .get(`${apiBaseUrl}/state_fuel_tax_rates/?limit=500`, authHeaders())
      .then((res) => {
        const rows = res.data.records || res.data || [];
        const byState = {};
        rows.forEach((r) => {
          if (r.state_code && r.diesel_tax_rate_per_gallon != null) {
            byState[r.state_code] = Number(r.diesel_tax_rate_per_gallon);
          }
        });
        setFuelTaxByState(byState);
      })
      .catch(() => {});

    // Real dispatcher-role users -- independent, best-effort. Without
    // this, dispatcherOptions below only ever knew about dispatchers
    // already assigned to SOME truck (derived from trucks.dispatcher_id
    // already on file), so a brand-new dispatcher with zero trucks yet
    // could never be picked in "Assign dispatcher." Confirmed real.
    axios
      .get(`${apiBaseUrl}/users/dispatchers`, authHeaders())
      .then((res) => setDispatcherUsers(res.data.records || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set Availability is a real-time "back at X" override meant for an
  // unplanned gap -- during ACTIVE planned time off (already started, not
  // just scheduled for later), the time-off end date is the more
  // authoritative signal, so the control is disabled rather than letting
  // the two sources of truth disagree. Merely upcoming/future time off does
  // NOT disable it -- the driver is still normally working until it starts.
  function activeTimeOffFor(driverId) {
    if (!driverId) return null;
    const now = new Date();
    return driverTimeOff.find((r) => {
      const did = r.driver_id?.id || r.driver_id;
      if (did !== driverId) return false;
      const start = parseUtcNaive(r.start_datetime);
      const end = parseUtcNaive(r.end_datetime);
      return start && end && start <= now && now <= end;
    }) || null;
  }

  // Broader than activeTimeOffFor on purpose: ANY time-off row that hasn't
  // ENDED yet (current OR still-upcoming) -- this is the "ON PTO" Fleet
  // Panel chip's own signal, and matches the same widened condition the
  // backend's match-truck-for-load/find-next-load now use, so a truck
  // flagged here is exactly the set of drivers currently excluded from new
  // matches. A time-off row that hasn't started yet still counts: booking
  // a load as "his last one" is meant to take a driver off the board
  // immediately, not just once the load itself delivers.
  function hasUpcomingTimeOff(driverId) {
    if (!driverId) return false;
    const now = new Date();
    return driverTimeOff.some((r) => {
      const did = r.driver_id?.id || r.driver_id;
      if (did !== driverId) return false;
      const end = parseUtcNaive(r.end_datetime);
      return end && end > now;
    });
  }

  // Mirrors LoadBoard.jsx's isOverduePto/latestPtoRowIdByDriverId -- a
  // multi-day stretch entered as several adjacent day rows means only the
  // driver's LATEST-ending 'pto' row can ever be "the" thing to confirm,
  // not each day in isolation. Deliberately keyed off "has this window
  // STARTED" rather than "has it ENDED": assigning a load to this driver
  // right now means they're back working, whether that's exactly on
  // schedule or a few days early, and either way nobody has told the
  // system yet -- see confirm-driver-return's own docstring on why this
  // must stay an explicit action, never inferred from the assignment
  // itself.
  const latestPtoRowIdByDriverId = useMemo(() => {
    const map = {};
    driverTimeOff.forEach((r) => {
      if (r.reason !== "pto") return;
      const did = r.driver_id?.id || r.driver_id;
      const end = parseUtcNaive(r.end_datetime);
      if (!did || !end) return;
      const current = map[did];
      if (!current || end > current.end) map[did] = { id: r.id, end };
    });
    return map;
  }, [driverTimeOff]);

  function unconfirmedPtoFor(driverId) {
    if (!driverId) return null;
    const row = driverTimeOff.find((r) => r.id === latestPtoRowIdByDriverId[driverId]?.id);
    if (!row || row.return_confirmed_at) return null;
    const start = parseUtcNaive(row.start_datetime);
    if (!start || start > new Date()) return null; // hasn't started yet -- nothing to confirm
    return row;
  }

  // Shared by every load-assignment entry point (Check Load, Log Broker
  // Call) -- pauses the click behind PtoReturnConfirmPopup when the driver
  // about to be assigned has unconfirmed home time, and runs `proceed`
  // (whatever that flow's own next step is) immediately/unchanged
  // otherwise, so the common no-PTO case is completely untouched.
  const [ptoReturnPrompt, setPtoReturnPrompt] = useState(null); // { row, driverName, proceed }
  const [ptoReturnConfirming, setPtoReturnConfirming] = useState(false);
  function guardUnconfirmedPtoReturn(driverId, driverName, proceed) {
    const row = unconfirmedPtoFor(driverId);
    if (!row) { proceed(); return; }
    setPtoReturnPrompt({ row, driverName, proceed });
  }
  function confirmPtoReturnAndProceed() {
    if (!ptoReturnPrompt) return;
    const { row, proceed } = ptoReturnPrompt;
    setPtoReturnConfirming(true);
    axios
      .post(`${apiBaseUrl}/scripted/fleet/confirm-driver-return`, { id: row.id }, authHeaders())
      .then(() => {
        reloadTrucksAndDrivers();
        setPtoReturnPrompt(null);
        proceed();
      })
      .catch((err) => toast.error(err.response?.data?.error || err.response?.data?.detail || "Couldn't confirm return"))
      .finally(() => setPtoReturnConfirming(false));
  }

  function reloadLoads() {
    return axios
      .get(ACTIVE_LOADS_URL, authHeaders())
      .then((res) => setLoads(res.data.records || res.data || []))
      .catch(() => {});
  }

  // Real stops between a load's first pickup and last drop (see load_stops'
  // own migration docstring) -- [] for a normal single-pickup/single-drop
  // load, which is the vast majority, so the Route tab's plain From/To view
  // stays completely untouched for those. Keyed by load id so switching
  // between loads doesn't show a stale previous load's stops for a beat.
  const [loadStopsByLoadId, setLoadStopsByLoadId] = useState({});
  function reloadLoadStops(loadId) {
    if (!loadId) return;
    axios
      .get(`${apiBaseUrl}/load_stops/?load_id=${loadId}&limit=50`, authHeaders())
      .then((res) => {
        const rows = (res.data.records || res.data || []).slice().sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        setLoadStopsByLoadId((prev) => ({ ...prev, [loadId]: rows }));
      })
      .catch(() => {});
  }

  // Truck positions/HOS come from the Motive/Samsara sync jobs running in the
  // background -- without this, the map only ever showed whatever was true at
  // page load. Loads are refreshed on this SAME tick now (not only on user
  // action): the stop-detection engine writes arrival/dwell/departure onto the
  // load rows every sync, and Operational Center's Stop Intelligence reads
  // those columns -- so without a periodic loads refresh a truck could arrive
  // at a facility in the backend and OC would not reflect it until a manual
  // reload. In-transit loads are a small set (~tens), so this is cheap; the
  // candidate alerts have their own 60s poll (useAlerts).
  function reloadTrucksAndDrivers() {
    Promise.all([
      axios.get(`${apiBaseUrl}/trucks/?limit=1000`, authHeaders()),
      axios.get(`${apiBaseUrl}/drivers/?limit=1000`, authHeaders()),
      axios.get(`${apiBaseUrl}/driver_time_off/?limit=500`, authHeaders()),
    ])
      .then(([truckRes, driverRes, timeOffRes]) => {
        const truckRows = (truckRes.data.records || truckRes.data || []).filter((t) => t.current_lat != null);
        const driverRows = driverRes.data.records || driverRes.data || [];
        const driverMap = {};
        driverRows.forEach((d) => (driverMap[d.id] = d));
        setTrucks(truckRows);
        setDrivers(driverMap);
        setDriverTimeOff(timeOffRes.data.records || timeOffRes.data || []);
      })
      .catch(() => {});
  }

  useEffect(() => {
    const id = setInterval(() => {
      reloadTrucksAndDrivers();
      reloadLoads();   // keep stop-detection state (arrival/dwell/departure) fresh
    }, 180000);   // every 3 min -- matches the Motive/Samsara sync cadence (truck
                  //  positions/HOS only change that often), and stops the Current
                  //  Load map+route from re-fetching/recalculating every 45s while
                  //  a dispatcher is reading it.
    return () => clearInterval(id);
  }, []);

  // Delivered/cancelled are the two ways a load's saved route plan stops
  // being "the active trip" -- the route_options rows themselves aren't
  // deleted (still useful history), this just moves the load out of the
  // in-progress state a dispatcher is actively managing.
  function setLoadStatus(loadId, status) {
    // "Delivered" is also the trip's real closeout moment -- stamp
    // actual_delivery_at here the same way confirmPickup stamps
    // actual_pickup_at, so both halves of the real (not GPS-guessed) trip
    // timeline end up recorded. A generic field PUT is enough for this one
    // (unlike pickup, there's no separate business validation to enforce
    // and it's terminal -- once delivered/cancelled the button disappears,
    // so there's no double-click/idempotency concern to guard against).
    // We deliberately PRESERVE delivery_arrival_detected_at here (physical
    // evidence of when GPS first saw the truck at the receiver). It must
    // survive administrative confirmation for future shadow analysis /
    // facility learning. Nothing keys off it being NULL: the OC "at facility"
    // view gates on actual_delivery_at (stopIntel.deriveStopState), and the
    // backend re-processing/recovery/autoconfirm all gate on actual_delivery_at.
    const body =
      status === "delivered"
        ? { status, actual_delivery_at: new Date().toISOString() }
        : { status };
    return axios
      .put(`${apiBaseUrl}/loads/${loadId}`, body, authHeaders())
      .then(() => {
        // Optimistically drop the load from local state the instant the PUT
        // succeeds, so it disappears from the board RIGHT AWAY -- `loads`
        // only ever holds in_transit rows (see ACTIVE_LOADS_URL), and both
        // "delivered" and "cancelled" are terminal, so the just-updated row
        // no longer belongs here either way. Without this the row lingered
        // on screen until reloadLoads()'s follow-up GET returned, which
        // re-pulls up to 2000 in_transit rows and can take several seconds
        // under load -- dispatchers read that lag as "cancel didn't work."
        setLoads((prev) => prev.filter((l) => (l.id || l) !== loadId));
        reloadLoads();   // reconcile with the server in the background
      })
      .catch(() => {});
  }

  // Cancel/Deliver/Confirm-pickup all go through this shared "are you
  // sure" dialog instead of firing straight from the triggering button --
  // see ConfirmActionModal's own comment for why. pendingConfirmAction
  // holds everything the dialog needs to render itself; the actual
  // mutation only runs once the dispatcher hits the colored confirm
  // button, via runPendingConfirmAction below.
  //
  // pendingConfirmActionRef mirrors the state but updates synchronously
  // (state doesn't, inside an already-scheduled .finally callback) -- it's
  // what lets dismissConfirmAction below stay clickable even while a
  // request is in flight. Without it, "Never mind" had to stay disabled
  // until that request settled, and this app's backend genuinely has slow
  // moments (confirmed live: a plain GET took 4+ seconds under load) --
  // that turned an ordinary slow response into a dialog the dispatcher
  // couldn't back out of at all. Dismissing doesn't cancel the underlying
  // request (fire-and-forget always finishes on its own), it only closes
  // the dialog -- so runPendingConfirmAction's own .finally has to check
  // the ref before touching state, or a slow FIRST request finishing late
  // would wrongly close whatever SECOND dialog the dispatcher opened next.
  const pendingConfirmActionRef = useRef(null);
  const [pendingConfirmAction, setPendingConfirmAction] = useState(null);
  const [confirmActionBusy, setConfirmActionBusy] = useState(false);

  function openConfirmAction(action) {
    pendingConfirmActionRef.current = action;
    setPendingConfirmAction(action);
  }

  function dismissConfirmAction() {
    pendingConfirmActionRef.current = null;
    setConfirmActionBusy(false);
    setPendingConfirmAction(null);
  }

  function requestCancelLoad(loadId, laneLabel) {
    openConfirmAction({
      tone: "danger",
      title: "Cancel this load?",
      description: laneLabel
        ? `This cancels ${laneLabel}. The truck stays unassigned and this can't be undone from here.`
        : "This cancels the load. The truck stays unassigned and this can't be undone from here.",
      confirmLabel: "Yes, cancel load",
      run: () => setLoadStatus(loadId, "cancelled"),
    });
  }

  function requestDeliverLoad(loadId, laneLabel) {
    openConfirmAction({
      tone: "success",
      title: "Confirm delivery?",
      description: laneLabel
        ? `This marks ${laneLabel} as delivered and closes the load out.`
        : "This marks the load as delivered and closes it out.",
      confirmLabel: "Yes, mark delivered",
      run: () => setLoadStatus(loadId, "delivered"),
    });
  }

  function requestConfirmStop(loadId, stopId, { label, isPickup } = {}) {
    openConfirmAction({
      tone: "success",
      title: isPickup ? "Confirm pickup?" : "Confirm arrival?",
      description: label
        ? `This records the real ${isPickup ? "pickup" : "arrival"} time at ${label}.`
        : `This records the real ${isPickup ? "pickup" : "arrival"} time for this stop.`,
      confirmLabel: isPickup ? "Yes, confirm pickup" : "Yes, confirm arrival",
      run: () => confirmPickup(loadId, stopId),
    });
  }

  function runPendingConfirmAction() {
    if (!pendingConfirmAction || confirmActionBusy) return;
    const action = pendingConfirmAction;
    setConfirmActionBusy(true);
    Promise.resolve(action.run()).finally(() => {
      if (pendingConfirmActionRef.current === action) {
        pendingConfirmActionRef.current = null;
        setConfirmActionBusy(false);
        setPendingConfirmAction(null);
      }
    });
  }

  const [confirmingPickupLoadId, setConfirmingPickupLoadId] = useState(null);

  // Dispatcher-confirmed (and, once a driver mobile app exists, driver-
  // confirmable -- same endpoint, different authenticated caller) pickup
  // moment. Real signal instead of the live-GPS-proximity guess
  // truckStatus() otherwise falls back to -- see that function.
  // stopId omitted -- the load's own first pickup (no load_stops row, see
  // that table's own migration docstring); given -- a real load_stops row
  // for a multi-stop load, which flips the load to "delivered" itself once
  // it's the last one in sequence (see /fleet/confirm-stop).
  function confirmPickup(loadId, stopId) {
    if (!loadId || confirmingPickupLoadId) return;
    setConfirmingPickupLoadId(stopId || loadId);
    return axios
      .post(`${apiBaseUrl}/scripted/fleet/confirm-stop`, { load_id: loadId, stop_id: stopId || undefined }, authHeaders())
      .then(() => {
        reloadLoads();
        if (stopId) reloadLoadStops(loadId);
      })
      .catch((err) => toast.error(err.response?.data?.error || err.response?.data?.detail || "Couldn't confirm this stop"))
      .finally(() => setConfirmingPickupLoadId(null));
  }

  // Availability override lives on whichever entity actually has a row to
  // attach it to: the driver, when this truck has one (it's a statement
  // about that person, and should follow them if they're reassigned) --
  // or the truck itself, when there's no driver at all (the common "empty
  // truck, nobody assigned yet" case, where there's no driver record to
  // hang a note on in the first place).
  function saveAvailability(entityType, entityId, availableAtLocal, note) {
    if (!availableAtLocal) return;
    // datetime-local gives a naive string in the dispatcher's OWN browser
    // clock -- convert to a real UTC instant before storing, the same
    // naive-UTC-column convention every other timestamp in this app uses.
    const availableAtUtc = new Date(availableAtLocal);
    if (Number.isNaN(availableAtUtc.getTime())) return;
    setSavingAvailability(true);
    axios
      .put(
        `${apiBaseUrl}/${entityType}/${entityId}`,
        { dispatcher_available_at: availableAtUtc.toISOString(), dispatcher_availability_note: note || null },
        authHeaders()
      )
      .then(() => {
        reloadTrucksAndDrivers();
        setAvailabilityFormOpen(false);
        setAvailabilityFormAt("");
        setAvailabilityFormNote("");
      })
      .catch(() => toast.error("Couldn't save availability"))
      .finally(() => setSavingAvailability(false));
  }

  function clearAvailability(entityType, entityId) {
    axios
      .put(`${apiBaseUrl}/${entityType}/${entityId}`, { dispatcher_available_at: null, dispatcher_availability_note: null }, authHeaders())
      .then(() => reloadTrucksAndDrivers())
      .catch(() => toast.error("Couldn't clear availability"));
  }

  // Shared by the booking-flow "going on time off after this load" toggle
  // AND the repointed Set Availability button -- both ultimately create the
  // same real driver_time_off row (via the same /fleet/save-time-off
  // endpoint the Driver Calendar itself uses), just anchored at whatever
  // load's delivery counts as "his last." start_local is derived FROM the
  // already-known delivery UTC instant (not typed), then round-tripped
  // through the dispatcher's own browser timezone -- simpler than resolving
  // each driver's real timezone here (that's still available on the Driver
  // Calendar itself for a precise edit later), and start/end are stored as
  // one real UTC instant regardless of which zone was used to write them.
  function createTimeOffAfterLoad(driverId, deliveryIsoLike, endLocalInputValue, note) {
    if (!driverId || !deliveryIsoLike || !endLocalInputValue) return Promise.reject(new Error("missing fields"));
    const deliveryDate = parseUtcNaive(deliveryIsoLike);
    if (!deliveryDate) return Promise.reject(new Error("invalid delivery date"));
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startLocal = zonedInputParts(deliveryDate, tz);
    return axios.post(
      `${apiBaseUrl}/scripted/fleet/save-time-off`,
      { driver_id: driverId, start_local: startLocal, end_local: `${endLocalInputValue}:00`, timezone: tz, note: note || null },
      authHeaders()
    );
  }

  // Composes the dispatcher-editable SMS text pushed to a driver at load
  // assignment -- only the "fuel" stops from the chosen route's trip plan
  // (rest/HOS stops aren't fuel-relevant to a driver checking "where do I
  // gas up"), in the same order the driver will actually reach them.
  function composeFuelStopsMessage(driverName, originLocation, destinationLocation, stops) {
    const firstName = driverName ? driverName.split(" ")[0] : "there";
    const trip = [originLocation, destinationLocation].filter(Boolean).join(" -> ") || "this trip";
    // fuel_and_rest stops are real fuel events too (a fuel stop and a
    // mandatory rest break landing at the same location) -- excluding them
    // would silently drop a real "mandatory" stop from the driver's list
    // whenever the two happened to combine.
    const fuelStops = (stops || []).filter((s) => s.type === "fuel" || s.type === "fuel_and_rest");
    if (fuelStops.length === 0) {
      return `Hi ${firstName}, heads up -- no fuel stops are planned for your trip ${trip}.`;
    }
    const lines = fuelStops.map((s, i) => {
      const cityState = [s.city, s.state].filter(Boolean).join(", ");
      const loc = [s.name, cityState].filter(Boolean).join(" - ") || "Fuel stop";
      const gal = s.gallons_purchased != null ? ` (~${Math.round(s.gallons_purchased)} gal)` : "";
      const mapLink = s.lat != null && s.lng != null ? `\n   https://maps.google.com/?q=${s.lat},${s.lng}` : "";
      return `${i + 1}) ${loc}${gal}${mapLink}`;
    });
    return `Hi ${firstName}, for your trip ${trip}, please fuel up at these mandatory stops:\n${lines.join("\n")}\n\nIf you don't think you can safely reach the next stop, fuel up before you get low -- don't risk running out. Let your dispatcher know if anything comes up.`;
  }

  function sendFuelStopsSms(driverId, message, phone, meta) {
    const payload = { driver_id: driverId, message };
    // Only sent when the driver has no phone on file -- the dispatcher
    // typed one into PushFuelStopsPopup's inline field, and the backend
    // persists it to the driver row before sending (see send-driver-sms).
    if (phone) payload.phone = phone;
    // AIVAN pilot linkage (backward compatible: fields added ONLY when present, so existing
    // 3-arg callers are unchanged). `meta` MUST be the values PINNED to the recommendation the
    // dispatcher saw at compose time (see pinFuelSmsMeta) -- never re-read live at send time, so a
    // background recalculation cannot silently swap the associated load/truck/decision version.
    // The backend (send-driver-sms -> pilot_capture_delivery) validates the association and records
    // mismatch / stale-plan flags WITHOUT changing send behavior.
    if (meta) {
      if (meta.load_id != null) payload.load_id = meta.load_id;
      if (meta.truck_id != null) payload.truck_id = meta.truck_id;
      if (meta.decision_id != null) payload.decision_id = meta.decision_id;
    }
    return axios.post(`${apiBaseUrl}/scripted/fleet/send-driver-sms`, payload, authHeaders());
  }

  // Per-load map of the latest route-decision version id returned by save-route-plan / select-route
  // (the pilot capture_route key). Keyed by load id; read at SMS-compose time so the pinned
  // decision_id refers to the exact saved plan the dispatcher is looking at. Never guessed.
  const routeDecisionByLoadRef = useRef({});

  // Remember the decision_id a save-route-plan / select-route response returned, keyed by its load.
  // Pass-through: returns the axios response so it can sit transparently inside a promise chain.
  function rememberRouteDecision(loadId, res) {
    const did = res && res.data && res.data.decision_id;
    if (loadId != null && did != null) routeDecisionByLoadRef.current[loadId] = did;
    return res;
  }

  // Snapshot the recommendation-version identity from the plan the dispatcher is looking at, so it
  // can be PINNED into component state next to the composed SMS text and sent verbatim (never
  // re-read live at send). `decision_id` is the capture_route version id save-route-plan/select-route
  // returned for THIS load; absent (and omitted) when we have none -- never guessed.
  function pinFuelSmsMeta(preview, truckId, loadId) {
    const d = (preview && preview.data) || preview || {};
    const m = {};
    const lid = loadId != null ? loadId : d.load_id;
    if (lid != null) m.load_id = lid;
    if (truckId != null) m.truck_id = truckId;
    const mapped = lid != null ? routeDecisionByLoadRef.current[lid] : null;
    const did = mapped != null ? mapped : d.decision_id;
    if (did != null) m.decision_id = did;   // the saved plan's capture_route version id for this load
    return Object.keys(m).length ? m : undefined;
  }

  const enriched = useMemo(() => {
    return trucks.map((t) => {
      const driver = drivers[t.current_driver_id?.id] || null;
      // A delivered/cancelled load must stop counting as "this truck's
      // active load" -- without this filter, closing or cancelling a load
      // via setLoadStatus() never frees the truck up in the UI (it keeps
      // showing the old route/stops indefinitely, even though the DB
      // write succeeded).
      //
      // A truck can also legitimately have MORE than one active load at
      // once -- a dispatcher books load 2 before load 1's delivery is
      // confirmed. The plain .find() this replaced grabbed whichever one
      // happened to come first in the API response (no ORDER BY from
      // /search/loads/), so a truck already loaded on trip 1 could show
      // trip 2's "heading to pickup" state instead. Mirror
      // backend/fleet_stats.py::active_load_ids_by_truck exactly: among a
      // truck's active loads, earliest pickup_datetime wins (nulls sort
      // last), created_at as the tiebreak.
      const activeLoadsForTruck = loads.filter(
        (l) => l.assigned_truck_id?.id === t.id && isActiveLoadStatus(l.status)
      );
      activeLoadsForTruck.sort((a, b) => {
        const aPickup = parseUtcNaive(a.pickup_datetime)?.getTime() ?? Infinity;
        const bPickup = parseUtcNaive(b.pickup_datetime)?.getTime() ?? Infinity;
        if (aPickup !== bPickup) return aPickup - bPickup;
        const aCreated = parseUtcNaive(a.created_at)?.getTime() ?? Infinity;
        const bCreated = parseUtcNaive(b.created_at)?.getTime() ?? Infinity;
        return aCreated - bCreated;
      });
      const load = activeLoadsForTruck[0] || null;
      const status = truckStatus(load, t);
      const fuelPct = t.tank_capacity_gallons ? ((t.current_fuel_gallons || 0) / t.tank_capacity_gallons) * 100 : null;
      const econEstimate = load ? estimateLoadEconomics(t, driver, load, status) : null;
      const flags = {
        lowFuel: fuelPct != null && fuelPct < LOW_FUEL_PCT,
        lowHos: driver?.hos_hours_remaining != null && Number(driver.hos_hours_remaining) < LOW_HOS_HOURS,
        behindSchedule: !!econEstimate?.behindSchedule,
        noDriver: !driver,
        noDispatcher: !t.dispatcher_id?.id,
        // Has a driver but nothing to do right now -- distinct from
        // "No driver" (which is its own filter for the opposite gap).
        emptyTruck: status === "free" && !!driver,
        resting: !!driver && isRestingDutyStatus(driver.hos_duty_status),
        // onPto: actively away RIGHT NOW. upcomingPto: has a real leave
        // booked but it hasn't started yet -- still normally working today.
        // Split from a single "On PTO" flag per dispatcher feedback: a
        // driver whose leave starts a week out showing up under a label
        // that plainly means "away right now" read as wrong. The UNION of
        // both is still the same "current OR future, not yet ended"
        // condition the backend uses to exclude a driver from new matches.
        onPto: !!activeTimeOffFor(driver?.id),
        upcomingPto: !activeTimeOffFor(driver?.id) && hasUpcomingTimeOff(driver?.id),
      };
      const atRisk = flags.lowFuel || flags.lowHos || flags.behindSchedule;
      return { truck: t, driver, load, status, fuelPct, econEstimate, flags, atRisk };
    });
  }, [trucks, drivers, loads, driverTimeOff]);

  // Pickup/delivery confirmation reminders -- surfaces trucks that are
  // physically at (or near) a pickup/delivery point but still waiting on a
  // dispatcher-confirmed pickup/delivery, now that truckStatus() no longer
  // auto-flips status from GPS proximity alone (see that function's own
  // comment). Deliberately scoped to THIS user's own trucks via
  // truck.dispatcher_id, ALWAYS -- independent of visibleDispatcherIds (the
  // "view other dispatchers' trucks" toggle below) -- confirming a pickup
  // isn't something a dispatcher should be nagged about for a truck that
  // isn't theirs to manage, even while they're peeking at someone else's.
  //
  // Dwell time (how long has it actually been sitting there) comes from
  // load.pickup_arrival_detected_at / delivery_arrival_detected_at --
  // stamped server-side by motive_sync.py/samsara_sync.py's own
  // _check_pickup_delivery_arrival on every position-sync tick, NOT tracked
  // client-side. An earlier version tracked "since when" in a browser ref,
  // which reset to zero on every page refresh -- confirmed real gap: a
  // dispatcher who refreshes more often than the dwell threshold would
  // otherwise NEVER see a truck escalate past "Approaching", no matter how
  // long it had genuinely been sitting there. toastedRef below is the only
  // thing still tracked client-side, and deliberately so -- it's just
  // "don't show the same toast twice in this tab session," not a fact
  // about the truck, so it's fine (even correct) for it to reset on reload,
  // same as the Announcements banner's own reappear-on-reload design.
  const toastedRef = useRef(new Set());
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // STOP INTELLIGENCE -- single source of truth for Operational Center's
  // stop awareness, computed by the pure stopIntel.partitionStopIntel from the
  // backend-authoritative stop columns + the possible_missed_* candidate
  // alerts, scoped to THIS dispatcher's own trucks (reuses the exact
  // truck.dispatcher_id === currentUserId model). nowTick (30s) advances the
  // live dwell durations without any extra API traffic.
  const stopIntel = useMemo(
    () => partitionStopIntel(enriched, openAlerts, currentUserId, nowTick),
    [enriched, openAlerts, currentUserId, nowTick]
  );
  const actionCount = stopIntel.actionRequired.length;
  const atFacilityCount = stopIntel.atFacility.length;
  const stopIntelCount = actionCount + atFacilityCount;
  // Urgency (red/bell) depends ONLY on ACTION REQUIRED -- an emphasized
  // AT FACILITY dwell is awareness, never an alarm and never forces the panel.
  const hasAction = actionCount > 0;

  // Auto-expand when a NEW actionable alert appears (tracked by stable
  // alert_event_id, not count) -- so 0->A, A->A+B, and even an A->B swap that
  // leaves the count at 1 all reopen the panel, while the SAME action on the
  // next poll never overrides a dispatcher's manual collapse, and AT FACILITY
  // changes never open it. Only force-open here; never auto-collapse.
  const [reminderPanelOpen, setReminderPanelOpen] = useState(false);
  const prevActionIdsRef = useRef(new Set());
  useEffect(() => {
    const { open, ids } = computeAutoExpand(prevActionIdsRef.current, stopIntel.actionRequired);
    if (open) setReminderPanelOpen(true);
    prevActionIdsRef.current = ids;
  }, [stopIntel.actionRequired]);

  // Review drawer + the canonical Confirm/Dismiss. Confirm/Dismiss ALWAYS go
  // through /fleet/resolve-missed-stop (no new lifecycle mutation path); on
  // completion we refresh both the alert feed and the loads so OC updates in
  // place. Stale/race (load advanced elsewhere) is handled by the endpoint,
  // which resolves the alert as lifecycle_advanced -- we just surface a note.
  const [missedReview, setMissedReview] = useState(null);
  const [resolvingMissed, setResolvingMissed] = useState(false);
  function resolveMissedStop(alertEventId, action) {
    if (!alertEventId || resolvingMissed) return;
    setResolvingMissed(true);
    axios
      .post(`${apiBaseUrl}/scripted/fleet/resolve-missed-stop`, { alert_event_id: alertEventId, action }, authHeaders())
      .then((res) => {
        const d = res.data || {};
        if (d.needs_resolution) {
          toast.error(d.message || "This truck already has a picked-up, undelivered load. Deliver that first.");
          return;
        }
        if (action === "dismiss") toast.success("Dismissed — load left unchanged.");
        else if (d.note) toast.success(`Load was already ${d.note.replace("load already ", "")}.`);
        else toast.success("Confirmed — load updated.");
        setMissedReview(null);
      })
      .catch(() => toast.error("Could not update the load. Please retry."))
      .finally(() => {
        setResolvingMissed(false);
        if (reloadAlerts) reloadAlerts();
        reloadLoads();
      });
  }

  // One-time toast when a NEW candidate appears for one of my trucks, keyed by
  // alert id so it never repeats. Opens the in-OC Review drawer (no navigation
  // to Alerts).
  useEffect(() => {
    stopIntel.actionRequired.forEach((a) => {
      if (a.type !== "possible_missed_pickup" && a.type !== "possible_missed_delivery") return;
      const key = `cand:${a.alert_event_id}`;
      if (toastedRef.current.has(key)) return;
      toastedRef.current.add(key);
      toast.custom(
        (t) => (
          <div style={{ ...styles.reminderToast, opacity: t.visible ? 1 : 0 }}>
            <Bell size={14} style={{ color: "var(--danger)", flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.reminderToastTitle}>
                Truck {a.truck_unit_number || "?"} — possible missed {a.type === "possible_missed_pickup" ? "pickup" : "delivery"}
              </div>
              <div style={styles.reminderToastBody}>{humanEvidence(a)}</div>
              <div style={styles.reminderToastActions}>
                <button type="button" style={styles.reminderToastLink} onClick={() => { toast.dismiss(t.id); setMissedReview(a); }}>Review</button>
              </div>
            </div>
            <X size={13} style={styles.reminderToastClose} onClick={() => toast.dismiss(t.id)} />
          </div>
        ),
        { duration: 12000, position: "top-right" }
      );
    });
  }, [stopIntel.actionRequired]);

  // Union of every real dispatcher-role user (/users/dispatchers, see its
  // own fetch above) and whoever's already assigned to a truck right now
  // (covers the edge case of a non-dispatcher-role user manually set as a
  // truck's dispatcher in the past) -- the source for both the map-layer
  // toggle list and the "Assign dispatcher" dropdown. Used to only be the
  // latter alone, which meant a brand-new dispatcher with zero trucks
  // could never be picked -- confirmed real, that's the actual fix here.
  const dispatcherOptions = useMemo(() => {
    const map = new Map();
    dispatcherUsers.forEach((d) => map.set(d.id, d.name || "Dispatcher"));
    trucks.forEach((t) => {
      const d = t.dispatcher_id;
      if (d?.id && !map.has(d.id)) map.set(d.id, d.display_value || "Dispatcher");
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [trucks, dispatcherUsers]);

  // Everyone starts seeing just their own trucks, admins included -- based
  // on whether the CURRENT USER actually dispatches anything (is present in
  // dispatcherOptions), not on role. An admin who's also assigned as a
  // dispatcher on some trucks gets the same "just mine first" default a
  // regular dispatcher does; an admin who dispatches nothing has no "mine"
  // to default to, so falls back to everyone (same as before) rather than
  // opening to a confusing near-empty view. Either way, the dispatcher
  // toggle panel is right there to broaden visibility afterward. Only
  // initializes once real dispatcher data is in, and only fires once (a
  // later manual toggle shouldn't get silently reset by a later trucks
  // refetch).
  // A "real dispatcher" is a signed-in user who is themselves in the dispatcher
  // list; admins/managers are not. Drives the DEFAULT visibility scope (below).
  const isRealDispatcher = useMemo(
    () => dispatcherOptions.some((d) => d.id === currentUserId),
    [dispatcherOptions, currentUserId]
  );
  const dispatcherVisibilityInitRef = useRef(false);
  useEffect(() => {
    if (dispatcherVisibilityInitRef.current || dispatcherOptions.length === 0) return;
    dispatcherVisibilityInitRef.current = true;
    // Default scope: a real dispatcher sees ONLY their own trucks -- the unassigned
    // pool is OFF by default, but they can opt in via the "Unassigned" toggle in
    // the dispatcher panel. Admins/managers default to everything (unassigned on).
    setVisibleDispatcherIds(
      isRealDispatcher
        ? new Set([currentUserId])
        : new Set([...dispatcherOptions.map((d) => d.id), UNASSIGNED_KEY])
    );
  }, [dispatcherOptions, currentUserId, isRealDispatcher]);

  const dispatcherVisibleTrucks = useMemo(() => {
    if (!visibleDispatcherIds) return enriched;
    return enriched.filter((e) => {
      const did = e.truck.dispatcher_id?.id;
      // Unassigned trucks (no dispatcher on file) show only when the "Unassigned"
      // toggle is on -- OFF by default for dispatchers, ON by default for admins.
      // Assigned trucks follow their own dispatcher's visibility toggle.
      if (!did) return visibleDispatcherIds.has(UNASSIGNED_KEY);
      return visibleDispatcherIds.has(did);
    });
  }, [enriched, visibleDispatcherIds]);

  // Reads off dispatcherVisibleTrucks (the dispatcher-toggle panel's own
  // filtered result), not raw enriched -- these used to ignore the
  // dispatcher toggle entirely, so selecting "just me" in the Map layers
  // panel narrowed the map pins (via mapVisibleTrucks below) but left the
  // left-hand truck list, the status-filter counts, and the free/heading-
  // to-pickup/loaded filter itself still showing the WHOLE fleet --
  // confirmed real, the two were independent pipelines. Now the dispatcher
  // selection is the upstream filter everything else (list, counts,
  // status filter, search) runs through, same as intended.
  const fleetCounts = useMemo(() => {
    const counts = {
      total: 0, free: 0, heading_to_pickup: 0, loaded: 0,
      lowHos: 0, behindSchedule: 0, noDriver: 0, noDispatcher: 0, emptyTruck: 0, resting: 0, onPto: 0, upcomingPto: 0,
    };
    dispatcherVisibleTrucks.forEach((e) => {
      counts.total++;
      counts[e.status]++;
      if (e.flags.lowHos) counts.lowHos++;
      if (e.flags.behindSchedule) counts.behindSchedule++;
      if (e.flags.noDriver) counts.noDriver++;
      if (e.flags.noDispatcher) counts.noDispatcher++;
      if (e.flags.emptyTruck) counts.emptyTruck++;
      if (e.flags.resting) counts.resting++;
      if (e.flags.onPto) counts.onPto++;
      if (e.flags.upcomingPto) counts.upcomingPto++;
    });
    return counts;
  }, [dispatcherVisibleTrucks]);

  const fleetSummary = useMemo(() => {
    let totalProfit = 0;
    dispatcherVisibleTrucks.forEach((e) => {
      if (e.econEstimate) totalProfit += e.econEstimate.profit;
    });
    return { totalProfit };
  }, [dispatcherVisibleTrucks]);

  // Single-select, like statusFilter -- every EXTRA_FILTER_DEFS key reads
  // straight off e.flags[extraFilter]. ("atRisk" used to be a special-cased
  // union filter here; it's no longer a chip, see EXTRA_FILTER_DEFS.)
  const [extraFilter, setExtraFilter] = useState(null);
  // Drives the "Show all" reset action -- true whenever any of the three
  // Fleet Panel filters (status segment, extra-filter chip, search) is
  // narrowing the list away from the full fleet. Deliberately doesn't
  // include visibleDispatcherIds -- that's a separate, deliberate
  // cross-dispatcher visibility toggle (Map layers panel), not a "filter"
  // this reset is meant to touch.
  const hasActiveFleetFilters = statusFilter !== "all" || extraFilter !== null || search.trim().length > 0;

  const visibleList = useMemo(() => {
    const q = search.trim().toLowerCase();
    return dispatcherVisibleTrucks.filter((e) => {
      if (statusFilter !== "all" && e.status !== statusFilter) return false;
      if (extraFilter && !e.flags[extraFilter]) return false;
      if (!q) return true;
      return (
        e.truck.unit_number?.toLowerCase().includes(q) ||
        e.driver?.driver_name?.toLowerCase().includes(q)
      );
    });
  }, [dispatcherVisibleTrucks, statusFilter, extraFilter, search]);

  // Map markers should reflect the SAME status/extraFilter/search filters
  // applied to the left-hand truck list, not just the separate dispatcher-
  // visibility toggle. Confirmed real: filtering the list down to e.g.
  // "Low fuel" trucks left every OTHER truck still plotted on the map,
  // since the map read dispatcherVisibleTrucks (dispatcher-toggle only)
  // instead of the list's own filtered result -- these were two
  // independent filter pipelines that never intersected. Intersecting by
  // id (not just re-filtering enriched a third way) keeps the dispatcher-
  // visibility toggle's own semantics -- an unassigned truck with no
  // dispatcher is never hidden by it -- exactly as-is.
  const mapVisibleTrucks = useMemo(() => {
    const visibleIds = new Set(visibleList.map((e) => e.truck.id));
    return dispatcherVisibleTrucks.filter((e) => visibleIds.has(e.truck.id));
  }, [dispatcherVisibleTrucks, visibleList]);

  const selected = enriched.find((e) => e.truck.id === selectedTruckId) || null;
  const selectedTruck = selected?.truck || null;
  const selectedDriver = selected?.driver || null;
  // Soonest time-off row for this driver that hasn't ended yet (current OR
  // upcoming) -- same "hasn't ENDED" condition as flags.onPto/upcomingPto
  // above, just resolved to the actual row so the Overview tab can show a
  // real from/to range instead of just a yes/no flag. Explicit ask: seeing
  // only a start date (no end) left it unclear when the driver's actually
  // back.
  const selectedDriverPto = useMemo(() => {
    if (!selectedDriver?.id) return null;
    const now = new Date();
    const rows = driverTimeOff.filter((r) => {
      const did = r.driver_id?.id || r.driver_id;
      if (did !== selectedDriver.id) return false;
      const end = parseUtcNaive(r.end_datetime);
      return !!end && end > now;
    });
    if (!rows.length) return null;
    rows.sort((a, b) => (parseUtcNaive(a.start_datetime) || 0) - (parseUtcNaive(b.start_datetime) || 0));
    return rows[0];
  }, [driverTimeOff, selectedDriver]);
  // Every active (non-delivered/cancelled) load assigned to this truck --
  // usually just one, but a truck can have a second load already lined up
  // while still finishing its first (see the busy-truck fuel/rest-stop
  // fix). enriched only ever picks ONE via .find() for quick-glance list
  // purposes; this is the real list the detail panel's load switcher
  // needs. Sorted soonest-pickup-first so "Load 1" reads as "the one
  // that's actually happening first," not insertion order.
  const selectedTruckLoads = useMemo(() => {
    if (!selectedTruckId) return [];
    return loads
      .filter((l) => l.assigned_truck_id?.id === selectedTruckId && isActiveLoadStatus(l.status))
      .sort((a, b) => new Date(a.pickup_datetime || a.created_at) - new Date(b.pickup_datetime || b.created_at));
  }, [loads, selectedTruckId]);
  // The Current Load tab always shows THE truck's genuinely current load --
  // the one it's actually on right now -- never a dispatcher-pickable
  // choice among several. A truck can have a second load already lined up
  // while still finishing its first (see selectedTruckLoads' own comment);
  // that one belongs on the Next Trips tab, not here. in_transit is the
  // unambiguous signal for "current" -- falls back to the soonest-pickup
  // load only when nothing is in_transit yet (e.g. a truck with only
  // upcoming, not-yet-started assignments).
  const selectedLoad = selectedTruckLoads.find((l) => l.status === "in_transit") || selectedTruckLoads[0] || null;
  // Next Trips tab's own list -- deliberately excludes whichever load the
  // Current Load tab is showing (selectedLoad). That load's real route
  // (with actual chosen route type, tolls, fuel/rest stops) already has a
  // home on Current Load; showing it again here too, off a simpler preview
  // line, just produced two different-looking routes for the same trip.
  // "Next Trips" only ever means what's genuinely still ahead of that one.
  const nextTripsList = selectedTruckLoads.filter((l) => l.id !== selectedLoad?.id);
  // Stable refs so the cascade helpers below (deliberately kept with empty
  // dep arrays -- they're fired-and-forgotten from callbacks, not re-created
  // every render) always read the CURRENT truck's load list/selection
  // instead of a stale one captured at callback-definition time.
  const selectedTruckLoadsRef = useRef(selectedTruckLoads);
  selectedTruckLoadsRef.current = selectedTruckLoads;
  const selectedLoadIdRef = useRef(selectedLoad?.id ?? null);
  selectedLoadIdRef.current = selectedLoad?.id ?? null;

  useEffect(() => {
    if (selectedLoad?.id && !(selectedLoad.id in loadStopsByLoadId)) {
      reloadLoadStops(selectedLoad.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLoad?.id]);
  const selectedLoadStops = selectedLoad ? loadStopsByLoadId[selectedLoad.id] || [] : [];

  const routeSegments = useMemo(() => {
    if (!selectedTruck || !selectedLoad) return null;
    if (selectedLoad.origin_lat == null || selectedLoad.destination_lat == null) return null;
    const truckPos = [Number(selectedTruck.current_lat), Number(selectedTruck.current_lng)];
    const originPos = [Number(selectedLoad.origin_lat), Number(selectedLoad.origin_lng)];
    const destPos = [Number(selectedLoad.destination_lat), Number(selectedLoad.destination_lng)];
    return { deadhead: [truckPos, originPos], loaded: [originPos, destPos], originPos, destPos };
  }, [selectedTruck, selectedLoad]);

  const [roadRoute, setRoadRoute] = useState(null);

  useEffect(() => {
    if (!routeSegments) {
      setRoadRoute(null);
      return;
    }
    let cancelled = false;
    setRoadRoute(null);
    (async () => {
      try {
        const [deadhead, loaded] = await Promise.all([
          fetchRoadRoute(...routeSegments.deadhead[0], ...routeSegments.deadhead[1]),
          fetchRoadRoute(...routeSegments.loaded[0], ...routeSegments.loaded[1]),
        ]);
        if (!cancelled) setRoadRoute({ deadhead, loaded });
      } catch (e) {
        if (!cancelled) setRoadRoute(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeSegments]);

  // Actual driven GPS breadcrumbs for the selected load -- the same
  // /fleet/route-monitor feed the Route Monitor modal uses, pulled here so the
  // Current Load tab can overlay what has REALLY been driven (empty approach +
  // loaded-so-far) on top of the planned route, giving a dispatcher immediate
  // visual feedback instead of only the planned start->end line. Keyed on the
  // load id alone (not a live-position dep) so it fetches once per load
  // selection, not on every position poll.
  const [loadBreadcrumbs, setLoadBreadcrumbs] = useState(null);
  useEffect(() => {
    if (!selectedLoad?.id) {
      setLoadBreadcrumbs(null);
      return;
    }
    let cancelled = false;
    setLoadBreadcrumbs(null);
    const toPath = (arr) =>
      (arr || []).map((b) => [b.lat, b.lng]).filter(([la, ln]) => la != null && ln != null);
    axios
      .get(`${apiBaseUrl}/scripted/fleet/route-monitor`, { ...authHeaders(), params: { load_id: selectedLoad.id } })
      .then((res) => {
        if (cancelled) return;
        const d = res.data || {};
        setLoadBreadcrumbs({ loaded: toPath(d.breadcrumbs), deadhead: toPath(d.deadhead_breadcrumbs) });
      })
      .catch(() => {
        if (!cancelled) setLoadBreadcrumbs(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLoad?.id]);

  // Route + fuel/HOS plan for the assigned load. Once computed, this is
  // PERSISTED (route_options table) so re-opening this load later -- the
  // whole point being to stop burning paid HERE calls on a trip that
  // hasn't changed -- reads the saved plan with zero API calls instead of
  // recomputing. Only a real recompute (initial load with nothing saved
  // yet, or an explicit "Refresh routes" click) calls HERE again. The
  // saved plan sticks around for the life of the load (until delivered or
  // cancelled) rather than expiring on its own.
  const [loadEconomics, setLoadEconomics] = useState(null);
  // Show/hide + color + isolate/cycle for the Route tab's map -- shared
  // logic (see useRouteVisibility.js), not a Route-tab-only copy.
  const routeVisibility = useRouteVisibility(
    loadEconomics?.data?.routes_considered,
    loadEconomics?.data?.chosen_route?.route_type
  );
  // Land on a load's Route tab showing the pickup leg AND the route
  // actually being run -- not every alternative candidate stacked on the
  // map at once, but also not just the delivery leg on its own (a
  // dispatcher redirected here right after assigning a load needs to see
  // the truck's whole path to the freight, not just the loaded half of
  // it). A dispatcher who wants to see why a route lost (e.g. compare it
  // to the toll-free one) can still toggle others back on manually; this
  // only sets the default. Keyed on the chosen route type + which
  // load/truck it's for, not the whole loadEconomics object, so it
  // doesn't fight a dispatcher's own toggling on every unrelated re-fetch
  // of the same load's data.
  const chosenRouteTypeForIsolate = loadEconomics?.data?.chosen_route?.route_type;
  useEffect(() => {
    if (chosenRouteTypeForIsolate) routeVisibility.isolate([PICKUP_ROUTE_KEY, chosenRouteTypeForIsolate]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenRouteTypeForIsolate, selectedTruckId, selectedLoad?.id]);
  // Full multi-stop itinerary per real route option -- unlike
  // recommend_fuel_stop (which only ever answers "what's the next fuel
  // stop"), this simulates the whole trip and can return several fuel
  // stops and several mandatory HOS rest breaks, which a coast-to-coast
  // haul genuinely needs. Keyed by route_type: { [route_type]: {loading, data, error} }.
  const [fullTripPlans, setFullTripPlans] = useState({});
  // { hasSaved, savedAt, refreshing } -- drives the "Saved plan" vs
  // "Refresh routes" affordance in the sidebar.
  const [routePlanMeta, setRoutePlanMeta] = useState(null);
  // route_type currently open in the details/explain modal, or null.
  const [routeModalType, setRouteModalType] = useState(null);
  // { loading, messages: [{question, answer}], error, question } for the
  // modal's AI explain + follow-up chat. Reset each time a different
  // route's modal is opened.
  const [routeExplain, setRouteExplain] = useState(null);

  const askRouteExplain = useCallback(
    async (question) => {
      const options = loadEconomics?.data?.routes_considered;
      const route = options?.find((r) => r.route_type === routeModalType);
      if (!route) return;
      const plan = fullTripPlans[routeModalType]?.data;
      // geometry is real HERE polyline data (thousands of [lat,lng] points
      // per leg) -- fine to keep client-side for map rendering, but has no
      // business going over the wire to an endpoint that only reads
      // numeric fields and hands them to an LLM prompt.
      const withoutGeometry = (r) => { const { geometry, ...rest } = r; return rest; };
      setRouteExplain((prev) => ({ ...(prev || { messages: [] }), loading: true, question: "" }));
      try {
        const res = await axios.post(
          `${apiBaseUrl}/scripted/fleet/explain-single-route`,
          {
            load_id: selectedLoad?.id,
            route: { ...withoutGeometry(route), trip_plan: plan },
            all_routes: options.map((r) => ({ ...withoutGeometry(r), trip_plan: fullTripPlans[r.route_type]?.data })),
            question: question || undefined,
            history: (routeExplain?.messages || []).map((m) => ({ question: m.question, answer: m.answer })),
            truck_context: { hourly_value: null },
          },
          authHeaders()
        );
        setRouteExplain((prev) => ({
          loading: false,
          messages: [...(prev?.messages || []), { question: question || "Why is this route ranked here?", answer: res.data.answer }],
        }));
      } catch (err) {
        setRouteExplain((prev) => ({ ...(prev || { messages: [] }), loading: false, error: err.response?.data?.detail || err.message }));
      }
    },
    [loadEconomics, fullTripPlans, routeModalType, selectedLoad, routeExplain]
  );

  useEffect(() => {
    if (!routeModalType) {
      setRouteExplain(null);
      return;
    }
    // Not auto-fetched -- the dispatcher taps "Analyze this route" when they
    // actually want the AI take, instead of every route click firing an LLM call.
    setRouteExplain({ loading: false, messages: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeModalType]);

  function routesFromSavedRows(rows) {
    // Defensive de-dupe by route_type, keeping the newest row (get-route-
    // plan sorts ascending by created_at, so last-in-wins here). Normally
    // save-route-plan supersedes the prior batch before inserting a new
    // one, so there's only ever one active row per type -- but that
    // supersede-then-insert isn't atomic, so two overlapping saves for the
    // same load (e.g. a fast double-click, or a refresh racing a cascade
    // save) can both read "nothing superseded yet" and each insert their
    // own full batch, leaving more than one active row per route_type.
    // Confirmed real: this showed up as every route appearing twice.
    const deduped = {};
    rows.forEach((r) => { deduped[r.route_type] = r; });
    rows = Object.values(deduped);
    return rows.map((r) => ({
      route_type: r.route_type,
      distance_miles: r.distance_miles != null ? Number(r.distance_miles) : null,
      duration_hours: r.duration_hours != null ? Number(r.duration_hours) : null,
      toll_cost: r.toll_cost != null ? Number(r.toll_cost) : 0,
      fuel_cost: r.fuel_cost != null ? Number(r.fuel_cost) : null,
      driver_cost: r.driver_cost != null ? Number(r.driver_cost) : null,
      other_cost: r.other_cost != null ? Number(r.other_cost) : null,
      // Carry the fixed-cost line items through a cascade re-save so a
      // chained-load refresh doesn't blank out insurance/office-payroll/
      // maintenance that an earlier save already stored (see
      // buildRouteOptionsPayload for the full why).
      insurance_cost: r.insurance_cost != null ? Number(r.insurance_cost) : null,
      office_payroll_cost: r.office_payroll_cost != null ? Number(r.office_payroll_cost) : null,
      maintenance_cost: r.maintenance_cost != null ? Number(r.maintenance_cost) : null,
      total_cost: r.total_cost != null ? Number(r.total_cost) : null,
      contribution_profit: r.contribution_profit != null ? Number(r.contribution_profit) : null,
      // Present only on rows that came from a wait-aware save (broker
      // call / check load / find-next-load assign) -- carried through so a
      // chained-load cascade refresh doesn't silently drop real wait data
      // an earlier save already computed correctly.
      wait_hours: r.wait_hours != null ? Number(r.wait_hours) : null,
      wait_cost: r.wait_cost != null ? Number(r.wait_cost) : null,
      profit_after_wait: r.profit_after_wait != null ? Number(r.profit_after_wait) : null,
      deadhead_miles: r.deadhead_miles != null ? Number(r.deadhead_miles) : null,
      loaded_miles: r.loaded_miles != null ? Number(r.loaded_miles) : null,
      toll_count: r.toll_details?.toll_count || 0,
      toll_names: r.toll_details?.toll_names || [],
      // Real per-toll {name, lat, lng, price_usd} list -- without this,
      // toll markers only ever rendered for a FRESH estimate-trip-cost
      // compute, never for a load's already-saved plan (the common case:
      // any quoted or previously-viewed load), since save-route-plan used
      // to persist only a count+names summary. Loads saved before that
      // backend fix still won't have this until their plan is refreshed.
      toll_stops: r.toll_details?.toll_stops || [],
      geometry: r.geometry,
      live_eta: r.live_eta || null,
      live_pickup_eta: r.live_pickup_eta || null,
      live_eta_checked_at: r.live_eta_checked_at || null,
      live_off_route_miles: r.live_off_route_miles != null ? Number(r.live_off_route_miles) : null,
      // Set only once the truck's gone far enough off the planned route
      // (see trip_progress_check.py's OFF_ROUTE_THRESHOLD_MILES fallback)
      // that a fresh HERE route -- not a trim of the stale planned one --
      // is backing the live ETA. Recency (not just presence) is what
      // matters here -- see LIVE_ROUTE_RECALC_STALE_MINUTES below.
      live_route_computed_at: r.live_route_computed_at || null,
    }));
  }

  // A chained-forward load's OWN route (path/tolls/geometry) doesn't
  // change when an earlier load on the same truck gets refreshed or gets
  // a different route picked -- only the fuel/HOS/eta it inherits does
  // (plan-full-trip's busy-truck substitution reads the earlier load's
  // CURRENT is_chosen row live). So this re-simulates against the
  // load's own already-saved geometry instead of calling
  // estimate-trip-cost again -- zero new paid HERE calls. If the load
  // has no saved plan yet, there's nothing stale to fix -- it'll compute
  // fresh, correctly, whenever the dispatcher opens it.
  const cascadeRefreshChainedLoad = useCallback(
    async (truckId, load) => {
      let savedRes;
      try {
        savedRes = await axios.get(`${apiBaseUrl}/scripted/fleet/get-route-plan`, {
          params: { load_id: load.id },
          ...authHeaders(),
        });
      } catch (e) {
        return;
      }
      if (!savedRes.data.has_saved_plan) return;
      const routes = routesFromSavedRows(savedRes.data.routes);
      const planResults = await Promise.all(
        routes.map((opt) =>
          axios
            .post(`${apiBaseUrl}/scripted/fleet/plan-full-trip`, { truck_id: truckId, load_id: load.id, geometry: opt.geometry, distance_miles: opt.distance_miles, duration_hours: opt.duration_hours }, authHeaders())
            .then((r) => r.data)
            .catch(() => null)
        )
      );
      const merged = routes.map((opt, i) => ({ ...opt, trip_plan: planResults[i] }));
      const chosenType = savedRes.data.chosen_route_type || routes[0]?.route_type;
      try {
        rememberRouteDecision(load.id, await axios.post(
          `${apiBaseUrl}/scripted/fleet/save-route-plan`,
          { load_id: load.id, routes: merged, chosen_route_type: chosenType },
          authHeaders()
        ));
      } catch (e) {
        // best-effort -- the dispatcher will get correct numbers next time
        // this load's plan loads or the next cascade runs
      }
      // If the dispatcher currently has this exact load open, reflect the
      // corrected numbers immediately instead of leaving the panel stale
      // until they navigate away and back.
      if (selectedLoadIdRef.current === load.id) {
        setFullTripPlans((prev) => {
          const next = { ...prev };
          merged.forEach((r) => {
            next[r.route_type] = { loading: false, data: r.trip_plan };
          });
          return next;
        });
      }
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Fires after a load's route plan actually changes (a Refresh, or the
  // dispatcher picking a different alternate) -- keeps every LATER load on
  // the same truck's chained fuel/HOS/eta numbers in sync, in pickup order,
  // without the dispatcher having to remember to manually refresh each one.
  // One-directional: a later load's own refresh never needs to touch an
  // earlier one, since the earlier load's plan never depends on it.
  const cascadeToChainedLoads = useCallback(
    async (truckId, sourceLoad) => {
      if (!sourceLoad) return;
      const sourcePickup = new Date(sourceLoad.pickup_datetime || sourceLoad.created_at).getTime();
      const chained = selectedTruckLoadsRef.current.filter((l) => {
        if (l.id === sourceLoad.id) return false;
        const t = new Date(l.pickup_datetime || l.created_at).getTime();
        return t > sourcePickup;
      });
      for (const l of chained) {
        await cascadeRefreshChainedLoad(truckId, l);
      }
    },
    [cascadeRefreshChainedLoad]
  );

  const fetchFreshRoutePlan = useCallback(async (truckId, load) => {
    const estRes = await axios.post(
      `${apiBaseUrl}/scripted/fleet/estimate-trip-cost`,
      { truck_id: truckId, load_id: load.id, persist: false },
      authHeaders()
    );
    const estData = estRes.data;
    let options = estData.routes_considered || [];

    // Real per-hop road + tolls through this load's own load_stops, same
    // fix already applied to Check Load/Log Broker Call -- without this,
    // the Route tab (the ONE place a dispatcher watches an ALREADY-
    // assigned multi-stop load) kept drawing estimate-trip-cost's straight
    // origin-to-destination line and never learned a stop existed at all,
    // even though the stop LIST panel (a separate, correct data source)
    // showed it. Confirmed real and user-reported: a load with a real
    // Charlotte stop drew a line straight from Chicago to the Ohio/PA
    // area, nowhere near it. Best-effort -- a normal load with no stops
    // (the vast majority) gets stopRows.length === 0 and this whole block
    // is a no-op, same as before.
    let multiStopByType = {};
    try {
      const stopsRes = await axios.get(`${apiBaseUrl}/load_stops/?load_id=${load.id}&limit=50`, authHeaders());
      const stopRows = (stopsRes.data.records || stopsRes.data || []).filter((s) => s.lat != null && s.lng != null);
      if (stopRows.length && load.origin_lat != null && load.destination_lat != null) {
        const msRes = await axios.post(
          `${apiBaseUrl}/scripted/fleet/multi-stop-route-options`,
          {
            origin_lat: load.origin_lat, origin_lng: load.origin_lng,
            destination_lat: load.destination_lat, destination_lng: load.destination_lng,
            intermediate_stops: stopRows.map((s) => ({ lat: Number(s.lat), lng: Number(s.lng) })),
          },
          authHeaders()
        );
        multiStopByType = Object.fromEntries((msRes.data.routes || []).map((c) => [c.route_type, c]));
      }
    } catch (e) {
      // Falls through to the plain (straight-line) plan already computed
      // above -- a multi-stop-route-options failure shouldn't block the
      // Route tab from showing anything at all.
    }

    options = options.map((opt) => {
      const c = multiStopByType[opt.route_type];
      if (!c) return opt;
      const oldTollCost = opt.toll_cost || 0;
      return {
        ...opt,
        distance_miles: c.distance_miles ?? opt.distance_miles,
        duration_hours: c.duration_hours ?? opt.duration_hours,
        toll_cost: c.toll_cost_usd ?? oldTollCost,
        total_cost: (opt.total_cost || 0) - oldTollCost + (c.toll_cost_usd ?? oldTollCost),
        toll_stops: [
          ...(opt.toll_stops || []).filter((t) => t.leg === "deadhead"),
          ...(c.toll_stops || []),
        ],
        leg_geometries: c.leg_geometries,
      };
    });

    const planResults = await Promise.all(
      options.map((opt) =>
        axios
          .post(`${apiBaseUrl}/scripted/fleet/plan-full-trip`, {
            truck_id: truckId, load_id: load.id, geometry: opt.geometry,
            distance_miles: opt.distance_miles, duration_hours: opt.duration_hours,
            // This route's own real per-hop path (when this load has real
            // stops) -- plan-full-trip already resolves load_stops itself
            // from load_id alone, but without real leg_geometries handed
            // in, it falls back to fetching ITS OWN default per-hop path
            // for every one of the 5 options independently, which produces
            // the exact same combined route 5 times over (see that
            // endpoint's own leg_geometries comment).
            leg_geometries: opt.leg_geometries,
          }, authHeaders())
          .then((r) => r.data)
          .catch(() => null)
      )
    );
    const merged = options.map((opt, i) => ({ ...opt, trip_plan: planResults[i] }));
    try {
      rememberRouteDecision(load.id, await axios.post(
        `${apiBaseUrl}/scripted/fleet/save-route-plan`,
        { load_id: load.id, routes: merged, chosen_route_type: estData.chosen_route?.route_type },
        authHeaders()
      ));
    } catch (e) {
      // Persistence is best-effort -- still show the freshly computed plan
      // even if saving it failed (dispatcher just won't get the free
      // reload next time; not worth blocking the view over).
    }
    return { estData, merged };
  }, []);

  // Guards against two overlapping calls (e.g. a double-fired mount effect)
  // both falling into the "no saved plan yet, compute one" branch and each
  // inserting their own row per route -- save_route_plan only supersedes
  // rows that exist at the moment it reads them, so two concurrent callers
  // race past each other and both persist, leaving more than one row
  // marked is_chosen for the same load (multiple routes showing "In use").
  const loadRoutePlanInFlightRef = useRef(null);

  const loadRoutePlan = useCallback(
    async (forceRefresh) => {
      if (!selectedTruckId || !selectedLoad) return;
      const loadId = selectedLoad.id;
      if (loadRoutePlanInFlightRef.current === loadId) return;
      loadRoutePlanInFlightRef.current = loadId;

      setLoadEconomics({ loading: true });
      setFullTripPlans({});
      setRoutePlanMeta((m) => ({ ...(m || {}), refreshing: !!forceRefresh }));

      try {
        if (!forceRefresh) {
          try {
            const savedRes = await axios.get(`${apiBaseUrl}/scripted/fleet/get-route-plan`, {
              params: { load_id: loadId },
              ...authHeaders(),
            });
            if (savedRes.data.has_saved_plan) {
              const routes = routesFromSavedRows(savedRes.data.routes);
              const chosenType = savedRes.data.chosen_route_type || routes[0]?.route_type;
              const chosen = routes.find((r) => r.route_type === chosenType) || routes[0];
              setLoadEconomics({
                loading: false,
                data: {
                  routing_source: "here",
                  chosen_route: chosen,
                  routes_considered: routes,
                  revenue: Number(selectedLoad.rate_offered || 0),
                },
              });
              const tp = {};
              savedRes.data.routes.forEach((r) => {
                tp[r.route_type] = { loading: false, data: r.trip_plan };
              });
              setFullTripPlans(tp);
              setRoutePlanMeta({ hasSaved: true, savedAt: savedRes.data.saved_at, refreshing: false });
              return;
            }
          } catch (e) {
            // fall through to a fresh compute below
          }
        }

        const { estData, merged } = await fetchFreshRoutePlan(selectedTruckId, selectedLoad);
        setLoadEconomics({ loading: false, data: estData });
        const tp = {};
        merged.forEach((r) => {
          tp[r.route_type] = { loading: false, data: r.trip_plan };
        });
        setFullTripPlans(tp);
        setRoutePlanMeta({ hasSaved: true, savedAt: new Date().toISOString(), refreshing: false });
        // This load's own plan just changed (or was computed for the first
        // time) -- sync any later-in-sequence load on the same truck so its
        // fuel/HOS/eta don't silently go stale. Not awaited: the current
        // load's panel is already updated above, and cascade is a
        // background sync for OTHER loads the dispatcher isn't looking at
        // right now.
        cascadeToChainedLoads(selectedTruckId, selectedLoad).catch(() => {});
      } catch (err) {
        setLoadEconomics({ loading: false, error: err.response?.data?.detail || err.message });
        setRoutePlanMeta(null);
      } finally {
        if (loadRoutePlanInFlightRef.current === loadId) loadRoutePlanInFlightRef.current = null;
      }
    },
    [selectedTruckId, selectedLoad, fetchFreshRoutePlan, cascadeToChainedLoads]
  );

  useEffect(() => {
    routeVisibility.showAll();
    if (!selectedTruckId || !selectedLoad) {
      setLoadEconomics(null);
      setFullTripPlans({});
      setRoutePlanMeta(null);
      return;
    }
    loadRoutePlan(false);
    // Key on the load ID, NOT the selectedLoad object: selectedLoad is
    // recomputed via .find() every render, so the 3-min reloadLoads() tick
    // produces a fresh object reference for the SAME load and, under an
    // object dep, re-fired this effect -> re-pulled the saved route plan
    // (get-route-plan) and redrew the map every 3 min for no reason (the
    // planned route is immutable once saved). Id-keying fetches the plan
    // once per actual load selection; live truck position/stop-detection
    // still refresh on the tick via reloadTrucksAndDrivers()/reloadLoads().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTruckId, selectedLoad?.id]);

  const selectRoute = useCallback(
    async (routeType) => {
      if (!selectedLoad) return;
      setLoadEconomics((prev) => {
        if (!prev?.data) return prev;
        const chosen = prev.data.routes_considered.find((r) => r.route_type === routeType);
        if (!chosen) return prev;
        return { ...prev, data: { ...prev.data, chosen_route: chosen } };
      });
      try {
        rememberRouteDecision(selectedLoad.id, await axios.post(`${apiBaseUrl}/scripted/fleet/select-route`, { load_id: selectedLoad.id, route_type: routeType }, authHeaders()));
      } catch (e) {
        // best-effort -- the local view already reflects the choice
      }
      // Picking a different alternate changes this load's ending fuel/HOS/
      // eta -- keep any chained-forward load on the same truck in sync,
      // same as after a Refresh.
      cascadeToChainedLoads(selectedTruckId, selectedLoad).catch(() => {});
    },
    [selectedLoad, selectedTruckId, cascadeToChainedLoads]
  );

  const econ = loadEconomics?.data?.chosen_route || null;
  // plan-full-trip's simulation is the real HOS-aware source of truth --
  // it walks deadhead-then-loaded as ONE continuous timeline (so pickup
  // waiting pushes delivery back instead of both being computed
  // independently from "now"), and threads in every mandatory rest/fuel
  // stop along the way. Prefer it whenever it's loaded; only fall back to
  // the naive duration_hours-from-now math while it's still in flight, so
  // the panel isn't blank on first render.
  const chosenTripPlan = econ?.route_type ? fullTripPlans[econ.route_type]?.data : null;
  // The committed plan's own eta -- kept only as a fallback for the brief
  // window before live_eta has ever been checked (an older saved route
  // from before live_eta was seeded at save time, or a row that hasn't hit
  // its first scheduled check yet). Never shown on its own anymore -- a
  // dispatcher used to see this next to a separate "Current estimate" and
  // had to reconcile two numbers that could legitimately be hours apart
  // (one frozen at save time, one continuously live); this is the single
  // ETA now, live_eta first, this only as a stand-in until live_eta exists.
  const committedEtaDate = useMemo(() => {
    if (chosenTripPlan?.eta) return parseUtcNaive(chosenTripPlan.eta);
    if (!econ?.duration_hours) return null;
    return new Date(Date.now() + econ.duration_hours * 3600 * 1000);
  }, [chosenTripPlan, econ]);
  // Written continuously by the "Check In-Transit Load Progress" scheduled
  // job (trip_progress_check.py), which re-simulates this route against
  // the truck's real live position/HOS every few minutes.
  const liveEtaDate = econ?.live_eta ? parseUtcNaive(econ.live_eta) : null;
  const liveEtaCheckedAt = econ?.live_eta_checked_at ? parseUtcNaive(econ.live_eta_checked_at) : null;
  const etaDate = liveEtaDate || committedEtaDate;
  // True only while a genuinely fresh HERE route (not a trim of the stale
  // planned one) is backing this load's live ETA/HOS/fuel-stop timing --
  // see trip_progress_check.py's OFF_ROUTE_THRESHOLD_MILES fallback.
  const liveRouteComputedAt = econ?.live_route_computed_at ? parseUtcNaive(econ.live_route_computed_at) : null;
  const liveRouteRecalculated =
    liveRouteComputedAt != null &&
    (Date.now() - liveRouteComputedAt.getTime()) / 60000 < LIVE_ROUTE_RECALC_STALE_MINUTES;
  const dueDate = parseUtcNaive(selectedLoad?.delivery_datetime);
  const isLate = etaDate && dueDate ? etaDate > dueDate : false;
  const profitPerMile = econ && econ.distance_miles ? econ.contribution_profit / econ.distance_miles : null;
  const profitPerHour = econ && econ.duration_hours ? econ.contribution_profit / econ.duration_hours : null;

  // Only meaningful while the truck hasn't reached the shipper yet. Same
  // real simulation as etaDate above -- its "pickup_eta" is the moment the
  // truck physically reaches the shipper, already HOS-aware (a mandatory
  // rest stop on the deadhead leg pushes this back too, unlike a straight
  // haversine/speed guess). Falls back to a quick haversine estimate only
  // while the real plan hasn't loaded yet.
  const pickupEtaDate = useMemo(() => {
    if (!selectedTruck || !selectedLoad || selected?.status !== "heading_to_pickup") return null;
    // Prefer the LIVE pickup ETA (rewritten every cycle by trip_progress_check),
    // then the frozen trip_plan value -- mirrors Delivery ETA's live_eta -> frozen
    // fallback (etaDate above) so pickup and delivery never diverge live-vs-stale
    // on the same active row. Guard above keeps this pre-pickup only.
    if (econ?.live_pickup_eta) return parseUtcNaive(econ.live_pickup_eta);
    if (chosenTripPlan?.pickup_eta) return parseUtcNaive(chosenTripPlan.pickup_eta);
    if (selectedLoad.origin_lat == null) return null;
    const miles = haversineMiles(
      Number(selectedTruck.current_lat), Number(selectedTruck.current_lng),
      Number(selectedLoad.origin_lat), Number(selectedLoad.origin_lng)
    ) * 1.2;
    return new Date(Date.now() + (miles / EST_AVG_SPEED_MPH) * 3600 * 1000);
  }, [selectedTruck, selectedLoad, selected?.status, chosenTripPlan, econ]);
  const pickupDueDate = parseUtcNaive(selectedLoad?.pickup_datetime);
  const pickupWaitHours = chosenTripPlan?.pickup_wait_hours != null
    ? chosenTripPlan.pickup_wait_hours
    : (pickupEtaDate && pickupDueDate && pickupEtaDate < pickupDueDate ? (pickupDueDate - pickupEtaDate) / 3600000 : 0);
  const lateForPickupHours = chosenTripPlan?.late_for_pickup_hours || 0;
  // Real fact once it's happened (actual_pickup_at vs. the appointment) --
  // colors the "Picked up" row green/amber the same way the still-pending
  // "Pick Up"/ETA rows already do via lateForPickupHours/isLate below.
  const pickupActualDate = parseUtcNaive(selectedLoad?.actual_pickup_at);
  const pickupWasLate = pickupActualDate && pickupDueDate ? pickupActualDate > pickupDueDate : false;
  const [activeTab, setActiveTab] = useState("overview");

  // TMS inbound load queue (Phase 2) — status='queued' loads pulled from the TMS, surfaced in the Fleet
  // Panel behind a Trucks|Queue toggle. Read-only list for now (the assign flow lands in the next update).
  // Scoped to the dispatcher's own loads (loads.dispatcher_id); admins (non-dispatchers) see all queued.
  const [fleetTab, setFleetTab] = useState("trucks");
  const [queueLoads, setQueueLoads] = useState([]);
  useEffect(() => {
    let alive = true;
    const fetchQueue = () => {
      const conditions = [{ field: "status", operator: "is", value: "queued" }];
      if (isRealDispatcher && currentUserId) {
        conditions.push({ field: "dispatcher_id", operator: "is", value: currentUserId });
      }
      const url = `${apiBaseUrl}/search/loads/?filter_json=${encodeURIComponent(
        JSON.stringify({ conditions })
      )}&limit=500`;
      axios
        .get(url, authHeaders())
        .then((r) => { if (alive) setQueueLoads(r.data.records || r.data || []); })
        .catch(() => {});
    };
    fetchQueue();
    const id = setInterval(fetchQueue, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [isRealDispatcher, currentUserId]);

  // Assign-from-queue (Deploy 2, v2): reuse the REAL "Assign Load" (Broker Call) modal — same native UI,
  // same fleet-ranking of trucks, same assign + plan-route flow. We pre-fill it from the queued load and
  // remember the queued load's id so the Broker flow UPDATES that existing load (no duplicate row, and
  // the pull stays idempotent since the row keeps its external_load_id) instead of creating a new one.
  // submitBrokerCall / submitBrokerManual seed brokerQuotedLoadIdRef from this ref; saveBrokerQuote's
  // ensureLoadId then reuses it, and assignBrokerCall PUTs that same load to in_transit.
  const brokerSourceQueuedLoadIdRef = useRef(null);
  function openQueueAssign(load) {
    setBrokerManual((p) => ({
      ...p,
      origin_location: load.origin_location || "",
      destination_location: load.destination_location || "",
      pickup_datetime_local: String(load.pickup_datetime || "").slice(0, 16),     // "YYYY-MM-DDTHH:mm"
      delivery_deadline_local: String(load.delivery_datetime || "").slice(0, 16),
      equipment_type: "",
      offered_rate: load.rate_offered != null ? String(load.rate_offered) : "",
      load_weight_lbs: load.load_weight_lbs != null ? String(load.load_weight_lbs) : "",
      customer_name: load.customer_name || "",
      hazmat_status: load.hazmat_status === "hazardous" ? "hazardous" : "non_hazardous",
    }));
    brokerSourceQueuedLoadIdRef.current = load.id;   // Broker flow updates THIS load, doesn't create one
    prefillQueueStops(load.id, "broker");            // carry the DataTruck intermediate stops into the plan
    setBrokerMode("manual");
    setBrokerCallOpen(true);
  }
  // Pull the queued load's load_stops and prefill the modal's INTERMEDIATE stops (everything between the
  // first=origin pickup and last=destination), preserving each stop's pickup/delivery type. lat/lng already
  // come from DataTruck, so no geocoding — the route plans through every stop, not just origin→dest.
  function prefillQueueStops(loadId, which) {
    const setter = which === "broker" ? setBrokerStops : setCheckLoadStops;
    setter([]);  // clear first so a failed/empty fetch leaves no stale stops
    axios.get(`${apiBaseUrl}/load_stops/?load_id=${loadId}&limit=50`, authHeaders())
      .then((res) => {
        const rows = (res.data.records || res.data || []).slice().sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        const intermediate = rows.slice(1, -1).map((s) => ({
          location: s.location || "",
          // DataTruck/load_stops vocab is pickup/delivery; the modal's dropdown value for a drop is
          // "dropoff" (not "delivery") — map it so the prefilled stop selects the right option.
          stop_type: s.stop_type === "pickup" ? "pickup" : "dropoff",
          appointment_datetime_local: String(s.appointment_datetime || "").slice(0, 16),
          lat: s.lat != null ? Number(s.lat) : null,
          lng: s.lng != null ? Number(s.lng) : null,
          timezone: s.timezone || null,
          resolving: false, error: null,
        }));
        setter(intermediate);
      })
      .catch(() => {});
  }
  // Cancel (decline) a queued load. Sets status='cancelled' — it leaves the queue, stays out of all
  // reporting, and the pull won't re-queue it (upsert only restamps queued/null, not cancelled).
  const [qcLoad, setQcLoad] = useState(null);   // queued load pending cancel-confirm; null = closed
  const [qcBusy, setQcBusy] = useState(false);
  const [qcError, setQcError] = useState(null);
  function openQueueCancel(load) { setQcError(null); setQcLoad(load); }
  function confirmQueueCancel() {
    if (!qcLoad) return;
    const id = qcLoad.id;
    setQcBusy(true); setQcError(null);
    axios.put(`${apiBaseUrl}/loads/${id}`, { status: "cancelled" }, authHeaders())
      .then(() => {
        setQueueLoads((prev) => prev.filter((l) => l.id !== id));
        setQcLoad(null); setQcBusy(false);
        toast.success("Load removed from the queue");
      })
      .catch((e) => { setQcError(e.response?.data?.detail || e.response?.data?.error || "Couldn't cancel this load."); setQcBusy(false); });
  }
  // "Assign to a truck" path — skip the fleet ranking: pick a truck, then route into the native truck-first
  // Check Load flow pre-filled against it. checkLoadSourceQueuedLoadIdRef mirrors the broker ref so the quote
  // step reuses the queued load (updates in place, no duplicate). qpLoad = the load awaiting a truck pick.
  const checkLoadSourceQueuedLoadIdRef = useRef(null);
  const [qpLoad, setQpLoad] = useState(null);
  const [qpSearch, setQpSearch] = useState("");
  function openQueueAssignToTruck(load, truckId) {
    setSelectedTruckId(truckId);
    setCheckLoadManual((p) => ({
      ...p,
      origin_location: load.origin_location || "",
      destination_location: load.destination_location || "",
      pickup_datetime_local: String(load.pickup_datetime || "").slice(0, 16),
      delivery_deadline_local: String(load.delivery_datetime || "").slice(0, 16),
      equipment_type: "",
      offered_rate: load.rate_offered != null ? String(load.rate_offered) : "",
      load_weight_lbs: load.load_weight_lbs != null ? String(load.load_weight_lbs) : "",
      customer_name: load.customer_name || "",
      hazmat_status: load.hazmat_status === "hazardous" ? "hazardous" : "non_hazardous",
    }));
    checkLoadSourceQueuedLoadIdRef.current = load.id;
    prefillQueueStops(load.id, "check");             // carry the DataTruck intermediate stops into the plan
    setQpLoad(null); setQpSearch("");
    setCheckLoadMode("manual");
    setCheckLoadOpen(true);
    setCheckLoadStep(1);
  }

  // Inline editor for the selected load's pickup/delivery DUE DATES (Current
  // Load tab). null = closed; otherwise { pickup, delivery, saving, error }
  // where pickup/delivery are facility-local wall-clock strings for the
  // <input type="datetime-local"> controls. Saving hits the dedicated
  // /fleet/update-load-due-dates endpoint, which also clears the load's timing
  // alarms IFF the new date resolves them (a user action -- no polling added).
  const [dueDateEdit, setDueDateEdit] = useState(null);
  function openDueDateEditor() {
    if (!selectedLoad) return;
    setDueDateEdit({
      pickup: (zonedInputParts(parseUtcNaive(selectedLoad.pickup_datetime), selectedLoad.origin_timezone) || "").slice(0, 16),
      delivery: (zonedInputParts(parseUtcNaive(selectedLoad.delivery_datetime), selectedLoad.destination_timezone) || "").slice(0, 16),
      saving: false,
      error: null,
    });
  }
  function saveDueDates() {
    if (!selectedLoad || !dueDateEdit) return;
    const { pickup, delivery } = dueDateEdit;
    if (!pickup && !delivery) { setDueDateEdit((s) => ({ ...s, error: "Enter a pickup or delivery date" })); return; }
    if (pickup && !hasSaneOperationalYear(pickup)) { setDueDateEdit((s) => ({ ...s, error: "Pickup date is out of range" })); return; }
    if (delivery && !hasSaneOperationalYear(delivery)) { setDueDateEdit((s) => ({ ...s, error: "Delivery date is out of range" })); return; }
    if (pickup && delivery && isDeliveryBeforePickup(pickup, delivery)) { setDueDateEdit((s) => ({ ...s, error: "Delivery can't be before pickup" })); return; }
    const body = { load_id: selectedLoad.id };
    if (pickup) body.pickup_datetime = facilityLocalToUtcNaive(pickup, selectedLoad.origin_timezone);
    if (delivery) body.delivery_datetime = facilityLocalToUtcNaive(delivery, selectedLoad.destination_timezone);
    setDueDateEdit((s) => ({ ...s, saving: true, error: null }));
    axios
      .post(`${apiBaseUrl}/scripted/fleet/update-load-due-dates`, body, authHeaders())
      .then((res) => {
        const d = res.data || {};
        reloadLoads();
        setDueDateEdit(null);
        const cleared = d.alerts_cleared || 0;
        const kept = d.alerts_kept_open || 0;
        if (cleared && kept) toast.success(`Dates saved -- cleared ${cleared} alarm${cleared > 1 ? "s" : ""}; ${kept} still apply.`);
        else if (cleared) toast.success(`Dates saved -- cleared ${cleared} alarm${cleared > 1 ? "s" : ""}.`);
        else if (kept) toast.success(`Dates saved -- ${kept} alarm${kept > 1 ? "s" : ""} still apply.`);
        else toast.success("Dates saved.");
      })
      .catch((err) => setDueDateEdit((s) => ({ ...(s || {}), saving: false, error: err.response?.data?.error || err.response?.data?.detail || "Couldn't save dates" })));
  }
  // Next Trips preview -- single-select (one route at a time, "see them one
  // after another"), not a multi-select overlay: clicking a different trip
  // replaces the previous preview instead of stacking on top of it.
  const [nextTripPreviewId, setNextTripPreviewId] = useState(null);
  const [nextTripPreviewRoute, setNextTripPreviewRoute] = useState(null); // {loaded: [[lat,lng],...]} | null

  useEffect(() => {
    if (!nextTripPreviewId) {
      setNextTripPreviewRoute(null);
      return;
    }
    const trip = nextTripsList.find((l) => l.id === nextTripPreviewId);
    if (!trip || trip.origin_lat == null || trip.destination_lat == null) {
      setNextTripPreviewRoute(null);
      return;
    }
    let cancelled = false;
    setNextTripPreviewRoute(null);
    (async () => {
      try {
        const loaded = await fetchRoadRoute(Number(trip.origin_lat), Number(trip.origin_lng), Number(trip.destination_lat), Number(trip.destination_lng));
        if (!cancelled) setNextTripPreviewRoute({ loaded });
      } catch (e) {
        if (!cancelled) setNextTripPreviewRoute(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextTripPreviewId]);

  const nextTripPreview = nextTripsList.find((l) => l.id === nextTripPreviewId) || null;
  const nextTripPreviewFitPoints = useMemo(() => {
    if (!nextTripPreview || nextTripPreview.origin_lat == null || nextTripPreview.destination_lat == null) return null;
    return [
      [Number(nextTripPreview.origin_lat), Number(nextTripPreview.origin_lng)],
      [Number(nextTripPreview.destination_lat), Number(nextTripPreview.destination_lng)],
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextTripPreview?.id]);

  // Opening the Route tab is a "just this truck" moment -- auto-enable
  // the existing Focus-this-truck map layer instead of making the
  // dispatcher toggle it by hand every time. Doesn't force it back off on
  // its own if they manually turn it off while still on this tab.
  useEffect(() => {
    if (activeTab === "route") setFocusTruckOnly(true);
  }, [activeTab]);
  // The detail column (Overview/Route/Fuel Stop/Next Load) is a fixed 290px
  // -- fine for a quick glance, cramped for anything with real content
  // (negotiation grids, route cards, narrative text). Widening it on demand
  // instead of permanently, so the truck list + map stay visible by default.
  const [detailExpanded, setDetailExpanded] = useState(false);

  // "Check Load" tab -- runs the exact real-routing/HOS pipeline
  // match_truck_for_load already uses for the broker-call wizard's
  // analysis/routes steps, but scoped to just THIS truck (the same
  // truck_id override "Check this truck" already relies on) with no
  // fleet-wide ranking at all. Useful for a hypothetical load that isn't
  // (and may never be) a real row in `loads` yet -- unlike Find Next Load,
  // which only searches loads already in the system. Deliberately
  // separate state from the broker-call wizard's (brokerRoutePreview/
  // truckOptions/etc.) rather than sharing it -- both can be open at once
  // for different trucks/loads, and sharing state would let one clobber
  // the other's in-progress result.
  // origin_timezone/destination_timezone come straight from the agent's
  // parse (it has to resolve them itself to turn "pickup tomorrow noon"
  // into a real UTC instant) -- carried on checkLoadForm so step 2/3 can
  // format pickup/delivery times in the right local clock, same as the
  // broker-call wizard's `p.origin_timezone`/`p.destination_timezone`.
  const [checkLoadForm, setCheckLoadForm] = useState({
    origin_location: "", destination_location: "", pickup_datetime: "",
    delivery_deadline: "", equipment_type: "", offered_rate: "", load_weight_lbs: "",
    origin_timezone: null, destination_timezone: null,
    // Left blank during exploration (this is a hypothetical check against a
    // truck, not yet a real commitment) -- required before assignCheckLoad()
    // will actually submit, so a real load never inherits the old permanent
    // "Check load (unspecified)" placeholder. See saveCheckLoadQuote/
    // assignCheckLoad for how NULL vs. this value is used.
    customer_name: "",
  });
  const [checkLoadMessage, setCheckLoadMessage] = useState("");
  // Default is manual entry, not the AI parse -- load_intake_agent is a
  // real LLM round trip (parse the sentence, call the tool, write a reply)
  // on top of match-truck-for-load's own real HERE calls, and it's only
  // actually needed for its one job here (turning free text into these
  // same structured fields) -- a dispatcher who already knows origin/
  // destination/pickup/equipment/rate shouldn't have to pay that latency
  // just to get to the exact same Analysis screen. AI parsing stays one
  // click away (the "Describe it" tab), it's just not the default anymore.
  const [checkLoadMode, setCheckLoadMode] = useState("manual"); // "manual" | "describe"
  const [checkLoadManual, setCheckLoadManual] = useState({
    origin_location: "", destination_location: "",
    pickup_datetime_local: "", delivery_deadline_local: "",
    equipment_type: "", offered_rate: "", load_weight_lbs: "",
    // Captured here (step 1, alongside the lane fields) instead of at
    // assign time -- real dispatcher complaint: the old assign-time-only
    // required field lived in the side panel, but the expanded route-map
    // modal's own "Assign Truck X" button skipped straight to
    // assignCheckLoad() with nothing on screen to satisfy it, so a
    // dispatcher working from the map got silently blocked with no visible
    // way to fix it. Optional now, not required -- assignCheckLoad() falls
    // back to "Unknown" rather than ever blocking the assign click again.
    customer_name: "",
    // Mandatory, unlike everything else on this step -- "" (not yet
    // chosen) blocks both submitCheckLoad and submitCheckLoadManual from
    // proceeding to the Analysis step. See the step-1 select's own comment
    // for why this can't default to either value.
    hazmat_status: "",
  });
  // Lane history -- explicit ask: before asking a broker for a price, see
  // what recent real loads on roughly this same lane actually paid.
  // Deliberately a SEPARATE, lightweight geocode from the one
  // match-truck-for-load does at submit time -- this only needs a real
  // lat/lng to search nearby loads with, not the full HERE routing that
  // submit triggers, so it can resolve the moment the dispatcher tabs out
  // of either field instead of waiting for "Check this load."
  const [checkLoadOriginGeo, setCheckLoadOriginGeo] = useState(null); // {lat,lng} | null
  const [checkLoadDestGeo, setCheckLoadDestGeo] = useState(null);
  const [checkLoadLaneHistory, setCheckLoadLaneHistory] = useState(null); // null | {loading:true} | {loading:false, matches, radius_miles} | {loading:false, error}
  function resolveCheckLoadLaneField(field) {
    const text = field === "origin" ? checkLoadManual.origin_location : checkLoadManual.destination_location;
    geocodeForLaneHistory(text).then((geo) => {
      if (field === "origin") {
        setCheckLoadOriginGeo(geo);
        fetchLaneHistory(geo, checkLoadDestGeo, null, setCheckLoadLaneHistory);
      } else {
        setCheckLoadDestGeo(geo);
        fetchLaneHistory(checkLoadOriginGeo, geo, null, setCheckLoadLaneHistory);
      }
    });
  }
  // Real stops between the first pickup and the last drop -- e.g. a
  // broker's "pick up at A, then B, deliver at C" job (~1-in-10 real
  // loads, per dispatcher feedback). Deliberately NOT sent to
  // match-truck-for-load's own candidate ranking (that stays at today's
  // first-pickup/last-drop approximation, same posture estimate-trip-cost
  // already has for its own simpler model -- full per-leg simulation for
  // every ranked candidate would revive the exact timeout/perf problems
  // #158/#162 already had to fix once) -- these only feed the route/map
  // preview (plan-full-trip, which already knows how to fetch real HERE
  // geometry per hop on its own) and the load_stops rows created once a
  // truck is actually assigned. Each stop resolves its own lat/lng/
  // timezone via /fleet/geocode-location before it can be used -- same
  // real HERE-first geocoder now backing origin/destination everywhere
  // else in this app.
  const [checkLoadStops, setCheckLoadStops] = useState([]); // [{location, stop_type, appointment_datetime_local, lat, lng, timezone, resolving, error}]

  function addCheckLoadStop() {
    setCheckLoadStops((prev) => [
      ...prev,
      { location: "", stop_type: "dropoff", appointment_datetime_local: "", lat: null, lng: null, timezone: null, resolving: false, error: null },
    ]);
  }
  function removeCheckLoadStop(index) {
    setCheckLoadStops((prev) => prev.filter((_, i) => i !== index));
  }
  function updateCheckLoadStop(index, patch) {
    setCheckLoadStops((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }
  function resolveCheckLoadStop(index) {
    const stop = checkLoadStops[index];
    if (!stop || !stop.location.trim() || stop.resolving) return;
    updateCheckLoadStop(index, { resolving: true, error: null });
    axios
      .post(`${apiBaseUrl}/scripted/fleet/geocode-location`, { location: stop.location }, authHeaders())
      .then((res) => {
        updateCheckLoadStop(index, {
          lat: res.data.lat, lng: res.data.lng, timezone: res.data.timezone,
          location: res.data.display_name || stop.location, resolving: false,
        });
      })
      .catch((err) => {
        updateCheckLoadStop(index, { resolving: false, error: err.response?.data?.error || "Couldn't resolve this location" });
      });
  }
  // Every stop must be resolved (real lat/lng) before this is usable --
  // an unresolved location has nothing real to simulate/save against.
  const checkLoadStopsReady = checkLoadStops.every((s) => s.lat != null && s.lng != null);
  // {lat, lng, location, stop_type, appointment_datetime} for plan-full-trip's
  // own body.intermediate_stops (not-yet-persisted-load path) and for the
  // real load_stops rows created once a truck is actually assigned --
  // datetime-local is the dispatcher's OWN browser clock, same conversion
  // convention submitCheckLoadManual already uses for pickup/delivery.
  function buildCheckLoadStopsPayload() {
    return checkLoadStops
      .filter((s) => s.lat != null && s.lng != null)
      .map((s) => {
        const dt = s.appointment_datetime_local ? new Date(s.appointment_datetime_local) : null;
        return {
          lat: s.lat, lng: s.lng, location: s.location, stop_type: s.stop_type,
          appointment_datetime: dt && !Number.isNaN(dt.getTime()) ? dt.toISOString() : null,
        };
      });
  }
  const [checkLoadStep, setCheckLoadStep] = useState(1);
  const [checkLoadResult, setCheckLoadResult] = useState(null); // {loading, data, error}
  const [checkLoadRoutePreview, setCheckLoadRoutePreview] = useState(null); // {loading, data, error}
  const [checkLoadRouteTripPlans, setCheckLoadRouteTripPlans] = useState({}); // route_type -> plan_full_trip response
  // See brokerExcludedStations' identical comment -- this flow's own copy.
  const [checkLoadExcludedStations, setCheckLoadExcludedStations] = useState([]);
  const [checkLoadChosenRouteType, setCheckLoadChosenRouteType] = useState(null);
  const checkLoadRouteVisibility = useRouteVisibility(checkLoadRoutePreview?.data?.best_match_route_options, checkLoadChosenRouteType);
  // The per-route fuel/HOS stops load a few seconds AFTER the route lines
  // (one plan-full-trip sim per route -- see fetchCheckLoadRoutes). Until
  // they've settled, a dispatcher shouldn't be able to commit the truck
  // without seeing its fuel plan. checkLoadRouteTripPlans goes {} -> fully
  // populated in one setState once the plan-full-trip batch resolves (even
  // on error every route gets a null entry, so a non-empty map reliably
  // means "settled", never a permanent block). routesShown gates this to
  // the post-"Show routes" window so a plain assign without viewing routes
  // is unaffected.
  const checkLoadRoutesShown = !!(checkLoadRoutePreview?.data?.best_match_route_options?.length);
  const checkLoadFuelLoading =
    !!checkLoadRoutePreview?.loading ||
    (checkLoadRoutesShown && Object.keys(checkLoadRouteTripPlans).length === 0);
  // Same pattern as the broker-call wizard's brokerQuotedLoadIdRef/
  // brokerQuoteRoutesRef -- the load created the moment real routes are
  // priced here, and the routes/trip-plans cached for re-saving with a
  // recomputed profit at assign time.
  const checkLoadQuotedLoadIdRef = useRef(null);
  const checkLoadQuoteRoutesRef = useRef(null);
  // Bumped every time a new Check Load session starts or the truck/modal
  // resets -- async callbacks (fetchCheckLoadRoutes/saveCheckLoadQuote)
  // capture the value at their own start and refuse to apply their result
  // if it's changed by the time they resolve. Without this, a stale
  // response landing after the dispatcher switched trucks/loads could
  // silently overwrite state for whatever's on screen NOW with data for
  // whatever was on screen THEN -- confirmed real risk (no AbortController
  // or staleness guard existed anywhere in this flow).
  const checkLoadGenerationRef = useRef(0);
  const [checkLoadAssigning, setCheckLoadAssigning] = useState(false);
  const [checkLoadAssignResult, setCheckLoadAssignResult] = useState(null); // {loadId} once assigned
  // "This is his last load" -- captured alongside the assign click rather
  // than as a separate follow-up step, since the whole point is a driver
  // never has a gap where he could still get queued a new load after being
  // marked as going on time off. Read by assignCheckLoad's own success
  // handler, not by the button itself, so both trigger points (the inline
  // button and the expanded-map popup's logAction) pick up the same values.
  const [checkLoadPtoEnabled, setCheckLoadPtoEnabled] = useState(false);
  const [checkLoadPtoUntil, setCheckLoadPtoUntil] = useState(""); // datetime-local value
  const [checkLoadPtoNote, setCheckLoadPtoNote] = useState("");
  // Gates the PtoConfirmPopup -- shown only once the dispatcher actually
  // clicks the commit button, not as a standing checkbox in the form (the
  // earlier inline-toggle version read as cluttered).
  const [checkLoadPtoPromptOpen, setCheckLoadPtoPromptOpen] = useState(false);
  // Step 1 of the assignment wizard (push fuel stops) -- see brokerFuelSms* twins.
  const [checkLoadFuelSmsOpen, setCheckLoadFuelSmsOpen] = useState(false);
  const [checkLoadFuelSmsText, setCheckLoadFuelSmsText] = useState("");
  // Pilot: the recommendation identity PINNED when the SMS was composed (so a background
  // route recalculation can't swap the associated load/truck/decision under the dispatcher).
  const checkLoadFuelSmsMetaRef = useRef(null);
  const [checkLoadFuelSmsSending, setCheckLoadFuelSmsSending] = useState(false);
  const [checkLoadFuelSmsResult, setCheckLoadFuelSmsResult] = useState(null);
  // Only populated (and only shown as an editable field) when the driver
  // has no phone on file -- see PushFuelStopsPopup.
  const [checkLoadFuelSmsPhone, setCheckLoadFuelSmsPhone] = useState("");
  // Popup, not a tab -- runs the exact same wizard shape as Log Broker Call
  // (free text -> analysis -> route map) so both flows share one proven
  // parse/map/legend implementation instead of two independently-
  // maintained copies that can (and did) drift apart.
  const [checkLoadOpen, setCheckLoadOpen] = useState(false);
  const [checkLoadMapModalOpen, setCheckLoadMapModalOpen] = useState(false);

  // Phase 1 of the two-stage Load Check -- a free (OSRM, no HERE cost),
  // city/zip-level rough estimate for a lane before exact addresses exist
  // or a specific truck has been picked. "Get exact quote" below hands the
  // same origin/destination straight into the real Check Load flow
  // (Phase 2) instead of making the dispatcher retype them.
  const [quickCheckOpen, setQuickCheckOpen] = useState(false);
  const [quickCheckForm, setQuickCheckForm] = useState({ origin_location: "", destination_location: "", load_weight_lbs: "", expected_rate: "" });
  const [quickCheckResult, setQuickCheckResult] = useState(null); // {loading, data, error}
  // Which real flow Quick Check was opened to feed into -- set by whichever
  // entry button opened it, since Quick Check is now step 1 of BOTH flows
  // rather than a separate standalone feature. "check_load" only ever gets
  // set from the per-truck trigger (selectedTruck is already in context
  // there); "broker_call" is fleet-wide, no truck required.
  const [quickCheckPurpose, setQuickCheckPurpose] = useState("broker_call"); // "check_load" | "broker_call"
  // Hidden by default so the result reads as one clean set of negotiation
  // numbers first -- the cost-breakdown/what's-missing detail is opt-in via
  // "Show AI Insights" rather than always taking up the bottom of the card.
  const [showQuickCheckInsights, setShowQuickCheckInsights] = useState(false);

  function openQuickCheck(purpose) {
    setQuickCheckPurpose(purpose);
    setQuickCheckForm({ origin_location: "", destination_location: "", load_weight_lbs: "", expected_rate: "" });
    setQuickCheckResult(null);
    setShowQuickCheckInsights(false);
    setQuickCheckOpen(true);
  }

  function submitQuickCheck() {
    const f = quickCheckForm;
    if (!f.origin_location.trim() || !f.destination_location.trim() || quickCheckResult?.loading) return;
    setQuickCheckResult({ loading: true });
    axios
      .post(
        `${apiBaseUrl}/scripted/fleet/quick-load-estimate`,
        {
          origin_location: f.origin_location,
          destination_location: f.destination_location,
          expected_rate: f.expected_rate ? Number(f.expected_rate) : null,
          // Only meaningful (and only sent) when Quick Check was opened
          // from a specific truck's own "Check a load against this truck"
          // button -- lets the estimate use that truck's real mpg/cost
          // numbers AND a real (free, haversine-based) deadhead cost from
          // its current position, instead of always falling back to
          // fleet-wide averages with no deadhead at all.
          truck_id: quickCheckPurpose === "check_load" ? selectedTruckId : null,
        },
        authHeaders()
      )
      .then((res) => setQuickCheckResult({ loading: false, data: res.data }))
      .catch((err) => setQuickCheckResult({ loading: false, error: err.response?.data?.error || err.response?.data?.detail || err.message }));
  }

  // Quick Check is now the mandatory first step of both real flows -- these
  // hand its structured fields (no free text anywhere) straight into each
  // flow's own structured manual form instead of a free-text pre-fill, so
  // nothing needs to be retyped and there's no AI-parsing step in between.
  function proceedToCheckLoad() {
    checkLoadSourceQueuedLoadIdRef.current = null;   // normal (non-queue) check-load → create a fresh load
    setCheckLoadStops([]);
    setCheckLoadManual((p) => ({
      ...p,
      origin_location: quickCheckForm.origin_location,
      destination_location: quickCheckForm.destination_location,
      load_weight_lbs: quickCheckForm.load_weight_lbs,
      offered_rate: quickCheckForm.expected_rate,
    }));
    setCheckLoadMode("manual");
    setQuickCheckOpen(false);
    setQuickCheckResult(null);
    setCheckLoadOpen(true);
    setCheckLoadStep(1);
  }

  function proceedToBrokerCall() {
    brokerSourceQueuedLoadIdRef.current = null;   // normal (non-queue) broker call → create a fresh load
    setBrokerStops([]);
    setBrokerManual((p) => ({
      ...p,
      origin_location: quickCheckForm.origin_location,
      destination_location: quickCheckForm.destination_location,
      load_weight_lbs: quickCheckForm.load_weight_lbs,
      offered_rate: quickCheckForm.expected_rate,
    }));
    setBrokerMode("manual");
    setQuickCheckOpen(false);
    setQuickCheckResult(null);
    setBrokerCallOpen(true);
  }

  // Free-text intake, same load_intake_agent the broker-call wizard uses
  // (submitBrokerCall/fetchTruckOptions) -- parse first, then match against
  // just this one truck (truck_id override) instead of the whole fleet.
  function submitCheckLoad() {
    if (!selectedTruckId || !checkLoadMessage.trim() || !checkLoadManual.hazmat_status || checkLoadResult?.loading) return;
    setCheckLoadResult({ loading: true });
    setCheckLoadRoutePreview(null);
    setCheckLoadRouteTripPlans({});
    setCheckLoadExcludedStations([]);
    checkLoadQuotedLoadIdRef.current = checkLoadSourceQueuedLoadIdRef.current;  // reuse queued load if opened from Queue; else null = new
    checkLoadQuoteRoutesRef.current = null;
    checkLoadGenerationRef.current += 1;
    setCheckLoadAssignResult(null);
    axios
      .post(
        `${apiBaseUrl}/agents/load_intake_agent/run`,
        // truck_id here tells load_intake_agent (see its system prompt) to
        // scope its OWN match_truck_for_load tool call to just this truck,
        // instead of the whole fleet -- so that real, already-paid-for
        // result can be reused below instead of firing a second, identical
        // truck-scoped search just to populate this same UI.
        { inputs: { message: checkLoadMessage, current_datetime: new Date().toISOString(), truck_id: selectedTruckId } },
        authHeaders()
      )
      .then((res) => {
        const out = res.data.output || "";
        const idx = out.indexOf("---JSON---");
        let parsed = null;
        if (idx !== -1) {
          try { parsed = JSON.parse(out.slice(idx + 10).trim()); } catch { parsed = null; }
        }
        if (!parsed) {
          setCheckLoadResult({ loading: false, error: "Couldn't understand that load -- try including origin, destination, and pickup time." });
          return null;
        }
        // Delivery due date and offered rate are mandatory -- the AI parse
        // isn't always able to pull both from free text, so rather than
        // silently proceeding with a null deadline/rate (the old fallback
        // behavior), bounce to the Manual tab pre-filled with whatever WAS
        // parsed and make the dispatcher supply the missing required
        // field(s) directly. Pickup/delivery times are deliberately left for
        // the dispatcher to re-enter there rather than copied over: the AI
        // path resolves them as real UTC, but the manual tab's datetime-local
        // inputs are raw facility-local wall-clock digits (timestamps_are_utc:
        // false) -- carrying the UTC value over unconverted would silently
        // shift both times by the origin's UTC offset.
        if (parsed.delivery_deadline == null || parsed.offered_rate == null) {
          setCheckLoadManual((p) => ({
            ...p,
            origin_location: parsed.origin_location || p.origin_location,
            destination_location: parsed.destination_location || p.destination_location,
            equipment_type: parsed.equipment_type || p.equipment_type,
            offered_rate: parsed.offered_rate != null ? String(parsed.offered_rate) : p.offered_rate,
            load_weight_lbs: parsed.load_weight_lbs != null ? String(parsed.load_weight_lbs) : p.load_weight_lbs,
          }));
          setCheckLoadMode("manual");
          setCheckLoadResult({
            loading: false,
            error: "Delivery due date and offered rate are required -- enter them below (pickup/delivery times need to be re-entered here too).",
          });
          return null;
        }
        if (!hasSaneOperationalYear(parsed.pickup_datetime) || !hasSaneOperationalYear(parsed.delivery_deadline)) {
          setCheckLoadManual((p) => ({
            ...p,
            origin_location: parsed.origin_location || p.origin_location,
            destination_location: parsed.destination_location || p.destination_location,
            equipment_type: parsed.equipment_type || p.equipment_type,
            offered_rate: parsed.offered_rate != null ? String(parsed.offered_rate) : p.offered_rate,
            load_weight_lbs: parsed.load_weight_lbs != null ? String(parsed.load_weight_lbs) : p.load_weight_lbs,
          }));
          setCheckLoadMode("manual");
          setCheckLoadResult({
            loading: false,
            error: "That description's pickup/delivery date looks wrong (an unusual year) -- enter both below to be sure.",
          });
          return null;
        }
        if (isDeliveryBeforePickup(parsed.pickup_datetime, parsed.delivery_deadline)) {
          setCheckLoadManual((p) => ({
            ...p,
            origin_location: parsed.origin_location || p.origin_location,
            destination_location: parsed.destination_location || p.destination_location,
            equipment_type: parsed.equipment_type || p.equipment_type,
            offered_rate: parsed.offered_rate != null ? String(parsed.offered_rate) : p.offered_rate,
            load_weight_lbs: parsed.load_weight_lbs != null ? String(parsed.load_weight_lbs) : p.load_weight_lbs,
          }));
          setCheckLoadMode("manual");
          setCheckLoadResult({
            loading: false,
            error: "That description's delivery due date is before its pickup time -- enter both below (pickup/delivery times need to be re-entered here too).",
          });
          return null;
        }
        setCheckLoadForm({
          origin_location: parsed.origin_location || "",
          destination_location: parsed.destination_location || "",
          pickup_datetime: parsed.pickup_datetime || "",
          delivery_deadline: parsed.delivery_deadline || "",
          equipment_type: parsed.equipment_type || "",
          offered_rate: parsed.offered_rate != null ? String(parsed.offered_rate) : "",
          origin_timezone: parsed.origin_timezone || null,
          destination_timezone: parsed.destination_timezone || null,
          customer_name: checkLoadManual.customer_name,
          hazmat_status: checkLoadManual.hazmat_status,
        });

        // Only reuse the agent's own tool call if it actually respected the
        // truck_id scoping (best_match really is this truck) AND actually
        // carries real route geometry -- otherwise fall through to firing
        // our own truck-scoped request, same as before this reuse existed.
        // Real, confirmed bug: load_intake_agent's own system prompt never
        // tells its LLM to pass include_route_geometry (deliberately --
        // see match-truck-for-load's own comment on why an agent must
        // never get the full geometry blob back as a tool result, which
        // once caused a 14k-to-1.17M-token runaway). That means the
        // agent's raw tool call NEVER includes best_match_route_options,
        // so checking truck_id alone always looked like a valid reuse and
        // silently skipped the fresh, geometry-inclusive call below --
        // "Check a load against this truck" showed real Analysis numbers
        // but its Routes tab was empty on every single run, not just
        // sometimes.
        const reused = matchToolResultFromAgentRun(res.data);
        if (reused && reused.best_match?.truck_id === selectedTruckId && reused.best_match_route_options?.length) {
          return { data: reused };
        }

        // include_route_geometry costs nothing extra -- the real HERE calls
        // for the winning truck's route alternatives already happen on
        // every match-truck-for-load request regardless of this flag (see
        // that script's own comment on the "winning truck" block); the flag
        // only controls whether the response includes what was already
        // fetched. Requesting it here means step 3 (fetchCheckLoadRoutes)
        // never needs to re-run this whole call just to get geometry.
        return axios.post(
          `${apiBaseUrl}/scripted/fleet/match-truck-for-load`,
          {
            truck_id: selectedTruckId,
            origin_location: parsed.origin_location,
            destination_location: parsed.destination_location,
            pickup_datetime: parsed.pickup_datetime,
            delivery_deadline: parsed.delivery_deadline,
            equipment_type: parsed.equipment_type,
            offered_rate: parsed.offered_rate,
            timestamps_are_utc: true,
            include_route_geometry: true,
          },
          authHeaders()
        );
      })
      .then((res) => {
        if (!res) return; // parse failed above, error already set
        setCheckLoadResult({ loading: false, data: res.data });
        setCheckLoadStep(2);
      })
      .catch((err) => setCheckLoadResult({ loading: false, error: err.response?.data?.error || err.response?.data?.detail || err.message }));
  }

  // Fast path -- skips load_intake_agent entirely (no LLM round trip) and
  // goes straight to the same direct, geometry-inclusive match-truck-for-load
  // call submitCheckLoad's own fallback already uses. The agent's only real
  // job in the free-text path is turning a sentence into these exact fields,
  // so a dispatcher who already knows them shouldn't have to wait on an LLM
  // call just to reach the same Analysis screen.
  function submitCheckLoadManual() {
    const m = checkLoadManual;
    if (!selectedTruckId || !m.origin_location.trim() || !m.destination_location.trim() || !m.pickup_datetime_local || !m.hazmat_status || checkLoadResult?.loading) return;
    setCheckLoadResult({ loading: true });
    setCheckLoadRoutePreview(null);
    setCheckLoadRouteTripPlans({});
    setCheckLoadExcludedStations([]);
    checkLoadQuotedLoadIdRef.current = checkLoadSourceQueuedLoadIdRef.current;  // reuse queued load if opened from Queue; else null = new
    checkLoadQuoteRoutesRef.current = null;
    checkLoadGenerationRef.current += 1;
    setCheckLoadAssignResult(null);

    // datetime-local gives a naive clock-time string with NO timezone
    // attached -- it's just digits ("2026-08-31T23:06"), not a browser-local
    // instant. It's meant as the pickup FACILITY's own wall-clock time (a
    // Chicago pickup at "23:06" means 23:06 in Chicago, regardless of what
    // timezone the dispatcher is physically sitting in) -- so send it
    // through untouched and let match-truck-for-load do the real conversion
    // it already supports (geocodes origin/destination, resolves each one's
    // real IANA zone via timezone_for_point, converts with
    // local_naive_to_utc_naive) via timestamps_are_utc: false. Confirmed
    // real bug this fixes: converting via `new Date(...).toISOString()` here
    // used the DISPATCHER's own browser timezone instead, so a Bulgaria-based
    // dispatcher typing a Chicago pickup time got it shifted by the full
    // Bulgaria-Chicago offset (8h) before it ever reached the backend --
    // e.g. showing a truck as late for a pickup that, in Chicago's own
    // clock, hadn't happened yet.
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(m.pickup_datetime_local)) {
      setCheckLoadResult({ loading: false, error: "Enter a valid pickup date/time." });
      return;
    }

    const zipErr = zipOnlyError(m.origin_location, m.destination_location);
    if (zipErr) {
      setCheckLoadResult({ loading: false, error: zipErr });
      return;
    }

    setCheckLoadForm({
      origin_location: m.origin_location, destination_location: m.destination_location,
      pickup_datetime: m.pickup_datetime_local, delivery_deadline: m.delivery_deadline_local || "",
      equipment_type: m.equipment_type, offered_rate: m.offered_rate,
      load_weight_lbs: m.load_weight_lbs,
      origin_timezone: null, destination_timezone: null,
      customer_name: m.customer_name,
      hazmat_status: m.hazmat_status,
    });

    axios
      .post(
        `${apiBaseUrl}/scripted/fleet/match-truck-for-load`,
        {
          truck_id: selectedTruckId,
          origin_location: m.origin_location,
          destination_location: m.destination_location,
          pickup_datetime: m.pickup_datetime_local,
          delivery_deadline: m.delivery_deadline_local || null,
          equipment_type: m.equipment_type || null,
          offered_rate: m.offered_rate ? Number(m.offered_rate) : null,
          load_weight_lbs: m.load_weight_lbs ? Number(m.load_weight_lbs) : null,
          timestamps_are_utc: false,
          include_route_geometry: true,
        },
        authHeaders()
      )
      .then((res) => {
        setCheckLoadResult({ loading: false, data: res.data });
        // match-truck-for-load's own response is the single source of truth
        // for the real, facility-timezone-converted UTC values (see its own
        // "Copy these, not whatever was passed in" comment) -- overwrite the
        // optimistic naive-string placeholders set above with the real ones
        // so every downstream consumer (route preview, load creation,
        // negotiation logging) uses correct UTC, not raw local digits.
        setCheckLoadForm((prev) => ({
          ...prev,
          pickup_datetime: res.data.pickup_datetime ?? prev.pickup_datetime,
          delivery_deadline: res.data.delivery_deadline ?? prev.delivery_deadline,
          origin_timezone: res.data.origin_timezone ?? prev.origin_timezone,
          destination_timezone: res.data.destination_timezone ?? prev.destination_timezone,
        }));
        setCheckLoadStep(2);
      })
      .catch((err) => setCheckLoadResult({ loading: false, error: err.response?.data?.error || err.response?.data?.detail || err.message }));
  }

  // Trip-stop detail (fuel/rest markers) for step 3's map -- reuses the
  // route options + geometry submitCheckLoad already fetched (see its
  // comment) instead of re-running match-truck-for-load. Only the
  // plan-full-trip calls below are new here, and those are pure local
  // simulation against geometry we already have -- no additional real
  // HERE cost at all.
  function fetchCheckLoadRoutes() {
    if (!selectedTruckId || checkLoadRoutePreview?.loading) return;
    const data = checkLoadResult?.data;
    if (!data?.best_match_route_options?.length) {
      setCheckLoadRoutePreview({ loading: false, error: "No real route alternatives came back for this truck." });
      return;
    }
    setCheckLoadRoutePreview({ loading: true });
    checkLoadRouteVisibility.showAll();
    // Deliberately starts unselected -- explicit ask: "no route should be
    // selected in the beginning ... at the moment I select a route" is
    // when it becomes chosen. assignCheckLoad's own booking path/the
    // backend's save-route-plan script both already resolve a real route
    // on their own if this stays null the whole time a dispatcher never
    // clicks one, so leaving it unset here is safe, not just a UI choice.
    setCheckLoadChosenRouteType(null);

    const routes = data.best_match_route_options || [];
    const origin = data.origin;
    const destination = data.destination;
    if (!routes.length || !origin || !destination) {
      setCheckLoadRoutePreview({ loading: false, data });
      return;
    }
    setCheckLoadRouteTripPlans({});
    // Captured now, checked after the real (non-instant) HERE/simulation
    // calls below resolve -- if the dispatcher has switched trucks/loads
    // (or closed/restarted this flow) in the meantime, this generation is
    // stale and its result must be discarded, not applied on top of
    // whatever's actually on screen now.
    const myGeneration = checkLoadGenerationRef.current;
    const intermediateStopsPayload = buildCheckLoadStopsPayload();
    // Real, genuinely distinct routes through the stops (toll-free + up to
    // 4 more, one real HERE via-waypoint call each -- see that endpoint's
    // own docstring) -- without this, every route_type's plan-full-trip
    // call below independently recomputed the exact same default per-hop
    // path with nothing to tell them apart, so all 5 "alternatives" for a
    // multi-stop load rendered as one identical line with the same
    // (wrong-route) toll markers. Skipped entirely for a normal load (no
    // stops), which keeps using match-truck-for-load's own real
    // alternatives exactly as before.
    const multiStopRoutesPromise = intermediateStopsPayload.length
      ? axios
          .post(
            `${apiBaseUrl}/scripted/fleet/multi-stop-route-options`,
            {
              origin_lat: origin.lat, origin_lng: origin.lng,
              destination_lat: destination.lat, destination_lng: destination.lng,
              intermediate_stops: intermediateStopsPayload,
            },
            authHeaders()
          )
          .then((r) => r.data.routes || [])
          .catch(() => [])
      : Promise.resolve([]);

    multiStopRoutesPromise.then((multiStopRoutes) => {
      if (checkLoadGenerationRef.current !== myGeneration) return;
      const multiStopByType = Object.fromEntries(multiStopRoutes.map((c) => [c.route_type, c]));
      // Merge the real multi-hop numbers into each of match-truck-for-
      // load's own labeled slots (toll_free/route_1/../route_5) -- keeps
      // every downstream consumer (the route pills, saveCheckLoadQuote's
      // persisted route_options rows, the map) working off ONE updated
      // `routes` array instead of two disagreeing sources. Only the
      // toll-related total_cost component is corrected here -- fuel_cost/
      // driver_cost/etc. are unaffected by which physical path is driven,
      // so they're left as match-truck-for-load's own (already real,
      // truck/driver-specific) numbers rather than re-deriving the whole
      // cost formula a second time.
      const routesForRender = routes.map((opt) => {
        const c = multiStopByType[opt.route_type];
        if (!c) return opt;
        const oldTollCost = opt.toll_cost || 0;
        return {
          ...opt,
          distance_miles: c.distance_miles ?? opt.distance_miles,
          duration_hours: c.duration_hours ?? opt.duration_hours,
          toll_cost: c.toll_cost_usd ?? oldTollCost,
          total_cost: (opt.total_cost || 0) - oldTollCost + (c.toll_cost_usd ?? oldTollCost),
          toll_stops: [
            ...(opt.toll_stops || []).filter((t) => t.leg === "deadhead"),
            ...(c.toll_stops || []),
          ],
          multi_stop_leg_geometries: c.leg_geometries,
        };
      });

      Promise.all(
        routesForRender.map((opt) =>
          axios
            .post(
              `${apiBaseUrl}/scripted/fleet/plan-full-trip`,
              {
                truck_id: selectedTruckId,
                origin_lat: origin.lat, origin_lng: origin.lng, origin_location: origin.display_name,
                destination_lat: destination.lat, destination_lng: destination.lng, destination_location: destination.display_name,
                geometry: opt.geometry,
                distance_miles: opt.distance_miles, duration_hours: opt.duration_hours,
                // Not yet a real load_id (that's created just below in
                // saveCheckLoadQuote) -- plan-full-trip's own body path
                // accepts these directly for exactly this not-yet-persisted
                // preview case. multi_stop_leg_geometries (this route's own
                // real per-hop path, when it exists) takes priority over
                // plan-full-trip's own self-fetched fallback -- passing it
                // explicitly is what makes each of the 5 route_types
                // simulate against ITS OWN real path instead of every one
                // of them independently re-deriving the same single default.
                intermediate_stops: intermediateStopsPayload,
                leg_geometries: opt.multi_stop_leg_geometries,
              },
              authHeaders()
            )
            .then((r) => [opt.route_type, r.data])
            .catch(() => [opt.route_type, null])
        )
      ).then((pairs) => {
        if (checkLoadGenerationRef.current !== myGeneration) return;
        const tripPlanByType = Object.fromEntries(pairs);
        const routesWithPlans = attachTripPlans(routesForRender, pairs);  // GM3A: bind plan to its own route by position
        setCheckLoadRouteTripPlans(tripPlanByType);
        setCheckLoadRoutePreview({ loading: false, data: { ...data, best_match_route_options: routesWithPlans } });
        saveCheckLoadQuote(routesWithPlans, tripPlanByType, origin, destination, myGeneration);
      });
    });
  }

  // "This gas station's way off, try another combination" -- see
  // excludeBrokerFuelStation's identical comment, this flow's own copy.
  // Re-simulates against the SAME already-merged routes stored in
  // checkLoadRoutePreview (multi-stop-route-options isn't re-fetched --
  // nothing about the physical route geometry changed, only which fuel
  // station gets picked along it).
  function refetchCheckLoadTripPlans(excludedStationIds) {
    const routes = checkLoadRoutePreview?.data?.best_match_route_options;
    const origin = checkLoadRoutePreview?.data?.origin;
    const destination = checkLoadRoutePreview?.data?.destination;
    if (!routes?.length || !origin || !destination || !selectedTruckId) return;
    setCheckLoadRouteTripPlans({});
    const myGeneration = checkLoadGenerationRef.current;
    const intermediateStopsPayload = buildCheckLoadStopsPayload();
    Promise.all(
      routes.map((opt) =>
        axios
          .post(
            `${apiBaseUrl}/scripted/fleet/plan-full-trip`,
            {
              truck_id: selectedTruckId,
              origin_lat: origin.lat, origin_lng: origin.lng, origin_location: origin.display_name,
              destination_lat: destination.lat, destination_lng: destination.lng, destination_location: destination.display_name,
              geometry: opt.geometry,
              distance_miles: opt.distance_miles, duration_hours: opt.duration_hours,
              intermediate_stops: intermediateStopsPayload,
              leg_geometries: opt.multi_stop_leg_geometries,
              excluded_truck_stop_ids: excludedStationIds.map((s) => s.id),
            },
            authHeaders()
          )
          .then((r) => [opt.route_type, r.data])
          .catch(() => [opt.route_type, null])
      )
    ).then((pairs) => {
      if (checkLoadGenerationRef.current !== myGeneration) return;
      const tripPlanByType = Object.fromEntries(pairs);
      const routesWithPlans = attachTripPlans(routes, pairs);  // GM3A: bind plan to its own route by position
      setCheckLoadRouteTripPlans(tripPlanByType);
      saveCheckLoadQuote(routesWithPlans, tripPlanByType, origin, destination, myGeneration);
    });
  }

  function excludeCheckLoadFuelStation(stopId, stopName) {
    if (!stopId || checkLoadExcludedStations.some((s) => s.id === stopId)) return;
    const next = [...checkLoadExcludedStations, { id: stopId, name: stopName || "that station" }];
    setCheckLoadExcludedStations(next);
    refetchCheckLoadTripPlans(next);
  }

  function resetCheckLoadExcludedStations() {
    setCheckLoadExcludedStations([]);
    refetchCheckLoadTripPlans([]);
  }

  // Same reasoning as the broker-call wizard's saveBrokerQuote -- persist
  // the routes/prices the moment they're computed here, not just if a new
  // "Assign this truck" action gets clicked, so a hypothetical that never
  // gets assigned still leaves a real "quoted" record instead of being
  // computed live and thrown away.
  function saveCheckLoadQuote(routes, tripPlanByType, origin, destination, myGeneration) {
    const form = checkLoadForm;
    if (!form || !origin || !destination || !routes?.length) return;
    if (myGeneration == null) myGeneration = checkLoadGenerationRef.current;
    checkLoadQuoteRoutesRef.current = { routes, tripPlanByType };
    const offeredNum = form.offered_rate !== "" && form.offered_rate != null ? Number(form.offered_rate) : null;
    const payload = buildRouteOptionsPayload(routes, tripPlanByType, offeredNum);

    const ensureLoadId = checkLoadQuotedLoadIdRef.current
      ? Promise.resolve(checkLoadQuotedLoadIdRef.current)
      : axios
          .get(`${apiBaseUrl}/equipment_types/?limit=50`, authHeaders())
          .then((eqRes) => {
            const eqRows = eqRes.data.records || eqRes.data || [];
            const eqMatch = form.equipment_type ? eqRows.find((e) => e.code === form.equipment_type) : null;
            return axios.post(
              `${apiBaseUrl}/loads/`,
              {
                // NULL, not a placeholder -- this row may never become a
                // real commitment (Check Load is pure exploration up to
                // this point). assignCheckLoad() is the actual commitment
                // moment and requires a real customer name at that point;
                // anything left in "quoted" status keeps this honestly NULL
                // rather than a permanent fake value. Views that need
                // something to display fall back to "origin -> destination".
                customer_name: form.customer_name?.trim() || null,
                hazmat_status: form.hazmat_status,
                load_source: "check_load",
                // origin.display_name/destination.display_name (the real
                // geocoded place name match-truck-for-load already resolved
                // to compute these same lat/lng) win over form.origin_location/
                // destination_location -- the manual-entry fast path stores
                // whatever raw text the dispatcher typed there (often just a
                // zip code, per real dispatcher usage), which is exactly what
                // this app's own board/load views should NOT be showing.
                origin_location: origin.display_name || form.origin_location,
                destination_location: destination.display_name || form.destination_location,
                // keep the city label above; ALSO preserve the typed zip for reporting
                origin_zip: zipFromText(form.origin_location),
                destination_zip: zipFromText(form.destination_location),
                origin_lat: origin.lat, origin_lng: origin.lng,
                destination_lat: destination.lat, destination_lng: destination.lng,
                pickup_datetime: form.pickup_datetime,
                // "" (delivery deadline is optional, genuinely left blank
                // often) sent as-is 500s the whole request --
                // psycopg2.errors.InvalidDatetimeFormat, "" isn't a valid
                // timestamp. That failure was being silently swallowed
                // by this chain's own .catch(() => {}), which meant
                // checkLoadQuotedLoadIdRef.current never got set and the
                // "Assign" button permanently no-op'd with zero feedback --
                // confirmed real, reproduced live. When no deadline was
                // given at all, fall back to pickup + the route's own
                // simulated duration rather than leaving this NULL -- see
                // addHoursToNaiveUtc's own docstring for why (a NULL here
                // makes the load invisible on the Fleet Load Board).
                delivery_datetime: form.delivery_deadline || addHoursToNaiveUtc(form.pickup_datetime, routes?.[0]?.duration_hours),
                origin_timezone: form.origin_timezone,
                destination_timezone: form.destination_timezone,
                rate_offered: offeredNum ?? 0,
                // NOT NULL on loads -- checkLoadForm never captured this
                // (unlike the broker-call agent's parsed.loaded_miles), but
                // every route option already carries its own real computed
                // loaded_miles, so read it from there instead.
                miles_loaded: routes[0]?.loaded_miles ?? 0,
                required_equipment_type_id: eqMatch ? eqMatch.id : null,
                load_weight_lbs: form.load_weight_lbs ? Number(form.load_weight_lbs) : null,
                status: "quoted",
              },
              authHeaders()
            );
          })
          .then((loadRes) => {
            const loadId = loadRes.data.record?.id || loadRes.data.id;
            // A stale caller (dispatcher already switched trucks/loads by
            // the time this real POST /loads/ resolves) must not stamp
            // checkLoadQuotedLoadIdRef with a load id for a session that's
            // no longer the one on screen -- the fresh session for the
            // NEW selection creates and owns its own quoted load instead.
            if (checkLoadGenerationRef.current === myGeneration) {
              checkLoadQuotedLoadIdRef.current = loadId;
            }
            const stopsPayload = buildCheckLoadStopsPayload();
            if (!stopsPayload.length) return loadId;
            // load_stops rows for a real multi-stop job -- created once,
            // right alongside the load itself; harmless no-op for the
            // normal (no extra stops) case above.
            return Promise.all(
              stopsPayload.map((s, i) =>
                axios.post(
                  `${apiBaseUrl}/load_stops/`,
                  {
                    load_id: loadId, sequence: i + 1, stop_type: s.stop_type,
                    location: s.location, lat: s.lat, lng: s.lng,
                    appointment_datetime: s.appointment_datetime,
                  },
                  authHeaders()
                )
              )
            ).then(() => loadId);
          });

    ensureLoadId
      .then((loadId) =>
        axios.post(`${apiBaseUrl}/scripted/fleet/save-route-plan`, { load_id: loadId, routes: payload }, authHeaders())
          .then((r) => rememberRouteDecision(loadId, r))
      )
      .catch((err) => {
        // Was a silent no-op -- if ensureLoadId itself fails (e.g. the
        // POST /loads/ 500s), the ref this flow's own "Assign"/"Log this
        // call" action depends on never gets set, and that button
        // downstream silently no-ops with zero indication why. Confirmed
        // real: an empty (optional) delivery deadline sent as "" 500'd
        // this exact call (psycopg2 InvalidDatetimeFormat), swallowed
        // here, and the dispatcher just saw a dead button.
        if (checkLoadGenerationRef.current === myGeneration && !checkLoadQuotedLoadIdRef.current) {
          toast.error(err.response?.data?.detail || "Couldn't save this quote -- try again or check the load details");
        }
      });
  }

  // New: Check Load had no assign action at all before -- it was pure
  // exploration. Mirrors createLoadFromBrokerCall's quoted-load path (this
  // flow always has a quoted load by the time this is callable, since the
  // button only renders once step 3's routes have loaded) but skips
  // rate_negotiations -- Check Load never framed itself as a negotiation
  // flow the way Log Broker Call does, so there's no floor/counter context
  // to log here.
  // --- Double-booking pre-check (runs BEFORE the SMS / home-time popups) ---
  // The backend write guard stays authoritative; this shows a styled warning early and, on confirm,
  // captures the backend's exact conflict token to carry into the write. Cancel just closes cleanly.
  const [doubleBook, setDoubleBook] = useState(null);
  const [doubleBookBusy, setDoubleBookBusy] = useState(false);
  const doubleBookTokenRef = useRef(null);

  function runDoubleBookCheck(truckId, pickup, delivery, excludeLoadId, proceed) {
    if (doubleBookBusy || doubleBook) return;   // a check is already in flight / modal open
    doubleBookTokenRef.current = null;
    if (!truckId) { proceed(); return; }
    setDoubleBookBusy(true);
    axios
      .get(`${apiBaseUrl}/fleet/loads/check-double-book`, {
        ...authHeaders(),
        params: { truck_id: truckId, pickup: pickup || "", delivery: delivery || "", exclude_load_id: excludeLoadId || "" },
      })
      .then((r) => {
        if (r.data && r.data.conflict) {
          setDoubleBook({
            unit: r.data.unit,
            conflicts: r.data.conflicts || [],
            message: r.data.message,
            onConfirm: () => { doubleBookTokenRef.current = r.data.token; setDoubleBook(null); proceed(); },
          });
        } else {
          proceed();
        }
      })
      .catch(() => { proceed(); }) // check failed -> the authoritative write guard still enforces
      .finally(() => setDoubleBookBusy(false));
  }

  // If the truck's tank capacity is unconfirmed (NULL), prompt for it before
  // running `onReady` (the assign/show-routes action). If it's already known,
  // proceed immediately. Skipping leaves it NULL -> optimizer assumes 200.
  function ensureTankCapacity(truck, onReady) {
    if (!truck || truck.tank_capacity_gallons != null) {
      if (onReady) onReady();
      return;
    }
    setCapacityPrompt({ truckId: truck.id, unit: truck.unit_number, onReady: onReady || (() => {}) });
  }

  async function saveTankCapacity(truckId, gallons) {
    const t = trucks.find((x) => x.id === truckId);
    const body = { tank_capacity_gallons: gallons };
    // current_fuel_gallons is stored as gauge% × capacity (200 while unconfirmed),
    // so a smaller new tank would otherwise read nonsensically (e.g. "102 / 80 gal").
    // Rescale current fuel to the new tank: new = (old_fuel / old_capacity) × new_capacity,
    // capped at the tank. The telematics sync recomputes this from live gauge% on its
    // next tick (and coalesce-keeps this value when the ELD is dark).
    let newFuel = null;
    const oldDiv = t && t.tank_capacity_gallons ? Number(t.tank_capacity_gallons) : 200;
    if (t && t.current_fuel_gallons != null && oldDiv > 0) {
      newFuel = Math.round(Math.min(gallons, (Number(t.current_fuel_gallons) / oldDiv) * gallons) * 10) / 10;
      body.current_fuel_gallons = newFuel;
    }
    await axios.put(`${apiBaseUrl}/trucks/${truckId}`, body, authHeaders());
    setTrucks((prev) => prev.map((x) => (x.id === truckId ? { ...x, tank_capacity_gallons: gallons, ...(newFuel != null ? { current_fuel_gallons: newFuel } : {}) } : x)));
    // Drop any cached route previews/trip-plans so routes recompute with the new
    // capacity -- otherwise a plan computed at the old tank could linger on screen.
    setBrokerRoutePreview(null); setBrokerRouteTripPlans({});
    setCheckLoadRoutePreview(null); setCheckLoadRouteTripPlans({});
  }

  async function saveDriverPhone(driverId, phone) {
    const clean = (phone || "").trim();
    await axios.put(`${apiBaseUrl}/drivers/${driverId}`, { phone: clean || null }, authHeaders());
    setDrivers((prev) => ({ ...prev, [driverId]: { ...(prev[driverId] || {}), phone: clean || null } }));
  }

  function assignCheckLoad() {
    const quotedLoadId = checkLoadQuotedLoadIdRef.current;
    const cached = checkLoadQuoteRoutesRef.current;
    if (!quotedLoadId || !selectedTruckId || checkLoadAssigning) return;
    setCheckLoadAssigning(true);
    const form = checkLoadForm;
    // Optional, captured up front on step 1 -- never blocks the assign
    // click. Previously required HERE (every entry path funnels through
    // this function: the plain Assign button, the expanded-map popup's own
    // Assign action, and the fuel-SMS/PTO popups' deferred onNo/onConfirm
    // calls), but the map-modal path had no visible field to satisfy it
    // with, so it silently blocked dispatchers with nothing on screen to
    // fix. Falls back to "Unknown" rather than the old permanent "Check
    // load (unspecified)" placeholder.
    const customerName = form.customer_name?.trim() || "Unknown";
    const finalRateOffered = form.offered_rate !== "" && form.offered_rate != null ? Number(form.offered_rate) : 0;
    // Captured before closeCheckLoadModal() resets this state back to
    // defaults in the success handler below.
    const ptoEnabled = checkLoadPtoEnabled;
    const ptoUntil = checkLoadPtoUntil;
    const ptoNote = checkLoadPtoNote;
    const ptoDriverId = selectedDriver?.id || null;

    axios
      .put(
        `${apiBaseUrl}/loads/${quotedLoadId}`,
        {
          assigned_truck_id: selectedTruckId, status: "in_transit", rate_offered: finalRateOffered,
          customer_name: customerName, load_source: "check_load",
          confirm_double_book_token: doubleBookTokenRef.current || undefined,
        },
        authHeaders()
      )
      .then(() => {
        if (!cached) return null;
        const payload = buildRouteOptionsPayload(cached.routes, cached.tripPlanByType, finalRateOffered);
        return axios.post(
          `${apiBaseUrl}/scripted/fleet/save-route-plan`,
          { load_id: quotedLoadId, routes: payload, chosen_route_type: checkLoadChosenRouteType },
          authHeaders()
        ).then((r) => rememberRouteDecision(quotedLoadId, r));
      })
      .then(() => {
        setCheckLoadAssignResult({ loadId: quotedLoadId });
        toast.success("Truck assigned to this load");
        reloadLoads();
        // Best-effort, separate from the load assignment itself -- a
        // failure here shouldn't make the dispatcher think the load
        // assignment (which already succeeded) failed too.
        if (ptoEnabled && ptoDriverId) {
          // form.delivery_deadline is the optional deadline FIELD, not
          // necessarily what actually landed in the load's own
          // delivery_datetime -- saveCheckLoadQuote falls back to pickup +
          // the route's own duration when the field was left blank (see
          // its own comment), and this must anchor PTO to that SAME value
          // or a deadline-less load silently never schedules time off at
          // all (createTimeOffAfterLoad rejects outright on a missing
          // date) -- confirmed real, reproduced live.
          const ptoStartsAt = form.delivery_deadline || addHoursToNaiveUtc(form.pickup_datetime, cached?.routes?.[0]?.duration_hours);
          createTimeOffAfterLoad(ptoDriverId, ptoStartsAt, ptoUntil, ptoNote || `Home time after load to ${form.destination_location || "destination"}`)
            .then(() => {
              toast.success("Time off scheduled after this load");
              reloadTrucksAndDrivers();
            })
            .catch((err) => toast.error(err.response?.data?.error || err.response?.data?.detail || "Load assigned, but couldn't schedule time off"));
        }
        // Same reasoning as Log Broker Call's identical redirect -- the
        // truck's already selected here (Check Load is always opened
        // against one), so just close the modal and jump to its Route tab.
        closeCheckLoadModal();
        setActiveTab("route");
      })
      .catch((err) => toast.error(err.response?.data?.detail || "Couldn't assign this truck"))
      .finally(() => setCheckLoadAssigning(false));
  }

  // Shared by the modal's own × button and the post-assign redirect above.
  function closeCheckLoadModal() {
    setCheckLoadOpen(false); setCheckLoadMapModalOpen(false); setCheckLoadStep(1);
    setCheckLoadResult(null); setCheckLoadRoutePreview(null); setCheckLoadRouteTripPlans({});
    setCheckLoadExcludedStations([]);
    setCheckLoadChosenRouteType(null); checkLoadRouteVisibility.showAll();
    checkLoadQuotedLoadIdRef.current = null; checkLoadSourceQueuedLoadIdRef.current = null; checkLoadQuoteRoutesRef.current = null; checkLoadGenerationRef.current += 1;
    setCheckLoadAssignResult(null);
    setCheckLoadPtoEnabled(false); setCheckLoadPtoUntil(""); setCheckLoadPtoNote(""); setCheckLoadPtoPromptOpen(false);
    setCheckLoadFuelSmsOpen(false); setCheckLoadFuelSmsText(""); setCheckLoadFuelSmsSending(false); setCheckLoadFuelSmsResult(null); setCheckLoadFuelSmsPhone("");
    setCheckLoadManual((p) => ({ ...p, customer_name: "" }));
  }

  // Step 1 of the assignment wizard -- see openBrokerFuelSmsStep's twin.
  // selectedDriver here is already a full driver record (unlike the broker
  // flow's selectedCandidate), so no truck lookup is needed to get its id.
  function openCheckLoadFuelSmsStep() {
    const stops = checkLoadRouteTripPlans[checkLoadChosenRouteType]?.stops || [];
    setCheckLoadFuelSmsText(composeFuelStopsMessage(selectedDriver.driver_name, checkLoadRoutePreview?.data?.origin?.display_name, checkLoadRoutePreview?.data?.destination?.display_name, stops));
    // Pin the QUOTED load (the one save-route-plan wrote the plan + decision against) so load_id and
    // decision_id refer to the same plan; fall back to the board's selected load for load_id only.
    checkLoadFuelSmsMetaRef.current = pinFuelSmsMeta(checkLoadRoutePreview, selectedTruckId, checkLoadQuotedLoadIdRef.current || selectedLoadIdRef.current);
    setCheckLoadFuelSmsPhone(selectedDriver.phone || "");
    setCheckLoadFuelSmsResult(null);
    setCheckLoadFuelSmsOpen(true);
  }

  function sendCheckLoadFuelSms() {
    if (checkLoadFuelSmsSending) return;   // guard against double-send
    setCheckLoadFuelSmsSending(true);
    // Only pass phone through when the driver had none on file -- if
    // selectedDriver.phone was already set, checkLoadFuelSmsPhone just
    // echoes it back and the backend has nothing new to persist.
    const phoneToSave = selectedDriver.phone ? null : checkLoadFuelSmsPhone.trim();
    sendFuelStopsSms(selectedDriver.id, checkLoadFuelSmsText, phoneToSave, checkLoadFuelSmsMetaRef.current)
      .then((res) => setCheckLoadFuelSmsResult(res.data))
      .catch((err) => setCheckLoadFuelSmsResult({ sent: false, reason: err.response?.data?.error || "Request failed" }))
      .finally(() => setCheckLoadFuelSmsSending(false));
  }

  // Deep-link support: ?truck=<id>&tab=<route|fuel|next> lets other pages
  // (the Alerts list, the Fleet Command Center's "Attention needed" strip)
  // land the dispatcher directly on a specific truck instead of making them
  // search for it again. Only applies once, on the first render where the
  // truck list has actually loaded -- trucks doesn't exist yet on mount, and
  // this must never re-fire on a later trucks refresh and yank the
  // dispatcher back to a truck they've since navigated away from.
  const deepLinkAppliedRef = useRef(false);
  // Read by the "reset to Overview on truck change" effect below -- that
  // effect and this one both react to selectedTruckId changing, and would
  // otherwise race (whichever's setActiveTab call lands last wins), silently
  // dropping the deep-linked tab back to Overview.
  const pendingDeepLinkTabRef = useRef(null);
  useEffect(() => {
    if (deepLinkAppliedRef.current || trucks.length === 0) return;
    const truckId = searchParams.get("truck");
    if (!truckId) return;
    if (!trucks.some((t) => t.id === truckId)) return;
    deepLinkAppliedRef.current = true;
    const tab = searchParams.get("tab");
    if (tab) pendingDeepLinkTabRef.current = tab;
    setSelectedTruckId(truckId);
  }, [trucks, searchParams]);

  // Manual availability override: lets a dispatcher tell the board "this
  // driver is out until X" for personal-matters cases Motive/HOS data has
  // no way to know about. Purely informational for now -- it's shown next
  // to (and takes visual priority over) the HOS-based reset estimate, but
  // doesn't feed into match_truck_for_load or any other matching logic.
  const [availabilityFormOpen, setAvailabilityFormOpen] = useState(false);
  const [availabilityFormAt, setAvailabilityFormAt] = useState("");
  const [availabilityFormNote, setAvailabilityFormNote] = useState("");
  const [savingAvailability, setSavingAvailability] = useState(false);

  // Manually assigning a driver to a truck -- current_driver_id is a real,
  // already-writable column (Motive's own sync uses it too), so this is
  // just a UI for the same field, not a new data model. Matters most for
  // the ~20% of the fleet with no driver on file at all: match_truck_for_load
  // ranks those behind every staffed truck since there's no HOS data to say
  // when (or whether) they can actually run a load -- setting a driver here
  // is what turns one back into a normal, fully-ranked candidate.
  const [driverAssignFormOpen, setDriverAssignFormOpen] = useState(false);
  const [hosDetailsOpen, setHosDetailsOpen] = useState(false);
  const [driverAssignSelectedId, setDriverAssignSelectedId] = useState("");
  const [savingDriverAssign, setSavingDriverAssign] = useState(false);

  function assignDriver(truckId, driverId) {
    setSavingDriverAssign(true);
    axios
      .put(`${apiBaseUrl}/trucks/${truckId}`, { current_driver_id: driverId || null }, authHeaders())
      .then(() => {
        reloadTrucksAndDrivers();
        setDriverAssignFormOpen(false);
        setDriverAssignSelectedId("");
        toast.success(driverId ? "Driver assigned" : "Driver unassigned");
      })
      .catch(() => toast.error("Couldn't update the truck's driver"))
      .finally(() => setSavingDriverAssign(false));
  }

  // Manually reassigning which dispatcher owns a truck -- same pattern as
  // assignDriver above, same real writable column (dispatcher_id). The
  // picker only offers dispatchers already known from dispatcherOptions
  // (derived from trucks already on file) -- there's no separate /users/
  // fetch here, since the "dispatcher" role has no ACL access to that
  // table and a brand-new dispatcher with zero trucks has nothing to pick
  // from yet regardless.
  const [dispatcherAssignFormOpen, setDispatcherAssignFormOpen] = useState(false);
  const [dispatcherAssignSelectedId, setDispatcherAssignSelectedId] = useState("");
  const [savingDispatcherAssign, setSavingDispatcherAssign] = useState(false);

  function assignDispatcher(truckId, dispatcherId) {
    setSavingDispatcherAssign(true);
    axios
      .put(`${apiBaseUrl}/trucks/${truckId}`, { dispatcher_id: dispatcherId || null }, authHeaders())
      .then(() => {
        reloadTrucksAndDrivers();
        setDispatcherAssignFormOpen(false);
        setDispatcherAssignSelectedId("");
        toast.success(dispatcherId ? "Dispatcher reassigned" : "Dispatcher cleared");
      })
      .catch(() => toast.error("Couldn't update the truck's dispatcher"))
      .finally(() => setSavingDispatcherAssign(false));
  }

  const [brokerCallOpen, setBrokerCallOpen] = useState(false);
  const [brokerMessage, setBrokerMessage] = useState("");
  const [brokerCallResult, setBrokerCallResult] = useState(null); // {loading, narrative, parsed, error}
  // Structured entry, mirroring checkLoadManual's shape -- the default/only
  // way in now (per dispatcher feedback: "keep the boxes ... don't allow
  // free text"). "Describe it (AI-assisted)" stays available as an opt-in
  // secondary tab, same manual|describe pattern Check Load already shipped
  // (#163), rather than removing the AI path outright.
  const [brokerMode, setBrokerMode] = useState("manual"); // "manual" | "describe"
  const [brokerManual, setBrokerManual] = useState({
    origin_location: "", destination_location: "",
    pickup_datetime_local: "", delivery_deadline_local: "",
    equipment_type: "", offered_rate: "", load_weight_lbs: "",
    // Optional, shared by both manual and free-text/AI modes -- see the
    // input's own render comment. Manual mode has no other source for this
    // (submitBrokerManual's own parsed object never carried one before);
    // AI mode uses this as an override when set, else keeps whatever the
    // agent itself parsed out of the free text.
    customer_name: "",
    // Mandatory -- see checkLoadManual.hazmat_status's own comment, same
    // reasoning applies here.
    hazmat_status: "",
  });
  // Mirrors checkLoadOriginGeo/checkLoadLaneHistory exactly -- see that
  // state's own comment.
  const [brokerOriginGeo, setBrokerOriginGeo] = useState(null);
  const [brokerDestGeo, setBrokerDestGeo] = useState(null);
  const [brokerLaneHistory, setBrokerLaneHistory] = useState(null);
  function resolveBrokerLaneField(field) {
    const text = field === "origin" ? brokerManual.origin_location : brokerManual.destination_location;
    geocodeForLaneHistory(text).then((geo) => {
      if (field === "origin") {
        setBrokerOriginGeo(geo);
        fetchLaneHistory(geo, brokerDestGeo, null, setBrokerLaneHistory);
      } else {
        setBrokerDestGeo(geo);
        fetchLaneHistory(brokerOriginGeo, geo, null, setBrokerLaneHistory);
      }
    });
  }
  // Mirrors checkLoadStops exactly -- same "pick up at A, then B, deliver
  // at C" job a broker occasionally quotes as one load (see that state's
  // own comment). Deliberately NOT sent to match-truck-for-load's
  // fleet-wide ranking (stays at today's first-pickup/last-drop
  // approximation, same posture as Check Load's stops) -- only feeds the
  // route/map preview and the load_stops rows created once a load exists.
  const [brokerStops, setBrokerStops] = useState([]); // [{location, stop_type, appointment_datetime_local, lat, lng, timezone, resolving, error}]

  function addBrokerStop() {
    setBrokerStops((prev) => [
      ...prev,
      { location: "", stop_type: "dropoff", appointment_datetime_local: "", lat: null, lng: null, timezone: null, resolving: false, error: null },
    ]);
  }
  function removeBrokerStop(index) {
    setBrokerStops((prev) => prev.filter((_, i) => i !== index));
  }
  function updateBrokerStop(index, patch) {
    setBrokerStops((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }
  function resolveBrokerStop(index) {
    const stop = brokerStops[index];
    if (!stop || !stop.location.trim() || stop.resolving) return;
    updateBrokerStop(index, { resolving: true, error: null });
    axios
      .post(`${apiBaseUrl}/scripted/fleet/geocode-location`, { location: stop.location }, authHeaders())
      .then((res) => {
        updateBrokerStop(index, {
          lat: res.data.lat, lng: res.data.lng, timezone: res.data.timezone,
          location: res.data.display_name || stop.location, resolving: false,
        });
      })
      .catch((err) => {
        updateBrokerStop(index, { resolving: false, error: err.response?.data?.error || "Couldn't resolve this location" });
      });
  }
  const brokerStopsReady = brokerStops.every((s) => s.lat != null && s.lng != null);
  function buildBrokerStopsPayload() {
    return brokerStops
      .filter((s) => s.lat != null && s.lng != null)
      .map((s) => {
        const dt = s.appointment_datetime_local ? new Date(s.appointment_datetime_local) : null;
        return {
          lat: s.lat, lng: s.lng, location: s.location, stop_type: s.stop_type,
          appointment_datetime: dt && !Number.isNaN(dt.getTime()) ? dt.toISOString() : null,
        };
      });
  }
  const [creatingLoadFromCall, setCreatingLoadFromCall] = useState(false);
  const [createdLoadResult, setCreatedLoadResult] = useState(null); // {loadId} once created
  // Same "going on time off after this load" capture as Check Load's own
  // (see checkLoadPtoEnabled's comment) -- kept as separate state since
  // Log Broker Call and Check Load are two independent modals that can
  // both be open... well, not literally at once, but each has its own
  // full reset-on-close cycle and shouldn't share state.
  const [brokerPtoEnabled, setBrokerPtoEnabled] = useState(false);
  const [brokerPtoUntil, setBrokerPtoUntil] = useState("");
  const [brokerPtoNote, setBrokerPtoNote] = useState("");
  const [brokerPtoPromptOpen, setBrokerPtoPromptOpen] = useState(false);
  // Step 1 of the assignment wizard (push fuel stops), runs before the PTO
  // step above -- same "separate state per flow" reasoning as brokerPto*.
  const [brokerFuelSmsOpen, setBrokerFuelSmsOpen] = useState(false);
  const [brokerFuelSmsDriverId, setBrokerFuelSmsDriverId] = useState(null);
  const [brokerFuelSmsText, setBrokerFuelSmsText] = useState("");
  const brokerFuelSmsMetaRef = useRef(null);   // pilot: pinned recommendation identity at compose time
  const [brokerFuelSmsSending, setBrokerFuelSmsSending] = useState(false);
  const [brokerFuelSmsResult, setBrokerFuelSmsResult] = useState(null);
  // Only populated (and only shown as an editable field) when the driver
  // has no phone on file -- see PushFuelStopsPopup.
  const [brokerFuelSmsPhone, setBrokerFuelSmsPhone] = useState("");
  const [brokerRoutePreview, setBrokerRoutePreview] = useState(null); // {loading, data, error, truckId}
  const [brokerChosenRouteType, setBrokerChosenRouteType] = useState(null);
  const brokerRouteVisibility = useRouteVisibility(brokerRoutePreview?.data?.best_match_route_options, brokerChosenRouteType);
  const [brokerRouteTripPlans, setBrokerRouteTripPlans] = useState({}); // route_type -> plan_full_trip response
  // Same fuel/HOS-stops-load-after-the-route-lines gate as Check Load (see
  // checkLoadFuelLoading): block "Log to Truck" until the per-route
  // plan-full-trip sims settle, so a dispatcher never commits before seeing
  // the fuel plan. brokerRouteTripPlans goes {}-> fully populated in one
  // setState (null entries on error -> never a permanent block).
  const brokerRoutesShown = !!(brokerRoutePreview?.data?.best_match_route_options?.length);
  const brokerFuelLoading =
    !!brokerRoutePreview?.loading ||
    (brokerRoutesShown && Object.keys(brokerRouteTripPlans).length === 0);
  // Per-truck cache of the real (paid) match-truck-for-load response --
  // geometry + tolls only, keyed by truck_id. The fleet-wide ranking pass
  // only ever fetches real routes/tolls for the #1 winner (see
  // fetchTruckOptions' truckOptions.data.best_match shortcut below), so
  // switching to any OTHER candidate genuinely needs one real HERE call
  // the first time. Without this cache, bouncing back and forth between
  // candidates (B -> C -> B) re-paid for B's routes every single time,
  // even though the road geometry/toll cost hadn't changed. Deliberately
  // does NOT cache the trip plan (fuel/rest stops) alongside it -- that
  // simulation is free (local, no HERE cost) and depends on truck/driver
  // state that keeps moving in real time, so it's always re-run fresh
  // against this cached geometry, every visit (see fetchBrokerRoutes).
  const brokerRouteCacheRef = useRef({});
  // The most recently fetched real loaded-leg (pickup -> destination) HERE
  // data, keyed to a vehicle profile (axle_count/trailer_axle_count) by the
  // backend itself -- see match-truck-for-load's reusable_loaded_routes.
  // The loaded leg is the SAME real road route for any two trucks sharing
  // an equipment profile, so passing this back as reuse_loaded_route on the
  // NEXT candidate fetch lets the backend skip re-buying that identical
  // HERE request and only fetch the genuinely-per-truck deadhead leg --
  // cuts a same-profile candidate's first-view cost from 3 real HERE
  // requests down to 1. Not scoped per-profile (just "the last one seen")
  // since the backend itself verifies the profile actually matches before
  // reusing it -- a mismatch just falls through to a normal full fetch.
  const loadedRouteCacheRef = useRef(null);
  // The load created the moment real routes are priced (see
  // saveBrokerQuote) -- a ref, not state, since it's only ever read inside
  // async handlers (never rendered) and doesn't need to trigger a re-render
  // when it's set. Reused across the whole wizard session (e.g. switching
  // candidate trucks re-prices the SAME quoted load rather than spawning a
  // new one each time); reset back to null wherever the wizard restarts.
  const brokerQuotedLoadIdRef = useRef(null);
  // Same purpose as checkLoadGenerationRef -- bumped on every new broker
  // call session / modal reset, checked by async callbacks before they
  // apply a result, so a stale response for an abandoned call can't
  // silently overwrite the load/candidate currently on screen.
  const brokerCallGenerationRef = useRef(0);
  // {routes, tripPlanByType} from the last saveBrokerQuote call -- lets
  // createLoadFromBrokerCall re-save with contribution_profit recomputed
  // against the final agreed rate without re-fetching anything.
  const brokerQuoteRoutesRef = useRef(null);

  // The wizard: 1 (pick a truck from the ranked list) -> 2 (that truck's
  // negotiation numbers + a live-editable offer) -> 3 (real routes for
  // that truck, then create the load). Replaces the old single-scroll
  // panel where picking a different truck meant scrolling back up to see
  // its numbers -- each step only shows what's relevant to the decision
  // being made right now.
  const [wizardStep, setWizardStep] = useState(1);
  const [truckOptions, setTruckOptions] = useState(null); // {loading, data, error} -- data.candidates is the real-priced ranked list
  const [selectedCandidateTruckId, setSelectedCandidateTruckId] = useState(null);
  // Stations the dispatcher has rejected off the map ("too far off route,
  // try another combination") for THIS quote only -- [{id, name}], never
  // persisted. Passed to plan-full-trip as excluded_truck_stop_ids so
  // simulate_trip_stops picks the next-best real station instead. Reset
  // whenever a fresh search starts (see submitBrokerCall/submitBrokerManual).
  const [brokerExcludedStations, setBrokerExcludedStations] = useState([]);
  // The broker's number as currently being negotiated -- starts as whatever
  // the agent parsed from the call, but is a live input in step 2 so a
  // counter-offer can be plugged in and profit/counter recalculated
  // instantly (computeNegotiation mirrors the backend's own formula).
  const [offeredRate, setOfferedRate] = useState(null);
  // Real cargo weight -- unlike offeredRate, never AI-parsed (load_intake_agent
  // has no reason to guess at it from free text), a dispatcher-entered
  // manual override only. Feeds match-truck-for-load's own gross_weight_kg
  // (see fetchBrokerRoutes) so weight-restricted routing actually applies.
  const [loadWeightLbs, setLoadWeightLbs] = useState(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);

  // include_route_geometry costs nothing extra here -- the real HERE calls
  // for the winning truck's route alternatives already happen on every
  // match-truck-for-load request regardless of this flag (the fleet-wide
  // search always prices the winning truck's real routes, geometry or
  // not); the flag only controls whether the response includes what was
  // already fetched. Requesting it up front means fetchBrokerRoutes can
  // often skip its own full re-fetch below (see that function).
  function fetchTruckOptions(parsedOverride) {
    const p = parsedOverride || brokerCallResult?.parsed;
    if (!p || truckOptions?.loading) return;
    setTruckOptions({ loading: true });
    axios
      .post(
        `${apiBaseUrl}/scripted/fleet/match-truck-for-load`,
        {
          origin_location: p.origin_location,
          destination_location: p.destination_location,
          pickup_datetime: p.pickup_datetime,
          delivery_deadline: p.delivery_deadline,
          equipment_type: p.equipment_type,
          offered_rate: p.offered_rate,
          load_weight_lbs: p.load_weight_lbs ?? null,
          // AI-parsed broker calls already resolved a real UTC pickup time
          // themselves (default true here); submitBrokerManual sets
          // parsed.timestamps_are_utc = false so its raw facility-local
          // digits get converted with the pickup city's real timezone
          // instead of being taken as already-UTC.
          timestamps_are_utc: p.timestamps_are_utc !== false,
          include_route_geometry: true,
        },
        authHeaders()
      )
      .then((res) => {
        setTruckOptions({ loading: false, data: res.data });
        if (res.data.reusable_loaded_routes) loadedRouteCacheRef.current = res.data.reusable_loaded_routes;
        // match-truck-for-load's response is the single source of truth for
        // the real, facility-timezone-converted UTC values (see its own
        // "Copy these, not whatever was passed in" comment) -- copy them
        // back onto brokerCallResult.parsed so every downstream consumer
        // (route preview, quote saving, load creation) uses the corrected
        // values instead of whatever naive string was originally submitted.
        setBrokerCallResult((prev) =>
          prev?.parsed
            ? {
                ...prev,
                parsed: {
                  ...prev.parsed,
                  pickup_datetime: res.data.pickup_datetime ?? prev.parsed.pickup_datetime,
                  delivery_deadline: res.data.delivery_deadline ?? prev.parsed.delivery_deadline,
                  origin_timezone: res.data.origin_timezone ?? prev.parsed.origin_timezone,
                  destination_timezone: res.data.destination_timezone ?? prev.parsed.destination_timezone,
                },
              }
            : prev
        );
      })
      .catch((err) => setTruckOptions({ loading: false, error: err.response?.data?.error || err.response?.data?.detail || err.message }));
  }

  // Shared by fetchBrokerRoutes' reuse path and its fresh-fetch path --
  // pure local simulation against geometry already fetched, no additional
  // real HERE cost either way.
  function fetchBrokerTripPlans(truckId, routes, origin, destination, excludedStationIds = brokerExcludedStations) {
    if (!routes?.length || !origin || !destination) return Promise.resolve({});
    setBrokerRouteTripPlans({});
    // See checkLoadGenerationRef's comment -- same staleness guard, this
    // flow's own version.
    const myGeneration = brokerCallGenerationRef.current;
    // Real stops between first pickup and last drop -- [] for a normal
    // load, which keeps taking the exact same code path as before this
    // existed. plan-full-trip already knows how to simulate through these
    // directly (same intermediate_stops contract Check Load's route preview
    // uses), so no separate multi-stop-route-options call is needed here to
    // get the cost/fuel/rest numbers right for each route type.
    const intermediateStopsPayload = buildBrokerStopsPayload();
    return Promise.all(
      routes.map((opt) =>
        axios
          .post(
            `${apiBaseUrl}/scripted/fleet/plan-full-trip`,
            {
              truck_id: truckId,
              origin_lat: origin.lat, origin_lng: origin.lng, origin_location: origin.display_name,
              destination_lat: destination.lat, destination_lng: destination.lng, destination_location: destination.display_name,
              geometry: opt.geometry,
              distance_miles: opt.distance_miles, duration_hours: opt.duration_hours,
              intermediate_stops: intermediateStopsPayload,
              // Stations the dispatcher has explicitly rejected ("too far
              // off route") for THIS quote -- see simulate_trip_stops' own
              // docstring. Session-scoped only, never persisted.
              excluded_truck_stop_ids: excludedStationIds.map((s) => s.id),
            },
            authHeaders()
          )
          .then((r) => [opt.route_type, r.data])
          .catch(() => [opt.route_type, null])
      )
    ).then((pairs) => {
      const tripPlanByType = Object.fromEntries(pairs);
      const routesWithPlans = attachTripPlans(routes, pairs);  // GM3A: bind plan to its own route by position
      if (brokerCallGenerationRef.current === myGeneration) {
        setBrokerRouteTripPlans(tripPlanByType);
        saveBrokerQuote(routesWithPlans, tripPlanByType, myGeneration);
      }
      return tripPlanByType;
    });
  }

  // "This gas station's way off, try another combination" -- excludes one
  // real station (by its truck_stop_id, the same id simulate_trip_stops
  // keys its own candidate pool by) from every route being compared, then
  // re-runs just the trip-plan simulation (no new HERE routing call, no
  // re-fetch of match-truck-for-load) so the next-best real station gets
  // picked instead. next is passed explicitly rather than read back off
  // brokerExcludedStations state, which wouldn't have updated yet inside
  // this same tick.
  function excludeBrokerFuelStation(stopId, stopName) {
    if (!stopId || brokerExcludedStations.some((s) => s.id === stopId)) return;
    const next = [...brokerExcludedStations, { id: stopId, name: stopName || "that station" }];
    setBrokerExcludedStations(next);
    const routes = brokerRoutePreview?.data?.best_match_route_options;
    const origin = brokerRoutePreview?.data?.origin;
    const destination = brokerRoutePreview?.data?.destination;
    if (routes?.length && origin && destination && selectedCandidateTruckId) {
      fetchBrokerTripPlans(selectedCandidateTruckId, routes, origin, destination, next);
    }
  }

  function resetBrokerExcludedStations() {
    setBrokerExcludedStations([]);
    const routes = brokerRoutePreview?.data?.best_match_route_options;
    const origin = brokerRoutePreview?.data?.origin;
    const destination = brokerRoutePreview?.data?.destination;
    if (routes?.length && origin && destination && selectedCandidateTruckId) {
      fetchBrokerTripPlans(selectedCandidateTruckId, routes, origin, destination, []);
    }
  }

  // Persists the routes/prices the dispatcher is looking at the moment
  // they're actually computed here -- not just if/when "Create load & log
  // negotiation" gets clicked -- so a lane that gets priced but never
  // booked still leaves a real "quoted" record instead of the routes being
  // computed live and thrown away. Reuses the same quoted load across
  // repeated calls in one wizard session (switching candidate trucks, or
  // the AI/dispatcher's own re-price) via brokerQuotedLoadIdRef --
  // save-route-plan already supersedes the prior route set on each call,
  // so this never orphans an old batch. Best-effort: a failure here must
  // not block the dispatcher from seeing the freshly computed preview.
  function saveBrokerQuote(routes, tripPlanByType, myGeneration) {
    const p = brokerCallResult?.parsed;
    if (!p || !routes?.length) return;
    if (myGeneration == null) myGeneration = brokerCallGenerationRef.current;
    // Cached so createLoadFromBrokerCall can re-save with contribution_profit
    // recomputed against the FINAL agreed rate at assignment time, without
    // needing to re-fetch routes/trip plans it already has.
    brokerQuoteRoutesRef.current = { routes, tripPlanByType };
    const payload = buildRouteOptionsPayload(routes, tripPlanByType, offeredRate ?? p.offered_rate);

    // Same reasoning as createLoadFromBrokerCall's own fallbacks -- the
    // manual-entry path's `parsed` (submitBrokerManual) never carries
    // origin_lat/lng, timezones, or loaded_miles the AI path's parsed does,
    // so these fall back to truckOptions.data (the same match-truck-for-load
    // response already fetched for both entry paths). Confirmed real:
    // without this, a manually-entered broker call crashed the moment
    // routes were fetched (this runs automatically from
    // fetchBrokerTripPlans) with a NOT NULL violation on loads.miles_loaded.
    const matchData = truckOptions?.data;
    const originLat = p.origin_lat ?? matchData?.origin?.lat ?? null;
    const originLng = p.origin_lng ?? matchData?.origin?.lng ?? null;
    const destinationLat = p.destination_lat ?? matchData?.destination?.lat ?? null;
    const destinationLng = p.destination_lng ?? matchData?.destination?.lng ?? null;
    const originTimezone = p.origin_timezone ?? matchData?.origin_timezone ?? null;
    const destinationTimezone = p.destination_timezone ?? matchData?.destination_timezone ?? null;
    const milesLoaded = routes?.[0]?.loaded_miles ?? p.loaded_miles ?? matchData?.loaded_miles ?? null;
    // Same reasoning as originLat/originLng above -- match-truck-for-load's
    // own resolved place name (matchData.origin.display_name) wins over
    // p.origin_location, which for the manual-entry path is whatever raw
    // text the dispatcher typed (often just a zip code in real usage).
    const originDisplayName = matchData?.origin?.display_name || p.origin_location;
    const destinationDisplayName = matchData?.destination?.display_name || p.destination_location;

    const ensureLoadId = brokerQuotedLoadIdRef.current
      ? Promise.resolve(brokerQuotedLoadIdRef.current)
      : axios
          .get(`${apiBaseUrl}/equipment_types/?limit=50`, authHeaders())
          .then((eqRes) => {
            const eqRows = eqRes.data.records || eqRes.data || [];
            const eqMatch = p.equipment_type ? eqRows.find((e) => e.code === p.equipment_type) : null;
            return axios.post(
              `${apiBaseUrl}/loads/`,
              {
                // brokerManual.customer_name (typed once, shared by both
                // manual and AI modes -- see that field's own render
                // comment) wins whenever set, regardless of which mode
                // produced `p` or whether it was typed before/after the AI
                // parse resolved. Falls back to whatever the AI itself
                // extracted, then the placeholder as a last resort -- never
                // blocks assign either way.
                customer_name: brokerManual.customer_name?.trim() || p.customer_name || "Broker call (unspecified)",
                hazmat_status: brokerManual.hazmat_status,
                origin_location: originDisplayName,
                destination_location: destinationDisplayName,
                // keep the city label above; ALSO preserve the typed zip for reporting
                origin_zip: zipFromText(brokerManual.origin_location || p.origin_location),
                destination_zip: zipFromText(brokerManual.destination_location || p.destination_location),
                origin_lat: originLat, origin_lng: originLng,
                destination_lat: destinationLat, destination_lng: destinationLng,
                pickup_datetime: p.pickup_datetime,
                // Falls back to pickup + the route's own simulated
                // duration when no deadline was given -- see
                // addHoursToNaiveUtc's own docstring (a NULL here makes
                // the load invisible on the Fleet Load Board).
                delivery_datetime: p.delivery_deadline || addHoursToNaiveUtc(p.pickup_datetime, routes?.[0]?.duration_hours),
                origin_timezone: originTimezone,
                destination_timezone: destinationTimezone,
                rate_offered: offeredRate ?? p.offered_rate ?? p.suggested_counter ?? 0,
                miles_loaded: milesLoaded,
                required_equipment_type_id: eqMatch ? eqMatch.id : null,
                load_weight_lbs: loadWeightLbs,
                status: "quoted",
              },
              authHeaders()
            );
          })
          .then((loadRes) => {
            const loadId = loadRes.data.record?.id || loadRes.data.id;
            if (brokerCallGenerationRef.current === myGeneration) {
              brokerQuotedLoadIdRef.current = loadId;
            }
            const stopsPayload = buildBrokerStopsPayload();
            if (!stopsPayload.length) return loadId;
            // load_stops rows for a real multi-stop job -- created once,
            // right alongside the load itself, same as Check Load's own
            // saveCheckLoadQuote; harmless no-op for the normal (no extra
            // stops) case above.
            return Promise.all(
              stopsPayload.map((s, i) =>
                axios.post(
                  `${apiBaseUrl}/load_stops/`,
                  {
                    load_id: loadId, sequence: i + 1, stop_type: s.stop_type,
                    location: s.location, lat: s.lat, lng: s.lng,
                    appointment_datetime: s.appointment_datetime,
                  },
                  authHeaders()
                )
              )
            ).then(() => loadId);
          });

    ensureLoadId
      .then((loadId) =>
        axios.post(`${apiBaseUrl}/scripted/fleet/save-route-plan`, { load_id: loadId, routes: payload }, authHeaders())
          .then((r) => rememberRouteDecision(loadId, r))
      )
      .catch((err) => {
        // Was a silent no-op -- if ensureLoadId itself fails (e.g. the
        // POST /loads/ 500s), the ref this flow's own "Assign"/"Log this
        // call" action depends on never gets set, and that button
        // downstream silently no-ops with zero indication why. Confirmed
        // real: an empty (optional) delivery deadline sent as "" 500'd
        // this exact call (psycopg2 InvalidDatetimeFormat), swallowed
        // here, and the dispatcher just saw a dead button.
        if (brokerCallGenerationRef.current === myGeneration && !brokerQuotedLoadIdRef.current) {
          toast.error(err.response?.data?.detail || "Couldn't save this quote -- try again or check the load details");
        }
      });
  }

  // Lets a dispatcher check a SPECIFIC truck of their own choosing against
  // this load, bypassing the AI's ranked shortlist entirely -- the backend
  // already supports this (match_truck_for_load's truck_id override,
  // requested_truck_check), it just had no UI wired to it before. The
  // result is merged into the existing ranked list (replacing that truck's
  // entry if the AI had already scored it) rather than replacing the whole
  // list, so a dispatcher can compare "my truck" against the AI's picks
  // side by side instead of losing the ranked view.
  const [manualTruckQuery, setManualTruckQuery] = useState("");
  const [manualTruckChecking, setManualTruckChecking] = useState(false);
  const [manuallyCheckedTruckIds, setManuallyCheckedTruckIds] = useState(() => new Set());

  function checkSpecificTruck(truckId) {
    const p = brokerCallResult?.parsed;
    if (!p || manualTruckChecking) return;
    setManualTruckChecking(true);
    axios
      .post(
        `${apiBaseUrl}/scripted/fleet/match-truck-for-load`,
        {
          origin_location: p.origin_location,
          destination_location: p.destination_location,
          pickup_datetime: p.pickup_datetime,
          delivery_deadline: p.delivery_deadline,
          equipment_type: p.equipment_type,
          offered_rate: offeredRate ?? p.offered_rate,
          truck_id: truckId,
          timestamps_are_utc: true,
        },
        authHeaders()
      )
      .then((res) => {
        const checked = (res.data.candidates || [])[0];
        if (!checked) return;
        setManuallyCheckedTruckIds((prev) => new Set(prev).add(checked.truck_id));
        setTruckOptions((prev) => {
          if (!prev?.data) return prev;
          const existing = prev.data.candidates || [];
          const withoutThisTruck = existing.filter((c) => c.truck_id !== checked.truck_id);
          return { ...prev, data: { ...prev.data, candidates: [...withoutThisTruck, checked] } };
        });
        setManualTruckQuery("");
      })
      .catch((err) => window.alert(err.response?.data?.error || err.response?.data?.detail || err.message))
      .finally(() => setManualTruckChecking(false));
  }

  // Real route/toll/time alternatives for whichever truck is currently
  // selected in the wizard (step 3) -- re-fetches only when the selected
  // truck actually changes (tracked via brokerRoutePreview.truckId), not
  // on every step-3 visit.
  function fetchBrokerRoutes() {
    const p = brokerCallResult?.parsed;
    const truckId = selectedCandidateTruckId || p?.matched_truck_id;
    if (!p || !truckId || brokerRoutePreview?.loading) return;
    const myGeneration = brokerCallGenerationRef.current;

    // fetchTruckOptions already requested include_route_geometry, so if
    // the dispatcher is looking at the SAME truck it already fully priced
    // (the common case: viewing the auto-picked winner without switching
    // candidates), reuse that response instead of re-running the whole
    // match pipeline a second time -- this was a real, measured duplicate:
    // every broker call paid for the fleet-wide search's real HERE calls
    // twice for no reason.
    if (!loadWeightLbs && truckOptions?.data?.best_match?.truck_id === truckId && truckOptions.data.best_match_route_options?.length) {
      const data = truckOptions.data;
      setBrokerRoutePreview({ loading: false, data, truckId });
      brokerRouteVisibility.showAll();
      // Deliberately starts unselected -- see checkLoadChosenRouteType's
      // reset in fetchCheckLoadRoutes for why this is safe.
      setBrokerChosenRouteType(null);
      fetchBrokerTripPlans(truckId, data.best_match_route_options, data.origin, data.destination);
      return;
    }

    // Any OTHER candidate already viewed once this wizard session -- the
    // fleet-wide ranking pass never fetches real routes for anyone but the
    // #1 winner (that's the whole point of the shortcut above: paying for
    // real HERE routing on all 5 shortlisted trucks just to show a ranked
    // list would be real money for candidates the dispatcher usually never
    // even opens). So the FIRST time a non-top candidate is opened, a real
    // fetch below is unavoidable -- but once it's been fetched, the real
    // geometry/tolls don't change, so reuse THOSE for the rest of this
    // session instead of re-paying HERE every time the dispatcher bounces
    // back to a truck they've already looked at. The fuel/rest stop
    // simulation is different: it's free (local, no HERE cost) and depends
    // on truck/driver state (fuel level, HOS hours remaining) that keeps
    // moving in real time, so that part still re-runs on every visit --
    // only the expensive routing/tolls half is cached.
    // Never read (or, below, written) when a real weight is set -- same
    // "no reuse for a real weight" rule as the backend's own route cache
    // and dedup logic (see match-truck-for-load's own comments): a cached
    // entry from an earlier no-weight (or different-weight) view must
    // never be served back for this one.
    const cached = !loadWeightLbs ? brokerRouteCacheRef.current[truckId] : null;
    if (cached) {
      setBrokerRoutePreview({ loading: false, data: cached.data, truckId });
      brokerRouteVisibility.showAll();
      setBrokerChosenRouteType(null);
      fetchBrokerTripPlans(truckId, cached.data.best_match_route_options, cached.data.origin, cached.data.destination);
      return;
    }

    setBrokerRoutePreview({ loading: true, truckId });
    axios
      .post(
        `${apiBaseUrl}/scripted/fleet/match-truck-for-load`,
        {
          origin_location: p.origin_location,
          destination_location: p.destination_location,
          pickup_datetime: p.pickup_datetime,
          delivery_deadline: p.delivery_deadline,
          equipment_type: p.equipment_type,
          offered_rate: offeredRate ?? p.offered_rate,
          load_weight_lbs: loadWeightLbs,
          truck_id: truckId,
          include_route_geometry: true,
          // If we already have a real loaded-leg fetch from ANY earlier
          // candidate this session (top pick or otherwise), hand it back --
          // the backend itself checks whether the profile actually matches
          // THIS truck before reusing it, so a mismatch just falls through
          // to a normal full fetch. See loadedRouteCacheRef's declaration.
          reuse_loaded_route: loadedRouteCacheRef.current || undefined,
          // p.pickup_datetime/delivery_deadline here are already the
          // tool's own UTC-resolved values from the initial call (copied
          // through by the agent) -- tell it not to re-interpret them as
          // facility-local and shift them a second time.
          timestamps_are_utc: true,
        },
        authHeaders()
      )
      .then((res) => {
        // Cache the real (paid) routing/toll data regardless of whether
        // this specific wizard session is still the current one -- another
        // candidate/truck switch mid-flight shouldn't waste a real HERE
        // call that's still valid to reuse later, it just shouldn't be
        // APPLIED to whatever's on screen right now.
        // Never cached for reuse when a real weight was used -- see the
        // read-side gate above for why.
        if (!loadWeightLbs) brokerRouteCacheRef.current[truckId] = { data: res.data };
        if (res.data.reusable_loaded_routes) loadedRouteCacheRef.current = res.data.reusable_loaded_routes;

        if (brokerCallGenerationRef.current !== myGeneration) return;

        setBrokerRoutePreview({ loading: false, data: res.data, truckId });
        brokerRouteVisibility.showAll();
        setBrokerChosenRouteType(null);

        // Fuel/HOS rest stops per route -- same simulation the Route tab
        // uses once a load is live, run here against the not-yet-created
        // load's coordinates directly (plan_full_trip accepts those as a
        // preview-mode fallback when there's no load_id yet).
        fetchBrokerTripPlans(truckId, res.data.best_match_route_options, res.data.origin, res.data.destination);
      })
      .catch((err) => {
        if (brokerCallGenerationRef.current !== myGeneration) return;
        setBrokerRoutePreview({ loading: false, error: err.response?.data?.detail || err.message });
      });
  }

  function submitBrokerCall() {
    if (!brokerMessage.trim() || !brokerManual.hazmat_status || brokerCallResult?.loading) return;
    setBrokerCallResult({ loading: true });
    setCreatedLoadResult(null);
    setBrokerRoutePreview(null);
    setBrokerRouteTripPlans({});
    brokerQuotedLoadIdRef.current = brokerSourceQueuedLoadIdRef.current;  // reuse queued load if opened from the Queue; else null = new
    brokerQuoteRoutesRef.current = null;
    brokerCallGenerationRef.current += 1;
    brokerRouteCacheRef.current = {};
    loadedRouteCacheRef.current = null;
    setTruckOptions(null);
    setSelectedCandidateTruckId(null);
    setOfferedRate(null);
    setLoadWeightLbs(null);
    setBrokerExcludedStations([]);
    setMapModalOpen(false);
    setWizardStep(1);
    axios
      .post(
        `${apiBaseUrl}/agents/load_intake_agent/run`,
        { inputs: { message: brokerMessage, current_datetime: new Date().toISOString() } },
        authHeaders()
      )
      .then((res) => {
        const out = res.data.output || "";
        const idx = out.indexOf("---JSON---");
        const narrative = idx !== -1 ? out.slice(0, idx).trim() : out.trim();
        let parsed = null;
        if (idx !== -1) {
          try {
            parsed = JSON.parse(out.slice(idx + 10).trim());
          } catch {
            parsed = null;
          }
        }
        if (!parsed) {
          setBrokerCallResult({ loading: false, error: "Couldn't understand that load -- try including origin, destination, and pickup time." });
          return;
        }
        // Delivery due date and offered rate are mandatory -- see
        // submitCheckLoad's identical gate for why pickup/delivery times
        // are deliberately NOT carried over into the manual tab here (the
        // AI path's UTC-resolved times vs. the manual tab's raw
        // facility-local digits would silently shift by the UTC offset).
        if (parsed.delivery_deadline == null || parsed.offered_rate == null) {
          setBrokerManual((p) => ({
            ...p,
            origin_location: parsed.origin_location || p.origin_location,
            destination_location: parsed.destination_location || p.destination_location,
            equipment_type: parsed.equipment_type || p.equipment_type,
            offered_rate: parsed.offered_rate != null ? String(parsed.offered_rate) : p.offered_rate,
            load_weight_lbs: parsed.load_weight_lbs != null ? String(parsed.load_weight_lbs) : p.load_weight_lbs,
          }));
          setBrokerMode("manual");
          setBrokerCallResult({
            loading: false,
            error: "Delivery due date and offered rate are required -- enter them below (pickup/delivery times need to be re-entered here too).",
          });
          return;
        }
        if (!hasSaneOperationalYear(parsed.pickup_datetime) || !hasSaneOperationalYear(parsed.delivery_deadline)) {
          setBrokerManual((p) => ({
            ...p,
            origin_location: parsed.origin_location || p.origin_location,
            destination_location: parsed.destination_location || p.destination_location,
            equipment_type: parsed.equipment_type || p.equipment_type,
            offered_rate: parsed.offered_rate != null ? String(parsed.offered_rate) : p.offered_rate,
            load_weight_lbs: parsed.load_weight_lbs != null ? String(parsed.load_weight_lbs) : p.load_weight_lbs,
          }));
          setBrokerMode("manual");
          setBrokerCallResult({
            loading: false,
            error: "That description's pickup/delivery date looks wrong (an unusual year) -- enter both below to be sure.",
          });
          return;
        }
        if (isDeliveryBeforePickup(parsed.pickup_datetime, parsed.delivery_deadline)) {
          setBrokerManual((p) => ({
            ...p,
            origin_location: parsed.origin_location || p.origin_location,
            destination_location: parsed.destination_location || p.destination_location,
            equipment_type: parsed.equipment_type || p.equipment_type,
            offered_rate: parsed.offered_rate != null ? String(parsed.offered_rate) : p.offered_rate,
            load_weight_lbs: parsed.load_weight_lbs != null ? String(parsed.load_weight_lbs) : p.load_weight_lbs,
          }));
          setBrokerMode("manual");
          setBrokerCallResult({
            loading: false,
            error: "That description's delivery due date is before its pickup time -- enter both below (pickup/delivery times need to be re-entered here too).",
          });
          return;
        }
        setBrokerCallResult({ loading: false, narrative, parsed });
        setOfferedRate(parsed.offered_rate ?? null);
        const reused = matchToolResultFromAgentRun(res.data);
        if (reused) {
          setTruckOptions({ loading: false, data: reused });
          if (reused.reusable_loaded_routes) loadedRouteCacheRef.current = reused.reusable_loaded_routes;
        } else {
          fetchTruckOptions(parsed);
        }
      })
      .catch((err) => setBrokerCallResult({ loading: false, error: err.response?.data?.detail || err.message }));
  }

  // Structured-entry counterpart to submitBrokerCall -- no load_intake_agent
  // call at all, since every field it would have extracted is already typed
  // directly into boxes. Builds the exact same {narrative, parsed} shape
  // submitBrokerCall's AI path produces (narrative left null and unused --
  // the AI explanation feature was removed) so every downstream consumer
  // (negotiation, fetchBrokerRoutes, saveBrokerQuote, createLoadFromBrokerCall)
  // keeps working completely unmodified regardless of which tab was used.
  function submitBrokerManual() {
    const m = brokerManual;
    if (!m.origin_location.trim() || !m.destination_location.trim() || !m.pickup_datetime_local || !brokerStopsReady || !m.hazmat_status || brokerCallResult?.loading) return;
    setBrokerCallResult({ loading: true });
    setCreatedLoadResult(null);
    setBrokerRoutePreview(null);
    setBrokerRouteTripPlans({});
    brokerQuotedLoadIdRef.current = brokerSourceQueuedLoadIdRef.current;  // reuse queued load if opened from the Queue; else null = new
    brokerQuoteRoutesRef.current = null;
    brokerCallGenerationRef.current += 1;
    brokerRouteCacheRef.current = {};
    loadedRouteCacheRef.current = null;
    setTruckOptions(null);
    setSelectedCandidateTruckId(null);
    setBrokerExcludedStations([]);
    setMapModalOpen(false);
    setWizardStep(1);

    // Same naive-facility-clock convention submitCheckLoadManual uses (see
    // its own comment) -- send the raw datetime-local digits untouched and
    // let match-truck-for-load resolve the pickup city's real timezone
    // itself via timestamps_are_utc: false, instead of converting here using
    // the dispatcher's own browser timezone.
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(m.pickup_datetime_local)) {
      setBrokerCallResult({ loading: false, error: "Enter a valid pickup date/time." });
      return;
    }

    const zipErr = zipOnlyError(m.origin_location, m.destination_location);
    if (zipErr) {
      setBrokerCallResult({ loading: false, error: zipErr });
      return;
    }

    const parsed = {
      origin_location: m.origin_location,
      destination_location: m.destination_location,
      pickup_datetime: m.pickup_datetime_local,
      delivery_deadline: m.delivery_deadline_local || null,
      equipment_type: m.equipment_type || null,
      offered_rate: m.offered_rate ? Number(m.offered_rate) : null,
      load_weight_lbs: m.load_weight_lbs ? Number(m.load_weight_lbs) : null,
      customer_name: m.customer_name?.trim() || null,
      timestamps_are_utc: false,
    };
    setBrokerCallResult({ loading: false, narrative: null, parsed });
    setOfferedRate(parsed.offered_rate);
    setLoadWeightLbs(parsed.load_weight_lbs);
    fetchTruckOptions(parsed);
  }

  // Mirrors the existing "Assign to this truck" pattern -- a human-triggered
  // write, not something the agent commits automatically. Creates the load
  // (status stays "available", not assigned) and logs the negotiation
  // against the matched truck so there's a real record of the floor/counter
  // the recommendation was based on.
  function createLoadFromBrokerCall() {
    const p = brokerCallResult?.parsed;
    if (!p || creatingLoadFromCall) return;
    setCreatingLoadFromCall(true);
    // Captured before closeBrokerCallModal() resets this state back to
    // defaults in the success handler below.
    const ptoEnabled = brokerPtoEnabled;
    const ptoUntil = brokerPtoUntil;
    const ptoNote = brokerPtoNote;

    // If the dispatcher picked a different truck from "Show best options"
    // than the agent's own pick, log the negotiation against THAT truck --
    // not silently overriding what they explicitly chose. A route-level
    // override (below) still applies on top of whichever truck is active.
    const selectedCandidate = selectedCandidateTruckId
      ? (truckOptions?.data?.candidates || []).find((c) => c.truck_id === selectedCandidateTruckId)
      : null;
    const matchedTruckId = selectedCandidate ? selectedCandidate.truck_id : p.matched_truck_id;

    // If the dispatcher picked a different route in the preview than the
    // agent's own (cheapest) pick, log the negotiation against THAT route's
    // floor -- not silently overriding the agent's recommendation.
    // brokerChosenRouteType starts unselected (see fetchBrokerRoutes' own
    // reset -- explicit ask: no route is chosen until a dispatcher actually
    // picks one) and is set only by an explicit route click, so it's
    // routinely still null right here whenever a dispatcher logs a load
    // without ever touching a route. Falling back to the cheapest option is
    // what keeps that path from silently nulling delivery_datetime
    // (invisible-on-Load-Board bug) and making createTimeOffAfterLoad
    // reject before any network call ever fired (the generic "couldn't
    // schedule time off" toast, no real error surfaced) -- both confirmed
    // live via a direct API read showing delivery_datetime: null on a load
    // whose button clearly said "Toll-free route".
    const availableRoutes = brokerRoutePreview?.data?.best_match_route_options || [];
    const chosenRoute =
      availableRoutes.find((r) => r.route_type === brokerChosenRouteType)
      || availableRoutes.find((r) => r.route_type === cheapestRouteType(availableRoutes))
      || null;
    const minimumAcceptableRate = chosenRoute
      ? chosenRoute.minimum_acceptable_rate
      : selectedCandidate
      ? selectedCandidate.minimum_acceptable_rate
      : p.minimum_acceptable_rate;

    const finalRateOffered = offeredRate ?? p.offered_rate ?? chosenRoute?.suggested_counter ?? p.suggested_counter ?? minimumAcceptableRate ?? 0;
    const quotedLoadId = brokerQuotedLoadIdRef.current;

    // Same reasoning as matchedTruckId/minimumAcceptableRate above -- the
    // manual-entry path's own `parsed` (submitBrokerManual) never carries
    // origin_lat/lng, timezones, or loaded_miles the way the AI path's
    // parsed does (that one bakes them in from its own match-truck-for-load
    // tool call), so these fall back to truckOptions.data -- the SAME
    // match-truck-for-load response fetchTruckOptions already stored for
    // both entry paths. Confirmed real: without this, a manually-entered
    // broker call crashed on creation with a NOT NULL violation on
    // loads.miles_loaded, since p.loaded_miles was always undefined there.
    const matchData = truckOptions?.data;
    const originLat = p.origin_lat ?? matchData?.origin?.lat ?? null;
    const originLng = p.origin_lng ?? matchData?.origin?.lng ?? null;
    const destinationLat = p.destination_lat ?? matchData?.destination?.lat ?? null;
    const destinationLng = p.destination_lng ?? matchData?.destination?.lng ?? null;
    const originTimezone = p.origin_timezone ?? matchData?.origin_timezone ?? null;
    const destinationTimezone = p.destination_timezone ?? matchData?.destination_timezone ?? null;
    const milesLoaded = chosenRoute?.loaded_miles ?? selectedCandidate?.loaded_miles ?? p.loaded_miles ?? matchData?.loaded_miles ?? null;
    const originDisplayName = matchData?.origin?.display_name || p.origin_location;
    const destinationDisplayName = matchData?.destination?.display_name || p.destination_location;

    // The routes/prices for this lane were already saved as a "quoted"
    // load the moment they were computed (see saveBrokerQuote) -- assigning
    // just flips that SAME load active and marks whichever route the
    // dispatcher settled on as the one actually being run, instead of
    // creating a second, duplicate load row here. Only falls back to
    // creating one fresh (the old behavior) if no routes were ever computed
    // for this call at all -- i.e. the agent found no matching truck, so
    // there was nothing to quote in the first place.
    const assignPromise = quotedLoadId
      ? axios
          .put(
            `${apiBaseUrl}/loads/${quotedLoadId}`,
            matchedTruckId
              ? { assigned_truck_id: matchedTruckId, status: "in_transit", rate_offered: finalRateOffered, confirm_double_book_token: doubleBookTokenRef.current || undefined }
              : { rate_offered: finalRateOffered },
            authHeaders()
          )
          .then(() => {
            // Re-save with contribution_profit recomputed against the
            // FINAL agreed rate -- the quote-time save only had whatever
            // rate happened to be typed in when routes were first priced,
            // and that's the number fleet_stats.py's real profit/hour math
            // will read off this load once it's in_transit. Passing
            // chosen_route_type here already marks the dispatcher's picked
            // route as is_chosen on the fresh rows (save-route-plan's own
            // resolution logic), so a separate select-route call isn't
            // needed on top of this. Only worth doing when there's actually
            // a truck being assigned -- otherwise nothing was "chosen".
            const cached = brokerQuoteRoutesRef.current;
            if (!matchedTruckId || !cached) return null;
            const payload = buildRouteOptionsPayload(cached.routes, cached.tripPlanByType, finalRateOffered);
            return axios.post(
              `${apiBaseUrl}/scripted/fleet/save-route-plan`,
              { load_id: quotedLoadId, routes: payload, chosen_route_type: brokerChosenRouteType },
              authHeaders()
            ).then((r) => rememberRouteDecision(quotedLoadId, r));
          })
          .then(() => quotedLoadId)
      : axios
          .get(`${apiBaseUrl}/equipment_types/?limit=50`, authHeaders())
          .then((eqRes) => {
            const eqRows = eqRes.data.records || eqRes.data || [];
            const eqMatch = p.equipment_type ? eqRows.find((e) => e.code === p.equipment_type) : null;

            return axios.post(
              `${apiBaseUrl}/loads/`,
              {
                // brokerManual.customer_name (typed once, shared by both
                // manual and AI modes -- see that field's own render
                // comment) wins whenever set, regardless of which mode
                // produced `p` or whether it was typed before/after the AI
                // parse resolved. Falls back to whatever the AI itself
                // extracted, then the placeholder as a last resort -- never
                // blocks assign either way.
                customer_name: brokerManual.customer_name?.trim() || p.customer_name || "Broker call (unspecified)",
                hazmat_status: brokerManual.hazmat_status,
                origin_location: originDisplayName,
                destination_location: destinationDisplayName,
                // keep the city label above; ALSO preserve the typed zip for reporting
                origin_zip: zipFromText(brokerManual.origin_location || p.origin_location),
                destination_zip: zipFromText(brokerManual.destination_location || p.destination_location),
                origin_lat: originLat,
                origin_lng: originLng,
                destination_lat: destinationLat,
                destination_lng: destinationLng,
                pickup_datetime: p.pickup_datetime,
                // Falls back to pickup + the chosen route's own simulated
                // duration when no deadline was given -- see
                // addHoursToNaiveUtc's own docstring (a NULL here makes
                // the load invisible on the Fleet Load Board).
                delivery_datetime: p.delivery_deadline || addHoursToNaiveUtc(p.pickup_datetime, chosenRoute?.duration_hours),
                origin_timezone: originTimezone,
                destination_timezone: destinationTimezone,
                rate_offered: finalRateOffered,
                miles_loaded: milesLoaded,
                required_equipment_type_id: eqMatch ? eqMatch.id : null,
                load_weight_lbs: loadWeightLbs,
                // Same reasoning as the quoted-load path above: assign
                // directly when there's a matched truck, same as the
                // board's own manual-assign flow. Only stays unassigned
                // ("quoted") if the agent genuinely found no truck that
                // makes the deadline -- nothing to assign.
                ...(matchedTruckId ? { assigned_truck_id: matchedTruckId, status: "in_transit", confirm_double_book_token: doubleBookTokenRef.current || undefined } : { status: "quoted" }),
              },
              authHeaders()
            );
          })
          .then((loadRes) => loadRes.data.record?.id || loadRes.data.id);

    assignPromise
      .then((loadId) =>
        axios
          .post(
            `${apiBaseUrl}/rate_negotiations/`,
            {
              load_id: loadId,
              truck_id: matchedTruckId,
              rate_offered: offeredRate ?? p.offered_rate ?? null,
              minimum_acceptable_rate: minimumAcceptableRate,
              status: "open",
            },
            authHeaders()
          )
          .then(() => loadId)
      )
      .then((loadId) => {
        setCreatedLoadResult({ loadId });
        toast.success("Load created and negotiation logged");
        reloadLoads();
        // Best-effort, separate from the load itself -- same reasoning as
        // Check Load's identical hookup. Only possible when a truck (and
        // therefore a driver to attach time off to) was actually assigned.
        if (ptoEnabled && matchedTruckId) {
          const matchedTruck = trucks.find((t) => t.id === matchedTruckId);
          const ptoDriverId = matchedTruck?.current_driver_id?.id || matchedTruck?.current_driver_id || null;
          if (ptoDriverId) {
            // p.delivery_deadline is the optional deadline FIELD, not
            // necessarily what actually landed in the load's own
            // delivery_datetime -- saveBrokerQuote/the fallback-create path
            // above both fall back to pickup + a route's own duration when
            // the field was left blank, and this must anchor PTO to that
            // SAME kind of value or a deadline-less load silently never
            // schedules time off at all (createTimeOffAfterLoad rejects
            // outright on a missing date) -- confirmed real, reproduced live.
            const ptoStartsAt = p.delivery_deadline
              || addHoursToNaiveUtc(p.pickup_datetime, chosenRoute?.duration_hours ?? brokerQuoteRoutesRef.current?.routes?.[0]?.duration_hours);
            createTimeOffAfterLoad(ptoDriverId, ptoStartsAt, ptoUntil, ptoNote || `Home time after load to ${p.destination_location || "destination"}`)
              .then(() => {
                toast.success("Time off scheduled after this load");
                reloadTrucksAndDrivers();
              })
              .catch((err) => toast.error(err.response?.data?.error || err.response?.data?.detail || "Load created, but couldn't schedule time off"));
          }
        }
        // Land the dispatcher straight on this truck's Route tab -- that's
        // the natural next thing they want after committing a load, not
        // back at the modal they just finished with. Only when a truck was
        // actually assigned (matchedTruckId) -- a "quoted" (unassigned)
        // load has no truck/route to jump to yet.
        if (matchedTruckId) {
          closeBrokerCallModal();
          // The switch-truck effect below always resets activeTab to
          // "overview" unless a deep-linked tab is already queued -- same
          // mechanism the ?truck=&tab= URL deep link uses, so this redirect
          // rides the same rail instead of racing that effect with its own
          // setActiveTab call.
          pendingDeepLinkTabRef.current = "route";
          setSelectedTruckId(matchedTruckId);
        }
      })
      .catch((err) => toast.error(err.response?.data?.detail || "Couldn't create the load/negotiation"))
      .finally(() => setCreatingLoadFromCall(false));
  }

  // Shared by the modal's own × button and the post-log redirect above --
  // both need the exact same "blank slate" reset so reopening Log Broker
  // Call (for a different load) never shows stale state from the last one.
  function closeBrokerCallModal() {
    setBrokerCallOpen(false); setBrokerCallResult(null); setBrokerMessage(""); setBrokerManual({ origin_location: "", destination_location: "", pickup_datetime_local: "", delivery_deadline_local: "", equipment_type: "", offered_rate: "", load_weight_lbs: "", customer_name: "" }); setCreatedLoadResult(null); setBrokerRoutePreview(null); setBrokerRouteTripPlans({}); brokerQuotedLoadIdRef.current = null; brokerSourceQueuedLoadIdRef.current = null; brokerQuoteRoutesRef.current = null; brokerCallGenerationRef.current += 1; brokerRouteCacheRef.current = {}; loadedRouteCacheRef.current = null; setTruckOptions(null); setSelectedCandidateTruckId(null); setOfferedRate(null); setLoadWeightLbs(null); setBrokerExcludedStations([]); setMapModalOpen(false); setWizardStep(1); setBrokerPtoEnabled(false); setBrokerPtoUntil(""); setBrokerPtoNote(""); setBrokerPtoPromptOpen(false); setBrokerFuelSmsOpen(false); setBrokerFuelSmsDriverId(null); setBrokerFuelSmsText(""); setBrokerFuelSmsSending(false); setBrokerFuelSmsResult(null); setBrokerFuelSmsPhone("");
  }

  // Step 1 of the assignment wizard -- resolves the driver the same way
  // the PTO-after-load booking already does (matchedTruck.current_driver_id,
  // NOT candidate itself, which never carries a raw driver id) and
  // composes the editable SMS from whichever route is currently chosen.
  // Takes `candidate` as a param -- selectedCandidate is only ever derived
  // locally inside each render closure, not available at this scope.
  function openBrokerFuelSmsStep(candidate) {
    const matchedTruck = trucks.find((t) => t.id === candidate.truck_id);
    const driverId = matchedTruck?.current_driver_id?.id || matchedTruck?.current_driver_id || null;
    const stops = brokerRouteTripPlans[brokerChosenRouteType]?.stops || [];
    setBrokerFuelSmsDriverId(driverId);
    setBrokerFuelSmsText(composeFuelStopsMessage(candidate.driver_name, brokerRoutePreview?.data?.origin?.display_name, brokerRoutePreview?.data?.destination?.display_name, stops));
    brokerFuelSmsMetaRef.current = pinFuelSmsMeta(brokerRoutePreview, candidate.truck_id, brokerQuotedLoadIdRef.current);
    setBrokerFuelSmsPhone(drivers[driverId]?.phone || "");
    setBrokerFuelSmsResult(null);
    setBrokerFuelSmsOpen(true);
  }

  function sendBrokerFuelSms() {
    if (brokerFuelSmsSending) return;   // guard against double-send
    setBrokerFuelSmsSending(true);
    // Same rule as sendCheckLoadFuelSms: only pass a phone through when
    // the driver genuinely had none on file.
    const phoneToSave = drivers[brokerFuelSmsDriverId]?.phone ? null : brokerFuelSmsPhone.trim();
    sendFuelStopsSms(brokerFuelSmsDriverId, brokerFuelSmsText, phoneToSave, brokerFuelSmsMetaRef.current)
      .then((res) => setBrokerFuelSmsResult(res.data))
      .catch((err) => setBrokerFuelSmsResult({ sent: false, reason: err.response?.data?.error || "Request failed" }))
      .finally(() => setBrokerFuelSmsSending(false));
  }

  useEffect(() => {
    // Picking a truck that's actively on a load lands straight on Current Load
    // ("route") instead of Overview -- that's the view a dispatcher wants first
    // (where is it, what's driven so far). An explicit deep-linked tab still
    // wins, and a free/unassigned truck still defaults to Overview since it has
    // no current load to show.
    const resolvedTab = pendingDeepLinkTabRef.current || (selectedLoad ? "route" : "overview");
    setActiveTab(resolvedTab);
    // Deep-linking (or the post-booking redirect above) straight onto Next
    // Trips bypasses the tab button's own onClick, which is the only other
    // place that expands this panel -- without this, cards land in the
    // narrow default width and read as "hidden" until a dispatcher notices
    // the manual expand toggle.
    if (resolvedTab === "next") setDetailExpanded(true);
    pendingDeepLinkTabRef.current = null;
    setDriverAssignFormOpen(false);
    setDriverAssignSelectedId("");
    // checkLoadForm is deliberately NOT reset here -- a common use is
    // checking the same hypothetical load against a different truck to
    // compare, so the load details should survive a truck switch even
    // though the previous truck's RESULT (specific to that truck) must not.
    setCheckLoadStep(1);
    setCheckLoadResult(null);
    setCheckLoadRoutePreview(null);
    setCheckLoadRouteTripPlans({});
    setCheckLoadExcludedStations([]);
    setCheckLoadChosenRouteType(null);
    checkLoadRouteVisibility.showAll();
    checkLoadQuotedLoadIdRef.current = null;
    checkLoadQuoteRoutesRef.current = null;
    checkLoadGenerationRef.current += 1;
    setCheckLoadAssignResult(null);
    setNextTripPreviewId(null);
    setNextTripPreviewRoute(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTruckId]);

  // Mirrors the deep-link effect above in the other direction: keeps
  // ?truck=&tab= in the URL in sync with whatever's actually on screen, so a
  // dispatcher can copy the address bar at any point (after picking a truck,
  // after switching tabs) and it reproduces exactly this view for whoever
  // they send it to -- not just on the one-time incoming-link case that
  // effect handles. replace (not push) so clicking through trucks/tabs
  // doesn't spam the back-button history with every click.
  //
  // Skips writing while a deep-linked tab is still pending (pendingDeepLinkTabRef
  // non-null) -- selectedTruckId updates one render before the tab-reset
  // effect above actually applies that pending tab, and writing here in
  // between would briefly clobber the URL's real tab back to "overview".
  useEffect(() => {
    if (!selectedTruckId || pendingDeepLinkTabRef.current) return;
    const next = new URLSearchParams(searchParams);
    next.set("truck", selectedTruckId);
    next.set("tab", activeTab);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTruckId, activeTab]);

  // fuel_prices is an intentional history table (see fuel_price_import.py's
  // own docstring: "a fresh row per import run... callers that only care
  // about today's price should query the most recent price_date per
  // station") -- the daily email import never overwrites, it inserts. A
  // station with a week of import history has a week of rows all pointing
  // at the same truck_stop_id/lat-lng, which without this dedup rendered
  // as that many stacked markers at one point (confirmed real: a cluster
  // showing "6" at a single real station once clustering made the stacking
  // visible instead of just very slow, and skewed priceRange with stale
  // prices). Grouped by truck_stop_id where linked (the normal case);
  // unlinked legacy rows (pre-dating that join -- see mapFuelMarkers'
  // own comment) fall back to a rounded-coordinate key so duplicates among
  // THOSE collapse too, not just the common case.
  const latestFuelStations = useMemo(() => {
    const groups = new Map();
    for (const fp of fuelStations) {
      const stopRef = fp.truck_stop_id;
      const stopId = stopRef?.id || stopRef;
      const key = stopId || `coord-${Number(fp.lat).toFixed(4)},${Number(fp.lng).toFixed(4)}`;
      const existing = groups.get(key);
      if (!existing || new Date(fp.price_date || fp.created_at) > new Date(existing.price_date || existing.created_at)) {
        groups.set(key, fp);
      }
    }
    return [...groups.values()];
  }, [fuelStations]);

  const priceRange = useMemo(() => {
    const prices = latestFuelStations.map((s) => Number(s.price_per_gallon)).filter((p) => !isNaN(p));
    if (!prices.length) return { min: 0, max: 0 };
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }, [latestFuelStations]);

  // Positions come from truck_stops (real, Overpass-sourced locations);
  // prices come from fuel_prices, joined in via fuel_prices.truck_stop_id.
  // A fuel_prices row without a linked truck stop yet still renders at its
  // own lat/lng (today's curated data, pre-dating this join) so nothing
  // already shown on this layer disappears from the map. A truck stop with
  // no linked price renders too, just without a price color -- real
  // position, no cost data, which is an honest state to show rather than
  // hiding it.
  const mapFuelMarkers = useMemo(() => {
    const stopById = {};
    truckStops.forEach((ts) => { stopById[ts.id] = ts; });

    const linkedStopIds = new Set();
    const priced = latestFuelStations.map((fp) => {
      const stopRef = fp.truck_stop_id;
      const stopId = stopRef?.id || stopRef;
      const stop = stopId ? stopById[stopId] : null;
      if (stop) linkedStopIds.add(stop.id);
      return {
        id: fp.id,
        lat: stop ? stop.lat : fp.lat,
        lng: stop ? stop.lng : fp.lng,
        name: (stop && stop.name) || fp.station_name,
        state: (stop && stop.state) || fp.state,
        price_per_gallon: fp.price_per_gallon,
        hasPrice: true,
      };
    });

    const unpriced = truckStops
      .filter((ts) => !linkedStopIds.has(ts.id))
      .map((ts) => ({
        id: `stop-${ts.id}`,
        lat: ts.lat,
        lng: ts.lng,
        name: ts.name,
        price_per_gallon: null,
        hasPrice: false,
      }));

    return [...priced, ...unpriced];
  }, [latestFuelStations, truckStops]);

  // Bounds source for the Route tab's map view. MapFlyTo alone only
  // centers on the truck at a fixed zoom -- fine for a local pickup, but a
  // repositioning truck can be a thousand+ miles from its own pickup point,
  // which then sits entirely off-screen along with the whole loaded leg.
  // Sample first/mid/last of each visible segment (not every point --
  // that's plenty for a bounding box, and real HERE geometry can run into
  // the tens of thousands of points).
  const routeViewPoints = useMemo(() => {
    if (activeTab !== "route" || !routeSegments) return null;
    const routeOptions = loadEconomics?.data?.routes_considered;
    const chosenType = loadEconomics?.data?.chosen_route?.route_type;
    const chosenOption = routeOptions?.find((o) => o.route_type === chosenType);
    const points = [];
    if (!routeVisibility.isHidden(PICKUP_ROUTE_KEY)) {
      const deadheadPositions = chosenOption?.geometry?.deadhead?.length
        ? chosenOption.geometry.deadhead
        : (roadRoute?.deadhead || routeSegments.deadhead);
      if (deadheadPositions?.length) points.push(deadheadPositions[0], deadheadPositions[deadheadPositions.length - 1]);
    }
    const showAllRoutes = routeOptions?.length > 1;
    const candidateOptions = showAllRoutes ? routeOptions : [chosenOption].filter(Boolean);
    const visibleOptions = candidateOptions.filter((opt) => opt && !routeVisibility.isHidden(opt.route_type));
    visibleOptions.forEach((opt) => {
      // Same source as the polyline itself (see its own comment) -- a
      // multi-stop load's real road detour can swing well outside the
      // straight geometry.loaded line's own bounds, so fitting to that
      // alone cropped the real stop (and the bent road reaching it) clean
      // out of the visible map, confirmed real and user-reported.
      const legGeometries = fullTripPlans[opt.route_type]?.data?.leg_geometries || [];
      if (legGeometries.length > 0) {
        legGeometries.forEach((leg) => {
          if (leg?.length) points.push(leg[0], leg[Math.floor(leg.length / 2)], leg[leg.length - 1]);
        });
        return;
      }
      const positions = opt.geometry?.loaded?.length ? opt.geometry.loaded : (roadRoute?.loaded || routeSegments.loaded);
      if (positions?.length) {
        points.push(positions[0], positions[Math.floor(positions.length / 2)], positions[positions.length - 1]);
      }
    });
    // Include the ghost marker's own position (see its render block's
    // comment) so viewing a chained load's map actually pans/zooms to
    // show BOTH where the truck really is right now and where this load's
    // own deadhead will start -- without this, the ghost marker still
    // rendered correctly but sat off-screen unless the dispatcher happened
    // to manually pan there, which defeats the entire point of it.
    if (selectedLoad) {
      const idx = selectedTruckLoads.findIndex((l) => l.id === selectedLoad.id);
      const precedingLoad = idx > 0 ? selectedTruckLoads[idx - 1] : null;
      if (precedingLoad?.destination_lat != null && precedingLoad?.destination_lng != null) {
        points.push([Number(precedingLoad.destination_lat), Number(precedingLoad.destination_lng)]);
      }
    }
    // Fit the actual driven breadcrumbs too (deadhead approach + loaded path)
    // so a diverged real route is never cropped out of view behind the plan.
    [loadBreadcrumbs?.deadhead, loadBreadcrumbs?.loaded].forEach((path) => {
      if (path?.length) points.push(path[0], path[Math.floor(path.length / 2)], path[path.length - 1]);
    });
    return points.length >= 2 ? points : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, routeSegments, loadEconomics, routeVisibility.hidden, roadRoute, fullTripPlans, selectedLoad, selectedTruckLoads, loadBreadcrumbs]);

  // Identity for routeViewPoints that changes only when the dispatcher
  // actually picks something different -- NOT on every live-position poll
  // (see MapFitToPoints comment). Drives whether the map re-fits its bounds.
  const routeFitKey = useMemo(() => {
    const chosenType = loadEconomics?.data?.chosen_route?.route_type;
    return [
      selectedTruckId,
      selectedLoad?.id,
      chosenType,
      Array.from(routeVisibility.hidden).sort().join(","),
      // re-fit once the actual breadcrumbs arrive (they load async, after the
      // planned route) so a diverged real path pulls the bounds out to include it
      (loadBreadcrumbs?.loaded?.length || 0) + ":" + (loadBreadcrumbs?.deadhead?.length || 0),
    ].join("|");
  }, [selectedTruckId, selectedLoad, loadEconomics, routeVisibility.hidden, loadBreadcrumbs]);

  const mapCenter = selectedTruck
    ? [Number(selectedTruck.current_lat), Number(selectedTruck.current_lng)]
    : [37.5, -96.5];

  // Both early-return before the page's own root div below -- without their
  // own className/DARK_THEME_VARS they'd render with the app's default
  // (light) :root values instead of this page's dark theme, same portal-vs-
  // cascade gap as the createPortal modals elsewhere in this file. Confirmed
  // real: styles.state didn't even exist (a stale reference), so this was
  // rendering completely unstyled -- raw text top-left of a blank page.
  if (loading) {
    return (
      <div className="op-center-dark" style={{ ...themeVars, ...styles.loadingScreen }}>
        <div style={styles.loadingMark}>AI</div>
        <div style={styles.loadingSpinner} />
        <div style={styles.loadingText}>Loading dispatch console…</div>
        <div style={styles.loadingSubtext}>Pulling live truck positions, loads, and HOS status</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="op-center-dark" style={{ ...themeVars, ...styles.loadingScreen }}>
        <div style={{ ...styles.loadingText, color: "#f87171" }}>Failed to load: {error}</div>
      </div>
    );
  }

  return (
    <div className="op-center-dark" style={{ ...themeVars, ...styles.page }}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={() => navigate("/dispatch")} style={styles.exitBtn} title="Back to Fleet Command Center">
            <ArrowLeft size={15} />
          </button>
          <div style={styles.brandMark}><TruckIcon size={16} /></div>
          <div>
            <div style={styles.brandTitle}>Dispatch Console</div>
            <div style={styles.brandSubtitle}>Decision layer on top of your TMS — {trucks.length} trucks</div>
          </div>
        </div>

        <div style={styles.headerStats}>
          <div style={styles.headerStat}>
            <TrendingUp size={13} style={{ color: fleetSummary.totalProfit >= 0 ? "#4cae6d" : "#e05d5d" }} />
            <div>
              <div style={{ ...styles.headerStatValue, color: fleetSummary.totalProfit >= 0 ? "#4cae6d" : "#e05d5d" }}>
                ${Math.round(fleetSummary.totalProfit).toLocaleString()}
              </div>
              <div style={styles.headerStatLabel}>est. profit in transit</div>
            </div>
          </div>
          <ProfitPerMileStat apiBaseUrl={apiBaseUrl} />
        </div>

        <button
          onClick={() => openQuickCheck("broker_call")}
          style={styles.brokerCallBtn}
        >
          <Phone size={13} /> Quick Price Check
        </button>

        <button
          onClick={() => { brokerSourceQueuedLoadIdRef.current = null; setBrokerStops([]); setBrokerMode("manual"); setBrokerCallOpen(true); }}
          style={{ ...styles.brokerCallBtn, marginLeft: 8 }}
        >
          <Navigation size={13} /> Assign Load
        </button>

        {(() => {
          const total = alertCounts ? alertCounts.critical + alertCounts.warning + alertCounts.info : 0;
          return (
            <button
              onClick={() => navigate("/dispatch/alerts")}
              style={styles.bellBtn}
              title={total > 0 ? `${total} alert${total === 1 ? "" : "s"}` : "Alerts"}
            >
              <Bell size={16} color="var(--ink-2)" />
              {total > 0 && (
                <span style={{ ...styles.bellBadge, background: alertCounts.critical > 0 ? "#dc2626" : "#a16207" }}>
                  {total > 9 ? "9+" : total}
                </span>
              )}
            </button>
          );
        })()}

        {isImpersonating && (
          <div style={styles.impersonationChip}>
            <VenetianMask size={13} />
            <span>
              Impersonating <strong>{impersonatedUser?.sip_name || impersonatedUser?.email}</strong>
            </span>
            <button type="button" onClick={stopImpersonation} style={styles.stopImpersonationBtn}>
              Stop
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={toggleTheme}
          style={styles.themeToggleBtn}
          title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
        >
          {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
        </button>

        <UserMenu apiBaseUrl={apiBaseUrl} isImpersonating={isImpersonating} theme={theme === "dark" ? "dark" : "light"} />
      </header>

      <div style={{ padding: "0 14px" }}>
        <AnnouncementBanner apiBaseUrl={apiBaseUrl} authHeaders={authHeaders} />
      </div>

    <div style={styles.wrap}>
      {/* Tank-capacity prompt (portal) -- top-level so it mounts whether it was
          triggered from the Overview tab, "Assign Load to Truck", or the fleet-
          ranking "Assign Load" wizard (which can be open with no truck selected). */}
      {capacityPrompt && (
        <TankCapacityModal
          unit={capacityPrompt.unit}
          onConfirm={async (gallons) => {
            const cb = capacityPrompt.onReady;
            try { await saveTankCapacity(capacityPrompt.truckId, gallons); } catch (e) { /* best-effort */ }
            setCapacityPrompt(null);
            if (cb) cb();
          }}
          onSkip={() => {
            const cb = capacityPrompt.onReady;
            setCapacityPrompt(null);
            if (cb) cb();
          }}
          onCancel={() => setCapacityPrompt(null)}
        />
      )}

      {/* ---- Queue "Assign to a truck" picker: pick a truck, then route into the native Check Load flow ---- */}
      {qpLoad && (
        <div style={styles.modalOverlay} onClick={() => setQpLoad(null)}>
          <div style={{ ...styles.modalCard, width: 420 }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}><Navigation size={14} /> Assign to a truck</div>
              <button style={styles.modalCloseBtn} onClick={() => setQpLoad(null)}>×</button>
            </div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>{qpLoad.customer_name || "Load"} · {qpLoad.origin_location} → {qpLoad.destination_location}</div>
            <input value={qpSearch} onChange={(e) => setQpSearch(e.target.value)} placeholder="Search by driver or truck #…" style={{ ...styles.modalInput, marginBottom: 10 }} autoFocus />
            <div style={{ maxHeight: 320, overflow: "auto" }}>
              {(() => {
                const q = qpSearch.trim().toLowerCase();
                const rows = (visibleList || []).filter(({ truck, driver }) => !q || (truck.unit_number || "").toLowerCase().includes(q) || (driver?.driver_name || "").toLowerCase().includes(q));
                if (rows.length === 0) return <div style={{ textAlign: "center", color: "var(--muted)", padding: "20px 0", fontSize: 13 }}>No trucks match.</div>;
                return rows.map(({ truck, driver }) => (
                  <div key={truck.id} onClick={() => openQueueAssignToTruck(qpLoad, truck.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 6px", borderTop: "1px solid var(--line)", cursor: "pointer" }}>
                    <span style={{ width: 24, height: 24, borderRadius: "50%", background: "var(--bg)", border: "1px solid var(--line)", display: "grid", placeItems: "center", fontSize: 9, fontWeight: 700, color: "var(--muted)" }}>{driver ? driver.driver_name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase() : "—"}</span>
                    <span style={{ fontFamily: "ui-monospace, monospace", fontWeight: 600, fontSize: 12.5, color: "var(--ink)" }}>{truck.unit_number}</span>
                    <span style={{ fontSize: 12, color: "var(--muted)" }}>{driver ? driver.driver_name : "Unassigned"}</span>
                    <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--muted)" }}>›</span>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ---- Queue cancel confirmation ---- */}
      {qcLoad && (
        <div style={styles.modalOverlay} onClick={() => !qcBusy && setQcLoad(null)}>
          <div style={{ ...styles.modalCard, width: 400 }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Cancel this queued load?</div>
              <button style={styles.modalCloseBtn} onClick={() => !qcBusy && setQcLoad(null)}>×</button>
            </div>
            <div style={{ fontSize: 13, color: "var(--ink)", fontWeight: 600, marginBottom: 4 }}>{qcLoad.customer_name || "Load"} <span style={{ fontFamily: "ui-monospace, monospace", color: "var(--muted)", fontSize: 11, fontWeight: 400 }}>#{qcLoad.external_load_id || ""}</span></div>
            <div style={{ fontSize: 12.5, color: "var(--muted)", marginBottom: 12 }}>{qcLoad.origin_location} → {qcLoad.destination_location}</div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 14, lineHeight: 1.5 }}>Removes it from your queue. It won't be assigned, and it won't come back on the next pull.</div>
            {qcError && <div style={{ background: "rgba(225,29,72,.08)", border: "1px solid rgba(225,29,72,.3)", color: "#e11d48", fontSize: 12, borderRadius: 8, padding: "8px 10px", marginBottom: 12 }}>{qcError}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setQcLoad(null)} disabled={qcBusy} style={{ flex: 1, cursor: "pointer", borderRadius: 9, padding: "11px", fontSize: 13, fontWeight: 700, background: "var(--bg)", color: "var(--ink)", border: "1px solid var(--line)" }}>Keep in queue</button>
              <button onClick={confirmQueueCancel} disabled={qcBusy} style={{ flex: 1, border: 0, cursor: "pointer", borderRadius: 9, padding: "11px", fontSize: 13, fontWeight: 700, background: "#e11d48", color: "#fff", opacity: qcBusy ? 0.6 : 1 }}>{qcBusy ? "Cancelling…" : "Cancel load"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- List column ---- */}
      <div style={styles.listCol}>
        <div style={styles.fleetPanelTitle}>Fleet Panel</div>
        {/* Trucks | Queue toggle — inbound TMS loads live under the Queue tab, with a loud red count
            that stays visible from the Trucks tab so a waiting load can't slip by. */}
        <style>{`@keyframes tmsPing{0%{opacity:.6;transform:scale(1)}70%{opacity:0;transform:scale(1.6)}100%{opacity:0}}
          .tms-qbadge{position:relative}
          .tms-qbadge::after{content:"";position:absolute;inset:-3px;border-radius:999px;border:2px solid #f43f5e;opacity:0;animation:tmsPing 1.8s cubic-bezier(0,0,.2,1) infinite}
          @media (prefers-reduced-motion:reduce){.tms-qbadge::after{animation:none}}`}</style>
        <div style={{ display: "flex", gap: 4, background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 10, padding: 3, margin: "2px 0 10px" }}>
          {[{ k: "trucks", label: "Trucks" }, { k: "queue", label: "Queue" }].map(({ k, label }) => {
            const on = fleetTab === k;
            return (
              <button key={k} onClick={() => setFleetTab(k)} style={{ flex: 1, border: 0, cursor: "pointer", borderRadius: 7, padding: "7px 8px", fontSize: 12.5, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: on ? "var(--card)" : "transparent", color: on ? "var(--ink)" : "var(--muted)", boxShadow: on ? "0 1px 3px rgba(2,6,23,.12)" : "none" }}>
                {k === "trucks"
                  ? (<><TruckIcon size={13} /> {label} <span style={{ color: "var(--muted)", fontWeight: 600 }}>{fleetCounts.total}</span></>)
                  : (<>{label}{queueLoads.length > 0 && (<span className="tms-qbadge" style={{ background: "#f43f5e", color: "#fff", borderRadius: 999, fontSize: 11.5, fontWeight: 800, minWidth: 20, height: 20, display: "grid", placeItems: "center", padding: "0 6px", boxShadow: "0 1px 4px rgba(244,63,94,.5)" }}>{queueLoads.length}</span>)}</>)}
              </button>
            );
          })}
        </div>
        {fleetTab === "trucks" ? (<>
        <div style={styles.statusSegment}>
          {STATUS_FILTER_KEYS.map(({ key, label, color }) => {
            const active = statusFilter === key;
            const count = key === "all" ? fleetCounts.total : fleetCounts[key];
            return (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                style={{
                  ...styles.segmentBtn,
                  ...(active ? { background: `${color}1f`, color, boxShadow: `inset 0 0 0 1px ${color}66` } : {}),
                }}
              >
                <span style={{ ...styles.segmentDot, background: color }} />
                <span style={styles.segmentCount}>{count}</span>
                <span style={styles.segmentLabel}>{label}</span>
              </button>
            );
          })}
        </div>

        {(EXTRA_FILTER_DEFS.some((f) => fleetCounts[f.key] > 0) || hasActiveFleetFilters) && (
          <div style={styles.extraFilterRow}>
            {EXTRA_FILTER_DEFS.filter((f) => fleetCounts[f.key] > 0).map((f) => (
              <button
                key={f.key}
                onClick={() => setExtraFilter(extraFilter === f.key ? null : f.key)}
                style={{ ...styles.filterChip, ...(extraFilter === f.key ? styles.filterChipActive : {}) }}
              >
                {f.icon}
                {f.label}
                <span style={styles.filterChipCount}>{fleetCounts[f.key]}</span>
              </button>
            ))}
            {hasActiveFleetFilters && (
              <button
                onClick={() => { setStatusFilter("all"); setExtraFilter(null); setSearch(""); }}
                style={{ ...styles.filterChip, color: "var(--brand)", borderColor: "var(--brand)" }}
              >
                <Eye size={11} />
                Show all
              </button>
            )}
          </div>
        )}

        {stopIntelCount > 0 && (
          <div style={{ ...styles.reminderPanel, ...(hasAction ? styles.reminderPanelUrgent : {}) }}>
            <button style={styles.reminderHead} onClick={() => setReminderPanelOpen((o) => !o)}>
              <span style={{ ...styles.reminderBell, ...(hasAction ? styles.reminderBellUrgent : {}) }}>
                <Bell size={12} />
              </span>
              <span style={{ ...styles.reminderTitle, ...(hasAction ? styles.reminderTitleUrgent : {}) }}>
                Stop intelligence
              </span>
              <span style={{ ...styles.reminderCount, ...(hasAction ? styles.reminderCountUrgent : {}) }}>
                {stopIntelHeaderLabel(actionCount, atFacilityCount)}
              </span>
              {reminderPanelOpen ? <ChevronUp size={12} style={{ color: "var(--muted)" }} /> : <ChevronDown size={12} style={{ color: "var(--muted)" }} />}
            </button>
            {reminderPanelOpen && (
              <div style={styles.reminderList}>
                {/* 🔴 ACTION REQUIRED -- possible missed pickup/delivery + pickup overdue */}
                {stopIntel.actionRequired.length > 0 && (
                  <>
                    <div style={styles.siGroupHead}><span style={styles.siDotRed} /> Action required</div>
                    {stopIntel.actionRequired.map((a) => {
                      const isMissed = a.type === "possible_missed_pickup" || a.type === "possible_missed_delivery";
                      const title = a.type === "possible_missed_pickup" ? "Possible missed pickup"
                        : a.type === "possible_missed_delivery" ? "Possible missed delivery" : "Pickup overdue";
                      return (
                        <div key={a.alert_event_id} style={styles.reminderRow}>
                          <span style={{ ...styles.reminderStripe, background: "var(--danger)" }} />
                          <div style={styles.reminderInfo}>
                            <div style={styles.reminderInfoLine1}>
                              {a.truck_unit_number || "—"}
                              <span style={{ ...styles.reminderTier, color: "var(--danger)" }}>
                                {" · "}{title}{isMissed && a.confidence ? ` · ${a.confidence}` : ""}
                              </span>
                            </div>
                            <div style={styles.reminderInfoLine2}>
                              {isMissed ? humanEvidence(a)
                                : `Appointment passed${a.overdue_minutes ? ` ${formatDwell(a.overdue_minutes)} ago` : ""} · not picked up`}
                            </div>
                          </div>
                          <button
                            style={{ ...styles.reminderBtn, color: "var(--danger)", borderColor: "rgba(220,38,38,0.4)", background: "rgba(220,38,38,0.12)" }}
                            onClick={() => (isMissed ? setMissedReview(a) : setSelectedTruckId(a.truck_id))}
                          >
                            {isMissed ? "Review" : "View"}
                          </button>
                        </div>
                      );
                    })}
                  </>
                )}
                {/* 🟡 AT FACILITY -- awareness, calm (emphasized only when appt passed) */}
                {stopIntel.atFacility.length > 0 && (
                  <>
                    <div style={styles.siGroupHead}><span style={styles.siDotAmber} /> At facility</div>
                    {stopIntel.atFacility.map(({ truck: t, load, stop }) => {
                      const color = stop.emphasize ? "var(--warn)" : "var(--brand-2)";
                      const apptTime = formatTimeFacilityLocal(stop.apptAt, facilityTzForLeg(load, stop.leg));
                      const detail = stop.emphasize
                        ? `Appointment was ${formatDwell(stop.apptDeltaMin)} ago · still on site`
                        : apptTime ? `Appointment ${apptTime} · still on site` : "Still on site";
                      return (
                        <div key={`${load.id}:${stop.leg}`} style={styles.reminderRow}>
                          <span style={{ ...styles.reminderStripe, background: color }} />
                          <div style={styles.reminderInfo}>
                            <div style={styles.reminderInfoLine1}>
                              {t.unit_number}
                              <span style={{ ...styles.reminderTier, color }}>{" · "}{atFacilityLabel(stop)}</span>
                            </div>
                            <div style={styles.reminderInfoLine2}>{detail}</div>
                          </div>
                          <button
                            style={{ ...styles.reminderBtn, color: "var(--ink-2)", borderColor: "var(--line)", background: "var(--card)" }}
                            onClick={() => setSelectedTruckId(t.id)}
                          >
                            View
                          </button>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div style={styles.searchWrap}>
          <Search size={13} style={styles.searchIcon} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find truck or driver…"
            style={styles.searchInput}
          />
        </div>

        <div style={styles.truckList}>
          {visibleList.map(({ truck: t, driver, load, status, flags, econEstimate }) => {
            const isSelected = t.id === selectedTruckId;
            const statusColor = STATUS[status].color;
            const statusLabel = STATUS[status].label;
            const initials = driver
              ? driver.driver_name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase()
              : "—";
            const hosLow = flags.lowHos;
            const profit = econEstimate?.profit;
            const dueDateShort = load ? formatDueDateShort(load.delivery_datetime) : null;
            // Stop-detection chip: only when relevant. Keys off the PHYSICAL
            // load (deriveStopState uses the current leg), so a queued next
            // load never makes the card say AT PICKUP.
            const cardStop = deriveStopState(load, status, nowTick);
            const cardCandidate = candidateForLoad(openAlerts, load?.id);
            return (
              <div
                key={t.id}
                className="truck-card"
                onClick={() => setSelectedTruckId(t.id)}
                style={{
                  ...styles.truckRow,
                  borderLeftColor: statusColor,
                  ...(isSelected ? styles.truckRowSelected : {}),
                }}
              >
                <div style={styles.truckRowTop}>
                  <div style={styles.truckIdentity}>
                    <div style={{ ...styles.truckAvatar, background: `${statusColor}1f`, color: statusColor, borderColor: `${statusColor}55` }}>
                      <TruckIcon size={13} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={styles.truckUnit}>{t.unit_number}</div>
                      <div style={styles.truckMakeModel}>{[t.make, t.model].filter(Boolean).join(" ") || "—"}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                    <span style={{ ...styles.statusPillMini, background: `${statusColor}1f`, color: statusColor, borderColor: `${statusColor}55` }}>
                      {statusLabel}
                    </span>
                    {cardCandidate ? (
                      <span style={styles.chipMissed} title="Possible missed lifecycle — click to review">⚠ Missed?</span>
                    ) : cardStop?.atFacility ? (
                      <span style={{ ...styles.chipFacility, ...(cardStop.emphasize ? styles.chipFacilityEmph : {}) }}>
                        {cardStop.leg === "pickup" ? "At pickup" : "At delivery"} {formatDwell(cardStop.dwellMinutes)}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div style={styles.driverLine}>
                  <span style={{ ...styles.driverAvatar, ...(driver ? {} : styles.driverAvatarEmpty) }}>{initials}</span>
                  <span style={styles.driverName}>{driver ? driver.driver_name : "Unassigned"}</span>
                  <span style={styles.rowFlags}>
                    {flags.behindSchedule && <AlertTriangle size={11} style={{ color: "#e05d5d" }} title="Behind schedule" />}
                    <span style={styles.speedBadge}>
                      <Gauge size={10} style={{ verticalAlign: -1.5, marginRight: 2 }} />
                      {t.current_speed_mph ?? 0}
                    </span>
                  </span>
                </div>

                {load ? (
                  <>
                    <div style={styles.loadLine}>
                      <span style={styles.loadDest}>
                        <MapPin size={11} style={{ verticalAlign: -1.5, marginRight: 3, flexShrink: 0 }} />
                        {abbreviateLocation(load.origin_location)} → {abbreviateLocation(load.destination_location)}
                      </span>
                      {dueDateShort && <span style={styles.dueDateChip}>Due {dueDateShort}</span>}
                    </div>
                    <span
                      style={{
                        ...styles.scheduleChip,
                        ...(flags.behindSchedule ? styles.scheduleChipLate : styles.scheduleChipOnTime),
                      }}
                    >
                      {flags.behindSchedule ? "Behind schedule" : "On time"}
                    </span>
                  </>
                ) : (
                  <div style={styles.truckRowSub}>No active load</div>
                )}

                {(driver?.hos_hours_remaining != null || profit != null) && (
                  <div style={styles.hosRow}>
                    {driver?.hos_hours_remaining != null && (
                      <span style={{ ...styles.hosChip, ...(hosLow ? styles.hosChipLow : {}) }}>
                        <Clock size={10} />
                        {formatHm(Number(driver.hos_hours_remaining))} HOS left
                      </span>
                    )}
                    {profit != null && (
                      <span
                        title="Estimated profit on this truck's active load (rough, list-level estimate)"
                        style={{ ...styles.profitChip, ...(profit >= 0 ? styles.profitChipPos : styles.profitChipNeg) }}
                      >
                        {profit >= 0 ? "+" : "-"}${Math.abs(profit).toFixed(0)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {visibleList.length === 0 && (
            dispatcherVisibleTrucks.length === 0 && enriched.length > 0 ? (
              // The real first-login trap: a brand-new dispatcher has zero
              // trucks assigned to them yet (visibleDispatcherIds defaults
              // to "just me" -- see dispatcherVisibilityInitRef above), so
              // the panel renders completely blank with nothing pointing
              // them at the toggle (buried in the Map layers flyout) that
              // would actually fix it. Surfacing the fix inline, right
              // where the emptiness is, instead of relying on them finding
              // it on their own.
              <div style={{ ...styles.emptyState, textAlign: "center", padding: "20px 10px" }}>
                <div style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 4 }}>No trucks assigned to you yet</div>
                <div style={{ marginBottom: 12 }}>Show the whole fleet to find and claim a truck.</div>
                <button
                  onClick={() => setVisibleDispatcherIds(new Set(dispatcherOptions.map((d) => d.id)))}
                  style={styles.explainBtn}
                >
                  Show all trucks
                </button>
              </div>
            ) : (
              <div style={styles.emptyState}>No trucks match.</div>
            )
          )}
        </div>
        </>) : (
          <div style={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column", gap: 10, padding: "2px 2px 8px" }}>
            {queueLoads.length === 0 ? (
              <div style={{ textAlign: "center", padding: "34px 12px", color: "var(--muted)" }}>
                <div style={{ fontWeight: 700, color: "var(--ink)", marginBottom: 4 }}>Queue is empty</div>
                <div style={{ fontSize: 12 }}>New TMS loads will appear here as they arrive.</div>
              </div>
            ) : (
              queueLoads.map((l) => {
                const rpm = l.rate_offered && l.miles_loaded ? l.rate_offered / l.miles_loaded : null;
                return (
                  <div key={l.id} onClick={() => openQueueAssign(l)} style={{ border: "1px solid var(--line)", borderRadius: 12, background: "var(--card)", padding: 12, cursor: "pointer", color: "var(--ink)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: ".05em", color: "#7c3aed", background: "rgba(124,58,237,.1)", border: "1px solid rgba(124,58,237,.35)", borderRadius: 5, padding: "2px 6px", fontWeight: 700 }}>{l.load_source || "TMS"}</span>
                      <span style={{ fontSize: 13, fontWeight: 650, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{l.customer_name || "Load"}</span>
                      <span style={{ marginLeft: "auto", fontFamily: "ui-monospace, monospace", fontSize: 10.5, color: "var(--muted)", whiteSpace: "nowrap" }}>#{l.external_load_id || ""}</span>
                      <button onClick={(e) => { e.stopPropagation(); openQueueCancel(l); }} title="Cancel — remove from queue" style={{ border: 0, background: "transparent", color: "var(--muted)", cursor: "pointer", fontSize: 15, lineHeight: 1, padding: "0 0 0 6px" }}>✕</button>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 7, fontSize: 12.5, marginBottom: 6 }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#4cae6d", marginTop: 5, flexShrink: 0 }} />
                      <span>{l.origin_location || "—"}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 7, fontSize: 12.5, marginBottom: 9 }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#e11d48", marginTop: 5, flexShrink: 0 }} />
                      <span>{l.destination_location || "—"}</span>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", fontSize: 11.5, color: "var(--muted)" }}>
                      <span style={{ color: "#2f9e5e", fontWeight: 700, fontFamily: "ui-monospace, monospace" }}>{l.rate_offered != null ? `$${Number(l.rate_offered).toLocaleString()}` : "—"}</span>
                      {l.miles_loaded != null && <span>· {Math.round(l.miles_loaded)} mi</span>}
                      {rpm != null && <span>· ${rpm.toFixed(2)}/mi</span>}
                      {l.required_equipment_type_id && <span>· Equip set</span>}
                    </div>
                    <div style={{ marginTop: 10, display: "flex", gap: 7 }}>
                      <button onClick={(e) => { e.stopPropagation(); openQueueAssign(l); }} style={{ flex: 1, border: 0, cursor: "pointer", borderRadius: 8, padding: "7px", fontSize: 11.5, fontWeight: 650, background: "#10b981", color: "#fff" }}>⚡ Find best truck</button>
                      <button onClick={(e) => { e.stopPropagation(); setQpSearch(""); setQpLoad(l); }} style={{ flex: 1, cursor: "pointer", borderRadius: 8, padding: "7px", fontSize: 11.5, fontWeight: 650, background: "var(--bg)", color: "var(--ink)", border: "1px solid var(--line)" }}>Assign to a truck</button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* ---- Detail column: pinned, never scrolls with the list ---- */}
      {selectedTruck && (
        <div style={{ ...styles.detailCol, ...(detailExpanded ? styles.detailColExpanded : {}) }}>
          <>
            <div style={styles.detailHeader}>
              <button
                onClick={() => setDetailExpanded((v) => !v)}
                title={detailExpanded ? "Shrink panel" : "Expand panel"}
                style={styles.detailExpandBtn}
              >
                {detailExpanded ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
              </button>
              <div style={{ ...styles.statusPill, background: `${STATUS[selected.status].color}22`, color: STATUS[selected.status].color, borderColor: `${STATUS[selected.status].color}55` }}>
                {STATUS[selected.status].label}
              </div>
              <h2 style={styles.detailTitle}>{selectedTruck.unit_number}</h2>
              <div style={styles.detailSubtitle}>{selectedTruck.make} {selectedTruck.model} · {selectedTruck.year}</div>
            </div>

            <div style={styles.truckDetailsTitle}>Truck Details</div>

            <div style={styles.tabBar}>
              <TabButton label="Overview" active={activeTab === "overview"} onClick={() => setActiveTab("overview")} />
              <TabButton label="Current Load" active={activeTab === "route"} onClick={() => setActiveTab("route")} dot={selected.atRisk ? "#e05d5d" : null} />
              <TabButton label="Economics" active={activeTab === "economics"} onClick={() => setActiveTab("economics")} />
              <TabButton
                label="Next Trips"
                active={activeTab === "next"}
                onClick={() => { setActiveTab("next"); setDetailExpanded(true); }}
                dot={nextTripsList.length > 0 ? "#a78bfa" : null}
              />
            </div>

            <button
              onClick={() => ensureTankCapacity(selectedTruck, () => { checkLoadSourceQueuedLoadIdRef.current = null; setCheckLoadStops([]); setCheckLoadMode("manual"); setCheckLoadOpen(true); })}
              style={styles.checkLoadTriggerBtn}
            >
              <Navigation size={13} /> Assign Load to Truck
              {checkLoadResult?.data && <span style={{ ...styles.tabDot, background: "#4cae6d", marginLeft: 4 }} />}
            </button>

            {activeTab === "overview" && (
              <div style={styles.tabPanel}>
                <StatRow icon={<User size={13} />} label="Driver" value={selectedDriver ? selectedDriver.driver_name : "Unassigned"} />
                {!driverAssignFormOpen ? (
                  <button
                    style={{ ...styles.availabilityBtn, padding: "4px 8px", marginBottom: 8 }}
                    onClick={() => { setDriverAssignSelectedId(selectedDriver?.id || ""); setDriverAssignFormOpen(true); }}
                  >
                    {selectedDriver ? "Change driver" : "+ Assign driver"}
                  </button>
                ) : (
                  <div style={{ ...styles.availabilityForm, marginBottom: 8 }}>
                    <div style={styles.availabilityFormLabel}>
                      {selectedDriver ? "Reassign this truck to..." : "Which driver is running this truck?"}
                    </div>
                    <select
                      style={styles.availabilityInput}
                      value={driverAssignSelectedId}
                      onChange={(e) => setDriverAssignSelectedId(e.target.value)}
                    >
                      <option value="">— Unassigned —</option>
                      {Object.values(drivers)
                        .slice()
                        .sort((a, b) => (a.driver_name || "").localeCompare(b.driver_name || ""))
                        .map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.driver_name}{(d.last_truck_id?.id || d.last_truck_id) === selectedTruck.id ? " (last drove this truck)" : ""}
                          </option>
                        ))}
                    </select>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        style={styles.closeLoadBtn}
                        disabled={savingDriverAssign}
                        onClick={() => assignDriver(selectedTruck.id, driverAssignSelectedId)}
                      >
                        {savingDriverAssign ? "Saving…" : "Save"}
                      </button>
                      <button
                        style={styles.cancelLoadBtn}
                        onClick={() => { setDriverAssignFormOpen(false); setDriverAssignSelectedId(""); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                <StatRow icon={<Users size={13} />} label="Dispatcher" value={selectedTruck.dispatcher_id?.display_value || "Unassigned"} />
                {!dispatcherAssignFormOpen ? (
                  <button
                    style={{ ...styles.availabilityBtn, padding: "4px 8px", marginBottom: 8 }}
                    onClick={() => { setDispatcherAssignSelectedId(selectedTruck.dispatcher_id?.id || ""); setDispatcherAssignFormOpen(true); }}
                  >
                    {selectedTruck.dispatcher_id ? "Switch dispatcher" : "+ Assign dispatcher"}
                  </button>
                ) : (
                  <div style={{ ...styles.availabilityForm, marginBottom: 8 }}>
                    <div style={styles.availabilityFormLabel}>Which dispatcher handles this truck?</div>
                    <select
                      style={styles.availabilityInput}
                      value={dispatcherAssignSelectedId}
                      onChange={(e) => setDispatcherAssignSelectedId(e.target.value)}
                    >
                      <option value="">— Unassigned —</option>
                      {dispatcherOptions.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}{d.id === currentUserId ? " (you)" : ""}
                        </option>
                      ))}
                    </select>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        style={styles.closeLoadBtn}
                        disabled={savingDispatcherAssign}
                        onClick={() => assignDispatcher(selectedTruck.id, dispatcherAssignSelectedId)}
                      >
                        {savingDispatcherAssign ? "Saving…" : "Save"}
                      </button>
                      <button
                        style={styles.cancelLoadBtn}
                        onClick={() => { setDispatcherAssignFormOpen(false); setDispatcherAssignSelectedId(""); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {!editingDriverPhone ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, padding: "4px 0" }}>
                    <Phone size={13} />
                    <span style={{ color: "var(--muted)" }}>Phone</span>
                    <span style={{ marginLeft: "auto", color: selectedDriver?.phone ? "var(--ink)" : "var(--muted)" }}>
                      {selectedDriver?.phone || "—"}
                    </span>
                    {selectedDriver?.id && (
                      <button
                        onClick={() => { setDriverPhoneDraft(selectedDriver.phone || ""); setEditingDriverPhone(true); }}
                        style={{ background: "none", border: "none", color: "var(--brand)", cursor: "pointer", fontSize: 11, fontWeight: 700, padding: 0 }}
                      >
                        {selectedDriver.phone ? "Change" : "Add"}
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, padding: "4px 0" }}>
                    <Phone size={13} />
                    <input
                      type="tel"
                      value={driverPhoneDraft}
                      onChange={(e) => setDriverPhoneDraft(e.target.value)}
                      autoFocus
                      placeholder="e.g. (313) 671-1783"
                      style={{ flex: 1, minWidth: 0, fontSize: 12.5, padding: "5px 8px", border: "1px solid var(--line)", borderRadius: 6, color: "var(--ink)", background: "var(--bg)" }}
                    />
                    <button
                      onClick={async () => { try { await saveDriverPhone(selectedDriver.id, driverPhoneDraft); } catch (e) { /* best-effort */ } setEditingDriverPhone(false); }}
                      style={{ background: "none", border: "none", color: "var(--brand)", cursor: "pointer", fontSize: 11, fontWeight: 700 }}
                    >
                      Save
                    </button>
                    <button onClick={() => setEditingDriverPhone(false)} style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 11 }}>
                      Cancel
                    </button>
                  </div>
                )}
                {selectedDriverPto && (
                  <StatRow
                    icon={<Palmtree size={13} />}
                    label={activeTimeOffFor(selectedDriver?.id) ? "Home time" : "Upcoming home time"}
                    value={`${parseUtcNaive(selectedDriverPto.start_datetime)?.toLocaleDateString(undefined, { month: "short", day: "numeric" }) || "—"} – ${parseUtcNaive(selectedDriverPto.end_datetime)?.toLocaleDateString(undefined, { month: "short", day: "numeric" }) || "—"}`}
                    danger
                  />
                )}
                <StatRow
                  icon={<Check size={13} />}
                  label="Trips today"
                  value={tripsToday == null ? "—" : String(tripsToday)}
                  accent={tripsToday > 0}
                />
                <StatRow icon={<Gauge size={13} />} label="Speed" value={`${selectedTruck.current_speed_mph ?? 0} mph`} />
                <StatRow
                  icon={<Clock size={13} />}
                  label="HOS remaining"
                  value={selectedDriver?.hos_hours_remaining != null ? `${selectedDriver.hos_hours_remaining} hrs` : "—"}
                  warn={selectedDriver?.hos_hours_remaining != null && selectedDriver.hos_hours_remaining < 4}
                />
                {/* Placeholder disclaimer: a real HOS clock always carries a
                    duty status (Motive and most Samsara drivers do). When the
                    provider sends no clock for a driver, the sync leaves the
                    8h default -- flag that so no one plans off a fake number.
                    Scoped to exactly that case (8h + no duty status). */}
                {selectedDriver?.hos_hours_remaining != null &&
                  !selectedDriver.hos_duty_status &&
                  Number(selectedDriver.hos_hours_remaining) === 8 && (
                    <div style={{ fontSize: 10.5, color: "var(--muted)", lineHeight: 1.3, margin: "-2px 0 4px 26px" }}>
                      Default 8 h — Samsara isn't reporting this driver's HOS.
                    </div>
                  )}
                {(() => {
                  const elapsed = restingElapsed(selectedDriver, clockNow);
                  if (!elapsed) return null;
                  // The confirmed fact -- always shown while resting,
                  // regardless of hours remaining. Never implies WHY he
                  // stopped or how long he'll stay stopped.
                  return (
                    <StatRow
                      icon={<Clock size={13} />}
                      label="Resting"
                      value={`${elapsed.hours}h ${elapsed.minutes}m`}
                      accent
                    />
                  );
                })()}
                {(() => {
                  const reset = estimateHosReset(selectedDriver);
                  if (!reset) return null;
                  // Speculative on purpose, kept visually/textually distinct
                  // from "Resting" above -- only shown once he's actually
                  // maxed out on hours (can't legally drive regardless of
                  // intent), so a full reset is the one thing we can
                  // reasonably assume rather than guess at. Still just an
                  // assumption about DURATION, not a confirmed fact -- he
                  // could stay stopped longer than the mandatory minimum.
                  let value = "Mandatory reset should be satisfied by now";
                  if (!reset.isPast) {
                    const clock = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" }).format(reset.availableAt);
                    const totalMinutes = Math.round(reset.hoursUntil * 60);
                    const h = Math.floor(totalMinutes / 60);
                    const m = totalMinutes % 60;
                    value = `~${h}h ${m}m more (~${clock}) if this is his ${reset.resetHours}h reset`;
                  }
                  return (
                    <StatRow
                      icon={<Clock size={13} />}
                      label="If resetting"
                      value={value}
                    />
                  );
                })()}

                {selectedDriver?.hos_recap?.days?.length > 0 && (() => {
                  const today = todayIsoUtc();
                  return (
                    <>
                      <button
                        type="button"
                        style={{ ...styles.availabilityBtn, padding: "4px 8px", marginTop: 4, marginBottom: hosDetailsOpen ? 8 : 0 }}
                        onClick={() => setHosDetailsOpen((v) => !v)}
                      >
                        {hosDetailsOpen ? "Hide HOS Details" : "HOS Details"}
                        {!hosDetailsOpen && selectedDriver.hos_cycle_hours_remaining != null && (
                          <span style={{ color: "var(--muted)", fontWeight: 500 }}> · {selectedDriver.hos_cycle_hours_remaining}h left in cycle</span>
                        )}
                      </button>
                      {hosDetailsOpen && (
                        <div style={styles.recapBox}>
                          <div style={styles.recapHeader}>
                            <span>Duty hours, last 8 days</span>
                            <span style={styles.recapTotal}>
                              {selectedDriver.hos_cycle_hours_remaining != null ? `${selectedDriver.hos_cycle_hours_remaining}h left in cycle` : ""}
                            </span>
                          </div>
                          <div style={styles.recapLegend}>
                            <span style={styles.recapLegendItem}><span style={{ ...styles.recapSwatch, background: "#a78bfa" }} /> Driving</span>
                            <span style={styles.recapLegendItem}><span style={{ ...styles.recapSwatch, background: "#5b6472" }} /> On-duty (not driving)</span>
                          </div>
                          <div style={styles.recapBars}>
                            {selectedDriver.hos_recap.days.map((d) => {
                              const driving = d.driving_hours ?? 0;
                              const notDriving = d.on_duty_hours ?? 0;
                              const total = driving + notDriving;
                              return (
                                <div key={d.date} style={styles.recapBarRow}>
                                  <span style={{ ...styles.recapBarLabel, color: d.date === today ? "var(--ink)" : "var(--muted)" }}>
                                    {shortWeekday(d.date)}
                                  </span>
                                  <div style={styles.recapBarTrack}>
                                    <div style={{ ...styles.recapBarFillDriving, width: `${Math.min(100, (driving / 14) * 100)}%` }} />
                                    <div style={{ ...styles.recapBarFillOnDuty, width: `${Math.min(100 - (driving / 14) * 100, (notDriving / 14) * 100)}%` }} />
                                  </div>
                                  <span style={styles.recapBarValue}>{formatHm(total)}</span>
                                </div>
                              );
                            })}
                          </div>
                          <div style={styles.recapCaption}>
                            Rolling 8-day window (FMCSA 70-hr/8-day cycle) -- not a Monday–Sunday week. Each day drops
                            off the back as a new one is added, so the total isn't tied to a calendar reset. "Left in
                            cycle" is Motive's own figure, not derived from the 8 days shown here.
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {(() => {
                  // Attach the override to the driver when this truck has
                  // one (a statement about that person -- should follow
                  // them). No driver at all -- the common "empty truck,
                  // nobody assigned" case -- attach it to the truck itself,
                  // since there's no driver row to hang a note on.
                  const entityType = selectedDriver ? "drivers" : "trucks";
                  const entity = selectedDriver || selectedTruck;
                  const subjectLabel = selectedDriver ? "Driver" : "Truck";
                  const overrideAt = parseUtcNaive(entity.dispatcher_available_at);
                  if (overrideAt) {
                    return (
                      <div style={styles.availabilityBox}>
                        <StatRow
                          icon={<Clock size={13} />}
                          label="Available (dispatcher-set)"
                          value={new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(overrideAt)}
                          accent
                        />
                        {entity.dispatcher_availability_note && (
                          <div style={styles.availabilityNote}>{entity.dispatcher_availability_note}</div>
                        )}
                        <button style={styles.cancelLoadBtn} onClick={() => clearAvailability(entityType, entity.id)}>
                          Clear override
                        </button>
                      </div>
                    );
                  }
                  // Currently mid a scheduled time off (already started, not
                  // just upcoming) -- that end date is the authoritative
                  // "back at" signal already, so a manual override here
                  // would just create a second, possibly-conflicting one.
                  // Merely-upcoming time off doesn't trigger this -- the
                  // driver is still normally workable until it starts.
                  const activeTimeOff = selectedDriver ? activeTimeOffFor(selectedDriver.id) : null;
                  if (activeTimeOff) {
                    return (
                      <div style={styles.availabilityBox}>
                        <StatRow
                          icon={<Clock size={13} />}
                          label="On scheduled time off"
                          value={`until ${new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(parseUtcNaive(activeTimeOff.end_datetime))}`}
                          accent
                        />
                        {activeTimeOff.note && <div style={styles.availabilityNote}>{activeTimeOff.note}</div>}
                        <div style={styles.availabilityNote}>
                          Set from the Driver Calendar -- can't add a manual availability override while active.
                        </div>
                      </div>
                    );
                  }
                  // Driver case: Set Availability now creates a real
                  // driver_time_off row (same one the booking-flow toggle
                  // and the Driver Calendar itself write), anchored to
                  // whichever of this truck's active loads delivers LAST --
                  // that's "his last load" for a truck queued two deep.
                  // Gated on that load actually existing: without one,
                  // there's nothing to anchor a start to, so the control is
                  // disabled rather than silently doing something else. The
                  // no-driver (truck-level) case below is untouched -- there's
                  // no driver here for "time off" to mean anything about.
                  if (selectedDriver) {
                    const ptoAnchorLoad = selectedTruckLoads.length
                      ? selectedTruckLoads.reduce(
                          (latest, l) => (!latest || new Date(l.delivery_datetime) > new Date(latest.delivery_datetime) ? l : latest),
                          null
                        )
                      : null;
                    if (!ptoAnchorLoad) {
                      return (
                        <div style={styles.availabilityBox}>
                          <button style={{ ...styles.availabilityBtn, opacity: 0.5, cursor: "default" }} disabled>
                            + Set availability
                          </button>
                          <div style={styles.availabilityNote}>
                            Only available while this truck has an active load -- time off starts when it delivers.
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div style={styles.availabilityBox}>
                        {!availabilityFormOpen ? (
                          <button style={styles.availabilityBtn} onClick={() => setAvailabilityFormOpen(true)}>
                            + Set availability
                          </button>
                        ) : (
                          <div style={styles.availabilityForm}>
                            <div style={styles.availabilityFormLabel}>
                              Time off starts when this load delivers
                              {formatFacilityLocal(ptoAnchorLoad.delivery_datetime, ptoAnchorLoad.destination_timezone) ? ` (~${formatFacilityLocal(ptoAnchorLoad.delivery_datetime, ptoAnchorLoad.destination_timezone)})` : ""}
                              {" "}-- available again at
                            </div>
                            <input
                              type="datetime-local"
                              style={styles.availabilityInput}
                              value={availabilityFormAt}
                              onChange={(e) => setAvailabilityFormAt(e.target.value)}
                            />
                            <input
                              type="text"
                              placeholder="Reason (optional) -- e.g. personal matter"
                              style={styles.availabilityInput}
                              value={availabilityFormNote}
                              onChange={(e) => setAvailabilityFormNote(e.target.value)}
                            />
                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                style={styles.closeLoadBtn}
                                disabled={!availabilityFormAt || savingAvailability}
                                onClick={() => {
                                  setSavingAvailability(true);
                                  createTimeOffAfterLoad(selectedDriver.id, ptoAnchorLoad.delivery_datetime, availabilityFormAt, availabilityFormNote)
                                    .then(() => {
                                      reloadTrucksAndDrivers();
                                      setAvailabilityFormOpen(false);
                                      setAvailabilityFormAt("");
                                      setAvailabilityFormNote("");
                                      toast.success("Time off scheduled");
                                    })
                                    .catch((err) => toast.error(err.response?.data?.error || err.response?.data?.detail || "Couldn't save time off"))
                                    .finally(() => setSavingAvailability(false));
                                }}
                              >
                                {savingAvailability ? "Saving…" : "Save"}
                              </button>
                              <button
                                style={styles.cancelLoadBtn}
                                onClick={() => {
                                  setAvailabilityFormOpen(false);
                                  setAvailabilityFormAt("");
                                  setAvailabilityFormNote("");
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <div style={styles.availabilityBox}>
                      {!availabilityFormOpen ? (
                        <button style={styles.availabilityBtn} onClick={() => setAvailabilityFormOpen(true)}>
                          + Set availability
                        </button>
                      ) : (
                        <div style={styles.availabilityForm}>
                          <div style={styles.availabilityFormLabel}>
                            {subjectLabel} unavailable until (no driver assigned yet)
                          </div>
                          <input
                            type="datetime-local"
                            style={styles.availabilityInput}
                            value={availabilityFormAt}
                            onChange={(e) => setAvailabilityFormAt(e.target.value)}
                          />
                          <input
                            type="text"
                            placeholder="Note (optional) -- e.g. next driver's name"
                            style={styles.availabilityInput}
                            value={availabilityFormNote}
                            onChange={(e) => setAvailabilityFormNote(e.target.value)}
                          />
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              style={styles.closeLoadBtn}
                              disabled={!availabilityFormAt || savingAvailability}
                              onClick={() => saveAvailability(entityType, entity.id, availabilityFormAt, availabilityFormNote)}
                            >
                              {savingAvailability ? "Saving…" : "Save"}
                            </button>
                            <button
                              style={styles.cancelLoadBtn}
                              onClick={() => {
                                setAvailabilityFormOpen(false);
                                setAvailabilityFormAt("");
                                setAvailabilityFormNote("");
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <StatRow icon={<Fuel size={13} />} label="Fuel" value={`${selectedTruck.current_fuel_gallons ?? "—"} / ${selectedTruck.tank_capacity_gallons ?? "200*"} gal`} warn={selected.flags.lowFuel} />
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, padding: "4px 0" }}>
                  <Fuel size={13} />
                  <span style={{ color: "var(--muted)" }}>Tank capacity</span>
                  <span style={{ marginLeft: "auto", color: selectedTruck.tank_capacity_gallons != null ? "var(--ink)" : "var(--muted)" }}>
                    {selectedTruck.tank_capacity_gallons != null ? `${selectedTruck.tank_capacity_gallons} gal` : "Not set — using 200"}
                  </span>
                  <button
                    onClick={() => setCapacityPrompt({ truckId: selectedTruck.id, unit: selectedTruck.unit_number, onReady: () => {} })}
                    style={{ background: "none", border: "none", color: "var(--brand)", cursor: "pointer", fontSize: 11, fontWeight: 700, padding: 0 }}
                  >
                    {selectedTruck.tank_capacity_gallons != null ? "Edit" : "Set"}
                  </button>
                </div>
                <StatRow icon={<ShieldHalf size={13} />} label="Safety reserve" value={`${selectedTruck.safety_reserve_gallons ?? "—"} gal`} />
              </div>
            )}

            {activeTab === "route" && (
              <div style={styles.tabPanel}>
                {selectedLoad ? (
                  <>
                    {(() => {
                      const st = selectedLoad.actual_pickup_at ? "loaded" : "heading_to_pickup";
                      const stop = deriveStopState(selectedLoad, st, nowTick);
                      const cand = candidateForLoad(openAlerts, selectedLoad.id);
                      if (cand) {
                        return (
                          <div style={styles.clMissedBox}>
                            <div style={styles.clMissedTitle}>
                              Possible missed {cand.leg === "pickup" ? "pickup" : "delivery"}{cand.confidence ? ` — ${cand.confidence}` : ""}
                            </div>
                            <div style={styles.clMissedBody}>{humanEvidence(cand)}</div>
                            <button style={styles.clReviewBtn} onClick={() => setMissedReview(cand)}>Review</button>
                          </div>
                        );
                      }
                      if (stop?.atFacility) {
                        const apptStr = formatTimeFacilityLocal(stop.apptAt, facilityTzForLeg(selectedLoad, stop.leg));
                        return (
                          <div style={{ ...styles.clFacilityBox, ...(stop.emphasize ? styles.clFacilityBoxEmph : {}) }}>
                            <div style={styles.clFacilityTitle}>{stop.leg === "pickup" ? "AT PICKUP" : "AT DELIVERY"}</div>
                            <div style={styles.clFacilityLine}>At {stop.leg === "pickup" ? "shipper" : "receiver"} for {formatDwell(stop.dwellMinutes)}</div>
                            {(stop.emphasize || apptStr) && (
                              <div style={styles.clFacilityLine}>
                                {stop.emphasize ? `Appointment was ${formatDwell(stop.apptDeltaMin)} ago` : `Appointment: ${apptStr}`}
                              </div>
                            )}
                            <div style={styles.clFacilitySub}>Still on site</div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    <StatRow label="Customer" value={selectedLoad.customer_name} />
                    <SectionDivider label="Trip Details" icon={<MapPin size={12} />} />
                    {selectedLoadStops.length === 0 ? (
                      <>
                        <StatRow icon={<MapPin size={13} />} label="From" value={selectedLoad.origin_location} />
                        <StatRow icon={<MapPin size={13} />} label="To" value={selectedLoad.destination_location} />
                        {selectedLoad.status === "in_transit" && !selectedLoad.actual_pickup_at && (
                          <button
                            onClick={() => requestConfirmStop(selectedLoad.id, undefined, {
                              label: `${selectedLoad.origin_location} → ${selectedLoad.destination_location}`, isPickup: true,
                            })}
                            disabled={confirmingPickupLoadId === selectedLoad.id}
                            style={{ ...styles.checkLoadTriggerBtn, marginBottom: 8 }}
                          >
                            {confirmingPickupLoadId === selectedLoad.id ? "Confirming…" : "Confirm pickup"}
                          </button>
                        )}
                        {selectedLoad.actual_pickup_at && (
                          <StatRow
                            icon={<Check size={13} />}
                            label="Picked up"
                            value={formatFacilityLocal(selectedLoad.actual_pickup_at, selectedLoad.origin_timezone) || "—"}
                            accent={!pickupWasLate}
                            warn={pickupWasLate}
                          />
                        )}
                      </>
                    ) : (
                      // Multi-stop load -- every stop in order, each with its own
                      // confirm-arrival action. The last stop's own confirmation
                      // (see confirmPickup/is_last_stop) flips the load to
                      // delivered itself, so there's no separate "Close load"
                      // button here the way the plain single-stop case has.
                      <>
                        <div style={styles.checkLoadStopsTitle}>{selectedLoadStops.length + 2} stops</div>
                        <div style={{ marginBottom: 8 }}>
                          <StatRow icon={<MapPin size={13} />} label="Stop 1 · Pickup" value={selectedLoad.origin_location} />
                          {!selectedLoad.actual_pickup_at ? (
                            <button
                              onClick={() => requestConfirmStop(selectedLoad.id, undefined, {
                                label: `${selectedLoad.origin_location} → ${selectedLoad.destination_location}`, isPickup: true,
                              })}
                              disabled={confirmingPickupLoadId === selectedLoad.id}
                              style={{ ...styles.checkLoadTriggerBtn, marginBottom: 8 }}
                            >
                              {confirmingPickupLoadId === selectedLoad.id ? "Confirming…" : "Confirm pickup"}
                            </button>
                          ) : (
                            <StatRow
                              icon={<Check size={13} />} label="Picked up"
                              value={formatFacilityLocal(selectedLoad.actual_pickup_at, selectedLoad.origin_timezone) || "—"}
                            />
                          )}
                          {selectedLoadStops.map((stop, i) => (
                            <div key={stop.id}>
                              <StatRow
                                icon={<MapPin size={13} />}
                                label={`Stop ${i + 2} · ${stop.stop_type === "pickup" ? "Pickup" : "Dropoff"}`}
                                value={stop.location}
                              />
                              {!stop.actual_arrival_at ? (
                                <button
                                  onClick={() => requestConfirmStop(selectedLoad.id, stop.id, { label: stop.location, isPickup: false })}
                                  disabled={confirmingPickupLoadId === stop.id}
                                  style={{ ...styles.checkLoadTriggerBtn, marginBottom: 8 }}
                                >
                                  {confirmingPickupLoadId === stop.id ? "Confirming…" : "Confirm arrival"}
                                </button>
                              ) : (
                                <StatRow
                                  icon={<Check size={13} />} label="Arrived"
                                  value={formatFacilityLocal(stop.actual_arrival_at, stop.timezone) || "—"}
                                />
                              )}
                            </div>
                          ))}
                          <StatRow icon={<MapPin size={13} />} label={`Stop ${selectedLoadStops.length + 2} · Delivery`} value={selectedLoad.destination_location} />
                          {/* The real final destination is always
                              loads.destination_* -- never a load_stops row
                              (see that table's own migration docstring) --
                              so it always needs its OWN explicit
                              confirmation, no matter how many intermediate
                              stops came before it. Confirmed real: letting
                              the last load_stops row's own confirmation
                              auto-deliver the load marked it delivered
                              while the truck still had this real final leg
                              left to drive. */}
                          {selectedLoad.status !== "delivered" && selectedLoad.status !== "cancelled" ? (
                            <button
                              onClick={() => requestDeliverLoad(selectedLoad.id, `${selectedLoad.origin_location} → ${selectedLoad.destination_location}`)}
                              style={{ ...styles.checkLoadTriggerBtn, marginBottom: 8 }}
                            >
                              Confirm delivery
                            </button>
                          ) : selectedLoad.status === "delivered" ? (
                            <StatRow
                              icon={<Check size={13} />} label="Delivered"
                              value={formatFacilityLocal(selectedLoad.actual_delivery_at, selectedLoad.destination_timezone) || "—"}
                            />
                          ) : null}
                        </div>
                        {selectedLoad.status !== "delivered" && selectedLoad.status !== "cancelled" && (
                          <div style={styles.loadLifecycleRow}>
                            <button
                              onClick={() => requestCancelLoad(selectedLoad.id, `${selectedLoad.origin_location} → ${selectedLoad.destination_location}`)}
                              style={styles.cancelLoadBtn}
                            >
                              Cancel Load
                            </button>
                          </div>
                        )}
                      </>
                    )}
                    {(selectedLoad.status === "delivered" || selectedLoad.status === "cancelled") && (
                      <div style={styles.loadClosedBadge}>
                        {selectedLoad.status === "delivered" ? "Delivered -- load closed" : "Cancelled"}
                      </div>
                    )}
                    {selected?.status === "heading_to_pickup" && (
                      <>
                        <StatRow
                          icon={<Clock size={13} />}
                          label="Pick Up"
                          value={formatFacilityLocal(selectedLoad.pickup_datetime, selectedLoad.origin_timezone) || "—"}
                          accent={lateForPickupHours === 0}
                          warn={lateForPickupHours > 0}
                        />
                        <StatRow
                          icon={<Navigation size={13} />}
                          label="Pickup ETA"
                          value={pickupEtaDate ? formatDateFacilityLocal(pickupEtaDate, selectedLoad.origin_timezone) : "—"}
                          warn={pickupWaitHours > 0 || lateForPickupHours > 0}
                        />
                        {pickupWaitHours > 0 && (
                          <div style={styles.riskBadge}>
                            <AlertTriangle size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                            Driver will wait ~{pickupWaitHours < 1 ? `${Math.round(pickupWaitHours * 60)} min` : `${pickupWaitHours.toFixed(1)} hrs`} at pickup
                          </div>
                        )}
                        {lateForPickupHours > 0 && (
                          <div style={styles.riskBadge}>
                            <AlertTriangle size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                            Truck can't make this pickup -- would arrive ~{lateForPickupHours.toFixed(1)} hrs after the appointment
                          </div>
                        )}
                      </>
                    )}
                    <StatRow
                      icon={<Clock size={13} />}
                      label="Due"
                      value={formatFacilityLocal(selectedLoad.delivery_datetime, selectedLoad.destination_timezone) || "—"}
                    />
                    <StatRow
                      icon={<Navigation size={13} />}
                      label="ETA"
                      value={
                        loadEconomics?.loading
                          ? "Calculating…"
                          : etaDate
                          ? formatDateFacilityLocal(etaDate, selectedLoad.destination_timezone)
                          : "—"
                      }
                      accent={etaDate != null && !isLate}
                      warn={isLate}
                    />
                    {selectedLoad.status !== "delivered" && selectedLoad.status !== "cancelled" && (
                      dueDateEdit ? (
                        <div style={{ marginTop: 10, padding: 10, border: "1px solid var(--border)", borderRadius: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)" }}>Edit due dates</div>
                          <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: "var(--muted)" }}>
                            Pickup (facility local)
                            <input
                              type="datetime-local"
                              value={dueDateEdit.pickup}
                              onChange={(e) => setDueDateEdit((s) => ({ ...s, pickup: e.target.value }))}
                              style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--panel)", color: "var(--ink)", fontSize: 12 }}
                            />
                          </label>
                          <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: "var(--muted)" }}>
                            Delivery (facility local)
                            <input
                              type="datetime-local"
                              value={dueDateEdit.delivery}
                              onChange={(e) => setDueDateEdit((s) => ({ ...s, delivery: e.target.value }))}
                              style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--panel)", color: "var(--ink)", fontSize: 12 }}
                            />
                          </label>
                          {dueDateEdit.error && <div style={styles.riskBadge}>{dueDateEdit.error}</div>}
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              onClick={saveDueDates}
                              disabled={dueDateEdit.saving}
                              style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid var(--brand)", background: "var(--brand)", color: "#fff", fontSize: 12, fontWeight: 700, cursor: dueDateEdit.saving ? "default" : "pointer", opacity: dueDateEdit.saving ? 0.6 : 1 }}
                            >
                              {dueDateEdit.saving ? "Saving…" : "Save dates"}
                            </button>
                            <button
                              onClick={() => setDueDateEdit(null)}
                              disabled={dueDateEdit.saving}
                              style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid var(--border)", background: "transparent", color: "var(--ink)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                            >
                              Cancel
                            </button>
                          </div>
                          <div style={{ fontSize: 10, color: "var(--muted)" }}>
                            Facility-local time. An alarm clears only if the new date resolves it.
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={openDueDateEditor}
                          style={{ marginTop: 10, alignSelf: "flex-start", padding: "6px 12px", borderRadius: 6, border: "1px solid var(--border)", background: "transparent", color: "var(--brand)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                        >
                          Edit pickup / delivery dates
                        </button>
                      )
                    )}
                    {econ?.live_off_route_miles != null && econ.live_off_route_miles > OFF_ROUTE_THRESHOLD_MILES && (
                      <div style={styles.riskBadge}>
                        <AlertTriangle size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                        {liveRouteRecalculated ? (
                          <>Route deviation detected -- ~{Math.round(econ.live_off_route_miles)} mi from the planned
                          route. Live ETA recalculated from the truck's real position.</>
                        ) : (
                          <>Truck is ~{Math.round(econ.live_off_route_miles)} mi from the planned route -- this ETA
                          assumes it rejoins at the nearest point, not a real recalculated path.</>
                        )}
                      </div>
                    )}
                    {isLate && (
                      <div style={styles.riskBadge}>
                        <AlertTriangle size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                        Behind schedule for the due date
                      </div>
                    )}
                    <StatRow
                      label="Price"
                      value={selectedLoad.rate_offered != null ? `$${Number(selectedLoad.rate_offered).toLocaleString()}` : "—"}
                      accent
                    />
                    <StatRow label="Booked by" value={selectedLoad.created_by?.display_value || "—"} />

                    <SectionDivider label="Mileage Details" icon={<Navigation size={12} />} />
                    <StatRow
                      label="Deadhead (to pickup)"
                      value={econ?.deadhead_miles != null ? `${econ.deadhead_miles} mi` : loadEconomics?.loading ? "…" : "—"}
                      warn={econ && econ.distance_miles ? econ.deadhead_miles / econ.distance_miles > 0.3 : false}
                    />
                    <StatRow label="Loaded (pickup → destination)" value={econ?.loaded_miles != null ? `${econ.loaded_miles} mi` : loadEconomics?.loading ? "…" : "—"} />
                    <StatRow label="Total" value={econ ? `${econ.distance_miles} mi` : loadEconomics?.loading ? "…" : "—"} />
                    {econ?.distance_miles > 0 && econ?.deadhead_miles != null && (
                      <div style={styles.caption}>
                        {Math.round((econ.deadhead_miles / econ.distance_miles) * 100)}% of this trip is empty miles to reach the pickup.
                      </div>
                    )}
                    {/* Multi-stop loads deliver themselves via the last
                        stop's own confirm-arrival action (see the branch
                        above) -- this generic pair is only for the plain
                        single-pickup/single-drop case. */}
                    {selectedLoadStops.length === 0 && selectedLoad.status !== "delivered" && selectedLoad.status !== "cancelled" && (
                      <div style={styles.loadLifecycleRow}>
                        <button
                          onClick={() => requestDeliverLoad(selectedLoad.id, `${selectedLoad.origin_location} → ${selectedLoad.destination_location}`)}
                          style={styles.closeLoadBtn}
                        >
                          Deliver Load
                        </button>
                        <button
                          onClick={() => requestCancelLoad(selectedLoad.id, `${selectedLoad.origin_location} → ${selectedLoad.destination_location}`)}
                          style={styles.cancelLoadBtn}
                        >
                          Cancel Load
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={styles.emptyState}>No active route.</div>
                )}
              </div>
            )}

            {/* Fuel Stop tab retired -- it called a separate, older
                recommendation endpoint (/fleet/recommend-fuel-stop) that
                predates the accuracy fixes now baked into the real trip
                plan (partial-fill, top-off-before-destination, chained-load
                ending-fuel accuracy). It could disagree with Route's own
                fuel stops, which are the accurate ones. Those are already
                shown in Route's Routes panel and on the map. */}

            {/* Pulled out of Route -- a dispatcher checking "is this truck
                on schedule" and a manager checking "what's this trip worth"
                are two different glances; cramming both into one tab meant
                neither was quick to scan. */}
            {activeTab === "economics" && (
              <div style={styles.tabPanel}>
                {selectedLoad ? (
                  <>
                    <StatRow label="Rate" value={`$${Number(selectedLoad.rate_offered).toLocaleString()}`} accent />
                    <StatRow
                      label="Fuel cost"
                      value={econ?.fuel_cost != null ? `$${econ.fuel_cost.toLocaleString()}` : loadEconomics?.loading ? "…" : "—"}
                    />
                    {chosenTripPlan?.total_fuel_tax > 0 && (
                      <div style={styles.caption}>
                        Includes ${chosenTripPlan.total_fuel_tax.toLocaleString()} state fuel tax
                        {chosenTripPlan.fuel_tax_by_state && Object.keys(chosenTripPlan.fuel_tax_by_state).length > 0 && (
                          <> ({Object.entries(chosenTripPlan.fuel_tax_by_state).map(([st, amt]) => `${st} $${amt}`).join(", ")})</>
                        )}
                      </div>
                    )}
                    <StatRow label="Driver pay" value={econ?.driver_cost != null ? `$${econ.driver_cost.toLocaleString()}` : loadEconomics?.loading ? "…" : "—"} />
                    <StatRow label="Tolls" value={econ?.toll_cost != null ? `$${econ.toll_cost.toLocaleString()}` : loadEconomics?.loading ? "…" : "—"} />
                    {econ?.toll_names?.length > 0 && (
                      <div style={styles.caption}>{econ.toll_names.join(", ")}</div>
                    )}
                    {/* Per-mile overhead the Cost Engine actually prices this
                        trip with -- same fields Quick Check/RouteLegendPanel
                        already surface elsewhere, just missing here before.
                        Only rendered when nonzero (a fleet with no per-mile
                        cost settings configured leaves these at 0, same
                        fallback estimate-trip-cost itself already applies). */}
                    {econ?.insurance_cost > 0 && <StatRow label="Insurance" value={`$${econ.insurance_cost.toLocaleString()}`} />}
                    {econ?.office_payroll_cost > 0 && <StatRow label="Office/payroll" value={`$${econ.office_payroll_cost.toLocaleString()}`} />}
                    {econ?.maintenance_cost > 0 && <StatRow label="Maintenance" value={`$${econ.maintenance_cost.toLocaleString()}`} />}
                    {econ?.other_cost > 0 && <StatRow label="Equipment" value={`$${econ.other_cost.toLocaleString()}`} />}
                    <StatRow label="Total cost" value={econ?.total_cost != null ? `$${econ.total_cost.toLocaleString()}` : loadEconomics?.loading ? "…" : "—"} />
                    <StatRow label="Profit" value={econ?.contribution_profit != null ? `$${econ.contribution_profit.toLocaleString()}` : loadEconomics?.loading ? "…" : "—"} accent={!!econ && econ.contribution_profit > 0} warn={!!econ && econ.contribution_profit <= 0} />
                    {econ?.deadhead_miles != null && econ?.loaded_miles != null && (
                      <div style={styles.caption}>
                        {econ.deadhead_miles} mi deadhead + {econ.loaded_miles} mi loaded = {econ.distance_miles ?? Math.round((econ.deadhead_miles + econ.loaded_miles) * 10) / 10} mi total
                        {econ.duration_hours != null && ` · ${econ.duration_hours}h`}
                      </div>
                    )}
                    {/* Wait cost is opportunity cost (the truck's idle time,
                        not cash out the door for most pay types) -- kept as
                        its own line, never subtracted from "Profit" above,
                        since blending the two would answer a different
                        question than either number alone does. */}
                    {econ?.wait_hours > 0 && (
                      <StatRow
                        label="Wait cost (opportunity)"
                        value={`$${(econ.wait_cost ?? 0).toLocaleString()} (${econ.wait_hours}h${econ.billable_wait_hours < econ.wait_hours ? `, ${econ.billable_wait_hours}h billable` : ""})`}
                      />
                    )}
                    <StatRow label="Profit / mile" value={profitPerMile != null ? `$${profitPerMile.toFixed(2)}` : "—"} />
                    <StatRow label="Profit / hour" value={profitPerHour != null ? `$${profitPerHour.toFixed(2)}` : "—"} />
                    {loadEconomics?.data?.routing_source === "haversine_fallback" && (
                      <div style={styles.caption}>Estimated (real routing unavailable for this pair)</div>
                    )}
                    {loadEconomics?.data?.routes_considered?.length > 1 && (
                      <div style={styles.caption}>
                        {routePlanMeta?.hasSaved && !routePlanMeta.refreshing && <span style={styles.routePlanSavedBadge}>Saved plan</span>}
                        {" "}Compare, pick, and show/hide routes from the Map layers panel.
                      </div>
                    )}
                  </>
                ) : (
                  <div style={styles.emptyState}>No active route.</div>
                )}
              </div>
            )}

            {activeTab === "next" && (
              <div style={styles.tabPanel}>
                {nextTripsList.length === 0 && (
                  <div style={styles.emptyState}>No upcoming trips booked on this truck.</div>
                )}
                {nextTripsList.length > 0 && (
                  <>
                    <div style={styles.nextLoadStats}>Click a trip to preview its route on the map.</div>
                    <div style={styles.candidateList}>
                      {nextTripsList.map((l) => {
                        const i = selectedTruckLoads.findIndex((x) => x.id === l.id);
                        const isInTransit = l.status === "in_transit";
                        const badgeColor = isInTransit ? STATUS.loaded.color : "#a78bfa";
                        const isSelected = nextTripPreviewId === l.id;
                        return (
                          <div
                            key={l.id}
                            onClick={() => setNextTripPreviewId((prev) => (prev === l.id ? null : l.id))}
                            style={{
                              ...styles.candidateCard,
                              ...(isSelected ? styles.candidateCardSelected : {}),
                            }}
                          >
                            <div style={styles.candidateTop}>
                              <span style={styles.candidateName}>
                                <span style={{
                                  ...styles.loadSwitcherBadge, color: badgeColor,
                                  borderColor: `${badgeColor}55`, background: `${badgeColor}1a`, marginRight: 6,
                                }}>
                                  {isInTransit ? "In transit" : "Upcoming"}
                                </span>
                                <span style={{ ...styles.loadSwitcherIndex, marginRight: 6 }}>Load {i + 1}</span>
                                {abbreviateLocation(l.origin_location)} → {abbreviateLocation(l.destination_location)}
                              </span>
                              <span style={styles.candidateProfit}>${l.rate_offered}</span>
                            </div>
                            <div style={styles.candidateMeta}>
                              Pick Up {formatFacilityLocal(l.pickup_datetime, l.origin_timezone) || "—"} · Due {formatFacilityLocal(l.delivery_datetime, l.destination_timezone) || "—"}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                requestCancelLoad(l.id, `${abbreviateLocation(l.origin_location)} → ${abbreviateLocation(l.destination_location)}`);
                              }}
                              style={{ ...styles.cancelLoadBtn, marginTop: 8 }}
                            >
                              Cancel
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Check Load moved to a popup (checkLoadOpen, below, near the
                Log broker call modal) so both flows share one map/legend
                implementation -- see checkLoadOpen block. */}
          </>
        </div>
      )}

      {/* ---- Map column ---- */}
      <div style={styles.mapCol}>
        {/* Two independent floating panels stacked in one column -- see the
            mapLayersPanelOpen/routesSectionOpen state comment above for why
            they're split. The filled dot on a route picks which one is
            actually "the plan" (selectRoute -- a business decision,
            persisted); the switch next to it controls whether that route
            is drawn at all (hiddenRouteTypes -- a display preference,
            local only). Turning a route's switch off hides its tolls and
            planned stops with it, same as the routes themselves. */}
        <div style={styles.mapPanelsStack}>
          <div style={styles.floatingPanel}>
            <div style={styles.layersPanelHeader} onClick={() => setMapLayersPanelOpen((v) => !v)}>
              <span style={styles.layersPanelTitle}><Layers size={12} /> Map layers</span>
              {mapLayersPanelOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
            {mapLayersPanelOpen && (
              <div style={styles.poiPanelBody}>
                {selectedTruck && (
                  <div style={styles.layerPoiRow}>
                    <span style={styles.layerPoiIcon}><TruckIcon size={13} /></span>
                    <div style={{ flex: 1 }}>
                      <div style={styles.layerPoiLabel}>Focus this truck</div>
                      <div style={styles.layerPoiSub}>Hide the rest of the fleet</div>
                    </div>
                    <ToggleSwitch on={focusTruckOnly} onClick={() => setFocusTruckOnly((v) => !v)} />
                  </div>
                )}
                <div style={styles.layerPoiRow}>
                  <span style={styles.layerPoiIcon}><Fuel size={13} /></span>
                  <div style={{ flex: 1 }}>
                    <div style={styles.layerPoiLabel}>Fuel stations</div>
                    <div style={styles.layerPoiSub}>Recommended stop always shown</div>
                  </div>
                  <ToggleSwitch on={showFuelStations} onClick={() => setShowFuelStations((v) => !v)} />
                </div>
                <div style={styles.layerPoiRow}>
                  <span style={styles.layerPoiIcon}><ShieldHalf size={13} /></span>
                  <div style={{ flex: 1 }}>
                    <div style={styles.layerPoiLabel}>Rest areas</div>
                    <div style={styles.layerPoiSub}>Reference truck-stop parking</div>
                  </div>
                  <ToggleSwitch on={showRestAreas} onClick={() => setShowRestAreas((v) => !v)} />
                </div>
                {dispatcherOptions.length > 1 && (
                  <>
                    <div
                      style={{ ...styles.layerPoiSectionLabel, ...styles.layerPoiSectionLabelClickable }}
                      onClick={() => setDispatcherListOpen((v) => !v)}
                    >
                      <span>
                        Dispatchers ({[...(visibleDispatcherIds || [])].filter((id) => id !== UNASSIGNED_KEY).length}/{dispatcherOptions.length} shown)
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setVisibleDispatcherIds(new Set([...dispatcherOptions.map((d) => d.id), UNASSIGNED_KEY]));
                          }}
                          style={styles.layerRefreshBtn}
                        >
                          All
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setVisibleDispatcherIds(new Set());
                          }}
                          style={styles.layerRefreshBtn}
                        >
                          None
                        </button>
                        {dispatcherListOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </span>
                    </div>
                    {dispatcherListOpen && dispatcherOptions.map((d) => (
                      <div key={d.id} style={styles.layerPoiRow}>
                        <span style={styles.layerPoiIcon}><Users size={13} /></span>
                        <div style={{ flex: 1 }}>
                          <div style={styles.layerPoiLabel}>{d.name}{d.id === currentUserId ? " (you)" : ""}</div>
                        </div>
                        <ToggleSwitch
                          on={visibleDispatcherIds?.has(d.id) ?? false}
                          onClick={() =>
                            setVisibleDispatcherIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(d.id)) next.delete(d.id);
                              else next.add(d.id);
                              return next;
                            })
                          }
                        />
                      </div>
                    ))}
                    {dispatcherListOpen && (
                      <div key="__unassigned__" style={styles.layerPoiRow}>
                        <span style={styles.layerPoiIcon}><UserX size={13} /></span>
                        <div style={{ flex: 1 }}>
                          <div style={styles.layerPoiLabel}>Unassigned (no dispatcher)</div>
                        </div>
                        <ToggleSwitch
                          on={visibleDispatcherIds?.has(UNASSIGNED_KEY) ?? false}
                          onClick={() =>
                            setVisibleDispatcherIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(UNASSIGNED_KEY)) next.delete(UNASSIGNED_KEY);
                              else next.add(UNASSIGNED_KEY);
                              return next;
                            })
                          }
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {activeTab === "route" && routeSegments && (() => {
            const options = loadEconomics?.data?.routes_considered || [];
            const chosenType = loadEconomics?.data?.chosen_route?.route_type;
            const chosenOption = options.find((o) => o.route_type === chosenType) || options[0];
            const deadheadMiles = chosenOption?.deadhead_miles;
            return (
              <div style={styles.floatingPanel}>
                <div style={styles.layersPanelHeader} onClick={() => setRoutesSectionOpen((v) => !v)}>
                  <span style={styles.layersPanelTitle}>
                    <Navigation size={12} />
                    Routes{!routesSectionOpen && options.length > 0 ? ` (${options.length})` : " -- pick one, show any"}
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {selectedLoad?.status === "in_transit" && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setRouteMonitorOpen(true); }}
                        title="See the planned route vs. the actual path the truck has taken"
                        style={styles.layerRefreshBtn}
                      >
                        Monitor
                      </button>
                    )}
                    {/* Once a load is picked up there's no plan left to
                        re-decide -- a full recalculation here would also
                        silently replan the driver's already-sent fuel
                        stops mid-trip. The ETA above is now a live figure
                        that updates itself every few minutes on its own;
                        nothing left for a manual refresh to do. */}
                    {options.length > 0 && !selectedLoad?.actual_pickup_at && (
                      <button
                        onClick={(e) => { e.stopPropagation(); loadRoutePlan(true); }}
                        disabled={routePlanMeta?.refreshing}
                        title="Recompute routes for this load"
                        style={styles.layerRefreshBtn}
                      >
                        {routePlanMeta?.refreshing ? "…" : "Refresh"}
                      </button>
                    )}
                    {routesSectionOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </span>
                </div>
                {routeMonitorOpen && (
                  <RouteMonitorModal
                    loadId={selectedLoad?.id}
                    unitNumber={selectedTruck?.unit_number}
                    onClose={() => setRouteMonitorOpen(false)}
                    themeClassName="op-center-dark"
                    themeVars={themeVars}
                  />
                )}
                {routesSectionOpen && (
                  <div style={styles.routesPanelBody}>
                    {options.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <RouteControls visibility={routeVisibility} hasPickup={!selectedLoad?.actual_pickup_at} />
                      </div>
                    )}
                    {!selectedLoad?.actual_pickup_at && (
                      <div style={styles.layerRouteRow}>
                        <span style={{ ...styles.routeSelectDot, background: "transparent", borderColor: "var(--muted)", cursor: "default" }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={styles.routeOptionTitle}>Pickup route</div>
                          <div style={styles.routeOptionMeta}>
                            Truck's current position to pickup{deadheadMiles != null ? ` · ${Math.round(deadheadMiles)} mi` : ""}
                          </div>
                        </div>
                        <ToggleSwitch on={!routeVisibility.isHidden(PICKUP_ROUTE_KEY)} color="var(--muted)" onClick={() => routeVisibility.toggle(PICKUP_ROUTE_KEY)} />
                      </div>
                    )}
                    {options.map((opt, i) => {
                      const isChosen = opt.route_type === chosenType;
                      const isHidden = routeVisibility.isHidden(opt.route_type);
                      const color = routeVisibility.colorFor(opt.route_type);
                      const plan = fullTripPlans[opt.route_type];
                      return (
                        <div key={opt.route_type} style={styles.layerRouteRow}>
                          <button
                            onClick={() => selectRoute(opt.route_type)}
                            title={isChosen ? "This is the route in use" : "Use this route"}
                            style={{
                              ...styles.routeSelectDot,
                              background: isChosen ? color : "transparent",
                              borderColor: color,
                            }}
                          />
                          <div
                            style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                            onClick={() => {
                              // Clicking a route both opens its detail modal
                              // AND isolates it on the map underneath, so the
                              // map still shows just this one route once the
                              // modal is closed -- not all of them at once.
                              routeVisibility.isolate(opt.route_type);
                              setRouteModalType(opt.route_type);
                            }}
                          >
                            <div style={styles.routeOptionTitle}>
                              Route {i + 1}
                              {isChosen && <span style={styles.routeOptionBadge}>In use</span>}
                            </div>
                            <div style={styles.routeOptionMeta}>
                              {opt.duration_hours}h driving
                              {plan?.data && (plan.data.incomplete_reason
                                ? " · couldn't fully plan fuel/rest stops"
                                : ` · ${plan.data.fuel_stop_count} fuel stop${plan.data.fuel_stop_count === 1 ? "" : "s"} · ${plan.data.rest_zone_count} HOS`)}
                            </div>
                            {(opt.insurance_cost > 0 || opt.office_payroll_cost > 0 || opt.maintenance_cost > 0) && (
                              <div style={{ ...styles.routeOptionMeta, marginTop: 1 }}>
                                {opt.insurance_cost > 0 && (
                                  <b style={{ color: "var(--ink-2)", fontWeight: 600 }}>${Math.round(opt.insurance_cost).toLocaleString()} insurance</b>
                                )}
                                {opt.office_payroll_cost > 0 && (
                                  <>{opt.insurance_cost > 0 ? " · " : ""}<b style={{ color: "var(--ink-2)", fontWeight: 600 }}>${Math.round(opt.office_payroll_cost).toLocaleString()} office/payroll</b></>
                                )}
                                {opt.maintenance_cost > 0 && (
                                  <>{(opt.insurance_cost > 0 || opt.office_payroll_cost > 0) ? " · " : ""}<b style={{ color: "var(--ink-2)", fontWeight: 600 }}>${Math.round(opt.maintenance_cost).toLocaleString()} maintenance</b></>
                                )}
                              </div>
                            )}
                            {opt.contribution_profit != null && (
                              <div style={{ marginTop: 3, fontSize: 12.5, fontWeight: 700, color: opt.contribution_profit < 0 ? "#f2a33d" : "var(--brand)" }}>
                                {opt.contribution_profit < 0 ? "-" : "+"}${Math.abs(Math.round(opt.contribution_profit)).toLocaleString()}
                                <span style={{ fontWeight: 400, color: "var(--muted)", marginLeft: 6 }}>tap for cost breakdown</span>
                              </div>
                            )}
                          </div>
                          <ToggleSwitch
                            on={!isHidden}
                            color={color}
                            onClick={() => routeVisibility.toggle(opt.route_type)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        <MapContainer center={mapCenter} zoom={5} style={styles.map} scrollWheelZoom={true} key={theme} className={theme === "light" ? "map-tiles-dim" : undefined}>
          {tileUrl && <TileLayer attribution={TILE_LAYER_ATTRIBUTION} url={tileUrl} />}
          {/* "Focus this truck" only ever makes sense while a truck is
              selected (its own toggle row is even hidden once nothing's
              selected) -- but the boolean itself outlived deselection, so
              the map's own focusTruckOnly filter kept matching against a
              null selectedTruckId and hid every marker, not just the one
              that had been focused. Confirmed real: reported as "I click
              the map and I lose the truck" after browsing a selected
              truck's tabs with focus mode on. */}
          <MapClickDeselect onDeselect={() => { setSelectedTruckId(null); setFocusTruckOnly(false); }} />
          {!routeViewPoints && !nextTripPreviewFitPoints && (
            <MapFlyTo
              lat={selectedTruck ? Number(selectedTruck.current_lat) : null}
              lng={selectedTruck ? Number(selectedTruck.current_lng) : null}
              zoom={7}
              truckId={selectedTruckId}
            />
          )}
          {routeViewPoints && <MapFitToPoints points={routeViewPoints} fitKey={routeFitKey} />}
          {!routeViewPoints && nextTripPreviewFitPoints && (
            <MapFitToPoints points={nextTripPreviewFitPoints} fitKey={nextTripPreviewId} />
          )}

          {(focusTruckOnly ? enriched.filter((e) => e.truck.id === selectedTruckId) : mapVisibleTrucks).map(({ truck: t, driver, load, status }) => (
            <Marker
              key={t.id}
              position={[Number(t.current_lat), Number(t.current_lng)]}
              icon={truckIcon(driver ? driver.driver_name.split(" ")[0] : t.unit_number, STATUS[status].color, t.id === selectedTruckId)}
              eventHandlers={{ click: () => setSelectedTruckId(t.id) }}
            >
              <Tooltip direction="right" offset={[24, 0]} opacity={1}>
                <div style={styles.tipInner}>
                  <div style={styles.tipTitle}>{t.unit_number} · {driver ? driver.driver_name : "Unassigned"}</div>
                  <div>{STATUS[status].label}</div>
                  <div>{t.current_fuel_gallons ?? "—"} / {t.tank_capacity_gallons ?? "—"} gal</div>
                  <div>{t.current_speed_mph ?? "—"} mph</div>
                  <div>HOS: {driver?.hos_hours_remaining != null ? `${driver.hos_hours_remaining} hrs left` : "—"}</div>
                  {load && <div>→ {load.destination_location}</div>}
                </div>
              </Tooltip>
            </Marker>
          ))}

          {/* Ghost marker: where this truck WILL be once it finishes an
              earlier chained load, not where it is right now (see
              ghostTruckIcon's own comment). Only for the second-or-later
              load in selectedTruckLoads' own pickup-ordered list -- the
              first load's own deadhead genuinely starts from the truck's
              live position, so it never gets a ghost of itself. */}
          {(() => {
            if (!selectedLoad) return null;
            const idx = selectedTruckLoads.findIndex((l) => l.id === selectedLoad.id);
            if (idx <= 0) return null;
            const precedingLoad = selectedTruckLoads[idx - 1];
            if (precedingLoad?.destination_lat == null || precedingLoad?.destination_lng == null) return null;
            // Explicit "en-US" (not the browser's own locale) -- an
            // unqualified toLocaleDateString here rendered as "18.08"
            // under this dispatcher's own locale, which reads as
            // day-slash-month and is easy to misparse as a due date
            // rather than the intended "August 18" shorthand.
            const deliveryDate = precedingLoad.delivery_datetime
              ? new Date(precedingLoad.delivery_datetime).toLocaleDateString("en-US", { month: "short", day: "numeric" })
              : null;
            return (
              <Marker
                position={[Number(precedingLoad.destination_lat), Number(precedingLoad.destination_lng)]}
                icon={ghostTruckIcon(`Starts here after Load ${idx}`, deliveryDate ? `~${deliveryDate}` : "once Load " + idx + " delivers")}
                zIndexOffset={-100}
              >
                <Tooltip direction="right" offset={[24, 0]} opacity={1}>
                  <div style={styles.tipInner}>
                    <div style={styles.tipTitle}>Projected start of Load {idx + 1}</div>
                    <div>Truck is still on Load {idx} ({precedingLoad.origin_location} → {precedingLoad.destination_location})</div>
                    <div>This load's own deadhead begins once that one delivers here.</div>
                  </div>
                </Tooltip>
              </Marker>
            );
          })()}

          {/* Every real route from routes_considered is drawn simultaneously
              (not one at a time), the chosen one in full color/weight, the
              rest greyed out but still visible -- so a manager can see what
              the system actually offered, not just what got picked. Deadhead
              is shared across all of them (HERE is only asked for
              alternatives on the long-haul loaded leg, not the short
              deadhead run to pickup) so it's drawn once. Falls back to a
              generic OSRM fetch only when HERE routing wasn't available at
              all (haversine_fallback) -- OSRM doesn't know about tolls or
              truck restrictions, so it must never be shown as if it were a
              real HERE alternative. */}
          {routeSegments && (() => {
            const routeOptions = loadEconomics?.data?.routes_considered;
            const chosenType = loadEconomics?.data?.chosen_route?.route_type;
            const chosenOption = routeOptions?.find((o) => o.route_type === chosenType);
            const deadheadPositions = chosenOption?.geometry?.deadhead?.length
              ? chosenOption.geometry.deadhead
              : (roadRoute?.deadhead || routeSegments.deadhead);

            const showAllRoutes = activeTab === "route" && routeOptions?.length > 1;
            const candidateOptions = showAllRoutes ? routeOptions : [chosenOption].filter(Boolean);
            const visibleOptions = candidateOptions.filter((opt) => !routeVisibility.isHidden(opt.route_type));

            // Toll collection points repeat across routes that share a
            // corridor (e.g. most LA-bound routes all eat the Chicago
            // Skyway) -- dedupe by name+position so it's one clear pin, not
            // a stack of identical markers. A toll on the chosen route
            // keeps the accent orange regardless of which other (dimmed)
            // route also happens to use it.
            const tollByKey = new Map();
            visibleOptions.forEach((opt) => {
              const isChosen = opt.route_type === chosenType;
              const color = routeVisibility.colorFor(opt.route_type);
              (opt.toll_stops || []).forEach((t) => {
                if (t.lat == null || t.lng == null) return;
                const key = `${t.name}|${t.lat.toFixed(3)}|${t.lng.toFixed(3)}`;
                const existing = tollByKey.get(key);
                if (!existing || isChosen) {
                  // The deadhead leg is computed once, not per alternative --
                  // a deadhead toll is the exact same real cost regardless
                  // of which loaded-route option is chosen, so it's colored
                  // neutral (matching the Pickup route line) instead of
                  // whichever route's color, which would wrongly imply it's
                  // specific to that one alternative.
                  tollByKey.set(key, { ...t, color: t.leg === "deadhead" ? "var(--muted)" : color });
                }
              });
            });

            return (
              <>
                {!routeVisibility.isHidden(PICKUP_ROUTE_KEY) && !selectedLoad?.actual_pickup_at && (
                  <Polyline
                    positions={deadheadPositions}
                    pathOptions={{
                      color: selected?.status === "heading_to_pickup" ? STATUS.heading_to_pickup.color : "var(--muted)",
                      weight: selected?.status === "heading_to_pickup" ? 5 : 4,
                      dashArray: "6 6",
                    }}
                  />
                )}
                {visibleOptions.map((opt) => {
                  const isChosen = opt.route_type === chosenType;
                  const color = routeVisibility.colorFor(opt.route_type);
                  // Real per-hop road through every one of this load's own
                  // load_stops (see fetchFreshRoutePlan's own comment) --
                  // opt.geometry.loaded alone is estimate-trip-cost's
                  // straight origin-to-destination line, which drew right
                  // past a real stop instead of the real road detour to
                  // reach it. Read from fullTripPlans (the same source the
                  // stop/rest/fuel markers just below already use) rather
                  // than off `opt` directly, since a SAVED plan reloaded
                  // via get-route-plan only carries leg_geometries inside
                  // its own trip_plan, not as a separate top-level field.
                  const legGeometries = fullTripPlans[opt.route_type]?.data?.leg_geometries || [];
                  if (legGeometries.length > 0) {
                    return legGeometries.map((leg, li) => leg?.length > 0 && (
                      <Polyline
                        key={`${opt.route_type || "single"}-leg-${li}`}
                        positions={leg}
                        pathOptions={{ color, weight: isChosen ? 7 : 5, opacity: isChosen ? 1 : 0.85 }}
                      />
                    ));
                  }
                  const positions = opt.geometry?.loaded?.length ? opt.geometry.loaded : (roadRoute?.loaded || routeSegments.loaded);
                  return (
                    <Polyline
                      key={opt.route_type || "single"}
                      positions={positions}
                      pathOptions={{ color, weight: isChosen ? 7 : 5, opacity: isChosen ? 1 : 0.85 }}
                    />
                  );
                })}
                {[...tollByKey.values()].map((t, i) => (
                  <Marker key={`toll-${i}-${t.lat}-${t.lng}`} position={[t.lat, t.lng]} icon={tollIcon(t.color)}>
                    <Tooltip direction="top" offset={[0, -10]}>
                      {t.name}{t.price_usd != null ? ` · $${Number(t.price_usd).toFixed(2)}` : ""}
                    </Tooltip>
                  </Marker>
                ))}
                <Marker position={routeSegments.originPos} icon={pinIcon("#5b9bd5", "S")}>
                  <Tooltip>Pickup: {selectedLoad.origin_location}</Tooltip>
                </Marker>
                {/* Real intermediate stops for a multi-stop load (see
                    load_stops) -- [] for a normal load, so this renders
                    nothing extra for the vast majority of loads. */}
                {selectedLoadStops
                  .filter((s) => s.lat != null && s.lng != null)
                  .map((s, i) => (
                    <Marker key={s.id} position={[Number(s.lat), Number(s.lng)]} icon={pinIcon("#e0745d", String(i + 2))}>
                      <Tooltip>{s.stop_type === "pickup" ? "Pickup" : "Dropoff"}: {s.location}</Tooltip>
                    </Marker>
                  ))}
                <Marker position={routeSegments.destPos} icon={pinIcon("#a78bfa", "E")}>
                  <Tooltip>Delivery: {selectedLoad.destination_location}</Tooltip>
                </Marker>
              </>
            );
          })()}

          {/* ACTUAL driven GPS breadcrumbs for the current load, drawn on top of
              the planned route on the Current Load tab -- amber = the empty
              deadhead approach to pickup, green = the loaded path driven so far.
              Same color language as the Route Monitor modal legend. Gives the
              dispatcher immediate "what has actually happened" feedback instead
              of only the planned start->end line. */}
          {activeTab === "route" && loadBreadcrumbs?.deadhead?.length > 1 && (
            <Polyline
              positions={loadBreadcrumbs.deadhead}
              pathOptions={{ color: "#f2a33d", weight: 3, opacity: 0.85 }}
            />
          )}
          {activeTab === "route" && loadBreadcrumbs?.loaded?.length > 1 && (
            <Polyline
              positions={loadBreadcrumbs.loaded}
              pathOptions={{ color: "#22c55e", weight: 4, opacity: 0.95 }}
            />
          )}

          {nextTripPreview && nextTripPreviewFitPoints && (
            <>
              <Polyline
                positions={nextTripPreviewRoute?.loaded || nextTripPreviewFitPoints}
                pathOptions={{ color: "#a78bfa", weight: 5, opacity: 0.9 }}
              />
              <Marker position={nextTripPreviewFitPoints[0]} icon={pinIcon("#a78bfa", "S")}>
                <Tooltip>Pickup: {nextTripPreview.origin_location} · ${nextTripPreview.rate_offered}</Tooltip>
              </Marker>
              <Marker position={nextTripPreviewFitPoints[1]} icon={pinIcon("#a78bfa", "E")}>
                <Tooltip>Delivery: {nextTripPreview.destination_location}</Tooltip>
              </Marker>
            </>
          )}

          {/* Clustered -- fuel_prices/rest_areas are nationwide reference
              tables (thousands of rows, no viewport bounding), so rendering
              them as individual Leaflet markers with custom HTML divIcons +
              Tooltips was mounting thousands of real DOM nodes at once and
              pegging the browser the moment either toggle was flipped on.
              MarkerClusterGroup collapses nearby points into a single
              count bubble that only expands into real markers as you zoom
              in, so the DOM only ever holds however many are actually
              distinguishable at the current zoom level. Gated on the
              toggle BEFORE mapping (not per-marker inside it) so flipping
              a toggle off skips the array walk entirely, not just the render. */}
          {showFuelStations && (
            <MarkerClusterGroup chunkedLoading spiderfyOnMaxZoom={false} disableClusteringAtZoom={11}>
              {mapFuelMarkers.map((s) => {
                const color = s.hasPrice ? priceColor(Number(s.price_per_gallon), priceRange.min, priceRange.max) : "var(--muted)";
                return (
                  <Marker
                    key={s.id}
                    position={[Number(s.lat), Number(s.lng)]}
                    icon={stationIcon(color, { size: 22 })}
                  >
                    <Tooltip direction="top" offset={[0, -16]} opacity={1}>
                      {(() => {
                        if (!s.hasPrice) return <>{s.name} · no price data</>;
                        const price = Number(s.price_per_gallon);
                        // price_per_gallon is this fleet's real fuel-card "Your
                        // Price" -- the contracted, discounted price with NO
                        // tax included at all (confirmed against the real
                        // source spreadsheet). The station's own state excise
                        // tax rate is a separate, real reference figure here,
                        // not something to subtract back out of the price --
                        // it was never in it. Real IFTA liability depends on
                        // miles driven per state, not just this station's own
                        // rate, so a true "cost including tax" figure needs
                        // route context (see the trip-plan stop tooltips,
                        // which show tax_adjusted_price_per_gallon) -- this
                        // generic station map has no route to weight that
                        // against, so it just shows the two real facts as-is.
                        const tax = s.state ? fuelTaxByState[s.state] : null;
                        if (tax == null) return <>{s.name} · ${price.toFixed(2)}/gal</>;
                        return (
                          <div>
                            <div style={styles.tooltipTitle}>{s.name}</div>
                            <TooltipRow label="Your price (no tax)" value={`$${price.toFixed(2)}/gal`} />
                            <TooltipRow label={`${s.state} diesel tax`} value={`$${tax.toFixed(2)}/gal`} />
                          </div>
                        );
                      })()}
                    </Tooltip>
                  </Marker>
                );
              })}
            </MarkerClusterGroup>
          )}

          {showRestAreas && (
            <MarkerClusterGroup chunkedLoading spiderfyOnMaxZoom={false} disableClusteringAtZoom={11}>
              {restAreas.map((r, i) => (
                <Marker key={`rest-${r.lat}-${r.lng}-${i}`} position={[Number(r.lat), Number(r.lng)]} icon={restAreaIcon()}>
                  <Tooltip direction="top" offset={[0, -10]}>{r.name || "Rest area"} · no fuel, parking only</Tooltip>
                </Marker>
              ))}
            </MarkerClusterGroup>
          )}

          {/* Every fuel stop and every HOS rest break for the WHOLE trip,
              not just the next one -- a long haul can need several of each.
              One plan per visible route, each projected onto THAT route's
              own real HERE road geometry (not a straight line) and colored
              to match its line/card, so it's clear which stops go with
              which road. */}
          {activeTab === "route" && loadEconomics?.data?.routes_considered?.length > 0 && (() => {
            const options = loadEconomics.data.routes_considered;
            const chosenType = loadEconomics.data.chosen_route?.route_type;
            return options.filter((opt) => !routeVisibility.isHidden(opt.route_type)).map((opt) => {
              const isChosen = opt.route_type === chosenType;
              const idx = options.indexOf(opt);
              const color = routeVisibility.colorFor(opt.route_type);
              const markerOpacity = isChosen ? 1 : 0.5;
              const plan = fullTripPlans[opt.route_type];
              if (!plan?.data?.stops) return null;
              // Numbers real intermediate stops (a multi-stop load's own
              // load_stops, see that table's migration docstring) 2, 3, ...
              // -- stop 1 is the origin pin (S) drawn separately below.
              let stopNum = 1;
              return (
                <React.Fragment key={`plan-${opt.route_type}`}>
                  {plan.data.stops.map((s, i) => {
                    if (s.lat == null || s.lng == null || s.type === "pickup" || s.type === "delivery" || s.type === "wait") return null;

                    if (s.type === "stop_pickup" || s.type === "stop_dropoff") {
                      stopNum += 1;
                      return (
                        <Marker
                          key={`full-trip-stop-${opt.route_type}-${i}`}
                          position={[Number(s.lat), Number(s.lng)]}
                          icon={pinIcon(color, String(stopNum))}
                          zIndexOffset={isChosen ? 500 : 250}
                          opacity={markerOpacity}
                        >
                          <Tooltip direction="top" offset={[0, -14]}>
                            <div>
                              <div style={styles.tooltipTitle}>
                                Route {idx + 1} · Stop {stopNum} · {s.type === "stop_pickup" ? "Pickup" : "Dropoff"}
                              </div>
                              <div>{s.location}</div>
                            </div>
                          </Tooltip>
                        </Marker>
                      );
                    }

                    if (s.type === "rest_zone") {
                      return (
                        <React.Fragment key={`full-trip-stop-${opt.route_type}-${i}`}>
                          <Circle
                            center={[Number(s.lat), Number(s.lng)]}
                            radius={(s.radius_miles || 15) * 1609.34}
                            pathOptions={{ color, fillColor: color, fillOpacity: isChosen ? 0.15 : 0.06, weight: 1.5, dashArray: "4 4", opacity: markerOpacity }}
                          >
                            <Tooltip direction="top" sticky>
                              <div>
                                <div style={styles.tooltipTitle}>
                                  Route {idx + 1} · HOS rest break {s.sequence} -- ~{s.radius_miles} mi area
                                </div>
                                <RestZoneTooltipRows s={s} />
                              </div>
                            </Tooltip>
                          </Circle>
                          <Marker
                            position={[Number(s.lat), Number(s.lng)]}
                            icon={restZoneIcon(color)}
                            zIndexOffset={isChosen ? 400 : 200}
                            opacity={markerOpacity}
                          >
                            <Tooltip direction="top" offset={[0, -10]}>
                              <div>
                                <div style={styles.tooltipTitle}>Route {idx + 1} · HOS rest break {s.sequence}</div>
                                <RestZoneTooltipRows s={s} />
                              </div>
                            </Tooltip>
                          </Marker>
                        </React.Fragment>
                      );
                    }

                    const isCombined = s.type === "fuel_and_rest";
                    // Deadhead-leg (pickup) stops match the deadhead polyline's own
                    // muted/dashed styling instead of the loaded route's color -- same
                    // leg-based split the toll markers above already use.
                    // Always this route's own color, even on the deadhead
                    // (pickup) leg -- unlike a toll (purely positional, so
                    // genuinely identical for every route sharing that
                    // stretch of road), WHICH station gets chosen here is
                    // decided by the whole route ahead (the proactive
                    // top-off logic checks for a cheaper station further
                    // down the entire trip, including the route-specific
                    // loaded leg) -- so two routes can legitimately pick a
                    // different station, or a different fill amount, for
                    // the "same" physical stretch. Graying it out implied
                    // it was shared/identical the way a deadhead toll is,
                    // which isn't actually true here.
                    const legColor = color;
                    return (
                      <React.Fragment key={`full-trip-stop-${opt.route_type}-${i}`}>
                        {isCombined && (
                          <Circle
                            center={[Number(s.lat), Number(s.lng)]}
                            radius={COMBINED_STOP_RING_MILES * 1609.34}
                            pathOptions={{ color: legColor, fillColor: legColor, fillOpacity: isChosen ? 0.15 : 0.06, weight: 1.5, dashArray: "4 4", opacity: markerOpacity }}
                          />
                        )}
                        <Marker
                          position={[Number(s.lat), Number(s.lng)]}
                          icon={stationIcon(legColor, {
                            size: 22, ringColor: legColor,
                            label: isCombined ? `F${s.fuel_sequence}+R` : `F${s.sequence}`,
                            labelColor: legColor,
                          })}
                          zIndexOffset={isChosen ? 500 : 250}
                          opacity={markerOpacity}
                        >
                          <Tooltip direction="top" offset={[0, -14]}>
                            <div>
                              <div style={styles.tooltipTitle}>
                                Route {idx + 1} · {isCombined ? `Fuel + HOS rest break ${s.rest_sequence}` : `Fuel stop ${s.sequence}`}: {s.name || "—"}
                              </div>
                              <FuelStopTooltipRows s={s} isCombined={isCombined} />
                            </div>
                          </Tooltip>
                        </Marker>
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            });
          })()}
        </MapContainer>
      </div>
    </div>

    {routeModalType && (() => {
      const options = loadEconomics?.data?.routes_considered || [];
      const route = options.find((r) => r.route_type === routeModalType);
      const idx = options.indexOf(route);
      const chosenType = loadEconomics?.data?.chosen_route?.route_type;
      const isChosen = routeModalType === chosenType;
      const color = routeVisibility.colorFor(routeModalType);
      const plan = fullTripPlans[routeModalType]?.data;
      if (!route) return null;
      return (
        <div style={styles.modalOverlay} onClick={() => setRouteModalType(null)}>
          <div style={{ ...styles.modalCard, width: 560 }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>
                <span style={{ ...styles.routeOptionSwatch, background: color }} />
                Route {idx + 1}{isChosen ? " -- in use" : ""}
              </div>
              <button onClick={() => setRouteModalType(null)} style={styles.modalCloseBtn}>×</button>
            </div>

            <div style={styles.routeOptionMeta}>
              {route.distance_miles != null && `${Math.round(route.distance_miles)} mi · `}
              {route.duration_hours}h driving
              {plan && (plan.incomplete_reason
                ? " · couldn't fully plan fuel/rest stops"
                : ` · ${plan.fuel_stop_count} fuel stops · ${plan.rest_zone_count} HOS breaks`)}
            </div>

            <div style={styles.legendHeadlineRow}>
              <div>
                <div style={styles.legendHeadlineValue}>{route.total_cost != null ? `$${Math.round(route.total_cost).toLocaleString()}` : "—"}</div>
                <div style={styles.legendHeadlineLabel}>total cost</div>
              </div>
              {route.contribution_profit != null && (
                <div>
                  <div style={{ ...styles.legendHeadlineValue, color: route.contribution_profit < 0 ? "#f2a33d" : "var(--brand)" }}>
                    {route.contribution_profit < 0 ? "-" : "+"}${Math.abs(Math.round(route.contribution_profit)).toLocaleString()}
                  </div>
                  <div style={styles.legendHeadlineLabel}>{route.contribution_profit < 0 ? "loss" : "profit"}</div>
                </div>
              )}
            </div>
            {route.distance_miles > 0 && route.total_cost != null && (
              <div style={styles.legendRateRow}>
                <span>${(route.total_cost / route.distance_miles).toFixed(2)}/mi</span>
                {route.duration_hours > 0 && <span>${(route.total_cost / route.duration_hours).toFixed(2)}/hr</span>}
              </div>
            )}

            <div style={styles.legendDividerLine} />
            <LegendRow label="Fuel" value={route.fuel_cost} />
            <LegendRow label="Tolls" value={route.toll_cost} />
            <LegendRow label="Driver pay" value={route.driver_cost} />
            {route.insurance_cost > 0 && <LegendRow label="Insurance" value={route.insurance_cost} />}
            {route.office_payroll_cost > 0 && <LegendRow label="Office/payroll" value={route.office_payroll_cost} />}
            {route.maintenance_cost > 0 && <LegendRow label="Maintenance" value={route.maintenance_cost} />}
            {route.other_cost > 0 && <LegendRow label="Equipment" value={route.other_cost} />}
            {route.wait_cost_cash > 0 && <LegendRow label="Wait time (driver pay)" value={route.wait_cost_cash} />}
            {route.wait_cost_opportunity > 0 && <LegendRow label="Wait time (lost revenue)" value={route.wait_cost_opportunity} />}
            {route.wait_cost > 0 && route.wait_cost_cash == null && <LegendRow label="Wait time" value={route.wait_cost} />}

            {!isChosen && (
              <button
                onClick={() => { selectRoute(routeModalType); }}
                style={{ ...styles.refreshRoutesBtn, marginTop: 8, marginLeft: 0 }}
              >
                Use this route
              </button>
            )}

            <SectionDivider label="Full itinerary" icon={<Navigation size={12} />} />
            {plan?.incomplete_reason && (
              <div style={{ ...styles.caption, color: "#f2a33d" }}>{plan.incomplete_reason}</div>
            )}
            {plan?.stops ? (
              <div style={styles.tripPlanStops}>
                {plan.stops.map((s, i) => (
                  <div key={i} style={styles.tripPlanStopRow}>
                    <span style={{ ...styles.tripPlanStopDot, background: s.type === "fuel" || s.type === "rest_zone" || s.type === "fuel_and_rest" ? color : (STOP_TYPE_COLOR[s.type] || "var(--muted)") }} />
                    <span style={styles.tripPlanStopLabel}>
                      {s.type === "rest_zone" ? (
                        <>
                          HOS rest break {s.sequence}: somewhere in this ~{s.radius_miles} mi area
                          {s.break_hours != null && ` · ${s.break_hours}h break`}
                          {s.nearby_options?.length > 0 && ` (${s.nearby_options.length} known option${s.nearby_options.length > 1 ? "s" : ""} nearby)`}
                        </>
                      ) : s.type === "fuel_and_rest" ? (
                        <>
                          Fuel + HOS rest break {s.rest_sequence} (combined): {s.name || "—"}
                          {s.estimated_cost != null && ` · $${s.estimated_cost} (${s.gallons_purchased} gal)`}
                          {s.break_hours != null && ` · ${s.break_hours}h break`}
                          {s.distance_from_route_miles != null && ` · ${s.distance_from_route_miles} mi off-route`}
                          {" -- no separate stop needed"}
                        </>
                      ) : s.type === "fuel" ? (
                        <>
                          Fuel stop {s.sequence}: {s.name || "—"}{s.partial_fill && " (partial fill)"}
                          {s.estimated_cost != null && ` · $${s.estimated_cost} (${s.gallons_purchased} gal)`}
                          {s.distance_from_route_miles != null && ` · ${s.distance_from_route_miles} mi off-route`}
                          {s.approximate && " · approximate"}
                          {s.comparison_note && <div style={styles.tripPlanTollNote}>{s.comparison_note}</div>}
                          {s.partial_fill_reason && <div style={styles.tripPlanTollNote}>{s.partial_fill_reason}</div>}
                        </>
                      ) : (
                        <>{STOP_TYPE_LABEL[s.type] || s.type}: {s.location || "—"}</>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={styles.emptyState}>Planning…</div>
            )}

            <SectionDivider label="AI explanation" icon={<TrendingUp size={12} />} />
            {routeExplain?.messages?.length === 0 && !routeExplain?.loading && (
              <button onClick={() => askRouteExplain(null)} style={{ ...styles.refreshRoutesBtn, marginLeft: 0 }}>
                Analyze this route
              </button>
            )}
            {routeExplain?.messages?.map((m, i) => (
              <div key={i} style={styles.routeExplainTurn}>
                {i > 0 && <div style={styles.routeExplainQuestion}>{m.question}</div>}
                {i === 0 ? <NarrativeText text={m.answer} /> : <div style={styles.routeExplainAnswer}>{m.answer}</div>}
              </div>
            ))}
            {routeExplain?.loading && <div style={styles.emptyState}>Asking the agent…</div>}
            {routeExplain?.error && <div style={{ ...styles.emptyState, color: "#f2a33d" }}>{routeExplain.error}</div>}

            {routeExplain?.messages?.length > 0 && (
              <div style={styles.routeExplainAskRow}>
                <input
                  value={routeExplain?.question || ""}
                  onChange={(e) => setRouteExplain((prev) => ({ ...(prev || { messages: [] }), question: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && routeExplain?.question?.trim()) askRouteExplain(routeExplain.question.trim());
                  }}
                  placeholder="Ask why this route, or how it compares…"
                  style={styles.routeExplainInput}
                />
                <button
                  onClick={() => routeExplain?.question?.trim() && askRouteExplain(routeExplain.question.trim())}
                  disabled={routeExplain?.loading || !routeExplain?.question?.trim()}
                  style={styles.refreshRoutesBtn}
                >
                  Ask
                </button>
              </div>
            )}
          </div>
        </div>
      );
    })()}

    {quickCheckOpen && (
      // No onClick-to-close on the overlay -- this form can hold real typed
      // input (origin/destination/weight/rate), and an accidental click
      // just outside the card used to silently discard it. Only the X
      // button closes it now, same reasoning as the Check Load/Broker Call
      // modals below.
      <div style={styles.modalOverlay}>
        <div style={{ ...styles.modalCard, width: 520, maxHeight: "85vh", overflowY: "auto" }}>
          <div style={styles.modalHeader}>
            <div style={styles.modalTitle}><Gauge size={14} /> Quick Price Check</div>
            <button
              onClick={() => { setQuickCheckOpen(false); setQuickCheckResult(null); }}
              style={styles.modalCloseBtn}
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ fontSize: 11.5, color: "var(--muted)", margin: "0 0 14px", lineHeight: 1.5 }}>
            This is a rough estimate that only calculates the route and part of the associated costs (tolls, fuel, etc..).
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input
              type="text" placeholder="Origin (e.g. Chicago, IL)" value={quickCheckForm.origin_location}
              onChange={(e) => setQuickCheckForm((p) => ({ ...p, origin_location: e.target.value }))}
              style={styles.modalInput}
            />
            <input
              type="text" placeholder="Destination (e.g. Boston, MA)" value={quickCheckForm.destination_location}
              onChange={(e) => setQuickCheckForm((p) => ({ ...p, destination_location: e.target.value }))}
              style={styles.modalInput}
            />
            <input
              type="number" placeholder="Load weight (lbs, optional)" value={quickCheckForm.load_weight_lbs}
              onChange={(e) => setQuickCheckForm((p) => ({ ...p, load_weight_lbs: e.target.value }))}
              title="Not used in this rough estimate (OSRM has no concept of weight-restricted routing) -- carried forward into the exact quote's real truck-legal routing."
              style={styles.modalInput}
            />
            <input
              type="number" placeholder="Broker's offer ($, optional)" value={quickCheckForm.expected_rate}
              onChange={(e) => setQuickCheckForm((p) => ({ ...p, expected_rate: e.target.value }))}
              title="Edit this any time during the call -- profit/floor/counter below update live, no need to re-run the estimate."
              style={styles.modalInput}
            />
            <button
              onClick={submitQuickCheck}
              disabled={quickCheckResult?.loading || !quickCheckForm.origin_location.trim() || !quickCheckForm.destination_location.trim()}
              style={{
                ...styles.brokerCallBtn, justifyContent: "center", marginRight: 0,
                opacity: (quickCheckResult?.loading || !quickCheckForm.origin_location.trim() || !quickCheckForm.destination_location.trim()) ? 0.5 : 1,
              }}
            >
              {quickCheckResult?.loading ? "Estimating…" : "Get Quick Price"}
            </button>
          </div>

          {quickCheckResult?.error && (
            <div style={{ marginTop: 12, fontSize: 12, color: "#f87171" }}>{quickCheckResult.error}</div>
          )}

          {quickCheckResult?.data && (() => {
            const r = quickCheckResult.data;
            // Live off the CURRENT text box, not the value at fetch time --
            // same reasoning route.weekly_balance_after already documents
            // for the exact-quote step: editing the offer mid-call must
            // never require a re-fetch just to see updated profit/floor/
            // counter numbers. Uses the exact same margins/formula as the
            // real Cost Engine (computeNegotiation), so this never disagrees
            // with what Phase 2 would say for the same total_cost.
            const liveRate = quickCheckForm.expected_rate !== "" ? Number(quickCheckForm.expected_rate) : null;
            const neg = computeNegotiation(r.total_cost, r.distance_miles, r.duration_hours, liveRate);
            const hasOffer = neg.profit != null;
            const profitColor = !hasOffer ? "var(--ink)" : neg.profit >= 0 ? "var(--brand)" : "#e0745d";
            const weeklyBalanceAfterLive = hasOffer && r.weekly_balance_before != null
              ? r.weekly_balance_before + neg.profit
              : null;
            const companyProfit = weeklyBalanceAfterLive != null
              ? Math.max(0, weeklyBalanceAfterLive) - Math.max(0, r.weekly_balance_before)
              : null;
            const justCoveredWeek = weeklyBalanceAfterLive != null && weeklyBalanceAfterLive >= 0;
            return (
              <div style={{ marginTop: 14, borderTop: "1px solid var(--line)", paddingTop: 14 }}>
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700,
                  color: "#d97706", background: "rgba(217,119,6,0.1)", border: "1px solid rgba(217,119,6,0.3)",
                  borderRadius: 999, padding: "3px 10px", marginBottom: 10,
                }}>
                  Rough estimate -- full cost detail comes in the next phase
                </div>
                <div style={styles.statGrid2}>
                  <div style={styles.statCell2}>
                    <div style={styles.statCell2Label}>Distance</div>
                    <div style={styles.statCell2Value}>{r.distance_miles} mi</div>
                    {r.deadhead_miles > 0 && (
                      <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
                        {r.deadhead_miles}mi deadhead + {r.loaded_miles}mi loaded
                      </div>
                    )}
                  </div>
                  <div style={styles.statCell2}>
                    <div style={styles.statCell2Label}>Drive time</div>
                    <div style={styles.statCell2Value}>{r.duration_hours}h</div>
                  </div>
                  <div style={styles.statCell2}>
                    <div style={styles.statCell2Label}>Est. total cost</div>
                    <div style={styles.statCell2Value}>${r.total_cost.toLocaleString()}</div>
                    {r.distance_miles > 0 && (
                      <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>
                        ${(r.total_cost / r.distance_miles).toFixed(2)}/mi
                      </div>
                    )}
                  </div>
                  {hasOffer && (
                    <>
                      <div style={styles.statCell2}>
                        <div style={styles.statCell2Label}>Est. profit</div>
                        <div style={{ ...styles.statCell2Value, color: profitColor }}>
                          {neg.profit >= 0 ? "+" : "-"}${Math.abs(Math.round(neg.profit)).toLocaleString()}
                        </div>
                      </div>
                      <div style={styles.statCell2}>
                        <div style={styles.statCell2Label}>Profit/mi</div>
                        <div style={{ ...styles.statCell2Value, color: profitColor }}>${neg.profitPerMile?.toFixed(2)}</div>
                      </div>
                    </>
                  )}
                </div>

                <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={styles.quickCheckSectionLabel}>Don't go below</div>
                    <div style={{
                      fontSize: 15, fontWeight: 800,
                      color: liveRate != null && liveRate < neg.floor ? "#e0745d" : "var(--ink)",
                    }}>
                      ${Math.round(neg.floor).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={styles.quickCheckSectionLabel}>Ask for</div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "var(--brand)" }}>
                      ${Math.round(neg.suggestedCounter).toLocaleString()}
                    </div>
                  </div>
                </div>
                {liveRate != null && liveRate < neg.floor && (
                  <div style={{ fontSize: 11, color: "#e0745d", marginTop: 4 }}>
                    Their offer is below your 15% floor -- this load would lose money at that rate.
                  </div>
                )}

                {r.weekly_balance_before != null && (
                  <div style={{ ...styles.legendWeeklyBalance, marginTop: 12 }}>
                    This truck's week: <b>{r.weekly_balance_before >= 0 ? "+" : "-"}${Math.abs(Math.round(r.weekly_balance_before)).toLocaleString()}</b>
                    {weeklyBalanceAfterLive != null && (
                      <>
                        {" → "}
                        <b style={{ color: weeklyBalanceAfterLive >= 0 ? "#4cae6d" : "#f2a33d" }}>
                          {weeklyBalanceAfterLive >= 0 ? "+" : "-"}${Math.abs(Math.round(weeklyBalanceAfterLive)).toLocaleString()}
                        </b>
                        {" if you take this load"}
                      </>
                    )}
                  </div>
                )}
                {justCoveredWeek && companyProfit > 0 && (
                  <div style={styles.legendCoveredBadge}>
                    <Check size={11} strokeWidth={3} />
                    Weekly truck cost covered -- ${Math.round(companyProfit).toLocaleString()} of this goes straight to company profit
                  </div>
                )}

                <button
                  onClick={() => setShowQuickCheckInsights((v) => !v)}
                  style={{ ...styles.legendDetailsToggle, marginTop: 12 }}
                >
                  {showQuickCheckInsights ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                  {showQuickCheckInsights ? "Hide" : "Show"} AI Insights
                </button>

                {showQuickCheckInsights && (
                  <>
                    <div style={{ marginTop: 14 }}>
                      <div style={styles.quickCheckSectionLabel}>
                        What this covers -- based on {r.assumptions?.source === "this truck's own numbers" ? "this truck's real numbers" : "fleet-wide averages"}
                      </div>
                      <div style={styles.quickCheckCostRow}><span>Fuel (@${r.assumptions?.fuel_price_per_gallon_assumed}/gal, {r.assumptions?.mpg_used} mpg)</span><span>${r.fuel_cost.toLocaleString()}</span></div>
                      <div style={styles.quickCheckCostRow}><span>Driver pay (@${r.assumptions?.driver_pay_rate_per_mile_used}/mi)</span><span>${r.driver_cost.toLocaleString()}</span></div>
                      <div style={styles.quickCheckCostRow}><span>Insurance</span><span>${r.insurance_cost.toLocaleString()}</span></div>
                      <div style={styles.quickCheckCostRow}><span>Office/payroll</span><span>${r.office_payroll_cost.toLocaleString()}</span></div>
                      <div style={styles.quickCheckCostRow}><span>Maintenance</span><span>${r.maintenance_cost.toLocaleString()}</span></div>
                      <div style={styles.quickCheckCostRow}><span>Other variable</span><span>${r.other_variable_cost.toLocaleString()}</span></div>
                      <div style={styles.quickCheckCostRow}><span>Equipment (~{r.trip_days_assumed} day{r.trip_days_assumed === 1 ? "" : "s"} of the weekly cost)</span><span>${r.equipment_cost.toLocaleString()}</span></div>
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <div style={styles.quickCheckSectionLabel}>Not calculated yet -- comes with the exact quote</div>
                      <ul style={styles.quickCheckMissingList}>
                        <li>Tolls -- $0 assumed here; the real quote prices every toll on the actual route</li>
                        <li>Real truck-legal routing -- weight/height restrictions aren't checked against city-center points</li>
                        <li>Real fuel-stop pricing -- uses the fleet's current ${r.assumptions?.fuel_price_per_gallon_assumed}/gal average, not the specific cheapest stations along the route</li>
                        <li>Exact mileage -- based on city centers, not your real pickup/delivery addresses</li>
                        <li>Pickup wait-time cost -- if the truck isn't free until later, the real quote adds the cost of it sitting idle until then (often the single biggest gap vs. this rough number)</li>
                      </ul>
                    </div>
                  </>
                )}

                <button
                  onClick={quickCheckPurpose === "check_load" ? proceedToCheckLoad : proceedToBrokerCall}
                  style={{ ...styles.brokerCallBtn, justifyContent: "center", marginTop: 12, width: "100%", marginRight: 0 }}
                >
                  {quickCheckPurpose === "check_load"
                    ? `Looks good -- check it against Truck ${selectedTruck?.unit_number || ""} →`
                    : "Looks good -- find the best truck →"}
                </button>
              </div>
            );
          })()}
        </div>
      </div>
    )}

    {brokerCallOpen && (
      // No onClick-to-close on the overlay -- a wizard this long (typed
      // fields, real match/negotiation results already fetched) shouldn't
      // be one stray click away from getting thrown out. Only the X button
      // closes it now, same as Quick Check/Check Load.
      <div style={styles.modalOverlay}>
        <div style={{ ...styles.modalCard, width: 760 }}>
          <div style={styles.modalHeader}>
            <div style={styles.modalTitle}><Phone size={14} /> Log broker call</div>
            <button
              onClick={closeBrokerCallModal}
              style={styles.modalCloseBtn}
            >
              ×
            </button>
          </div>

          {/* Shared by both modes below (manual grid and the free-text/AI
              parse) -- optional. If left blank, the AI mode still falls back
              to whatever broker name it parsed out of the free text itself;
              manual mode has no equivalent extraction, so this is the only
              way to record one there. Same "never required, falls back to a
              placeholder rather than blocking assign" pattern as Check
              Load's identical field -- see that one's own comment. */}
          <div style={{ marginBottom: 10 }}>
            <div style={styles.checkLoadManualLabel}>Customer / broker name (optional)</div>
            <input
              type="text"
              placeholder="Who is this load actually for?"
              value={brokerManual.customer_name}
              onChange={(e) => setBrokerManual((p) => ({ ...p, customer_name: e.target.value }))}
              style={styles.modalInput}
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={styles.checkLoadManualLabel}>Hazardous material? *</div>
            {/* Mandatory -- see checkLoadManual's hazmat select for the same
                reasoning. Shared by both manual and describe modes, same as
                customer name above. */}
            <select
              value={brokerManual.hazmat_status}
              onChange={(e) => setBrokerManual((p) => ({ ...p, hazmat_status: e.target.value }))}
              style={styles.modalInput}
            >
              <option value="">Select one…</option>
              <option value="non_hazardous">Non-Hazardous</option>
              <option value="hazardous">Hazardous</option>
            </select>
          </div>

          {brokerMode === "manual" ? (
            <div>
              <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 8, lineHeight: 1.4 }}>
                City/state is enough to negotiate and get real cost numbers here -- brokers rarely give the exact
                address until you've agreed to the rate. Add it once the rate confirmation comes in for the most
                accurate tolls and truck-legal routing.
              </div>
              <div style={styles.checkLoadManualGrid}>
                <input
                  type="text" placeholder="Origin ZIP (e.g. 75201) *" value={brokerManual.origin_location}
                  onChange={(e) => {
                    setBrokerManual((p) => ({ ...p, origin_location: e.target.value }));
                    setBrokerOriginGeo(null);
                    setBrokerLaneHistory(null);
                  }}
                  onBlur={() => resolveBrokerLaneField("origin")}
                  style={styles.modalInput}
                />
                <input
                  type="text" placeholder="Destination ZIP (e.g. 28202) *" value={brokerManual.destination_location}
                  onChange={(e) => {
                    setBrokerManual((p) => ({ ...p, destination_location: e.target.value }));
                    setBrokerDestGeo(null);
                    setBrokerLaneHistory(null);
                  }}
                  onBlur={() => resolveBrokerLaneField("destination")}
                  style={styles.modalInput}
                />
                <div>
                  <div style={styles.checkLoadManualLabel}>Pickup</div>
                  <USDateTimeInput
                    value={brokerManual.pickup_datetime_local}
                    onChange={(e) => setBrokerManual((p) => ({ ...p, pickup_datetime_local: e.target.value }))}
                    style={styles.modalInput}
                  />
                </div>
                <div>
                  <div style={styles.checkLoadManualLabel}>Delivery due date *</div>
                  <USDateTimeInput
                    value={brokerManual.delivery_deadline_local}
                    onChange={(e) => setBrokerManual((p) => ({ ...p, delivery_deadline_local: e.target.value }))}
                    style={styles.modalInput}
                  />
                </div>
                <input
                  type="text" placeholder="Equipment type (e.g. reefer, dry_van)" value={brokerManual.equipment_type}
                  onChange={(e) => setBrokerManual((p) => ({ ...p, equipment_type: e.target.value }))}
                  style={styles.modalInput}
                />
                <input
                  type="number" placeholder="Offered rate ($) *" value={brokerManual.offered_rate}
                  onChange={(e) => setBrokerManual((p) => ({ ...p, offered_rate: e.target.value }))}
                  style={styles.modalInput}
                />
                <input
                  type="number" placeholder="Load weight (lbs, optional)" value={brokerManual.load_weight_lbs}
                  onChange={(e) => setBrokerManual((p) => ({ ...p, load_weight_lbs: e.target.value }))}
                  title="Real cargo weight -- feeds HERE's truck-legal routing so weight-restricted roads/bridges are actually avoided. Left blank, routing applies no weight restriction."
                  style={styles.modalInput}
                />
              </div>

              <LaneHistoryPanel laneHistory={brokerLaneHistory} />

              {/* Real stops between the first pickup and the last drop -- a
                  broker occasionally quotes "pick up at A, then B, deliver
                  at C" as one job. Optional; a normal single-pickup/single-
                  drop load just leaves this empty. Mirrors Check Load's own
                  "Additional stops" section exactly. */}
              <div style={styles.checkLoadStopsSection}>
                <div style={styles.checkLoadStopsHeader}>
                  <div style={styles.checkLoadStopsTitle}>
                    Additional stops {brokerStops.length > 0 ? `(${brokerStops.length})` : ""}
                  </div>
                </div>
                {brokerStops.map((stop, i) => (
                  <div key={i} style={styles.checkLoadStopRow}>
                    <div style={styles.checkLoadStopBadge}>{i + 2}</div>
                    <div>
                      <div style={styles.checkLoadStopFields}>
                        <input
                          type="text" placeholder="Location (city, state or zip)" value={stop.location}
                          onChange={(e) => updateBrokerStop(i, { location: e.target.value, lat: null, lng: null })}
                          onBlur={() => resolveBrokerStop(i)}
                          style={styles.modalInput}
                        />
                        <select
                          value={stop.stop_type}
                          onChange={(e) => updateBrokerStop(i, { stop_type: e.target.value })}
                          style={styles.modalInput}
                        >
                          <option value="pickup">Pickup</option>
                          <option value="dropoff">Dropoff</option>
                        </select>
                        <USDateTimeInput
                          value={stop.appointment_datetime_local}
                          onChange={(e) => updateBrokerStop(i, { appointment_datetime_local: e.target.value })}
                          style={styles.modalInput}
                        />
                      </div>
                      {stop.resolving && <div style={styles.checkLoadStopResolved}>Resolving…</div>}
                      {!stop.resolving && stop.lat != null && (
                        <div style={styles.checkLoadStopResolved}>✓ {stop.location}</div>
                      )}
                      {!stop.resolving && stop.error && (
                        <div style={styles.checkLoadStopError}>{stop.error}</div>
                      )}
                    </div>
                    <button onClick={() => removeBrokerStop(i)} style={styles.checkLoadStopRemoveBtn} title="Remove stop">
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button onClick={addBrokerStop} style={styles.addStopBtn}>
                  <Plus size={13} /> Add stop
                </button>
              </div>

              {brokerCallResult?.error && <div style={{ ...styles.emptyState, color: "#f2a33d", marginTop: 8 }}>{brokerCallResult.error}</div>}
              <button
                onClick={submitBrokerManual}
                disabled={
                  brokerCallResult?.loading || !brokerManual.origin_location.trim() ||
                  !brokerManual.destination_location.trim() || !brokerManual.pickup_datetime_local ||
                  !brokerManual.delivery_deadline_local || !brokerManual.offered_rate || !brokerStopsReady ||
                  !brokerManual.hazmat_status ||
                  isDeliveryBeforePickup(brokerManual.pickup_datetime_local, brokerManual.delivery_deadline_local)
                }
                style={{
                  ...styles.explainBtn, marginTop: 10,
                  opacity: (brokerCallResult?.loading || !brokerManual.origin_location.trim() ||
                    !brokerManual.destination_location.trim() || !brokerManual.pickup_datetime_local ||
                    !brokerManual.delivery_deadline_local || !brokerManual.offered_rate || !brokerStopsReady ||
                    !brokerManual.hazmat_status ||
                    isDeliveryBeforePickup(brokerManual.pickup_datetime_local, brokerManual.delivery_deadline_local)) ? 0.5 : 1,
                }}
              >
                {brokerCallResult?.loading ? "Finding the best truck…" : "Find best truck & negotiation floor"}
              </button>
              {!brokerStopsReady && (
                <div style={{ ...styles.emptyState, marginTop: 6 }}>Resolve every added stop's location before continuing.</div>
              )}
              {brokerStopsReady && !brokerManual.hazmat_status && (
                <div style={{ ...styles.emptyState, marginTop: 6 }}>Select whether this load is hazardous.</div>
              )}
              {brokerStopsReady && brokerManual.hazmat_status && (!brokerManual.delivery_deadline_local || !brokerManual.offered_rate) && (
                <div style={{ ...styles.emptyState, marginTop: 6 }}>Delivery due date and offered rate are required.</div>
              )}
              {brokerStopsReady && brokerManual.delivery_deadline_local && brokerManual.offered_rate &&
                isDeliveryBeforePickup(brokerManual.pickup_datetime_local, brokerManual.delivery_deadline_local) && (
                <div style={{ ...styles.emptyState, marginTop: 6, color: "#e0553d" }}>Delivery due date can't be before pickup.</div>
              )}
            </div>
          ) : (
            <div>
              <textarea
                value={brokerMessage}
                onChange={(e) => setBrokerMessage(e.target.value)}
                placeholder="Describe the load as the broker gave it, e.g. 'Load from Dallas TX to Charlotte NC, picks up tomorrow noon, deliver within 4 days, reefer, offering $2400'"
                style={styles.modalTextarea}
                rows={3}
              />
              {brokerCallResult?.error && <div style={{ ...styles.emptyState, color: "#f2a33d", marginTop: 8 }}>{brokerCallResult.error}</div>}
              <button
                onClick={submitBrokerCall}
                disabled={!brokerMessage.trim() || !brokerManual.hazmat_status || brokerCallResult?.loading}
                style={{ ...styles.explainBtn, marginTop: 10, opacity: (!brokerMessage.trim() || !brokerManual.hazmat_status || brokerCallResult?.loading) ? 0.5 : 1 }}
              >
                {brokerCallResult?.loading ? "Finding the best truck…" : "Find best truck & negotiation floor"}
              </button>
              {!brokerManual.hazmat_status && brokerMessage.trim() && (
                <div style={{ ...styles.emptyState, marginTop: 6 }}>Select whether this load is hazardous (above).</div>
              )}
            </div>
          )}

          {brokerCallResult?.parsed && (() => {
            const p = brokerCallResult.parsed;
            const candidates = truckOptions?.data?.candidates || [];
            const selectedCandidate = candidates.find((c) => c.truck_id === selectedCandidateTruckId) || null;
            const fmt2 = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
            const fmt0 = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

            return (
              <div style={styles.tripPlanCard}>
                <div style={styles.wizardStepper}>
                  {[1, 2, 3].map((n, idx) => {
                    const label = n === 1 ? "Best matches" : n === 2 ? "Analysis" : "Routes";
                    const clickable = n === 1 || !!selectedCandidate;
                    const isDone = wizardStep > n;
                    const isActive = wizardStep === n;
                    return (
                      <React.Fragment key={n}>
                        <div
                          style={{ ...styles.wizardStepItem, cursor: clickable ? "pointer" : "default" }}
                          onClick={() => {
                            if (!clickable) return;
                            if (n === 3 && (!brokerRoutePreview || brokerRoutePreview.truckId !== selectedCandidateTruckId)) fetchBrokerRoutes();
                            setWizardStep(n);
                          }}
                        >
                          <div
                            style={{
                              ...styles.wizardStepDot,
                              ...(isDone ? { background: "var(--brand)", borderColor: "var(--brand)", color: "#fff" } : {}),
                              ...(isActive ? { borderColor: "var(--brand)", color: "var(--brand)" } : {}),
                            }}
                          >
                            {n}
                          </div>
                          <div style={{ ...styles.wizardStepLabel, ...((isActive || isDone) ? { color: "var(--ink)" } : {}) }}>{label}</div>
                        </div>
                        {idx < 2 && <div style={{ ...styles.wizardConnector, ...(wizardStep > n ? { background: "var(--brand)" } : {}) }} />}
                      </React.Fragment>
                    );
                  })}
                </div>

                {wizardStep === 1 && (() => {
                  const p = brokerCallResult?.parsed;
                  const filteredTrucks = manualTruckQuery.trim().length >= 2
                    ? trucks
                        .filter((t) => (t.unit_number || "").toLowerCase().includes(manualTruckQuery.trim().toLowerCase()))
                        .slice(0, 8)
                    : [];
                  const manualPicker = p && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>
                        Not seeing the truck you want? Check a specific one of your own:
                      </div>
                      <input
                        type="text"
                        value={manualTruckQuery}
                        onChange={(e) => setManualTruckQuery(e.target.value)}
                        placeholder="Search by truck # (e.g. 24298)"
                        style={styles.modalInput}
                        disabled={manualTruckChecking}
                      />
                      {filteredTrucks.length > 0 && (
                        <div style={{ border: "1px solid var(--line)", borderRadius: 7, marginTop: 4, overflow: "hidden" }}>
                          {filteredTrucks.map((t) => (
                            <div
                              key={t.id}
                              onClick={() => !manualTruckChecking && checkSpecificTruck(t.id)}
                              style={{
                                padding: "7px 10px", fontSize: 12, cursor: manualTruckChecking ? "default" : "pointer",
                                borderBottom: "1px solid var(--line)", color: "var(--ink-2)",
                                opacity: manualTruckChecking ? 0.5 : 1,
                              }}
                            >
                              Truck {t.unit_number}
                            </div>
                          ))}
                        </div>
                      )}
                      {manualTruckChecking && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>Checking that truck…</div>}
                    </div>
                  );

                  if (truckOptions?.loading) return <div style={styles.emptyState}>Ranking the fleet…</div>;
                  if (truckOptions?.error) return <div style={{ ...styles.emptyState, color: "#f2a33d" }}>{truckOptions.error}</div>;
                  if (!candidates.length) return <div>{manualPicker}<div style={styles.emptyState}>No candidate trucks came back.</div></div>;
                  const hasOffer = offeredRate != null;
                  const ranked = [...candidates].sort((a, b) => {
                    if (!hasOffer) return a.total_cost - b.total_cost;
                    const negA = computeNegotiation(a.total_cost, a.distance_miles, a.driving_hours, offeredRate);
                    const negB = computeNegotiation(b.total_cost, b.distance_miles, b.driving_hours, offeredRate);
                    return (negB.profit ?? 0) - (negA.profit ?? 0);
                  });
                  return (
                    <div>
                      {truckOptions?.data?.note && (
                        <div style={{ ...styles.emptyState, color: "#f2a33d", marginBottom: 8 }}>{truckOptions.data.note}</div>
                      )}
                      {manualPicker}
                      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)", marginBottom: 2 }}>
                        Here are your {ranked.length} best option{ranked.length === 1 ? "" : "s"}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 8 }}>
                        {hasOffer ? `Ranked by profit at the broker's ${fmt0(offeredRate)} offer.` : "Ranked by cost -- no offer given yet."} Tap one to see the full breakdown.
                      </div>
                      {(() => {
                        const topNeg = computeNegotiation(ranked[0].total_cost, ranked[0].distance_miles, ranked[0].driving_hours, offeredRate);
                        return ranked.map((c, i) => {
                          const neg = computeNegotiation(c.total_cost, c.distance_miles, c.driving_hours, offeredRate);
                          const metric = hasOffer ? neg.profit : c.total_cost;
                          const isLoss = hasOffer && metric != null && metric < 0;
                          const arrivalTime = c.search_mode === "loaded" ? formatCurrentTripArrival(c.current_trip_remaining_hours) : null;
                          return (
                            <div
                              key={c.truck_id}
                              onClick={() => {
                                // Fleet-ranking pick: capture the truck's real tank capacity (if
                                // unconfirmed) BEFORE advancing, so Show-routes/fuel planning for
                                // this candidate uses the true tank, not the 200 default.
                                const capTruck = trucks.find((x) => x.id === c.truck_id)
                                  || { id: c.truck_id, unit_number: c.unit_number, tank_capacity_gallons: null };
                                ensureTankCapacity(capTruck, () => { setSelectedCandidateTruckId(c.truck_id); setWizardStep(2); });
                              }}
                              style={{ ...styles.matchRow, flexWrap: "wrap", background: i === 0 ? "rgba(13,125,111,0.05)" : "transparent" }}
                            >
                              <div style={{ ...styles.matchRank, ...(i === 0 ? { background: "rgba(13,125,111,0.16)", color: "var(--brand)" } : {}) }}>{i + 1}</div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={styles.routeOptionTitle}>
                                  Truck {c.unit_number || "—"} · {c.driver_name || "No driver on file"}
                                  {c.is_returning_driver && (
                                    <span style={{ ...styles.routeOptionBadge, background: "rgba(242,163,61,0.16)", color: "#f2a33d" }}>
                                      Not in truck -- confirm
                                    </span>
                                  )}
                                  {c.late_for_pickup_hours > 0 && (
                                    <span style={{ ...styles.routeOptionBadge, background: "rgba(242,163,61,0.16)", color: "#f2a33d" }}>
                                      {c.late_for_pickup_hours}h late for pickup
                                    </span>
                                  )}
                                  {c.pickup_timing_uncertain && (
                                    <span
                                      style={{ ...styles.routeOptionBadge, background: "rgba(242,163,61,0.16)", color: "#f2a33d" }}
                                      title={c.pickup_timing_note}
                                    >
                                      Timing unconfirmed -- call driver
                                    </span>
                                  )}
                                  {i === 0 && <span style={styles.routeOptionBadge}>Top pick</span>}
                                  {arrivalTime && (
                                    <span style={{ ...styles.routeOptionBadge, background: "rgba(37,99,235,0.14)", color: "#7db8ff", borderColor: "rgba(37,99,235,0.4)" }}>
                                      Arrives {arrivalTime}
                                    </span>
                                  )}
                                  {manuallyCheckedTruckIds.has(c.truck_id) && (
                                    <span style={{ ...styles.routeOptionBadge, background: "rgba(37,99,235,0.12)", color: "#2563eb" }}>
                                      Manually checked
                                    </span>
                                  )}
                                  {c.dispatcher_id && c.dispatcher_id !== currentUserId && (
                                    <span style={{ ...styles.routeOptionBadge, background: "rgba(148,163,184,0.18)", color: "var(--muted)" }}>
                                      Handled by {c.dispatcher_name || "another dispatcher"}
                                    </span>
                                  )}
                                  {c.on_time_off && (
                                    <span style={{ ...styles.routeOptionBadge, background: "rgba(220,38,38,0.12)", color: "#dc2626" }}>
                                      Home -- ask driver
                                    </span>
                                  )}
                                  {!c.on_time_off && c.time_off_conflict_warning && (
                                    <span style={{ ...styles.routeOptionBadge, background: "rgba(220,38,38,0.12)", color: "#dc2626" }}>
                                      Home time starts mid-trip
                                    </span>
                                  )}
                                </div>
                                <div style={{ ...styles.routeOptionMeta, ...(arrivalTime ? { color: "#7db8ff" } : {}) }}>
                                  {c.distance_miles}mi · {c.driving_hours}h driving
                                  {c.needs_hos_reset ? ` · HOS reset (${c.hos_reset_hours}h)` : ""}
                                  {!c.meets_deadline ? " · won't make the deadline" : ""}
                                  {arrivalTime ? " · unload time not included" : ""}
                                </div>
                              </div>
                              <div style={{ textAlign: "right", fontWeight: 700, fontSize: 13, color: isLoss ? "#f2a33d" : "var(--ink)" }}>
                                {metric != null ? fmt0(metric) : "—"}
                                <div style={{ fontSize: 10, fontWeight: 400, color: "var(--muted)" }}>{hasOffer ? (isLoss ? "loss" : "profit") : "cost"}</div>
                              </div>
                              <div style={{ width: "100%", fontSize: 10.5, color: "var(--muted)", marginTop: 4, paddingLeft: 30, fontStyle: "italic" }}>
                                {matchWhyLine(c, neg, i === 0, topNeg)}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  );
                })()}

                {wizardStep === 2 && selectedCandidate && (() => {
                  const c = selectedCandidate;
                  const neg = computeNegotiation(c.total_cost, c.distance_miles, c.driving_hours, offeredRate);
                  const isLoss = neg.profit != null && neg.profit < 0;
                  const profitColor = isLoss ? "#f2a33d" : "var(--brand)";
                  const detailArrivalTime = c.search_mode === "loaded" ? formatCurrentTripArrival(c.current_trip_remaining_hours) : null;
                  return (
                    <div>
                      <button style={styles.wizardBackLink} onClick={() => setWizardStep(1)}>← Back to matches</button>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink)", marginBottom: 2 }}>
                        Truck {c.unit_number} · {c.driver_name || "No driver on file"}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>
                        {c.search_mode === "idle" ? "Idle" : (
                          <>
                            Finishing a nearby load
                            {detailArrivalTime && <> — <b style={{ color: "#7db8ff", fontWeight: 600 }}>arrives ~{detailArrivalTime}</b></>}
                          </>
                        )} · {c.deadhead_miles}mi deadhead to pickup
                        {detailArrivalTime && (
                          <>
                            <br />
                            <span style={{ color: "var(--warn)" }}>
                              Unload time not included — arrival and pickup-wait estimates below are best case; confirm with the driver before you commit this truck.
                            </span>
                          </>
                        )}
                      </div>

                      {c.dispatcher_id && c.dispatcher_id !== currentUserId && (
                        <div style={{
                          fontSize: 11.5, color: "var(--muted)", background: "rgba(148,163,184,0.1)",
                          border: "1px solid var(--line)", borderRadius: 7, padding: "5px 9px", marginBottom: 6,
                        }}>
                          This truck is handled by <b style={{ color: "var(--ink-2)" }}>{c.dispatcher_name || "another dispatcher"}</b> -- check with them before committing it to this load.
                        </div>
                      )}

                      {c.is_returning_driver && (
                        <div style={{
                          fontSize: 11.5, color: "#f2a33d", background: "rgba(242,163,61,0.1)",
                          border: "1px solid rgba(242,163,61,0.3)", borderRadius: 7, padding: "5px 9px", marginBottom: 6,
                        }}>
                          ⚠ <b>Confirm before committing</b> -- Motive shows no one currently logged into this truck. These
                          numbers use <b>{c.driver_name || "this driver"}'s</b> last known HOS reading (they're the last
                          driver on record for it), and the profit above only holds if they're actually resuming on this
                          schedule -- call to confirm before you commit the load.
                        </div>
                      )}

                      {c.on_time_off && (
                        <div style={{
                          fontSize: 11.5, color: "#dc2626", background: "rgba(220,38,38,0.1)",
                          border: "1px solid rgba(220,38,38,0.3)", borderRadius: 7, padding: "5px 9px", marginBottom: 6,
                        }}>
                          ⚠ <b>{c.driver_name || "This driver"} is currently on time off</b>
                          {c.time_off_until_local ? ` until ${c.time_off_until_local}` : ""} -- numbers below assume
                          they're back and ready to drive by then.
                        </div>
                      )}

                      {!c.on_time_off && c.time_off_conflict_warning && (
                        <div style={{
                          fontSize: 11.5, color: "#dc2626", background: "rgba(220,38,38,0.1)",
                          border: "1px solid rgba(220,38,38,0.3)", borderRadius: 7, padding: "5px 9px", marginBottom: 6,
                        }}>
                          ⚠ {c.time_off_conflict_warning}
                        </div>
                      )}

                      {c.hos_checked === false && (
                        <div style={{
                          fontSize: 11.5, color: "#f2a33d", background: "rgba(242,163,61,0.1)",
                          border: "1px solid rgba(242,163,61,0.3)", borderRadius: 7, padding: "5px 9px", marginBottom: 6,
                        }}>
                          ⚠ <b>No driver assigned to this truck</b> -- the route's real HOS/rest-break requirements
                          couldn't be checked. Any "0 HOS" stops you see are unverified, not confirmed safe -- assign
                          a driver before treating this trip's timing as reliable.
                        </div>
                      )}

                      {c.driver_available_at && (
                        <div style={{
                          fontSize: 11.5, color: "var(--ink-2)", background: "var(--bg)", border: "1px solid var(--line)",
                          borderRadius: 7, padding: "5px 9px", marginBottom: 6,
                        }}>
                          {c.needs_hos_reset ? (
                            <>
                              <b style={{ color: "var(--ink)" }}>{c.driver_name || "Driver"}</b> can be back on the road ~
                              <b style={{ color: "var(--ink)" }}>{formatFacilityLocal(c.driver_available_at, p.origin_timezone)}</b>
                              {" "}(drives as far as legally possible first, then takes the {c.hos_reset_hours}h break) -- reaches pickup ~
                              <b style={{ color: "var(--ink)" }}>{formatFacilityLocal(c.pickup_eta, p.origin_timezone)}</b>
                              .
                            </>
                          ) : (
                            <>
                              <b style={{ color: "var(--ink)" }}>{c.driver_name || "Driver"}</b> is currently{" "}
                              <b style={{ color: "var(--ink)" }}>in transit</b> on another load -- free once it delivers ~
                              <b style={{ color: "var(--ink)" }}>{formatFacilityLocal(c.driver_available_at, p.origin_timezone)}</b>
                              , no HOS break needed before this one -- reaches pickup ~
                              <b style={{ color: "var(--ink)" }}>{formatFacilityLocal(c.pickup_eta, p.origin_timezone)}</b>
                              .
                            </>
                          )}
                        </div>
                      )}

                      <div style={styles.statGrid2}>
                        <div style={styles.statCell2}>
                          <div style={styles.statCell2Label}>Total cost to run</div>
                          <div style={styles.statCell2Value}>{fmt2(c.total_cost)}</div>
                        </div>
                        <div style={styles.statCell2}>
                          <div style={styles.statCell2Label}>Minimum acceptable rate</div>
                          <div style={styles.statCell2Value}>{fmt2(neg.floor)}</div>
                        </div>
                        <div style={{ ...styles.statCell2, background: "rgba(13,125,111,0.07)" }}>
                          <div style={styles.statCell2Label}>Broker offered <span style={{ opacity: 0.7 }}>(edit me)</span></div>
                          <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
                            <span style={styles.statCell2Value}>$</span>
                            <input
                              type="number"
                              value={offeredRate ?? ""}
                              onChange={(e) => setOfferedRate(e.target.value === "" ? null : Number(e.target.value))}
                              style={styles.offerInput}
                            />
                          </div>
                        </div>
                        <div style={styles.statCell2}>
                          <div style={styles.statCell2Label}>Load weight <span style={{ opacity: 0.7 }}>(lbs, optional)</span></div>
                          <input
                            type="number"
                            value={loadWeightLbs ?? ""}
                            onChange={(e) => setLoadWeightLbs(e.target.value === "" ? null : Number(e.target.value))}
                            title="Feeds HERE's truck-legal routing so weight-restricted roads/bridges are actually avoided. Left blank, routing applies no weight restriction."
                            style={styles.offerInput}
                          />
                        </div>
                        <div style={styles.statCell2}>
                          <div style={styles.statCell2Label}>Suggested counter</div>
                          <div style={{ ...styles.statCell2Value, color: "var(--brand)" }}>{fmt2(neg.suggestedCounter)}</div>
                        </div>
                        <div style={styles.statCell2}>
                          <div style={styles.statCell2Label}>{isLoss ? "Loss if accepted" : "Profit if accepted"}</div>
                          <div style={{ ...styles.statCell2Value, color: profitColor }}>{fmt2(neg.profit)}</div>
                        </div>
                        <div style={styles.statCell2}>
                          <div style={styles.statCell2Label}>Profit / mile</div>
                          <div style={{ ...styles.statCell2Value, color: profitColor }}>{fmt2(neg.profitPerMile)}</div>
                        </div>
                        <div style={styles.statCell2}>
                          <div style={styles.statCell2Label}>Profit / hour</div>
                          <div style={{ ...styles.statCell2Value, color: profitColor }}>{fmt2(neg.profitPerHour)}</div>
                        </div>
                        <div style={{ ...styles.statCell2, ...(c.late_for_pickup_hours > 0 ? { background: "rgba(242,163,61,0.1)" } : {}) }}>
                          <div style={styles.statCell2Label}>{c.late_for_pickup_hours > 0 ? "Late for pickup" : c.search_mode === "loaded" ? "Wait for pickup (min.)" : "Wait for pickup"}</div>
                          <div style={{ ...styles.statCell2Value, color: (c.wait_hours > 0 || c.late_for_pickup_hours > 0) ? "#f2a33d" : "var(--ink)" }}>
                            {c.late_for_pickup_hours > 0 ? `${c.late_for_pickup_hours}h late` : `${c.wait_hours}h`}
                          </div>
                          {c.late_for_pickup_hours === 0 && c.wait_free_hours > 0 && (
                            <div style={{ fontSize: 9.5, color: "var(--muted)", marginTop: 2 }}>
                              {c.wait_free_hours}h free rest + {c.wait_billable_hours}h billable
                            </div>
                          )}
                        </div>
                      </div>

                      {c.late_for_pickup_hours > 0 && (
                        <div style={{
                          fontSize: 11.5, color: "#f2a33d", background: "rgba(242,163,61,0.1)",
                          border: "1px solid rgba(242,163,61,0.3)", borderRadius: 7, padding: "5px 9px", marginBottom: 6,
                        }}>
                          ⚠ This truck can't make the requested pickup time -- it would arrive <b>{c.late_for_pickup_hours}h after</b> the appointment. The numbers above still assume this truck gets the load anyway; confirm with the broker whether that pickup time is flexible before committing.
                        </div>
                      )}

                      <div style={{ fontSize: 10.5, color: "var(--muted)", marginBottom: 6 }}>
                        What's in that total cost: <b style={{ color: "var(--ink-2)" }}>{fmt0(c.fuel_cost)} fuel</b>
                        {" · "}<b style={{ color: "var(--ink-2)" }}>{fmt0(c.driver_cost)} driver pay</b>
                        {" · "}<b style={{ color: "var(--ink-2)" }}>{fmt0(c.toll_cost)} tolls</b>
                        {c.insurance_cost > 0 && (
                          <>{" · "}<b style={{ color: "var(--ink-2)" }}>{fmt0(c.insurance_cost)} insurance</b></>
                        )}
                        {c.office_payroll_cost > 0 && (
                          <>{" · "}<b style={{ color: "var(--ink-2)" }}>{fmt0(c.office_payroll_cost)} office/payroll</b></>
                        )}
                        {c.maintenance_cost > 0 && (
                          <>{" · "}<b style={{ color: "var(--ink-2)" }}>{fmt0(c.maintenance_cost)} maintenance</b></>
                        )}
                        {c.wait_cost_cash > 0 && (
                          <>{" · "}<b style={{ color: "var(--ink-2)" }}>{fmt0(c.wait_cost_cash)} wait time (driver pay)</b></>
                        )}
                        {c.wait_cost_opportunity > 0 && (
                          <>{" · "}<b style={{ color: "var(--ink-2)" }}>{fmt0(c.wait_cost_opportunity)} wait time (lost revenue)</b></>
                        )}
                        {c.wait_cost > 0 && c.wait_cost_cash == null && (
                          <><>{" · "}<b style={{ color: "var(--ink-2)" }}>{fmt0(c.wait_cost)} wait time</b></></>
                        )}
                        {(c.wait_cost_cash > 0 || c.wait_cost_opportunity > 0) && c.wait_free_hours > 0 && ` (${c.wait_billable_hours}h billable -- ${c.wait_free_hours}h rest not charged)`}
                      </div>

                      <div style={{ fontSize: 10.5, color: "var(--muted)", marginBottom: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        Broker came back with a new number? Type it above.
                        <button style={styles.useCounterBtn} onClick={() => setOfferedRate(Math.round(neg.suggestedCounter))}>
                          Use suggested counter ({fmt0(neg.suggestedCounter)})
                        </button>
                      </div>

                      <details style={{ marginBottom: 6 }}>
                        <summary style={{ fontSize: 11, color: "var(--muted)", cursor: "pointer" }}>Trip summary</summary>
                        <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "var(--ink-2)", background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 7, padding: "6px 9px", marginTop: 5 }}>
                          {candidateNarrative(c)}
                        </div>
                      </details>

                      <div style={{ display: "flex", gap: 8 }}>
                        <button style={{ ...styles.refreshRoutesBtn, marginLeft: 0 }} onClick={() => setWizardStep(1)}>Try another truck</button>
                        <button
                          style={{ ...styles.explainBtn, marginBottom: 0, flex: 1 }}
                          onClick={() => {
                            if (!brokerRoutePreview || brokerRoutePreview.truckId !== selectedCandidateTruckId) fetchBrokerRoutes();
                            setWizardStep(3);
                            // Jump straight to the big map -- that's where the
                            // actual route-picking decision happens, so making
                            // the dispatcher land on the small in-modal list
                            // first and click Maximize themselves was just an
                            // extra step between here and the real view.
                            setMapModalOpen(true);
                          }}
                        >
                          See route options
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {wizardStep === 3 && selectedCandidate && (
                  <div>
                    <button style={styles.wizardBackLink} onClick={() => setWizardStep(2)}>← Back to analysis</button>
                    {(() => {
                      if (brokerRoutePreview?.loading) return <div style={styles.emptyState}>Loading real route options…</div>;
                      if (brokerRoutePreview?.error) return <div style={{ ...styles.emptyState, color: "#f2a33d" }}>{brokerRoutePreview.error}</div>;
                      const routes = brokerRoutePreview?.data?.best_match_route_options;
                      if (!routes?.length) return <div style={styles.emptyState}>No real route alternatives came back for this truck.</div>;

                      // Before a route is picked, the map shows every real
                      // alternative (minus whatever the dispatcher manually
                      // hid via the toggle chips) so there's something real
                      // to compare. The instant one is selected, the map
                      // narrows to ONLY that route -- explicit ask: once
                      // you're choosing, the other lines on the map are
                      // just clutter, not still-live options.
                      const visibleRoutes = brokerChosenRouteType
                        ? routes.filter((r) => r.route_type === brokerChosenRouteType)
                        : routes.filter((r) => !brokerRouteVisibility.isHidden(r.route_type));
                      const pickupVisible = !brokerRouteVisibility.isHidden(PICKUP_ROUTE_KEY);
                      const boundsPoints = [];
                      if (pickupVisible && routes[0]?.geometry?.deadhead?.length) {
                        const dh = routes[0].geometry.deadhead;
                        boundsPoints.push(dh[0], dh[dh.length - 1]);
                      }
                      visibleRoutes.forEach((r) => {
                        const ld = r.geometry?.loaded;
                        if (ld?.length) boundsPoints.push(ld[0], ld[Math.floor(ld.length / 2)], ld[ld.length - 1]);
                      });

                      const truckLabel = selectedCandidate.driver_name ? selectedCandidate.driver_name.split(" ")[0] : selectedCandidate.unit_number;
                      const truckColor = selectedCandidate.search_mode === "loaded" ? STATUS.loaded.color : STATUS.free.color;

                      // Shared between the small in-panel map and the big
                      // popup (real screen real estate, not just a taller
                      // box in the same cramped modal) -- same markers/
                      // routes/tolls/stops either way, just a different
                      // container size. `sizeKey` re-triggers invalidateSize
                      // + a bounds refit each time the map's actual pixel
                      // box changes (small <-> popup), since Leaflet doesn't
                      // notice a CSS-driven container resize on its own.
                      const renderRouteMap = (heightPx, sizeKey) => (
                        <MapContainer center={[39.5, -98.35]} zoom={4} style={{ ...styles.map, minHeight: heightPx }} scrollWheelZoom={sizeKey === "popup"} key={theme} className={theme === "light" ? "map-tiles-dim" : undefined}>
                          {tileUrl && <TileLayer attribution={TILE_LAYER_ATTRIBUTION} url={tileUrl} />}
                          <MapInvalidateSize trigger={sizeKey} />
                          {boundsPoints.length >= 2 && <MapFitToPoints points={boundsPoints} fitKey={sizeKey} />}
                          {routes[0]?.geometry?.deadhead?.length > 0 && (
                            <Marker position={routes[0].geometry.deadhead[0]} icon={truckIcon(truckLabel, truckColor, false)}>
                              <Tooltip direction="top" offset={[0, -60]}>Truck {selectedCandidate.unit_number}'s current position</Tooltip>
                            </Marker>
                          )}
                          {/* Every candidate route for this one truck shares the
                              same deadhead leg (same truck, same pickup) --
                              drawn once, not once per visible route, so it
                              doesn't stack N identical dashed lines. */}
                          {pickupVisible && routes[0]?.geometry?.deadhead?.length > 0 && (
                            <Polyline positions={routes[0].geometry.deadhead} pathOptions={{ color: "var(--muted)", weight: 4, dashArray: "6 6" }} />
                          )}
                          {visibleRoutes.map((r, i) => {
                            const isChosen = r.route_type === brokerChosenRouteType;
                            const color = brokerRouteVisibility.colorFor(r.route_type);
                            const tripStops = brokerRouteTripPlans[r.route_type]?.stops || [];
                            // See Check Load's identical fix -- draws the
                            // real per-hop road path through a stop instead
                            // of match-truck-for-load's origin-to-destination-
                            // only line when plan-full-trip has real
                            // per-hop geometry to offer.
                            const legGeometries = brokerRouteTripPlans[r.route_type]?.leg_geometries || [];
                            // See Check Load's identical fix -- r.toll_stops
                            // is match-truck-for-load's straight-line pricing,
                            // wrong for a load that detours through a stop.
                            // Prefer plan-full-trip's own real per-hop tolls
                            // for the loaded portion once real per-hop
                            // geometry exists; the deadhead leg's tolls are
                            // unaffected by intermediate stops either way.
                            // r.toll_stops is already the authoritative
                            // merged real per-route tolls (see
                            // fetchBrokerRoutes' multi-stop-route-options
                            // merge) whenever this load has stops --
                            // tripPlanTolls only has anything in it on the
                            // rarer path where plan-full-trip had to
                            // self-fetch its own fallback geometry (no
                            // multi-stop-route-options data was available),
                            // so an EMPTY tripPlanTolls (not undefined --
                            // plan-full-trip always returns the key) must
                            // fall through to r.toll_stops, not silently
                            // drop its already-correct loaded-leg tolls.
                            const tripPlanTolls = brokerRouteTripPlans[r.route_type]?.toll_stops;
                            const tollStops = legGeometries.length > 0 && tripPlanTolls?.length
                              ? [...(r.toll_stops || []).filter((t) => t.leg === "deadhead"), ...tripPlanTolls]
                              : (r.toll_stops || []);
                            return (
                              <React.Fragment key={r.route_type}>
                                {legGeometries.length > 0 ? (
                                  legGeometries.map((leg, li) => leg?.length > 0 && (
                                    <Polyline
                                      key={`leg-${r.route_type}-${li}`}
                                      positions={leg}
                                      pathOptions={{ color, weight: isChosen ? 7 : 5, opacity: isChosen ? 1 : 0.85 }}
                                    />
                                  ))
                                ) : r.geometry?.loaded?.length > 0 && (
                                  <Polyline positions={r.geometry.loaded} pathOptions={{ color, weight: isChosen ? 7 : 5, opacity: isChosen ? 1 : 0.85 }} />
                                )}
                                {tollStops.map((t, ti) => (
                                  <Marker key={`toll-${r.route_type}-${ti}`} position={[t.lat, t.lng]} icon={tollIcon(t.leg === "deadhead" ? "var(--muted)" : color)}>
                                    <Tooltip direction="top" offset={[0, -10]}>
                                      {t.name}{t.price_usd != null ? ` · $${Number(t.price_usd).toFixed(2)}` : ""}
                                    </Tooltip>
                                  </Marker>
                                ))}
                                {(() => { let stopNum = 1; return tripStops.map((s, si) => {
                                  if (s.lat == null || s.lng == null || s.type === "pickup" || s.type === "delivery" || s.type === "wait") return null;
                                  if (s.type === "stop_pickup" || s.type === "stop_dropoff") {
                                    stopNum += 1;
                                    return (
                                      <Marker key={`stop-${r.route_type}-${si}`} position={[Number(s.lat), Number(s.lng)]} icon={pinIcon(color, String(stopNum))}>
                                        <Tooltip direction="top" offset={[0, -14]}>
                                          <div>
                                            <div style={styles.tooltipTitle}>Stop {stopNum} · {s.type === "stop_pickup" ? "Pickup" : "Dropoff"}</div>
                                            <div>{s.location}</div>
                                          </div>
                                        </Tooltip>
                                      </Marker>
                                    );
                                  }
                                  if (s.type === "rest_zone") {
                                    return (
                                      <React.Fragment key={`stop-${r.route_type}-${si}`}>
                                        <Circle
                                          center={[Number(s.lat), Number(s.lng)]}
                                          radius={(s.radius_miles || 15) * 1609.34}
                                          pathOptions={{ color, fillColor: color, fillOpacity: 0.12, weight: 1.5, dashArray: "4 4" }}
                                        />
                                        <Marker position={[Number(s.lat), Number(s.lng)]} icon={restZoneIcon(color)}>
                                          <Tooltip direction="top" offset={[0, -10]}>
                                            <div>
                                              <div style={styles.tooltipTitle}>HOS rest break {s.sequence} -- ~{s.radius_miles} mi area</div>
                                              <RestZoneTooltipRows s={s} />
                                            </div>
                                          </Tooltip>
                                        </Marker>
                                      </React.Fragment>
                                    );
                                  }
                                  const isCombined = s.type === "fuel_and_rest";
                                  // Always this route's own color, even on the deadhead
                    // (pickup) leg -- unlike a toll (purely positional, so
                    // genuinely identical for every route sharing that
                    // stretch of road), WHICH station gets chosen here is
                    // decided by the whole route ahead (the proactive
                    // top-off logic checks for a cheaper station further
                    // down the entire trip, including the route-specific
                    // loaded leg) -- so two routes can legitimately pick a
                    // different station, or a different fill amount, for
                    // the "same" physical stretch. Graying it out implied
                    // it was shared/identical the way a deadhead toll is,
                    // which isn't actually true here.
                    const legColor = color;
                                  return (
                                    <React.Fragment key={`stop-${r.route_type}-${si}`}>
                                      {isCombined && (
                                        <Circle
                                          center={[Number(s.lat), Number(s.lng)]}
                                          radius={COMBINED_STOP_RING_MILES * 1609.34}
                                          pathOptions={{ color: legColor, fillColor: legColor, fillOpacity: 0.12, weight: 1.5, dashArray: "4 4" }}
                                        />
                                      )}
                                      <Marker
                                        position={[Number(s.lat), Number(s.lng)]}
                                        icon={stationIcon(legColor, {
                                          size: 20, ringColor: legColor,
                                          label: isCombined ? `F${s.fuel_sequence}+R` : `F${s.sequence}`,
                                          labelColor: legColor,
                                        })}
                                      >
                                        <Tooltip direction="top" offset={[0, -12]}>
                                          <div>
                                            <div style={styles.tooltipTitle}>
                                              {isCombined ? `Fuel + HOS rest break ${s.rest_sequence}` : `Fuel stop ${s.sequence}`}: {s.name || "—"}
                                            </div>
                                            <FuelStopTooltipRows s={s} isCombined={isCombined} />
                                          </div>
                                        </Tooltip>
                                        {s.truck_stop_id && (
                                          <Popup>
                                            <div style={{ fontSize: 12, minWidth: 150 }}>
                                              <div style={{ fontWeight: 700, marginBottom: 6 }}>{s.name || "This station"}</div>
                                              <button
                                                onClick={() => excludeBrokerFuelStation(s.truck_stop_id, s.name)}
                                                style={styles.excludeStationBtn}
                                              >
                                                ✕ Don't use this station
                                              </button>
                                            </div>
                                          </Popup>
                                        )}
                                      </Marker>
                                    </React.Fragment>
                                  );
                                }); })()}
                              </React.Fragment>
                            );
                          })}
                          {brokerRoutePreview.data.origin && (
                            <Marker position={[brokerRoutePreview.data.origin.lat, brokerRoutePreview.data.origin.lng]} icon={pinIcon("#5b9bd5", "S")}>
                              <Tooltip>Pickup: {brokerRoutePreview.data.origin.display_name}</Tooltip>
                            </Marker>
                          )}
                          {brokerRoutePreview.data.destination && (
                            <Marker position={[brokerRoutePreview.data.destination.lat, brokerRoutePreview.data.destination.lng]} icon={pinIcon("#a78bfa", "E")}>
                              <Tooltip>Delivery: {brokerRoutePreview.data.destination.display_name}</Tooltip>
                            </Marker>
                          )}
                        </MapContainer>
                      );

                      return (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ ...styles.mapCol, height: 340, minHeight: 340, marginBottom: 8 }}>
                            <button
                              onClick={() => setMapModalOpen(true)}
                              title="Maximize map"
                              style={{
                                position: "absolute", top: 8, right: 8, zIndex: 1000,
                                width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
                                background: "var(--card)", border: "1px solid var(--line)", borderRadius: 7,
                                cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,.25)", color: "var(--ink)",
                              }}
                            >
                              <Maximize2 size={14} />
                            </button>
                            {renderRouteMap(340, "small")}
                          </div>
                          <ExcludedStationsChip excluded={brokerExcludedStations} onReset={resetBrokerExcludedStations} />

                          {mapModalOpen && createPortal(
                            <div
                              className="op-center-dark"
                              style={{
                                ...themeVars,
                                position: "fixed", inset: 0, zIndex: 2000, background: "rgba(2,6,23,0.72)",
                                display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
                              }}
                              onClick={() => setMapModalOpen(false)}
                            >
                              <div
                                style={{
                                  width: "92vw", height: "88vh", background: "var(--card)",
                                  border: "1px solid var(--line)", borderRadius: 14, padding: 14,
                                  display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,.5)",
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>
                                    Route map -- Truck {selectedCandidate.unit_number} · {selectedCandidate.driver_name || "No driver on file"}
                                  </div>
                                  <button
                                    onClick={() => setMapModalOpen(false)}
                                    style={{
                                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                                      fontSize: 12, fontWeight: 600, color: "var(--ink)", background: "var(--line)",
                                      border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer",
                                    }}
                                  >
                                    <Minimize2 size={13} /> Close
                                  </button>
                                </div>
                                {(p.pickup_datetime_local || p.delivery_deadline_local) && (
                                  <div style={{
                                    fontSize: 11.5, color: "var(--ink-2)", background: "var(--bg)", border: "1px solid var(--line)",
                                    borderRadius: 8, padding: "5px 9px", marginBottom: 6,
                                  }}>
                                    Per the broker call: <b style={{ color: "var(--ink)" }}>pickup</b> at {p.origin_location || "origin"}
                                    {p.pickup_datetime_local ? <> <b style={{ color: "var(--ink)" }}>{p.pickup_datetime_local}</b></> : ""} ·{" "}
                                    <b style={{ color: "var(--ink)" }}>delivery</b> at {p.destination_location || "destination"}
                                    {p.delivery_deadline_local ? <> by <b style={{ color: "var(--ink)" }}>{p.delivery_deadline_local}</b></> : ""}
                                  </div>
                                )}
                                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 10 }}>
                                  <RouteControls visibility={brokerRouteVisibility} />
                                  {routes.map((r, i) => {
                                    const isHiddenHere = brokerRouteVisibility.isHidden(r.route_type);
                                    const routeChipColor = brokerRouteVisibility.colorFor(r.route_type);
                                    return (
                                      <button
                                        key={r.route_type}
                                        onClick={() => brokerRouteVisibility.toggle(r.route_type)}
                                        style={{
                                          display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600,
                                          padding: "5px 10px", borderRadius: 999, cursor: "pointer",
                                          border: `1px solid ${isHiddenHere ? "var(--line)" : routeChipColor}`,
                                          background: isHiddenHere ? "var(--bg)" : `${routeChipColor}1a`,
                                          color: isHiddenHere ? "var(--muted)" : "var(--ink)",
                                          opacity: isHiddenHere ? 0.6 : 1,
                                        }}
                                      >
                                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: routeChipColor, flexShrink: 0 }} />
                                        {r.is_toll_avoidant ? "Toll-free route" : `Route ${i + 1}`}
                                      </button>
                                    );
                                  })}
                                </div>
                                <div style={{ flex: 1, position: "relative", borderRadius: 10, overflow: "hidden", border: "1px solid var(--line)" }}>
                                  {renderRouteMap("100%", "popup")}
                                  <FuelStopReviewBanner
                                    fuelOptimizationUnavailable={(brokerRouteTripPlans[brokerChosenRouteType] || Object.values(brokerRouteTripPlans)[0])?.fuel_optimization_unavailable}
                                    fuelOptimizationMessage={(brokerRouteTripPlans[brokerChosenRouteType] || Object.values(brokerRouteTripPlans)[0])?.fuel_optimization_message}
                                  />
                                  <ExcludedStationsChip excluded={brokerExcludedStations} onReset={resetBrokerExcludedStations} corner />
                                  <RouteLegendPanel
                                    routes={routes}
                                    hiddenRouteTypes={brokerRouteVisibility.hidden}
                                    colorFor={brokerRouteVisibility.colorFor}
                                    chosenRouteType={brokerChosenRouteType}
                                    tripPlans={brokerRouteTripPlans}
                                    offeredRate={offeredRate}
                                    originTz={p.origin_timezone}
                                    destTz={p.destination_timezone}
                                    onSelectRoute={setBrokerChosenRouteType}
                                    logAction={{
                                      label: `Log to Truck ${selectedCandidate.unit_number}`,
                                      onClick: () => {
                                        if (brokerFuelLoading) return;  // belt-and-suspenders; button is also disabled
                                        const candTruck = trucks.find((t) => t.id === selectedCandidate.truck_id);
                                        const candDriverId = candTruck?.current_driver_id?.id || candTruck?.current_driver_id || null;
                                        runDoubleBookCheck(selectedCandidate.truck_id, p.pickup_datetime, p.delivery_deadline, null, () => {
                                          guardUnconfirmedPtoReturn(candDriverId, selectedCandidate.driver_name, () => {
                                            if (selectedCandidate.driver_name) openBrokerFuelSmsStep(selectedCandidate);
                                            else createLoadFromBrokerCall();
                                          });
                                        });
                                      },
                                      // Disabled + "calculating" until the fuel/HOS stops finish loading.
                                      loading: creatingLoadFromCall || doubleBookBusy || brokerFuelLoading,
                                      loadingLabel: brokerFuelLoading ? "Calculating fuel & HOS stops…" : "Logging…",
                                      done: !!createdLoadResult,
                                      doneLabel: "Load created -- redirecting to its route…",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>,
                            document.body
                          )}

                          <div style={{ marginBottom: 6 }}>
                            <RouteControls visibility={brokerRouteVisibility} />
                          </div>
                          <div style={styles.layerRouteRow}>
                            <span style={{ ...styles.routeSelectDot, background: "transparent", borderColor: "var(--muted)", cursor: "default" }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={styles.routeOptionTitle}>Pickup route</div>
                              <div style={styles.routeOptionMeta}>Truck's current position to pickup</div>
                            </div>
                            <ToggleSwitch on={!brokerRouteVisibility.isHidden(PICKUP_ROUTE_KEY)} color="var(--muted)" onClick={() => brokerRouteVisibility.toggle(PICKUP_ROUTE_KEY)} />
                          </div>
                          {routes.map((r, i) => {
                            const isChosen = r.route_type === brokerChosenRouteType;
                            const isHidden = brokerRouteVisibility.isHidden(r.route_type);
                            const color = brokerRouteVisibility.colorFor(r.route_type);
                            const tripPlan = brokerRouteTripPlans[r.route_type];
                            // r.profit_if_offered was computed server-side against
                            // whatever offer was on the load WHEN this route list was
                            // fetched -- if the dispatcher has since edited the offer
                            // in step 2, that number's stale. Recomputing client-side
                            // from this route's own total_cost/distance/hours keeps it
                            // in sync with the live offer, same as steps 1 and 2.
                            const rNeg = computeNegotiation(r.total_cost, r.distance_miles, r.driving_hours, offeredRate);
                            const rIsLoss = rNeg.profit != null && rNeg.profit < 0;
                            return (
                              <div key={r.route_type} style={styles.layerRouteRow}>
                                <button
                                  onClick={() => setBrokerChosenRouteType(r.route_type)}
                                  title={isChosen ? "This is the route the negotiation numbers use" : "Use this route's numbers instead"}
                                  style={{ ...styles.routeSelectDot, background: isChosen ? color : "transparent", borderColor: color }}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={styles.routeOptionTitle}>
                                    {r.is_toll_avoidant ? "Toll-free route" : `Route ${i + 1}`}
                                    {isChosen && <span style={styles.routeOptionBadge}>Negotiating off this</span>}
                                    {rNeg.profit != null && (
                                      <span style={{ marginLeft: "auto", fontWeight: 700, fontSize: 12, color: rIsLoss ? "#f2a33d" : "var(--brand)" }}>
                                        {rIsLoss ? "-" : ""}${Math.abs(Math.round(rNeg.profit)).toLocaleString()} {rIsLoss ? "loss" : "profit"}
                                      </span>
                                    )}
                                  </div>
                                  <div style={styles.routeOptionMeta}>
                                    {Math.round(r.distance_miles)}mi · {r.driving_hours}h driving · ${r.toll_cost.toLocaleString()} tolls · floor ${r.minimum_acceptable_rate.toLocaleString()}
                                    {tripPlan && (tripPlan.incomplete_reason
                                      ? " · couldn't fully plan fuel/rest stops (see Routes tab)"
                                      : tripPlan.hos_checked === false
                                        ? ` · ${tripPlan.fuel_stop_count} fuel · HOS unchecked (no driver)`
                                        : ` · ${tripPlan.fuel_stop_count} fuel · ${tripPlan.rest_zone_count} HOS`)}
                                  </div>
                                  <div style={{ ...styles.routeOptionMeta, marginTop: 1 }}>
                                    ${Math.round(r.fuel_cost).toLocaleString()} fuel · ${Math.round(r.driver_cost).toLocaleString()} driver pay
                                    {" · "}${Math.round(r.other_cost).toLocaleString()} equipment
                                    {r.wait_cost > 0 && ` · $${Math.round(r.wait_cost).toLocaleString()} wait`}
                                  </div>
                                </div>
                                <ToggleSwitch
                                  on={!isHidden}
                                  color={color}
                                  onClick={() => brokerRouteVisibility.toggle(r.route_type)}
                                />
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {selectedCandidate && !createdLoadResult && (
                      <button
                        onClick={() => {
                          const candTruck = trucks.find((t) => t.id === selectedCandidate.truck_id);
                          const candDriverId = candTruck?.current_driver_id?.id || candTruck?.current_driver_id || null;
                          runDoubleBookCheck(selectedCandidate.truck_id, brokerCallResult?.parsed?.pickup_datetime, brokerCallResult?.parsed?.delivery_deadline, null, () => {
                            guardUnconfirmedPtoReturn(candDriverId, selectedCandidate.driver_name, () => {
                              if (selectedCandidate.driver_name) openBrokerFuelSmsStep(selectedCandidate);
                              else createLoadFromBrokerCall();
                            });
                          });
                        }}
                        disabled={creatingLoadFromCall || doubleBookBusy}
                        style={styles.explainBtn}
                      >
                        {creatingLoadFromCall ? "Creating…" : "Create load & log negotiation"}
                      </button>
                    )}
                    {brokerFuelSmsOpen && selectedCandidate?.driver_name && (
                      <PushFuelStopsPopup
                        driverName={selectedCandidate.driver_name}
                        message={brokerFuelSmsText}
                        onMessageChange={setBrokerFuelSmsText}
                        sending={brokerFuelSmsSending}
                        sendResult={brokerFuelSmsResult}
                        driverPhone={drivers[brokerFuelSmsDriverId]?.phone}
                        phoneValue={brokerFuelSmsPhone}
                        onPhoneChange={setBrokerFuelSmsPhone}
                        onSend={sendBrokerFuelSms}
                        onSkip={() => { setBrokerFuelSmsOpen(false); setBrokerPtoPromptOpen(true); }}
                        onContinue={() => { setBrokerFuelSmsOpen(false); setBrokerPtoPromptOpen(true); }}
                        onBack={() => setBrokerFuelSmsOpen(false)}
                      />
                    )}
                    {brokerPtoPromptOpen && selectedCandidate?.driver_name && (
                      <PtoConfirmPopup
                        driverName={selectedCandidate.driver_name}
                        expanded={brokerPtoEnabled}
                        onYes={() => setBrokerPtoEnabled(true)}
                        onNo={() => { setBrokerPtoPromptOpen(false); createLoadFromBrokerCall(); }}
                        until={brokerPtoUntil}
                        onUntilChange={setBrokerPtoUntil}
                        note={brokerPtoNote}
                        onNoteChange={setBrokerPtoNote}
                        onBack={() => setBrokerPtoEnabled(false)}
                        onBackStep={() => { setBrokerPtoPromptOpen(false); setBrokerFuelSmsOpen(true); }}
                        onConfirm={() => { setBrokerPtoPromptOpen(false); createLoadFromBrokerCall(); }}
                        confirmDisabled={!brokerPtoUntil}
                        confirming={creatingLoadFromCall}
                      />
                    )}
                    {createdLoadResult && (
                      <div style={{ ...styles.tripPlanEta, color: "#4cae6d" }}>
                        ✓ Load created and negotiation logged.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    )}

    {checkLoadOpen && selectedTruck && (
      // No onClick-to-close on the overlay -- same reasoning as Quick
      // Check/Log broker call above, this wizard can hold a fully-typed
      // form and real fetched match results. Only the X button closes it.
      <div style={styles.modalOverlay}>
        <div style={{ ...styles.modalCard, width: 760 }}>
          <div style={styles.modalHeader}>
            <div style={styles.modalTitle}><Navigation size={14} /> Assign Load to Truck {selectedTruck.unit_number}</div>
            <button
              onClick={closeCheckLoadModal}
              style={styles.modalCloseBtn}
            >
              ×
            </button>
          </div>

          <div style={styles.tripPlanCard}>
            <div style={styles.wizardStepper}>
              {[1, 2, 3].map((n, idx) => {
                const label = n === 1 ? "Load details" : n === 2 ? "Analysis" : "Routes";
                const clickable = n === 1 || (n === 2 && !!checkLoadResult?.data) || (n === 3 && !!checkLoadResult?.data?.best_match);
                const isDone = checkLoadStep > n;
                const isActive = checkLoadStep === n;
                return (
                  <React.Fragment key={n}>
                    <div
                      style={{ ...styles.wizardStepItem, cursor: clickable ? "pointer" : "default" }}
                      onClick={() => {
                        if (!clickable) return;
                        if (n === 3 && !checkLoadRoutePreview) fetchCheckLoadRoutes();
                        setCheckLoadStep(n);
                      }}
                    >
                      <div
                        style={{
                          ...styles.wizardStepDot,
                          ...(isDone ? { background: "var(--brand)", borderColor: "var(--brand)", color: "#fff" } : {}),
                          ...(isActive ? { borderColor: "var(--brand)", color: "var(--brand)" } : {}),
                        }}
                      >
                        {n}
                      </div>
                      <div style={{ ...styles.wizardStepLabel, ...((isActive || isDone) ? { color: "var(--ink)" } : {}) }}>{label}</div>
                    </div>
                    {idx < 2 && <div style={{ ...styles.wizardConnector, ...(checkLoadStep > n ? { background: "var(--brand)" } : {}) }} />}
                  </React.Fragment>
                );
              })}
            </div>

            {checkLoadStep === 1 && (
              <div>
                {/* Captured once here, up front, for BOTH manual and
                    describe modes below -- see checkLoadManual's own
                    customer_name comment for why this moved off the
                    assign-time side panel. Optional: assignCheckLoad()
                    falls back to "Unknown" if this is left blank. */}
                <div style={{ marginBottom: 10 }}>
                  <div style={styles.checkLoadManualLabel}>Customer / broker name (optional)</div>
                  <input
                    type="text"
                    placeholder="Who is this load actually for?"
                    value={checkLoadManual.customer_name}
                    onChange={(e) => setCheckLoadManual((p) => ({ ...p, customer_name: e.target.value }))}
                    style={styles.modalInput}
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={styles.checkLoadManualLabel}>Hazardous material? *</div>
                  {/* Mandatory, unlike customer name above -- starts on the
                      blank option (real value "", not a placeholder text
                      trick) so a dispatcher can't tab past it without a
                      conscious choice. See submitCheckLoad/
                      submitCheckLoadManual for where this actually blocks
                      progress to the Analysis step. */}
                  <select
                    value={checkLoadManual.hazmat_status}
                    onChange={(e) => setCheckLoadManual((p) => ({ ...p, hazmat_status: e.target.value }))}
                    style={styles.modalInput}
                  >
                    <option value="">Select one…</option>
                    <option value="non_hazardous">Non-Hazardous</option>
                    <option value="hazardous">Hazardous</option>
                  </select>
                </div>
                {checkLoadMode === "manual" ? (
                  <div>
                    <div style={styles.checkLoadManualGrid}>
                      <input
                        type="text" placeholder="Origin ZIP (e.g. 75201) *" value={checkLoadManual.origin_location}
                        onChange={(e) => {
                          setCheckLoadManual((p) => ({ ...p, origin_location: e.target.value }));
                          setCheckLoadOriginGeo(null);
                          setCheckLoadLaneHistory(null);
                        }}
                        onBlur={() => resolveCheckLoadLaneField("origin")}
                        style={styles.modalInput}
                      />
                      <input
                        type="text" placeholder="Destination ZIP (e.g. 28202) *" value={checkLoadManual.destination_location}
                        onChange={(e) => {
                          setCheckLoadManual((p) => ({ ...p, destination_location: e.target.value }));
                          setCheckLoadDestGeo(null);
                          setCheckLoadLaneHistory(null);
                        }}
                        onBlur={() => resolveCheckLoadLaneField("destination")}
                        style={styles.modalInput}
                      />
                      <div>
                        <div style={styles.checkLoadManualLabel}>Pickup</div>
                        <USDateTimeInput
                          value={checkLoadManual.pickup_datetime_local}
                          onChange={(e) => setCheckLoadManual((p) => ({ ...p, pickup_datetime_local: e.target.value }))}
                          style={styles.modalInput}
                        />
                      </div>
                      <div>
                        <div style={styles.checkLoadManualLabel}>Delivery due date *</div>
                        <USDateTimeInput
                          value={checkLoadManual.delivery_deadline_local}
                          onChange={(e) => setCheckLoadManual((p) => ({ ...p, delivery_deadline_local: e.target.value }))}
                          style={styles.modalInput}
                        />
                      </div>
                      <input
                        type="text" placeholder="Equipment type (e.g. reefer, dry_van)" value={checkLoadManual.equipment_type}
                        onChange={(e) => setCheckLoadManual((p) => ({ ...p, equipment_type: e.target.value }))}
                        style={styles.modalInput}
                      />
                      <input
                        type="number" placeholder="Offered rate ($) *" value={checkLoadManual.offered_rate}
                        onChange={(e) => setCheckLoadManual((p) => ({ ...p, offered_rate: e.target.value }))}
                        style={styles.modalInput}
                      />
                      <input
                        type="number" placeholder="Load weight (lbs, optional)" value={checkLoadManual.load_weight_lbs}
                        onChange={(e) => setCheckLoadManual((p) => ({ ...p, load_weight_lbs: e.target.value }))}
                        title="Real cargo weight -- feeds HERE's truck-legal routing so weight-restricted roads/bridges are actually avoided. Left blank, routing applies no weight restriction (same as before this field existed)."
                        style={styles.modalInput}
                      />
                    </div>

                    <LaneHistoryPanel laneHistory={checkLoadLaneHistory} />

                    {/* Real stops between the first pickup and the last drop -- a
                        broker occasionally quotes "pick up at A, then B, deliver
                        at C" as one job. Optional; a normal single-pickup/single-
                        drop load just leaves this empty. */}
                    <div style={styles.checkLoadStopsSection}>
                      <div style={styles.checkLoadStopsHeader}>
                        <div style={styles.checkLoadStopsTitle}>
                          Additional stops {checkLoadStops.length > 0 ? `(${checkLoadStops.length})` : ""}
                        </div>
                      </div>
                      {checkLoadStops.map((stop, i) => (
                        <div key={i} style={styles.checkLoadStopRow}>
                          <div style={styles.checkLoadStopBadge}>{i + 2}</div>
                          <div>
                            <div style={styles.checkLoadStopFields}>
                              <input
                                type="text" placeholder="Location (city, state or zip)" value={stop.location}
                                onChange={(e) => updateCheckLoadStop(i, { location: e.target.value, lat: null, lng: null })}
                                onBlur={() => resolveCheckLoadStop(i)}
                                style={styles.modalInput}
                              />
                              <select
                                value={stop.stop_type}
                                onChange={(e) => updateCheckLoadStop(i, { stop_type: e.target.value })}
                                style={styles.modalInput}
                              >
                                <option value="pickup">Pickup</option>
                                <option value="dropoff">Dropoff</option>
                              </select>
                              <USDateTimeInput
                                value={stop.appointment_datetime_local}
                                onChange={(e) => updateCheckLoadStop(i, { appointment_datetime_local: e.target.value })}
                                style={styles.modalInput}
                              />
                            </div>
                            {stop.resolving && <div style={styles.checkLoadStopResolved}>Resolving…</div>}
                            {!stop.resolving && stop.lat != null && (
                              <div style={styles.checkLoadStopResolved}>✓ {stop.location}</div>
                            )}
                            {!stop.resolving && stop.error && (
                              <div style={styles.checkLoadStopError}>{stop.error}</div>
                            )}
                          </div>
                          <button onClick={() => removeCheckLoadStop(i)} style={styles.checkLoadStopRemoveBtn} title="Remove stop">
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                      <button onClick={addCheckLoadStop} style={styles.addStopBtn}>
                        <Plus size={13} /> Add stop
                      </button>
                    </div>

                    {checkLoadResult?.error && (
                      <div style={{ ...styles.emptyState, color: "#f2a33d", marginTop: 8 }}>{checkLoadResult.error}</div>
                    )}
                    <button
                      onClick={submitCheckLoadManual}
                      disabled={
                        checkLoadResult?.loading || !checkLoadManual.origin_location.trim() ||
                        !checkLoadManual.destination_location.trim() || !checkLoadManual.pickup_datetime_local ||
                        !checkLoadManual.delivery_deadline_local || !checkLoadManual.offered_rate || !checkLoadStopsReady ||
                        !checkLoadManual.hazmat_status ||
                        isDeliveryBeforePickup(checkLoadManual.pickup_datetime_local, checkLoadManual.delivery_deadline_local)
                      }
                      style={{
                        ...styles.checkBtn, marginTop: 10,
                        opacity: (checkLoadResult?.loading || !checkLoadManual.origin_location.trim() ||
                          !checkLoadManual.destination_location.trim() || !checkLoadManual.pickup_datetime_local ||
                          !checkLoadManual.delivery_deadline_local || !checkLoadManual.offered_rate ||
                          !checkLoadStopsReady || !checkLoadManual.hazmat_status ||
                          isDeliveryBeforePickup(checkLoadManual.pickup_datetime_local, checkLoadManual.delivery_deadline_local)) ? 0.5 : 1,
                      }}
                    >
                      <Navigation size={13} /> {checkLoadResult?.loading ? "Checking…" : "Check this load against this truck"}
                    </button>
                    {!checkLoadStopsReady && (
                      <div style={{ ...styles.emptyState, marginTop: 6 }}>Resolve every added stop's location before continuing.</div>
                    )}
                    {checkLoadStopsReady && !checkLoadManual.hazmat_status && (
                      <div style={{ ...styles.emptyState, marginTop: 6 }}>Select whether this load is hazardous.</div>
                    )}
                    {checkLoadStopsReady && checkLoadManual.hazmat_status && (!checkLoadManual.delivery_deadline_local || !checkLoadManual.offered_rate) && (
                      <div style={{ ...styles.emptyState, marginTop: 6 }}>Delivery due date and offered rate are required.</div>
                    )}
                    {checkLoadStopsReady && checkLoadManual.delivery_deadline_local && checkLoadManual.offered_rate &&
                      isDeliveryBeforePickup(checkLoadManual.pickup_datetime_local, checkLoadManual.delivery_deadline_local) && (
                      <div style={{ ...styles.emptyState, marginTop: 6, color: "#e0553d" }}>Delivery due date can't be before pickup.</div>
                    )}
                  </div>
                ) : (
                  <div>
                    <textarea
                      value={checkLoadMessage}
                      onChange={(e) => setCheckLoadMessage(e.target.value)}
                      placeholder="Describe the load, e.g. 'Load from Dallas TX to Charlotte NC, picks up tomorrow noon, deliver within 4 days, reefer, offering $2400'"
                      style={styles.modalTextarea}
                      rows={3}
                    />
                    {checkLoadResult?.error && (
                      <div style={{ ...styles.emptyState, color: "#f2a33d", marginTop: 8 }}>{checkLoadResult.error}</div>
                    )}
                    <button
                      onClick={submitCheckLoad}
                      disabled={checkLoadResult?.loading || !checkLoadMessage.trim() || !checkLoadManual.hazmat_status}
                      style={{ ...styles.checkBtn, marginTop: 10, opacity: (checkLoadResult?.loading || !checkLoadMessage.trim() || !checkLoadManual.hazmat_status) ? 0.5 : 1 }}
                    >
                      <Navigation size={13} /> {checkLoadResult?.loading ? "Checking (AI parsing + search)…" : "Check this load against this truck"}
                    </button>
                    {!checkLoadManual.hazmat_status && checkLoadMessage.trim() && (
                      <div style={{ ...styles.emptyState, marginTop: 6 }}>Select whether this load is hazardous (above).</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {checkLoadStep === 2 && checkLoadResult?.data && (() => {
              const data = checkLoadResult.data;
              const c = data.best_match;
              const fmt2 = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
              const fmt0 = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
              if (!c) {
                return (
                  <div>
                    <button style={styles.wizardBackLink} onClick={() => setCheckLoadStep(1)}>← Edit load details</button>
                    <div style={{ ...styles.emptyState, color: "#f2a33d" }}>
                      {data.note || "This truck doesn't have a driver assigned, so real HOS/cost numbers can't be computed for it."}
                    </div>
                  </div>
                );
              }
              const offeredRateNum = checkLoadForm.offered_rate === "" ? null : Number(checkLoadForm.offered_rate);
              const neg = computeNegotiation(c.total_cost, c.distance_miles, c.driving_hours, offeredRateNum);
              const isLoss = neg.profit != null && neg.profit < 0;
              const profitColor = isLoss ? "#f2a33d" : "var(--brand)";
              const detailArrivalTime = c.search_mode === "loaded" ? formatCurrentTripArrival(c.current_trip_remaining_hours) : null;
              return (
                <div>
                  <button style={styles.wizardBackLink} onClick={() => setCheckLoadStep(1)}>← Edit load details</button>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink)", marginBottom: 2 }}>
                    {data.origin?.display_name} → {data.destination?.display_name}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>
                    {c.search_mode === "idle" ? "Idle" : (
                      <>
                        Finishing a nearby load
                        {detailArrivalTime && <> — <b style={{ color: "#7db8ff", fontWeight: 600 }}>arrives ~{detailArrivalTime}</b></>}
                      </>
                    )} · {c.deadhead_miles}mi deadhead to pickup
                    {detailArrivalTime && (
                      <>
                        <br />
                        <span style={{ color: "var(--warn)" }}>
                          Unload time not included — arrival and pickup-wait estimates below are best case; confirm with the driver before you commit this truck.
                        </span>
                      </>
                    )}
                  </div>

                  {data.note && (
                    <div style={{ fontSize: 11.5, color: "#f2a33d", background: "rgba(242,163,61,0.1)", border: "1px solid rgba(242,163,61,0.3)", borderRadius: 7, padding: "5px 9px", marginBottom: 6 }}>
                      {data.note}
                    </div>
                  )}

                  {c.dispatcher_id && c.dispatcher_id !== currentUserId && (
                    <div style={{ fontSize: 11.5, color: "var(--muted)", background: "rgba(148,163,184,0.1)", border: "1px solid var(--line)", borderRadius: 7, padding: "5px 9px", marginBottom: 6 }}>
                      This truck is handled by <b style={{ color: "var(--ink-2)" }}>{c.dispatcher_name || "another dispatcher"}</b> -- check with them before committing it to this load.
                    </div>
                  )}

                  {c.is_returning_driver && (
                    <div style={{ fontSize: 11.5, color: "#f2a33d", background: "rgba(242,163,61,0.1)", border: "1px solid rgba(242,163,61,0.3)", borderRadius: 7, padding: "5px 9px", marginBottom: 6 }}>
                      ⚠ <b>Confirm before committing</b> -- Motive shows no one currently logged into this truck. These
                      numbers use <b>{c.driver_name || "this driver"}'s</b> last known HOS reading -- call to confirm before you commit.
                    </div>
                  )}

                  {c.on_time_off && (
                    <div style={{ fontSize: 11.5, color: "#dc2626", background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: 7, padding: "5px 9px", marginBottom: 6 }}>
                      ⚠ <b>{c.driver_name || "This driver"} is currently on time off</b>
                      {c.time_off_until_local ? ` until ${c.time_off_until_local}` : ""} -- numbers below assume
                      they're back and ready to drive by then.
                    </div>
                  )}

                  {!c.on_time_off && c.time_off_conflict_warning && (
                    <div style={{ fontSize: 11.5, color: "#dc2626", background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: 7, padding: "5px 9px", marginBottom: 6 }}>
                      ⚠ {c.time_off_conflict_warning}
                    </div>
                  )}

                  {c.hos_checked === false && (
                    <div style={{ fontSize: 11.5, color: "#f2a33d", background: "rgba(242,163,61,0.1)", border: "1px solid rgba(242,163,61,0.3)", borderRadius: 7, padding: "5px 9px", marginBottom: 6 }}>
                      ⚠ <b>No driver assigned to this truck</b> -- the route's real HOS/rest-break requirements
                      couldn't be checked. Any "0 HOS" stops you see are unverified, not confirmed safe.
                    </div>
                  )}

                  <div style={styles.statGrid2}>
                    <div style={styles.statCell2}>
                      <div style={styles.statCell2Label}>Total cost to run</div>
                      <div style={styles.statCell2Value}>{fmt2(c.total_cost)}</div>
                    </div>
                    <div style={styles.statCell2}>
                      <div style={styles.statCell2Label}>Minimum acceptable rate</div>
                      <div style={styles.statCell2Value}>{fmt2(neg.floor)}</div>
                    </div>
                    <div style={{ ...styles.statCell2, background: "rgba(13,125,111,0.07)" }}>
                      <div style={styles.statCell2Label}>Offered rate <span style={{ opacity: 0.7 }}>(edit me)</span></div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
                        <span style={styles.statCell2Value}>$</span>
                        <input
                          type="number"
                          value={checkLoadForm.offered_rate}
                          onChange={(e) => setCheckLoadForm((f) => ({ ...f, offered_rate: e.target.value }))}
                          style={styles.offerInput}
                        />
                      </div>
                    </div>
                    <div style={styles.statCell2}>
                      <div style={styles.statCell2Label}>Suggested counter</div>
                      <div style={{ ...styles.statCell2Value, color: "var(--brand)" }}>{fmt2(neg.suggestedCounter)}</div>
                    </div>
                    <div style={styles.statCell2}>
                      <div style={styles.statCell2Label}>{isLoss ? "Loss if accepted" : "Profit if accepted"}</div>
                      <div style={{ ...styles.statCell2Value, color: profitColor }}>{fmt2(neg.profit)}</div>
                    </div>
                    <div style={styles.statCell2}>
                      <div style={styles.statCell2Label}>Profit / mile</div>
                      <div style={{ ...styles.statCell2Value, color: profitColor }}>{fmt2(neg.profitPerMile)}</div>
                    </div>
                    <div style={styles.statCell2}>
                      <div style={styles.statCell2Label}>Profit / hour</div>
                      <div style={{ ...styles.statCell2Value, color: profitColor }}>{fmt2(neg.profitPerHour)}</div>
                    </div>
                    <div style={{ ...styles.statCell2, ...(c.late_for_pickup_hours > 0 ? { background: "rgba(242,163,61,0.1)" } : {}) }}>
                      <div style={styles.statCell2Label}>{c.late_for_pickup_hours > 0 ? "Late for pickup" : c.search_mode === "loaded" ? "Wait for pickup (min.)" : "Wait for pickup"}</div>
                      <div style={{ ...styles.statCell2Value, color: (c.wait_hours > 0 || c.late_for_pickup_hours > 0) ? "#f2a33d" : "var(--ink)" }}>
                        {c.late_for_pickup_hours > 0 ? `${c.late_for_pickup_hours}h late` : `${c.wait_hours}h`}
                      </div>
                      {c.late_for_pickup_hours === 0 && c.wait_free_hours > 0 && (
                        <div style={{ fontSize: 9.5, color: "var(--muted)", marginTop: 2 }}>
                          {c.wait_free_hours}h free rest + {c.wait_billable_hours}h billable
                        </div>
                      )}
                      {c.hos_hours_remaining != null && (
                        <div style={{ fontSize: 9.5, color: "var(--muted)", marginTop: 2 }}>
                          Driver has {formatHm(Number(c.hos_hours_remaining))} HOS left
                        </div>
                      )}
                    </div>
                  </div>

                  {c.pickup_timing_uncertain && (
                    <div style={{ fontSize: 11.5, color: "#f2a33d", background: "rgba(242,163,61,0.1)", border: "1px solid rgba(242,163,61,0.3)", borderRadius: 7, padding: "5px 9px", marginBottom: 6 }}>
                      ⚠ {c.pickup_timing_note}
                    </div>
                  )}

                  {c.late_for_pickup_hours > 0 && (
                    <div style={{ fontSize: 11.5, color: "#f2a33d", background: "rgba(242,163,61,0.1)", border: "1px solid rgba(242,163,61,0.3)", borderRadius: 7, padding: "5px 9px", marginBottom: 6 }}>
                      ⚠ This truck can't make the requested pickup time -- it would arrive <b>{c.late_for_pickup_hours}h after</b> the appointment.
                    </div>
                  )}

                  <div style={{ fontSize: 10.5, color: "var(--muted)", marginBottom: 6 }}>
                    What's in that total cost: <b style={{ color: "var(--ink-2)" }}>{fmt0(c.fuel_cost)} fuel</b>
                    {" · "}<b style={{ color: "var(--ink-2)" }}>{fmt0(c.driver_cost)} driver pay</b>
                    {" · "}<b style={{ color: "var(--ink-2)" }}>{fmt0(c.toll_cost)} tolls</b>
                    {" · "}<b style={{ color: "var(--ink-2)" }}>{fmt0(c.other_cost)} equipment</b>
                    {c.wait_cost > 0 && (
                      <>
                        {" · "}
                        <b style={{ color: "var(--ink-2)" }}>{fmt0(c.wait_cost)} wait time</b>
                        {c.wait_free_hours > 0 && ` (${c.wait_billable_hours}h billable -- ${c.wait_free_hours}h rest not charged)`}
                      </>
                    )}
                  </div>

                  <details style={{ marginBottom: 6 }}>
                    <summary style={{ fontSize: 11, color: "var(--muted)", cursor: "pointer" }}>Trip summary</summary>
                    <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "var(--ink-2)", background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 7, padding: "6px 9px", marginTop: 5 }}>
                      {candidateNarrative(c)}
                    </div>
                  </details>

                  <button
                    style={{ ...styles.explainBtn, marginBottom: 0 }}
                    onClick={() => {
                      // Retry whenever there's no successful, non-empty
                      // result cached yet -- not just on the very first
                      // click. Previously this only checked "have we ever
                      // tried," so a one-off transient failure (e.g. a real
                      // HERE routing hiccup) got permanently stuck: every
                      // later click just re-displayed the same stale error,
                      // with no way to retry short of going back to step 1
                      // and resubmitting the whole load.
                      const hasRoutes = checkLoadRoutePreview?.data?.best_match_route_options?.length > 0;
                      if (!checkLoadRoutePreview || checkLoadRoutePreview.error || !hasRoutes) fetchCheckLoadRoutes();
                      setCheckLoadStep(3);
                      // Same reasoning as Log Broker Call's identical change --
                      // land directly on the big map where the route decision
                      // actually gets made, instead of the small in-modal list.
                      setCheckLoadMapModalOpen(true);
                    }}
                  >
                    See route options
                  </button>
                </div>
              );
            })()}

            {checkLoadStep === 3 && checkLoadResult?.data?.best_match && (
              <div>
                <button style={styles.wizardBackLink} onClick={() => setCheckLoadStep(2)}>← Back to analysis</button>
                {(() => {
                  if (checkLoadRoutePreview?.loading) return <div style={styles.emptyState}>Loading real route options…</div>;
                  if (checkLoadRoutePreview?.error) {
                    return (
                      <div style={{ ...styles.emptyState, color: "#f2a33d" }}>
                        <div>{checkLoadRoutePreview.error}</div>
                        <button style={{ ...styles.explainBtn, marginTop: 10 }} onClick={fetchCheckLoadRoutes}>
                          Retry
                        </button>
                      </div>
                    );
                  }
                  const routes = checkLoadRoutePreview?.data?.best_match_route_options;
                  if (!routes?.length) return <div style={styles.emptyState}>No real route alternatives came back for this truck.</div>;

                  const c = checkLoadResult.data.best_match;
                  const originTz = checkLoadRoutePreview.data.origin_timezone;
                  const destTz = checkLoadRoutePreview.data.destination_timezone;
                  // See Log Broker Call's identical fix -- the map narrows
                  // to ONLY the selected route the instant one is chosen,
                  // instead of leaving every alternative drawn alongside it.
                  const visibleRoutes = checkLoadChosenRouteType
                    ? routes.filter((r) => r.route_type === checkLoadChosenRouteType)
                    : routes.filter((r) => !checkLoadRouteVisibility.isHidden(r.route_type));
                  const pickupVisible = !checkLoadRouteVisibility.isHidden(PICKUP_ROUTE_KEY);
                  const boundsPoints = [];
                  if (pickupVisible && routes[0]?.geometry?.deadhead?.length) {
                    const dh = routes[0].geometry.deadhead;
                    boundsPoints.push(dh[0], dh[dh.length - 1]);
                  }
                  visibleRoutes.forEach((r) => {
                    const ld = r.geometry?.loaded;
                    if (ld?.length) boundsPoints.push(ld[0], ld[Math.floor(ld.length / 2)], ld[ld.length - 1]);
                  });
                  const truckLabel = c.driver_name ? c.driver_name.split(" ")[0] : c.unit_number;
                  const truckColor = c.search_mode === "loaded" ? STATUS.loaded.color : STATUS.free.color;

                  // Shared between the small in-panel map and the big popup,
                  // exact same pattern renderRouteMap uses in the broker-call
                  // wizard (see that block's comment for why sizeKey exists).
                  const renderCheckLoadMap = (heightPx, sizeKey) => (
                    <MapContainer center={[39.5, -98.35]} zoom={4} style={{ ...styles.map, minHeight: heightPx }} scrollWheelZoom={sizeKey === "popup"} key={theme} className={theme === "light" ? "map-tiles-dim" : undefined}>
                      {tileUrl && <TileLayer attribution={TILE_LAYER_ATTRIBUTION} url={tileUrl} />}
                      <MapInvalidateSize trigger={sizeKey} />
                      {boundsPoints.length >= 2 && <MapFitToPoints points={boundsPoints} fitKey={sizeKey} />}
                      {routes[0]?.geometry?.deadhead?.length > 0 && (
                        <Marker position={routes[0].geometry.deadhead[0]} icon={truckIcon(truckLabel, truckColor, false)}>
                          <Tooltip direction="top" offset={[0, -60]}>Truck {c.unit_number}'s current position</Tooltip>
                        </Marker>
                      )}
                      {/* Every candidate route for this one truck shares the
                          same deadhead leg -- drawn once, not once per
                          visible route. */}
                      {pickupVisible && routes[0]?.geometry?.deadhead?.length > 0 && (
                        <Polyline positions={routes[0].geometry.deadhead} pathOptions={{ color: "var(--muted)", weight: 4, dashArray: "6 6" }} />
                      )}
                      {visibleRoutes.map((r, i) => {
                        const isChosen = r.route_type === checkLoadChosenRouteType;
                        const color = checkLoadRouteVisibility.colorFor(r.route_type);
                        const tripStops = checkLoadRouteTripPlans[r.route_type]?.stops || [];
                        // A multi-stop load's real road path bends through
                        // each stop -- plan-full-trip returns the real
                        // per-hop HERE geometry it actually simulated
                        // against (leg_geometries) for exactly this reason.
                        // r.geometry.loaded alone is match-truck-for-load's
                        // origin-to-destination-only line (that endpoint
                        // deliberately never learns about intermediate
                        // stops), which drew straight through/past a real
                        // stop instead of the real road detour to reach it.
                        const legGeometries = checkLoadRouteTripPlans[r.route_type]?.leg_geometries || [];
                        // Same reasoning as legGeometries above -- r.toll_stops
                        // is match-truck-for-load's straight origin-to-
                        // destination pricing, wrong for any load that
                        // actually detours through a stop. Once plan-full-trip
                        // has real per-hop geometry, prefer its own real
                        // per-hop tolls for the loaded portion; the deadhead
                        // leg is unaffected by intermediate stops, so its
                        // tolls (r.toll_stops' "deadhead"-tagged entries)
                        // stay as-is either way.
                        // See fetchCheckLoadRoutes' multi-stop-route-options
                        // merge -- r.toll_stops is already the authoritative
                        // real per-route tolls whenever this load has stops.
                        // tripPlanTolls only has anything when plan-full-trip
                        // had to self-fetch its own fallback geometry
                        // instead; an EMPTY tripPlanTolls (always present as
                        // a key, just possibly []) must fall through to
                        // r.toll_stops rather than drop its already-correct
                        // loaded-leg tolls.
                        const tripPlanTolls = checkLoadRouteTripPlans[r.route_type]?.toll_stops;
                        const tollStops = legGeometries.length > 0 && tripPlanTolls?.length
                          ? [...(r.toll_stops || []).filter((t) => t.leg === "deadhead"), ...tripPlanTolls]
                          : (r.toll_stops || []);
                        return (
                          <React.Fragment key={r.route_type}>
                            {legGeometries.length > 0 ? (
                              legGeometries.map((leg, li) => leg?.length > 0 && (
                                <Polyline
                                  key={`leg-${r.route_type}-${li}`}
                                  positions={leg}
                                  pathOptions={{ color, weight: isChosen ? 7 : 5, opacity: isChosen ? 1 : 0.85 }}
                                />
                              ))
                            ) : r.geometry?.loaded?.length > 0 && (
                              <Polyline positions={r.geometry.loaded} pathOptions={{ color, weight: isChosen ? 7 : 5, opacity: isChosen ? 1 : 0.85 }} />
                            )}
                            {tollStops.map((t, ti) => (
                              <Marker key={`toll-${r.route_type}-${ti}`} position={[t.lat, t.lng]} icon={tollIcon(t.leg === "deadhead" ? "var(--muted)" : color)}>
                                <Tooltip direction="top" offset={[0, -10]}>
                                  {t.name}{t.price_usd != null ? ` · $${Number(t.price_usd).toFixed(2)}` : ""}
                                </Tooltip>
                              </Marker>
                            ))}
                            {(() => { let stopNum = 1; return tripStops.map((s, si) => {
                              if (s.lat == null || s.lng == null || s.type === "pickup" || s.type === "delivery" || s.type === "wait") return null;
                              if (s.type === "stop_pickup" || s.type === "stop_dropoff") {
                                stopNum += 1;
                                return (
                                  <Marker key={`stop-${r.route_type}-${si}`} position={[Number(s.lat), Number(s.lng)]} icon={pinIcon(color, String(stopNum))}>
                                    <Tooltip direction="top" offset={[0, -14]}>
                                      <div>
                                        <div style={styles.tooltipTitle}>Stop {stopNum} · {s.type === "stop_pickup" ? "Pickup" : "Dropoff"}</div>
                                        <div>{s.location}</div>
                                      </div>
                                    </Tooltip>
                                  </Marker>
                                );
                              }
                              if (s.type === "rest_zone") {
                                return (
                                  <React.Fragment key={`stop-${r.route_type}-${si}`}>
                                    <Circle
                                      center={[Number(s.lat), Number(s.lng)]}
                                      radius={(s.radius_miles || 15) * 1609.34}
                                      pathOptions={{ color, fillColor: color, fillOpacity: 0.12, weight: 1.5, dashArray: "4 4" }}
                                    />
                                    <Marker position={[Number(s.lat), Number(s.lng)]} icon={restZoneIcon(color)}>
                                      <Tooltip direction="top" offset={[0, -10]}>
                                        <div>
                                          <div style={styles.tooltipTitle}>HOS rest break {s.sequence} -- ~{s.radius_miles} mi area</div>
                                          <RestZoneTooltipRows s={s} />
                                        </div>
                                      </Tooltip>
                                    </Marker>
                                  </React.Fragment>
                                );
                              }
                              const isCombined = s.type === "fuel_and_rest";
                              // Always this route's own color, even on the deadhead
                    // (pickup) leg -- unlike a toll (purely positional, so
                    // genuinely identical for every route sharing that
                    // stretch of road), WHICH station gets chosen here is
                    // decided by the whole route ahead (the proactive
                    // top-off logic checks for a cheaper station further
                    // down the entire trip, including the route-specific
                    // loaded leg) -- so two routes can legitimately pick a
                    // different station, or a different fill amount, for
                    // the "same" physical stretch. Graying it out implied
                    // it was shared/identical the way a deadhead toll is,
                    // which isn't actually true here.
                    const legColor = color;
                              return (
                                <React.Fragment key={`stop-${r.route_type}-${si}`}>
                                  {isCombined && (
                                    <Circle
                                      center={[Number(s.lat), Number(s.lng)]}
                                      radius={COMBINED_STOP_RING_MILES * 1609.34}
                                      pathOptions={{ color: legColor, fillColor: legColor, fillOpacity: 0.12, weight: 1.5, dashArray: "4 4" }}
                                    />
                                  )}
                                  <Marker
                                    position={[Number(s.lat), Number(s.lng)]}
                                    icon={stationIcon(legColor, {
                                      size: 20, ringColor: legColor,
                                      label: isCombined ? `F${s.fuel_sequence}+R` : `F${s.sequence}`,
                                      labelColor: legColor,
                                    })}
                                  >
                                    <Tooltip direction="top" offset={[0, -12]}>
                                      <div>
                                        <div style={styles.tooltipTitle}>
                                          {isCombined ? `Fuel + HOS rest break ${s.rest_sequence}` : `Fuel stop ${s.sequence}`}: {s.name || "—"}{s.partial_fill && " (partial fill)"}
                                        </div>
                                        <FuelStopTooltipRows s={s} isCombined={isCombined} />
                                      </div>
                                    </Tooltip>
                                    {s.truck_stop_id && (
                                      <Popup>
                                        <div style={{ fontSize: 12, minWidth: 150 }}>
                                          <div style={{ fontWeight: 700, marginBottom: 6 }}>{s.name || "This station"}</div>
                                          <button
                                            onClick={() => excludeCheckLoadFuelStation(s.truck_stop_id, s.name)}
                                            style={styles.excludeStationBtn}
                                          >
                                            ✕ Don't use this station
                                          </button>
                                        </div>
                                      </Popup>
                                    )}
                                  </Marker>
                                </React.Fragment>
                              );
                            }); })()}
                          </React.Fragment>
                        );
                      })}
                      {checkLoadRoutePreview.data.origin && (
                        <Marker position={[checkLoadRoutePreview.data.origin.lat, checkLoadRoutePreview.data.origin.lng]} icon={pinIcon("#5b9bd5", "S")}>
                          <Tooltip>Pickup: {checkLoadRoutePreview.data.origin.display_name}</Tooltip>
                        </Marker>
                      )}
                      {checkLoadRoutePreview.data.destination && (
                        <Marker position={[checkLoadRoutePreview.data.destination.lat, checkLoadRoutePreview.data.destination.lng]} icon={pinIcon("#a78bfa", "E")}>
                          <Tooltip>Delivery: {checkLoadRoutePreview.data.destination.display_name}</Tooltip>
                        </Marker>
                      )}
                    </MapContainer>
                  );

                  return (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ ...styles.mapCol, height: 340, minHeight: 340, marginBottom: 8 }}>
                        <button
                          onClick={() => setCheckLoadMapModalOpen(true)}
                          title="Maximize map"
                          style={{
                            position: "absolute", top: 8, right: 8, zIndex: 1000,
                            width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
                            background: "var(--card)", border: "1px solid var(--line)", borderRadius: 7,
                            cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,.25)", color: "var(--ink)",
                          }}
                        >
                          <Maximize2 size={14} />
                        </button>
                        {renderCheckLoadMap(340, "small")}
                      </div>
                      <ExcludedStationsChip excluded={checkLoadExcludedStations} onReset={resetCheckLoadExcludedStations} />

                      {checkLoadMapModalOpen && createPortal(
                        <div
                          className="op-center-dark"
                          style={{
                            ...themeVars,
                            position: "fixed", inset: 0, zIndex: 2000, background: "rgba(2,6,23,0.72)",
                            display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
                          }}
                          onClick={() => setCheckLoadMapModalOpen(false)}
                        >
                          <div
                            style={{
                              width: "92vw", height: "88vh", background: "var(--card)",
                              border: "1px solid var(--line)", borderRadius: 14, padding: 14,
                              display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,.5)",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>
                                Route map -- Truck {c.unit_number} · {c.driver_name || "No driver on file"}
                              </div>
                              <button
                                onClick={() => setCheckLoadMapModalOpen(false)}
                                style={{
                                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                                  fontSize: 12, fontWeight: 600, color: "var(--ink)", background: "var(--line)",
                                  border: "none", borderRadius: 8, padding: "6px 12px", cursor: "pointer",
                                }}
                              >
                                <Minimize2 size={13} /> Close
                              </button>
                            </div>
                            <div style={{
                              fontSize: 11.5, color: "var(--ink-2)", background: "var(--bg)", border: "1px solid var(--line)",
                              borderRadius: 8, padding: "5px 9px", marginBottom: 6,
                            }}>
                              Hypothetical load: <b style={{ color: "var(--ink)" }}>pickup</b> at {checkLoadForm.origin_location || "origin"}
                              {" "}<b style={{ color: "var(--ink)" }}>{formatDateFacilityLocal(parseUtcNaive(checkLoadForm.pickup_datetime), checkLoadForm.origin_timezone) || ""}</b> ·{" "}
                              <b style={{ color: "var(--ink)" }}>delivery</b> at {checkLoadForm.destination_location || "destination"}
                              {checkLoadForm.delivery_deadline ? <> by <b style={{ color: "var(--ink)" }}>{formatDateFacilityLocal(parseUtcNaive(checkLoadForm.delivery_deadline), checkLoadForm.destination_timezone)}</b></> : ""}
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 10 }}>
                              <RouteControls visibility={checkLoadRouteVisibility} />
                              {routes.map((r, i) => {
                                const isHiddenHere = checkLoadRouteVisibility.isHidden(r.route_type);
                                const routeChipColor = checkLoadRouteVisibility.colorFor(r.route_type);
                                return (
                                  <button
                                    key={r.route_type}
                                    onClick={() => checkLoadRouteVisibility.toggle(r.route_type)}
                                    style={{
                                      display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600,
                                      padding: "5px 10px", borderRadius: 999, cursor: "pointer",
                                      border: `1px solid ${isHiddenHere ? "var(--line)" : routeChipColor}`,
                                      background: isHiddenHere ? "var(--bg)" : `${routeChipColor}1a`,
                                      color: isHiddenHere ? "var(--muted)" : "var(--ink)",
                                      opacity: isHiddenHere ? 0.6 : 1,
                                    }}
                                  >
                                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: routeChipColor, flexShrink: 0 }} />
                                    {r.is_toll_avoidant ? "Toll-free route" : `Route ${i + 1}`}
                                  </button>
                                );
                              })}
                            </div>
                            <div style={{ flex: 1, position: "relative", borderRadius: 10, overflow: "hidden", border: "1px solid var(--line)" }}>
                              {renderCheckLoadMap("100%", "popup")}
                              <FuelStopReviewBanner
                                fuelOptimizationUnavailable={(checkLoadRouteTripPlans[checkLoadChosenRouteType] || Object.values(checkLoadRouteTripPlans)[0])?.fuel_optimization_unavailable}
                                fuelOptimizationMessage={(checkLoadRouteTripPlans[checkLoadChosenRouteType] || Object.values(checkLoadRouteTripPlans)[0])?.fuel_optimization_message}
                              />
                              <ExcludedStationsChip excluded={checkLoadExcludedStations} onReset={resetCheckLoadExcludedStations} corner />
                              <RouteLegendPanel
                                routes={routes}
                                hiddenRouteTypes={checkLoadRouteVisibility.hidden}
                                colorFor={checkLoadRouteVisibility.colorFor}
                                chosenRouteType={checkLoadChosenRouteType}
                                tripPlans={checkLoadRouteTripPlans}
                                offeredRate={checkLoadForm.offered_rate === "" ? null : Number(checkLoadForm.offered_rate)}
                                originTz={originTz}
                                destTz={destTz}
                                onSelectRoute={setCheckLoadChosenRouteType}
                                logAction={{
                                  label: `Assign Truck ${selectedTruck.unit_number}`,
                                  onClick: () => {
                                    if (checkLoadFuelLoading) return;  // belt-and-suspenders; button is also disabled
                                    runDoubleBookCheck(selectedTruckId, checkLoadForm.pickup_datetime, checkLoadForm.delivery_deadline, checkLoadQuotedLoadIdRef.current, () => {
                                      guardUnconfirmedPtoReturn(selectedDriver?.id, selectedDriver?.driver_name, () => {
                                        if (selectedDriver) openCheckLoadFuelSmsStep();
                                        else assignCheckLoad();
                                      });
                                    });
                                  },
                                  // Disabled until the fuel/HOS stops finish loading, so a
                                  // dispatcher can't commit the truck before seeing its fuel
                                  // plan. loadingLabel distinguishes "still calculating fuel"
                                  // from the actual "assigning" spinner.
                                  loading: checkLoadAssigning || doubleBookBusy || checkLoadFuelLoading,
                                  loadingLabel: checkLoadFuelLoading ? "Calculating fuel & HOS stops…" : "Assigning…",
                                  done: !!checkLoadAssignResult,
                                  doneLabel: "Truck assigned -- redirecting to its route…",
                                }}
                              />
                            </div>
                          </div>
                        </div>,
                        document.body
                      )}

                      <div style={{ marginBottom: 6 }}>
                        <RouteControls visibility={checkLoadRouteVisibility} />
                      </div>
                      <div style={{ ...styles.routeOptionCard, marginBottom: 6 }}>
                        <div style={styles.routeOptionRow}>
                          <span style={{ ...styles.routeOptionSwatch, background: "var(--muted)" }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={styles.routeOptionTitle}>Pickup route</div>
                            <div style={styles.routeOptionMeta}>Truck's current position to pickup</div>
                          </div>
                          <button
                            onClick={() => checkLoadRouteVisibility.toggle(PICKUP_ROUTE_KEY)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: 2 }}
                          >
                            {checkLoadRouteVisibility.isHidden(PICKUP_ROUTE_KEY) ? <EyeOff size={13} /> : <Eye size={13} />}
                          </button>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {routes.map((r, i) => {
                          const isChosen = r.route_type === checkLoadChosenRouteType;
                          const isHidden = checkLoadRouteVisibility.isHidden(r.route_type);
                          const color = checkLoadRouteVisibility.colorFor(r.route_type);
                          const tripPlan = checkLoadRouteTripPlans[r.route_type];
                          const deliveryEtaLabel = tripPlan?.eta ? formatDateFacilityLocal(parseUtcNaive(tripPlan.eta), destTz) : null;
                          return (
                            <div
                              key={r.route_type}
                              onClick={() => setCheckLoadChosenRouteType(r.route_type)}
                              style={{
                                ...styles.routeOptionCard, cursor: "pointer", flex: "1 1 auto", minWidth: 160,
                                ...(isChosen ? styles.routeOptionCardActive : {}), ...(isHidden ? styles.routeOptionCardDimmed : {}),
                              }}
                            >
                              <div style={styles.routeOptionRow}>
                                <span style={{ ...styles.routeOptionSwatch, background: color }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={styles.routeOptionTitle}>
                                    {Math.round(r.distance_miles)}mi · ${Math.round(r.total_cost).toLocaleString()}
                                  </div>
                                  <div style={styles.routeOptionMeta}>
                                    {tripPlan?.total_trip_hours != null && !tripPlan.incomplete_reason ? `${tripPlan.total_trip_hours}h trip` : `${r.driving_hours}h drive`}
                                    {tripPlan && (tripPlan.incomplete_reason
                                      ? " · couldn't fully plan fuel/rest stops"
                                      : tripPlan.hos_checked === false
                                        ? ` · ${tripPlan.fuel_stop_count} fuel · HOS unchecked (no driver)`
                                        : ` · ${tripPlan.fuel_stop_count} fuel · ${tripPlan.rest_zone_count} HOS`)}
                                  </div>
                                  {deliveryEtaLabel && (
                                    <div style={{ ...styles.routeOptionMeta, marginTop: 1 }}>Delivery ETA: {deliveryEtaLabel}</div>
                                  )}
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    checkLoadRouteVisibility.toggle(r.route_type);
                                  }}
                                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)", padding: 2 }}
                                >
                                  {isHidden ? <EyeOff size={13} /> : <Eye size={13} />}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Customer/broker name is now captured on step 1 (see
                          checkLoadManual.customer_name) -- optional, falls
                          back to "Unknown" in assignCheckLoad() itself, so
                          this entry point (and the expanded-map modal's own
                          Assign button) never blocks on it again. */}
                      {!checkLoadAssignResult && (
                        <button
                          onClick={() => {
                            runDoubleBookCheck(selectedTruckId, checkLoadForm.pickup_datetime, checkLoadForm.delivery_deadline, checkLoadQuotedLoadIdRef.current, () => {
                              guardUnconfirmedPtoReturn(selectedDriver?.id, selectedDriver?.driver_name, () => {
                                if (selectedDriver) openCheckLoadFuelSmsStep();
                                else assignCheckLoad();
                              });
                            });
                          }}
                          disabled={checkLoadAssigning || doubleBookBusy}
                          style={styles.explainBtn}
                        >
                          {checkLoadAssigning ? "Assigning…" : `Assign truck ${selectedTruck.unit_number} to this load`}
                        </button>
                      )}
                      {checkLoadFuelSmsOpen && selectedDriver && (
                        <PushFuelStopsPopup
                          driverName={selectedDriver.driver_name}
                          message={checkLoadFuelSmsText}
                          onMessageChange={setCheckLoadFuelSmsText}
                          sending={checkLoadFuelSmsSending}
                          sendResult={checkLoadFuelSmsResult}
                          driverPhone={selectedDriver.phone}
                          phoneValue={checkLoadFuelSmsPhone}
                          onPhoneChange={setCheckLoadFuelSmsPhone}
                          onSend={sendCheckLoadFuelSms}
                          onSkip={() => { setCheckLoadFuelSmsOpen(false); setCheckLoadPtoPromptOpen(true); }}
                          onContinue={() => { setCheckLoadFuelSmsOpen(false); setCheckLoadPtoPromptOpen(true); }}
                          onBack={() => setCheckLoadFuelSmsOpen(false)}
                        />
                      )}
                      {checkLoadPtoPromptOpen && selectedDriver && (
                        <PtoConfirmPopup
                          driverName={selectedDriver.driver_name}
                          expanded={checkLoadPtoEnabled}
                          onYes={() => setCheckLoadPtoEnabled(true)}
                          onNo={() => { setCheckLoadPtoPromptOpen(false); assignCheckLoad(); }}
                          until={checkLoadPtoUntil}
                          onUntilChange={setCheckLoadPtoUntil}
                          note={checkLoadPtoNote}
                          onNoteChange={setCheckLoadPtoNote}
                          onBack={() => setCheckLoadPtoEnabled(false)}
                          onBackStep={() => { setCheckLoadPtoPromptOpen(false); setCheckLoadFuelSmsOpen(true); }}
                          onConfirm={() => { setCheckLoadPtoPromptOpen(false); assignCheckLoad(); }}
                          confirmDisabled={!checkLoadPtoUntil}
                          confirming={checkLoadAssigning}
                        />
                      )}
                      {checkLoadAssignResult && (
                        <div style={{ ...styles.tripPlanEta, color: "#4cae6d" }}>
                          ✓ Truck assigned to this load.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    <ConfirmActionModal
      open={!!pendingConfirmAction}
      tone={pendingConfirmAction?.tone}
      title={pendingConfirmAction?.title}
      description={pendingConfirmAction?.description}
      confirmLabel={pendingConfirmAction?.confirmLabel}
      busy={confirmActionBusy}
      onConfirm={runPendingConfirmAction}
      onCancel={dismissConfirmAction}
    />

    <DoubleBookModal
      open={!!doubleBook}
      data={doubleBook}
      busy={doubleBookBusy}
      onCancel={() => setDoubleBook(null)}
      onConfirm={() => { if (doubleBook && doubleBook.onConfirm) doubleBook.onConfirm(); }}
    />

    {missedReview && (() => {
      const c = missedReview;
      const leg = c.leg || (c.type === "possible_missed_pickup" ? "pickup" : "delivery");
      const isPickup = leg === "pickup";
      // Facility-local appointment (from the candidate's load), never browser tz.
      const candLoad = loads.find((l) => l.id === c.load_id) || null;
      const apptFacility = formatFacilityLocal(c.appointment_at, facilityTzForLeg(candLoad, leg)) || "—";
      return (
        <div style={{ ...styles.modalOverlay, zIndex: 2600 }} onClick={() => !resolvingMissed && setMissedReview(null)}>
          <div style={styles.stopDrawer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.stopDrawerHead}>
              <span style={styles.stopDrawerBadge}>Possible missed {isPickup ? "pickup" : "delivery"}{c.confidence ? ` · ${c.confidence}` : ""}</span>
              <X size={16} style={{ cursor: "pointer", color: "var(--muted)" }} onClick={() => !resolvingMissed && setMissedReview(null)} />
            </div>
            <div style={styles.stopDrawerTitle}>Truck {c.truck_unit_number || "—"}</div>
            <p style={styles.stopDrawerEvidence}>{humanEvidence(c)}</p>
            <div style={styles.stopDrawerFacts}>
              <div style={styles.sdRow}><span style={styles.sdLabel}>Type</span><span>{isPickup ? "Pickup" : "Delivery"}</span></div>
              <div style={styles.sdRow}><span style={styles.sdLabel}>Appointment</span><span>{apptFacility}</span></div>
              <div style={styles.sdRow}><span style={styles.sdLabel}>Dwell near facility</span><span>{c.dwell_minutes != null ? formatDwell(c.dwell_minutes) : "—"}</span></div>
              <div style={styles.sdRow}><span style={styles.sdLabel}>Closest approach</span><span>{c.closest_distance_miles != null ? `${Number(c.closest_distance_miles).toFixed(1)} mi` : "—"}</span></div>
              <div style={styles.sdRow}><span style={styles.sdLabel}>Current distance</span><span>{c.current_distance_miles != null ? `${Math.round(c.current_distance_miles)} mi away` : "—"}</span></div>
              <div style={styles.sdRow}><span style={styles.sdLabel}>Departed after stop</span><span>Yes</span></div>
              <div style={styles.sdRow}><span style={styles.sdLabel}>Current lifecycle</span><span>{isPickup ? "Awaiting pickup" : "In transit"}</span></div>
            </div>
            <details style={styles.stopDrawerDetails}>
              <summary style={styles.sdSummary}>Details / audit</summary>
              <div style={styles.sdCodes}>
                {Array.isArray(c.reason_codes) && c.reason_codes.length ? c.reason_codes.join(", ") : "—"}
                {c.source ? ` · detected ${c.source === "recovery" ? "from trip history" : "live"}` : ""}
              </div>
            </details>
            <div style={styles.stopDrawerActions}>
              <button disabled={resolvingMissed} style={styles.sdConfirmBtn} onClick={() => resolveMissedStop(c.alert_event_id, "confirm")}>
                {resolvingMissed ? "Working…" : `Confirm ${isPickup ? "pickup" : "delivery"}`}
              </button>
              <button disabled={resolvingMissed} style={styles.sdDismissBtn} onClick={() => resolveMissedStop(c.alert_event_id, "dismiss")}>
                Dismiss
              </button>
            </div>
          </div>
        </div>
      );
    })()}

    {ptoReturnPrompt && (
      <PtoReturnConfirmPopup
        driverName={ptoReturnPrompt.driverName}
        ptoRow={ptoReturnPrompt.row}
        confirming={ptoReturnConfirming}
        onConfirm={confirmPtoReturnAndProceed}
        onCancel={() => setPtoReturnPrompt(null)}
      />
    )}

    </div>
  );
}

// Real (not the client-side haversine estimate the header used to show)
// fleet $/mile across three windows, plus the logged-in dispatcher's own
// numbers for a "me vs fleet" comparison -- see backend/fleet_stats.py's
// now_fleet_profit_per_mile/average_fleet_profit_per_mile. $/mile, not
// $/hr, because that's the number dispatchers actually think in when
// comparing loads and negotiating rates. Polled every 60s (matches
// reloadTrucksAndDrivers' cadence) since "now" only reflects trucks
// currently in transit.
function ProfitPerMileStat({ apiBaseUrl }) {
  const [data, setData] = useState(null); // {fleet: {now,week,month}, dispatcher: {now,week,month}}

  useEffect(() => {
    let cancelled = false;
    // Load ONCE on mount (i.e. on page load / manual refresh) -- NOT on a 60s
    // poll. This is a slow-moving $/mile aggregate; polling it every 60s from
    // ~15-20 dispatcher tabs at once flooded the scripted-API executor and
    // starved each worker's async event loop (calls "completing" in 90s+,
    // periodic gunicorn WORKER TIMEOUTs), which took the whole Operational
    // Center down. Truck positions still refresh on their own 45s timer, so the
    // map keeps moving; these numbers just refresh when the page is reloaded.
    axios
      .get(`${apiBaseUrl}/scripted/fleet/profit-per-mile-summary`, authHeaders())
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [apiBaseUrl]);

  const tiers = [
    { key: "now", label: "Now", title: "Live $/mile across trucks currently in transit, using each one's already-saved real trip plan." },
    // Deliberately different from the calendar-week/month reports
    // elsewhere in the app (Weekly Truck Profit, Dispatcher Performance,
    // Truck Economics) -- this counts loads by DELIVERY date within a
    // trailing window (only completed trips have a real, realized $/mile
    // to report), those count loads by PICKUP date within a Monday-aligned
    // calendar week/month. Both are correct for what they're each trying
    // to answer; they just aren't the same set of loads, so don't expect
    // them to match.
    { key: "week", label: "7d", title: "Weighted $/mile across loads DELIVERED in the trailing 7 days (by delivery date) -- a different window than this app's calendar-week reports, which count by pickup date instead." },
    { key: "month", label: "30d", title: "Weighted $/mile across loads DELIVERED in the trailing 30 days (by delivery date) -- a different window than this app's calendar-month reports, which count by pickup date instead." },
  ];

  return (
    <div style={styles.phStatGroup}>
      {tiers.map(({ key, label, title }) => {
        const fleetVal = data?.fleet?.[key];
        const mineVal = data?.dispatcher?.[key];
        return (
          <div key={key} style={styles.phStatCell} title={title}>
            <div style={styles.phStatValue}>{fleetVal != null ? `$${fleetVal.toFixed(2)}` : "—"}</div>
            <div style={styles.phStatLabel}>{label} $/mi</div>
            {mineVal != null && (
              <div style={{ ...styles.phStatMine, color: mineVal >= (fleetVal ?? 0) ? "#4cae6d" : "#e0745d" }}>
                you ${mineVal.toFixed(2)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Shared by Check Load + Log Broker Call step 1 -- recent real loads on
// roughly the same lane (radius-matched, not string-matched -- see
// /fleet/lane-history's own docstring for why), so a dispatcher can see
// what this lane actually paid before asking a broker for a price.
// Renders nothing until both ends are resolved (laneHistory stays null
// until then), so it never flashes an empty box while typing.
const LANE_HISTORY_COLLAPSED_COUNT = 5;

function LaneHistoryPanel({ laneHistory }) {
  const [expanded, setExpanded] = useState(false);
  // Collapse back to the default 5 any time a fresh lane search comes in --
  // otherwise an expanded state from the PREVIOUS origin/destination would
  // silently carry over and show 20 rows for a lane the dispatcher hasn't
  // asked to expand yet.
  useEffect(() => { setExpanded(false); }, [laneHistory?.matches]);

  if (!laneHistory) return null;
  const matches = laneHistory.matches || [];
  const visibleMatches = expanded ? matches : matches.slice(0, LANE_HISTORY_COLLAPSED_COUNT);
  const hiddenCount = matches.length - visibleMatches.length;
  return (
    <div style={styles.laneHistoryBox}>
      {laneHistory.loading ? (
        <div style={styles.laneHistoryLoading}>Checking recent rates on this lane…</div>
      ) : laneHistory.error ? (
        <div style={styles.laneHistoryLoading}>{laneHistory.error}</div>
      ) : matches.length ? (
        <>
          <div style={styles.laneHistoryTitle}>
            Recent loads on this lane
            <span style={{ fontWeight: 400, color: "var(--muted)" }}> (within {laneHistory.radius_miles}mi of both ends)</span>
          </div>
          {visibleMatches.map((m) => (
            <div key={m.id} style={styles.laneHistoryRow}>
              <span style={styles.laneHistoryDate}>
                {parseUtcNaive(m.pickup_datetime)?.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" }) || "—"}
              </span>
              <span style={styles.laneHistoryRoute} title={`${m.origin_location} → ${m.destination_location}`}>
                {m.origin_location} → {m.destination_location}
              </span>
              <span style={styles.laneHistoryRate}>
                ${Math.round(m.rate_offered).toLocaleString()}
                {m.rate_per_mile ? <span style={{ color: "var(--muted)", fontWeight: 400 }}> (${m.rate_per_mile}/mi)</span> : ""}
              </span>
              <span style={styles.laneHistoryBy}>{m.booked_by || "—"}</span>
            </div>
          ))}
          {hiddenCount > 0 && (
            <button onClick={() => setExpanded(true)} style={styles.laneHistoryExpandBtn}>
              Show {hiddenCount} more
            </button>
          )}
          {expanded && matches.length > LANE_HISTORY_COLLAPSED_COUNT && (
            <button onClick={() => setExpanded(false)} style={styles.laneHistoryExpandBtn}>
              Show fewer
            </button>
          )}
        </>
      ) : (
        <div style={styles.laneHistoryLoading}>No recent loads found on this lane.</div>
      )}
    </div>
  );
}

function StatRow({ icon, label, value, warn, accent, danger }) {
  return (
    <div style={styles.statRow}>
      <span style={styles.statLabel}>
        {icon && <span style={styles.statIcon}>{icon}</span>}
        {label}
      </span>
      <span style={{ ...styles.statValue, color: danger ? "#dc2626" : warn ? "#f2a33d" : accent ? "#4cae6d" : "var(--ink)" }}>{value}</span>
    </div>
  );
}

// Compact expense/profit legend shown as a corner overlay on the expanded
// route map -- same numbers already in the route cards below the map, just
// visible without looking away from the map while comparing routes.
function LegendRow({ label, value, bold, color }) {
  if (value == null) return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "2px 0", fontWeight: bold ? 700 : 400 }}>
      <span style={{ color: "var(--muted)" }}>{label}</span>
      <span style={{ color: color || "var(--ink)" }}>${Math.round(value).toLocaleString()}</span>
    </div>
  );
}

// Map-tooltip key/value row -- label-left/value-right, bolded and
// non-wrapping since these live in a small Leaflet tooltip box (fixed
// min-width via the .leaflet-tooltip CSS rule). Without the weight
// difference, a label and its value are the same
// color/weight/size and the row reads as one run-on phrase instead of a
// "what this is" / "what it's worth" pair -- which was the actual
// complaint: hovering a stop showed a wall of text with no way to tell
// which part was the fact and which was the number.
function TooltipRow({ label, value }) {
  if (value == null) return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "1.5px 0" }}>
      <span style={{ color: "var(--muted)" }}>{label}</span>
      <span style={{ color: "var(--ink)", fontWeight: 600, whiteSpace: "nowrap", marginLeft: 8 }}>{value}</span>
    </div>
  );
}

// Shared fact list for every fuel-stop tooltip (Route tab, Log Broker
// Call, Check Load all render the exact same simulate_trip_stops-produced
// stop shape) -- one place to keep them in sync instead of three
// hand-copied blocks silently drifting apart.
function FuelStopTooltipRows({ s, isCombined }) {
  const price = s.price_per_gallon != null ? Number(s.price_per_gallon) : null;
  const tax = s.state_tax_rate != null ? Number(s.state_tax_rate) : null;
  const ifta = s.tax_adjusted_price_per_gallon != null ? Number(s.tax_adjusted_price_per_gallon) : null;
  return (
    <>
      {price != null && <TooltipRow label="Your price (no tax)" value={`$${price.toFixed(2)}/gal`} />}
      {tax != null && <TooltipRow label={`${s.state || "State"} diesel tax`} value={`$${tax.toFixed(2)}/gal`} />}
      {ifta != null && <TooltipRow label="True cost incl. IFTA" value={`$${ifta.toFixed(2)}/gal`} />}
      {s.estimated_cost != null && <TooltipRow label="Fill-up" value={`$${s.estimated_cost} (${s.gallons_purchased} gal)`} />}
      {s.distance_from_route_miles != null && <TooltipRow label="Off-route" value={`${s.distance_from_route_miles} mi`} />}
      {s.wide_search_fallback && (
        <TooltipRow label="Note" value="nothing closer -- widened search for a sparse stretch" />
      )}
      {isCombined && s.break_hours != null && <TooltipRow label="Combined HOS break" value={`${s.break_hours}h -- no separate stop`} />}
      {s.approximate && <TooltipRow label="Note" value="approximate location" />}
      {s.distance_since_prev_stop_miles != null && (
        <TooltipRow label="Since last stop" value={`${s.distance_since_prev_stop_miles} mi · ${s.hours_since_prev_stop}h`} />
      )}
      {s.daily_hours_remaining != null && (
        <TooltipRow
          label="HOS left here"
          value={`${s.daily_hours_remaining}h daily${s.cycle_hours_remaining != null ? ` / ${s.cycle_hours_remaining}h weekly` : ""}`}
        />
      )}
    </>
  );
}

// Shared fact list for every HOS rest-break tooltip, same reasoning as
// FuelStopTooltipRows.
function RestZoneTooltipRows({ s }) {
  return (
    <>
      {s.break_hours != null && <TooltipRow label="Break required" value={`${s.break_hours}h`} />}
      {s.distance_since_prev_stop_miles != null && (
        <TooltipRow label="Since last stop" value={`${s.distance_since_prev_stop_miles} mi · ${s.hours_since_prev_stop}h`} />
      )}
      {s.daily_hours_remaining != null && (
        <TooltipRow
          label="HOS left here"
          value={`${s.daily_hours_remaining}h daily${s.cycle_hours_remaining != null ? ` / ${s.cycle_hours_remaining}h weekly` : ""}`}
        />
      )}
      {s.nearby_options?.length > 0 && (
        <TooltipRow label="Nearby options" value={s.nearby_options.map((o) => o.name).filter(Boolean).join(", ")} />
      )}
    </>
  );
}

// Shared by both the Log Broker Call and Check Load expanded-map legends --
// used to be two independently-hand-rolled copies that dumped every number
// (distance, trip time, both ETAs, stop counts, a 4-line cost breakdown,
// total, profit -- ~11 rows per route) always-visible, which is exactly
// what read as "too much info, confusing" once more than one or two routes
// were shown at once. Redesigned around a simple hierarchy: the number a
// dispatcher actually decides on (total cost, profit, $/mi, $/hr) stays
// visible always; everything else (timing, stop counts, the cost
// breakdown) sits behind ONE shared toggle for the whole panel, so
// multiple routes stay aligned and comparable instead of each opening
// independently.
function RouteLegendPanel({ routes, hiddenRouteTypes, colorFor, chosenRouteType, tripPlans, offeredRate, originTz, destTz, onSelectRoute, logAction }) {
  const [showDetails, setShowDetails] = useState(false);
  const legendRoutesUnsorted = routes.filter((r) => !hiddenRouteTypes.has(r.route_type));
  if (!legendRoutesUnsorted.length) return null;
  const hasOffer = offeredRate != null && !Number.isNaN(offeredRate);

  // Cheapest always first, regardless of what order the API happened to
  // return routes in -- still used to badge "Cheapest", just no longer used
  // to privilege one route's column over the others visually.
  const legendRoutes = [...legendRoutesUnsorted].sort((a, b) => (a.total_cost ?? Infinity) - (b.total_cost ?? Infinity));
  const cheapestType = legendRoutes[0]?.route_type;
  const fastestType = legendRoutes.reduce((best, r) => {
    const h = tripPlans[r.route_type]?.total_trip_hours ?? r.driving_hours;
    const bestH = best.h;
    return h != null && h < bestH ? { type: r.route_type, h } : best;
  }, { type: null, h: Infinity }).type;

  function routeName(route) {
    return route.is_toll_avoidant ? "Toll-free route" : `Route ${routes.indexOf(route) + 1}`;
  }

  // No route reads as selected until the dispatcher actually clicks one --
  // explicit ask: "no route should be selected in the beginning ... at the
  // moment I select a route" is when it becomes chosen, not a silent
  // pre-pick a dispatcher might not even notice happened. chosenRouteType
  // itself starts (and stays) null until a real click -- see
  // fetchBrokerRoutes'/fetchCheckLoadRoutes' own reset -- so this only ever
  // resolves a route once one legitimately matches, never falls back to
  // "cheapest" for DISPLAY purposes the way the booking-time code still
  // safely does as its own last-resort net (see cheapestRouteType's own
  // comment) if the dispatcher logs without ever picking one here.
  const activeType = chosenRouteType && legendRoutes.some((r) => r.route_type === chosenRouteType)
    ? chosenRouteType
    : null;
  const activeRoute = activeType ? legendRoutes.find((r) => r.route_type === activeType) : null;

  // Panel widens with the number of route columns, capped so it never runs
  // off the map at 5+ routes.
  const panelWidth = Math.min(96 + legendRoutes.length * 104, 640);

  // Row-by-row spec-sheet layout, not per-route cards -- explicit ask: a
  // dispatcher needs to read "Fuel: $23 | $45 | ..." straight across, not
  // hunt the same stat inside N separate stacked cards. Each route is a
  // full grid COLUMN (header cell down through every value cell below it),
  // and the whole column is one click target for selecting that route --
  // clicking any cell in a route's column selects it, not just the header.
  const negByType = {};
  legendRoutes.forEach((r) => { negByType[r.route_type] = computeNegotiation(r.total_cost, r.distance_miles, r.driving_hours, offeredRate); });
  const weeklyBalanceAfterByType = {};
  legendRoutes.forEach((r) => {
    weeklyBalanceAfterByType[r.route_type] = hasOffer && r.weekly_balance_before != null
      ? r.weekly_balance_before + negByType[r.route_type].profit
      : r.weekly_balance_after;
  });

  const rows = [
    { key: "cost", label: "Total cost", big: true, fmt: (r) => `$${Math.round(r.total_cost).toLocaleString()}` },
    ...(hasOffer ? [{
      key: "profit", label: "Profit", big: true,
      fmt: (r) => {
        const p = negByType[r.route_type].profit;
        if (p == null) return "—";
        return `${p < 0 ? "-" : "+"}$${Math.abs(Math.round(p)).toLocaleString()}`;
      },
      color: (r) => {
        const p = negByType[r.route_type].profit;
        if (p == null) return "var(--ink)";
        return p < 0 ? "#f2a33d" : "#4cae6d";
      },
    }] : []),
    { key: "permile", label: "$/mi", fmt: (r) => (r.distance_miles ? `$${(r.total_cost / r.distance_miles).toFixed(2)}` : "—") },
    {
      key: "week", label: "Truck's week",
      fmt: (r) => {
        const after = weeklyBalanceAfterByType[r.route_type];
        if (r.weekly_balance_before == null) return "No loads yet";
        if (after == null) return `${r.weekly_balance_before >= 0 ? "+" : "-"}$${Math.abs(Math.round(r.weekly_balance_before)).toLocaleString()}`;
        return `→ ${after >= 0 ? "+" : "-"}$${Math.abs(Math.round(after)).toLocaleString()}`;
      },
      color: (r) => {
        const after = weeklyBalanceAfterByType[r.route_type];
        if (after == null) return "var(--ink)";
        return after >= 0 ? "#4cae6d" : "#f2a33d";
      },
    },
    ...(showDetails ? [
      { key: "distance", label: "Distance", fmt: (r) => (r.distance_miles != null ? `${Math.round(r.distance_miles)} mi` : "—") },
      {
        key: "triptime", label: "Trip time",
        fmt: (r) => {
          const h = tripPlans[r.route_type]?.total_trip_hours ?? r.driving_hours;
          return h != null ? `${h}h` : "—";
        },
      },
      { key: "tolls", label: "Tolls", fmt: (r) => `$${Math.round(r.toll_cost || 0).toLocaleString()}` },
      { key: "fuel", label: "Fuel", fmt: (r) => `$${Math.round(r.fuel_cost || 0).toLocaleString()}` },
      { key: "driverpay", label: "Driver pay", fmt: (r) => `$${Math.round(r.driver_cost || 0).toLocaleString()}` },
      { key: "maintenance", label: "Maintenance", fmt: (r) => `$${Math.round(r.maintenance_cost || 0).toLocaleString()}` },
      { key: "insurance", label: "Insurance", fmt: (r) => `$${Math.round(r.insurance_cost || 0).toLocaleString()}` },
      { key: "officepayroll", label: "Office payroll", fmt: (r) => `$${Math.round(r.office_payroll_cost || 0).toLocaleString()}` },
      ...(legendRoutes.some((r) => (r.wait_cost || 0) > 0)
        ? [{ key: "wait", label: "Wait time", fmt: (r) => (r.wait_cost > 0 ? `$${Math.round(r.wait_cost).toLocaleString()}` : "—") }]
        : []),
      {
        key: "stops", label: "Stops",
        fmt: (r) => {
          const tp = tripPlans[r.route_type];
          if (!tp) return "—";
          if (tp.incomplete_reason) return "incomplete";
          return tp.hos_checked === false ? `${tp.fuel_stop_count} fuel · HOS n/a` : `${tp.fuel_stop_count} fuel · ${tp.rest_zone_count} HOS`;
        },
      },
      {
        key: "eta", label: "Delivery ETA",
        fmt: (r) => {
          const tp = tripPlans[r.route_type];
          const eta = tp?.eta ? formatDateFacilityLocal(parseUtcNaive(tp.eta), destTz) : null;
          return eta || "—";
        },
      },
    ] : []),
  ];

  return (
    <div style={{ ...styles.legendPanel, width: panelWidth }}>
      {hasOffer && (
        <div style={styles.legendOfferedRate}>
          Negotiating at <b>${Math.round(offeredRate).toLocaleString()}</b>
        </div>
      )}
      <div style={styles.legendPickHeading}>
        {legendRoutes.length > 1 ? `${legendRoutes.length} route options — pick one` : "Route"}
      </div>
      <button onClick={() => setShowDetails((v) => !v)} style={styles.legendDetailsToggle}>
        {showDetails ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        {showDetails ? "Fewer rows" : "More rows (fuel, tolls, timing)"}
      </button>

      <div style={{ ...styles.legendTable, gridTemplateColumns: `74px repeat(${legendRoutes.length}, 1fr)` }}>
        <div />
        {legendRoutes.map((route) => {
          const isChosen = route.route_type === activeType;
          return (
            <div
              key={`hdr-${route.route_type}`}
              onClick={onSelectRoute ? () => onSelectRoute(route.route_type) : undefined}
              role={onSelectRoute ? "button" : undefined}
              tabIndex={onSelectRoute ? 0 : undefined}
              onKeyDown={onSelectRoute ? (e) => {
                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelectRoute(route.route_type); }
              } : undefined}
              aria-pressed={onSelectRoute ? isChosen : undefined}
              title={onSelectRoute ? "Select this route to log" : undefined}
              style={{
                ...styles.legendTableHeaderCell,
                ...(onSelectRoute ? { cursor: "pointer" } : {}),
                ...(isChosen ? styles.legendColumnSelected : {}),
              }}
            >
              <span style={{ ...styles.legendRadioOuter, borderColor: isChosen ? "var(--brand)" : "var(--line)" }}>
                {isChosen && <span style={styles.legendRadioInner} />}
              </span>
              <span style={{ ...styles.legendDot, background: colorFor(route.route_type) }} />
              <span style={styles.legendRouteLabel}>{routeName(route)}</span>
              {route.route_type === cheapestType && <span style={styles.legendCategoryBadge}>Cheapest</span>}
              {route.route_type === fastestType && <span style={{ ...styles.legendCategoryBadge, color: "#5b9bd5" }}>Fastest</span>}
            </div>
          );
        })}

        {rows.map((row) => (
          <React.Fragment key={row.key}>
            <div style={{ ...styles.legendTableRowLabel, ...(row.big ? styles.legendTableLabelBig : {}) }}>{row.label}</div>
            {legendRoutes.map((route) => {
              const isChosen = route.route_type === activeType;
              return (
                <div
                  key={`${row.key}-${route.route_type}`}
                  onClick={onSelectRoute ? () => onSelectRoute(route.route_type) : undefined}
                  style={{
                    ...styles.legendTableValueCell,
                    ...(row.big ? styles.legendTableValueBig : {}),
                    ...(onSelectRoute ? { cursor: "pointer" } : {}),
                    ...(isChosen ? styles.legendColumnSelected : {}),
                    ...(row.color ? { color: row.color(route) } : {}),
                  }}
                >
                  {row.fmt(route)}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {legendRoutes.some((r) => weeklyBalanceAfterByType[r.route_type] != null && weeklyBalanceAfterByType[r.route_type] >= 0
        && r.weekly_balance_before != null && Math.max(0, weeklyBalanceAfterByType[r.route_type]) - Math.max(0, r.weekly_balance_before) > 0
        && r.route_type === activeType) && (
        <div style={styles.legendCoveredBadge}>
          <Check size={11} strokeWidth={3} />
          Weekly truck cost covered by the selected route
        </div>
      )}

      {logAction && (
        <div style={styles.legendLogSection}>
          {logAction.done ? (
            <div style={styles.legendLogDone}>✓ {logAction.doneLabel || "Logged"}</div>
          ) : !activeRoute ? (
            // Nothing picked yet -- the button stays disabled rather than
            // silently logging against a route the dispatcher never
            // actually looked at (the booking code's own cheapest-route
            // fallback exists purely as a safety net for THAT case, not as
            // license to skip picking here).
            <div style={styles.legendLogPrompt}>Select a route above to continue</div>
          ) : (
            <button
              onClick={logAction.onClick}
              disabled={logAction.loading}
              style={{ ...styles.legendLogBtn, ...(logAction.loading ? styles.legendLogBtnDisabled : {}) }}
            >
              {logAction.loading ? (logAction.loadingLabel || "Logging…") : `${logAction.label} -- ${routeName(activeRoute)}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// The load-intake and route-explanation agents both write plain prose with
// **bold** and "- " bullet lines (asked for in their prompts as literal
// Markdown), but nothing rendered it -- dispatchers were reading raw
// asterisks. Only handles the subset those two prompts actually produce
// (bold spans, bullet blocks, paragraphs, a solo-bold line as a mini
// heading) rather than pulling in a full Markdown dependency for it.
function renderInlineBold(text, keyPrefix) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={`${keyPrefix}-b${i}`} style={{ color: "var(--brand)", fontWeight: 700 }}>
        {part}
      </strong>
    ) : (
      part
    )
  );
}

function NarrativeText({ text }) {
  if (!text) return null;
  const blocks = text.trim().split(/\n\s*\n/);
  return (
    <div>
      {blocks.map((block, bi) => {
        const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
        if (!lines.length) return null;

        if (lines.every((l) => /^[-*]\s+/.test(l))) {
          return (
            <ul key={bi} style={styles.narrativeList}>
              {lines.map((l, li) => (
                <li key={li} style={styles.narrativeListItem}>
                  {renderInlineBold(l.replace(/^[-*]\s+/, ""), `${bi}-${li}`)}
                </li>
              ))}
            </ul>
          );
        }

        if (lines.length === 1 && /^\*\*(.+)\*\*$/.test(lines[0])) {
          return (
            <div key={bi} style={styles.narrativeHeading}>
              {lines[0].replace(/^\*\*(.+)\*\*$/, "$1")}
            </div>
          );
        }

        return (
          <p key={bi} style={styles.narrativeParagraph}>
            {lines.map((l, li) => (
              <React.Fragment key={li}>
                {li > 0 && <br />}
                {renderInlineBold(l, `${bi}-${li}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

function SectionDivider({ label, icon }) {
  return (
    <div style={styles.sectionDivider}>
      {icon}
      <span>{label}</span>
    </div>
  );
}

function ToggleSwitch({ on, onClick, color = "var(--brand)" }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: 30, height: 17, borderRadius: 999, border: "none", cursor: "pointer",
        position: "relative", padding: 0, flexShrink: 0,
        background: on ? color : "var(--line)", transition: "background .18s ease",
      }}
    >
      <span
        style={{
          position: "absolute", top: 2, left: on ? 15 : 2, width: 13, height: 13, borderRadius: "50%",
          background: "#ffffff", transition: "left .18s ease",
        }}
      />
    </button>
  );
}

// Shared by the Check Load and Log Broker Call wizards' final assign step
// -- captures "this is his last load" right alongside the commit click
// itself, since the two flows are otherwise unrelated modals with their
// own independent state/reset cycles (see checkLoadPtoEnabled's own
// comment for why this isn't a single shared toggle instead).
// Replaces the earlier inline checkbox version (disliked -- read as clutter
// sitting in the form the whole time). Now gated entirely behind the actual
// commit click: nothing PTO-related is visible until the dispatcher tries
// to book, at which point this asks once, up front. Deliberately has no
// backdrop/× dismiss once expanded (entering the datetime step) -- every
// way out of that state is an explicit button, since an accidental
// dismiss-to-book here would silently create a real load. From the
// un-expanded Yes/No step, "No" is the equivalent of a dismiss and is
// exposed as its own explicit button for the same reason.
// Sits over the route map in both assignment flows -- the fuel stops shown
// here are exactly what PushFuelStopsPopup will compose and offer to text
// the driver once "Assign"/"Log to Truck" is clicked, so a dispatcher who
// doesn't like a station needs to catch it HERE (there's no separate
// "review fuel stops" screen -- the map view IS the review).
// "N station(s) excluded -- reset" -- surfaced so a dispatcher who's
// excluded a couple of stations while comparing routes doesn't forget
// they've narrowed the fuel-station pool for this quote. `corner` renders
// it as a small absolute-positioned pill over the map (the maximized/
// popup view, where FuelStopReviewBanner/RouteLegendPanel already use
// this same overlay convention); without it, renders inline below the
// small map preview instead.
function ExcludedStationsChip({ excluded, onReset, corner }) {
  if (!excluded?.length) return null;
  const pill = (
    <div
      style={{
        pointerEvents: "auto", display: "flex", alignItems: "center", gap: 8,
        background: "var(--card)", border: "1px solid var(--line)", borderRadius: 999,
        fontSize: 11, fontWeight: 600, color: "var(--ink-2)", padding: "5px 10px",
        boxShadow: corner ? "0 2px 8px rgba(0,0,0,0.25)" : "none",
      }}
    >
      {excluded.length} station{excluded.length === 1 ? "" : "s"} excluded
      <button
        onClick={onReset}
        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--brand)", fontWeight: 700, fontSize: 11, padding: 0 }}
      >
        Reset
      </button>
    </div>
  );
  if (!corner) return <div style={{ marginTop: 6 }}>{pill}</div>;
  return (
    <div style={{ position: "absolute", bottom: 10, left: 10, zIndex: 1100, pointerEvents: "none" }}>
      {pill}
    </div>
  );
}

// fuelOptimizationUnavailable/fuelOptimizationMessage: from this route's own
// plan_full_trip response (trip_planning.py's simulate_trip_stops) -- True
// whenever the truck has no real fuel reading synced from Motive/Samsara
// yet (current_fuel_gallons IS NULL, not just 0). Swaps the normal
// exclude-a-station tip for an honest "we don't know" notice instead of
// silently showing zero fuel stops with no explanation -- confirmed real
// this read as the feature being broken, not as fuel planning correctly
// declining to guess on missing data.
function FuelStopReviewBanner({ fuelOptimizationUnavailable, fuelOptimizationMessage }) {
  return (
    // Outer box only CENTERS the pill below -- it must stop short of the
    // route legend panel (legendPanel: right:12, width:232 -- reserves the
    // rightmost ~256px), not stretch behind it. Confirmed real: at full
    // width (right:10) this banner shared the exact same top offset as the
    // legend panel and, at a higher z-index, painted straight over its own
    // header ("Negotiating at $X" / "N route options -- pick one"), hiding
    // it entirely. pointerEvents:none on this wrapper keeps the empty
    // space around the pill from blocking map drag/zoom underneath it.
    <div
      style={{
        position: "absolute", top: 10, left: 50, right: 256, zIndex: 1100,
        display: "flex", justifyContent: "center", pointerEvents: "none",
      }}
    >
      <div
        style={{
          pointerEvents: "auto",
          display: "flex", alignItems: "center", gap: 8, maxWidth: "100%",
          background: fuelOptimizationUnavailable ? "var(--card)" : "#e0553d",
          color: fuelOptimizationUnavailable ? "var(--ink-2)" : "#fff",
          border: fuelOptimizationUnavailable ? "1px solid var(--line)" : "none",
          fontSize: 12, fontWeight: 600, padding: "8px 14px", borderRadius: 8,
          boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
        }}
      >
        <AlertTriangle size={15} style={{ flexShrink: 0 }} />
        {fuelOptimizationUnavailable ? (
          <span>{fuelOptimizationMessage || "No fuel information for this truck -- we can't plan fuel stops until a real reading syncs from Motive/Samsara."}</span>
        ) : (
          <span>Don't like a fuel station? <b>Click it and exclude it</b> -- we'll replan around it instantly. The driver gets these by text the second you assign this load.</span>
        )}
      </div>
    </div>
  );
}

// Step 1 of the load-assignment wizard, shown before PtoConfirmPopup --
// lets the dispatcher review/edit the fuel-stop SMS before it reaches the
// driver, or skip sending it entirely. Never blocks assignment on the send
// succeeding: Continue is always available once a Send attempt resolves
// (or immediately after Skip), so a RingCentral outage can't strand a
// dispatcher mid-booking.
function PushFuelStopsPopup({
  driverName, message, onMessageChange, sending, sendResult, onSend, onSkip, onContinue, onBack,
  driverPhone, phoneValue, onPhoneChange,
}) {
  const resolved = sendResult !== null;
  // driverPhone is the driver's phone as it was on file when this popup
  // opened (not what's typed into the field below), so this stays true
  // for the whole life of one open/send cycle even as the dispatcher types.
  const needsPhone = !driverPhone;
  const canSend = !needsPhone || (phoneValue || "").trim().length > 0;
  return createPortal(
    <div style={{ ...styles.modalOverlay, zIndex: 2500 }}>
      <div style={{ ...styles.modalCard, width: 420, padding: 18 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>
            Push fuel stops to {driverName}?
          </div>
          {onBack && (
            <button onClick={onBack} style={styles.modalBackBtn} disabled={sending} title="Back">
              <ArrowLeft size={17} />
            </button>
          )}
        </div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>
          Review and edit before sending -- this is exactly what {driverName?.split(" ")[0] || "the driver"} will receive by text.
        </div>
        {needsPhone && !resolved && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: "#f2a33d", fontWeight: 600, display: "block", marginBottom: 5 }}>
              No phone number on file for {driverName?.split(" ")[0] || "this driver"} -- enter one to send (saved to their profile).
            </label>
            <input
              type="tel"
              value={phoneValue}
              onChange={(e) => onPhoneChange(e.target.value)}
              disabled={sending}
              placeholder="e.g. +1 555 123 4567"
              style={{
                width: "100%", fontSize: 13, padding: "8px 10px", border: "1px solid var(--line)",
                borderRadius: 7, color: "var(--ink)", background: "var(--bg)", boxSizing: "border-box",
              }}
            />
          </div>
        )}
        <textarea
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          disabled={sending || resolved}
          rows={7}
          style={{
            width: "100%", fontSize: 13, padding: "9px 10px", border: "1px solid var(--line)",
            borderRadius: 7, color: "var(--ink)", background: "var(--bg)", marginBottom: 12,
            boxSizing: "border-box", fontFamily: "inherit", resize: "vertical",
            opacity: sending || resolved ? 0.7 : 1,
          }}
        />
        {resolved && (
          <div
            style={{
              fontSize: 12, fontWeight: 600, marginBottom: 12, padding: "8px 10px", borderRadius: 7,
              background: sendResult.sent ? "rgba(20,160,141,0.12)" : "rgba(242,163,61,0.12)",
              color: sendResult.sent ? "var(--brand)" : "#f2a33d",
            }}
          >
            {sendResult.sent
              ? `Sent to ${sendResult.to}${sendResult.used_test_phone ? " (test phone)" : ""}.`
              : `Not sent: ${sendResult.reason || sendResult.error || "unknown error"}`}
          </div>
        )}
        <div style={{ display: "flex", gap: 10 }}>
          {!resolved ? (
            <>
              <button onClick={onSkip} style={{ ...styles.popupSecondaryBtn, flex: 1 }} disabled={sending}>Skip</button>
              <button
                onClick={onSend}
                disabled={sending || !canSend}
                title={!canSend ? "Enter a phone number to send" : undefined}
                style={{ ...styles.explainBtn, flex: 1, opacity: sending || !canSend ? 0.5 : 1 }}
              >
                {sending ? "Sending…" : "Send SMS"}
              </button>
            </>
          ) : (
            <button onClick={onContinue} style={{ ...styles.explainBtn, flex: 1 }}>Continue</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// Prompts dispatch for a truck's real fuel tank capacity (max gallons) when it's
// unconfirmed. Integer-only, plausibility-bounded (50-300), with an explicit
// "are you sure" step. "Skip" leaves it unset (optimizer assumes 200). On
// confirm, the parent PUTs it to the truck and re-runs routing with the real
// number.
function TankCapacityModal({ unit, onConfirm, onSkip, onCancel }) {
  const [val, setVal] = useState("");
  const [confirming, setConfirming] = useState(false);
  const n = parseInt(val, 10);
  const valid = /^\d+$/.test(val.trim()) && n >= 50 && n <= 300;
  return createPortal(
    <div style={{ ...styles.modalOverlay, zIndex: 2600 }} onClick={onCancel}>
      <div style={{ ...styles.modalCard, width: 420, padding: 18, position: "relative" }} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onCancel}
          aria-label="Close"
          title="Close"
          style={{ position: "absolute", top: 8, right: 10, background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 20, lineHeight: 1, padding: 2 }}
        >
          ×
        </button>
        {!confirming ? (
          <>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)", marginBottom: 6 }}>
              Fuel tank capacity needed — Truck {unit}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--muted)", marginBottom: 12 }}>
              We don't have this truck's tank size yet. Please contact the driver for the max
              gallons the tank holds — it lets us optimize the fuel stops accurately. Whole gallons only.
            </div>
            <input
              type="text"
              inputMode="numeric"
              value={val}
              onChange={(e) => setVal(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="e.g. 180"
              autoFocus
              style={{
                width: "100%", fontSize: 15, padding: "9px 10px", border: "1px solid var(--line)",
                borderRadius: 7, color: "var(--ink)", background: "var(--bg)", boxSizing: "border-box", marginBottom: 6,
              }}
            />
            {val && !valid && (
              <div style={{ fontSize: 11, color: "#e0745d", marginBottom: 8 }}>Enter a whole number between 50 and 300.</div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
              <button onClick={onSkip} style={{ ...styles.popupSecondaryBtn, flex: 1 }}>Skip — assume 200</button>
              <button
                onClick={() => valid && setConfirming(true)}
                disabled={!valid}
                style={{ ...styles.explainBtn, flex: 1, opacity: valid ? 1 : 0.5, cursor: valid ? "pointer" : "not-allowed" }}
              >
                Confirm
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)", marginBottom: 12 }}>
              Confirm — Truck {unit} holds <span style={{ color: "var(--brand)" }}>{n} gallons</span>?
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirming(false)} style={{ ...styles.popupSecondaryBtn, flex: 1 }}>Back</button>
              <button onClick={() => onConfirm(n)} style={{ ...styles.explainBtn, flex: 1 }}>Yes, it's {n}</button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

function PtoConfirmPopup({
  driverName, expanded, onYes, onNo, until, onUntilChange, note, onNoteChange,
  onBack, onConfirm, confirmDisabled, confirming, onBackStep,
}) {
  // Portal to document.body, not rendered inline -- the wizard modals this
  // opens on top of are ALSO portal-rendered at document.body with their
  // own high z-index (see checkLoadMapModalOpen's 2000), so a plain nested
  // render here got trapped under them regardless of its own z-index
  // (confirmed live: an ancestor in that tree forms its own stacking
  // context). Portalling is what actually gets this above them.
  return createPortal(
    <div style={{ ...styles.modalOverlay, zIndex: 2500 }}>
      <div style={{ ...styles.modalCard, width: 380, padding: 18 }}>
        {!expanded ? (
          <>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)", lineHeight: 1.4 }}>
                Is {driverName} going on time off after this load?
              </div>
              {onBackStep && (
                <button onClick={onBackStep} style={styles.modalBackBtn} title="Back">
                  <ArrowLeft size={17} />
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={onNo} style={{ ...styles.popupSecondaryBtn, flex: 1 }}>No, just book it</button>
              <button onClick={onYes} style={{ ...styles.explainBtn, flex: 1 }}>Yes</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)" }}>{driverName}'s time off</div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3, marginBottom: 14 }}>
              Starts the moment this load delivers.
            </div>
            <label style={{ fontSize: 11, color: "var(--muted)", display: "block", marginBottom: 10 }}>
              Available again at
              <input
                type="datetime-local"
                value={until}
                onChange={(e) => onUntilChange(e.target.value)}
                style={{
                  display: "block", width: "100%", marginTop: 4, fontSize: 13, padding: "7px 9px",
                  border: "1px solid var(--line)", borderRadius: 7, color: "var(--ink)", background: "var(--bg)",
                  boxSizing: "border-box",
                }}
              />
            </label>
            <input
              type="text"
              placeholder="Note (optional) -- e.g. home for the week"
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              style={{
                width: "100%", fontSize: 13, padding: "7px 9px", border: "1px solid var(--line)",
                borderRadius: 7, color: "var(--ink)", background: "var(--bg)", marginBottom: 16, boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={onBack} style={{ ...styles.popupSecondaryBtn, flex: 1 }} disabled={confirming}>Back</button>
              <button
                onClick={onConfirm}
                disabled={confirmDisabled || confirming}
                style={{ ...styles.explainBtn, flex: 1, opacity: confirmDisabled || confirming ? 0.5 : 1 }}
              >
                {confirming ? "Booking…" : "Confirm & book"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// Fires the moment a dispatcher tries to assign a load (Check Load or Log
// Broker Call) to a driver whose latest 'pto' row hasn't been confirmed
// returned -- see guardUnconfirmedPtoReturn's own comment for why this has
// to be an explicit, blocking step rather than something inferred from the
// assignment itself. "Cancel" leaves everything untouched: the load stays
// unassigned and the PTO row stays unconfirmed, exactly as if the dispatcher
// had never clicked Assign.
function PtoReturnConfirmPopup({ driverName, ptoRow, onConfirm, onCancel, confirming }) {
  const fmt = (v) => {
    const d = parseUtcNaive(v);
    return d
      ? d.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "numeric", minute: "2-digit" })
      : "—";
  };
  return createPortal(
    <div style={{ ...styles.modalOverlay, zIndex: 2500 }}>
      <div style={{ ...styles.modalCard, width: 400, padding: 18 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
          <AlertTriangle size={18} color="#ef4444" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)", lineHeight: 1.4 }}>
            {driverName} was on home time -- confirm they're back?
          </div>
        </div>
        <div style={{ fontSize: 12.5, color: "var(--muted)", lineHeight: 1.5, marginBottom: 16 }}>
          Home time {fmt(ptoRow.start_datetime)} – {fmt(ptoRow.end_datetime)}, never confirmed returned.
          Confirming updates the Load Board and clears the overdue flag before this load gets assigned.
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onCancel} style={{ ...styles.popupSecondaryBtn, flex: 1 }} disabled={confirming}>Cancel</button>
          <button
            onClick={onConfirm}
            disabled={confirming}
            style={{ ...styles.explainBtn, flex: 1, opacity: confirming ? 0.5 : 1 }}
          >
            {confirming ? "Confirming…" : "Confirm & assign"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Shared "are you sure" dialog for the three load-lifecycle actions that
// can't be undone from the app (Cancel Load, Deliver Load, Confirm
// pickup/arrival) -- these previously fired straight from the button
// click with zero confirmation, so one misclick against the wrong load
// (easy to do from the dense candidate-card lists) silently cancelled or
// closed out a real trip. Deliberately its own styled card instead of
// window.confirm() -- a bare browser confirm() carries no branding, can't
// show which load/lane is affected, and (being synchronous) blocks the
// whole tab while open.
function ConfirmActionModal({ open, tone = "danger", title, description, confirmLabel, cancelLabel = "Never mind", busy, onConfirm, onCancel }) {
  if (!open) return null;
  const paletteByTone = {
    danger: { fg: "#e05d5d", bg: "rgba(224,93,93,0.14)", border: "rgba(224,93,93,0.35)", Icon: AlertTriangle },
    success: { fg: "#4cae6d", bg: "rgba(76,174,109,0.14)", border: "rgba(76,174,109,0.35)", Icon: Check },
  };
  const p = paletteByTone[tone] || paletteByTone.danger;
  const { Icon } = p;
  return createPortal(
    <div
      className="cam-overlay-in"
      style={{ ...styles.modalOverlay, zIndex: 3000 }}
      onClick={onCancel}
    >
      <div
        className="cam-card-in"
        style={{ ...styles.modalCard, width: 400, padding: 22, textAlign: "center" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            width: 48, height: 48, borderRadius: "50%", margin: "0 auto 14px",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: p.bg, border: `1px solid ${p.border}`, color: p.fg,
          }}
        >
          <Icon size={22} />
        </div>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: "var(--ink)", marginBottom: 6 }}>{title}</div>
        {description && (
          <div style={{ fontSize: 12.5, color: "var(--muted)", lineHeight: 1.5, marginBottom: 20 }}>{description}</div>
        )}
        {/* Cancel is never disabled, even while busy -- dismissing only
            closes the dialog, it doesn't (and can't) cancel the in-flight
            request, which finishes in the background on its own either
            way. This app's dev backend has genuine slow moments, and a
            dispatcher trapped in a dialog with no way out during one of
            them is worse than letting them back out of a request that's
            still quietly completing. */}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onCancel} style={{ ...styles.popupSecondaryBtn, flex: 1 }}>
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              background: p.fg, color: "#fff", border: `1px solid ${p.fg}`,
              borderRadius: 7, padding: "9px 10px", fontSize: 12.5, fontWeight: 700,
              cursor: busy ? "default" : "pointer", opacity: busy ? 0.75 : 1,
            }}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Native <input type="datetime-local"> renders its placeholder/typed text
// (dd.mm.yyyy, mm/dd/yyyy, whatever) according to the OS's own locale, not
// this app's code or even the page's <html lang> -- Chromium's date/time
// form-control widget has never respected document/element lang for its
// own display text. Confirmed real: this box showed Bulgarian-style
// "дд.мм.гггг г." on a Bulgarian-locale Windows machine even though
// index.html declares lang="en". A JS date library is the only way to
// force mm/dd/yyyy regardless of the dispatcher's OS -- react-datepicker
// is already a dependency (see FormView.js's own use of it), so this
// mirrors that exact pattern rather than adding a new one. Keeps the
// same value/onChange(e) contract as the native input it replaces (a
// naive "YYYY-MM-DDTHH:mm" local-wall-clock string, no timezone) so every
// existing caller's state shape and downstream submit logic needed zero
// changes -- only the input tag itself was swapped.
function USDateTimeInput({ value, onChange, style }) {
  const selected = value ? new Date(value) : null;
  return (
    <DatePicker
      selected={Number.isNaN(selected?.getTime()) ? null : selected}
      onChange={(date) => {
        if (!date || Number.isNaN(date.getTime())) {
          onChange({ target: { value: "" } });
          return;
        }
        const pad = (n) => String(n).padStart(2, "0");
        const local = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
        onChange({ target: { value: local } });
      }}
      showTimeSelect
      timeIntervals={15}
      dateFormat="MM/dd/yyyy h:mm aa"
      placeholderText="mm/dd/yyyy h:mm AM/PM"
      customInput={<input style={style} />}
      portalId="sip-datepicker-portal"
      popperPlacement="bottom-start"
    />
  );
}

function TabButton({ label, active, onClick, dot }) {
  return (
    <button onClick={onClick} style={{ ...styles.tabButton, ...(active ? styles.tabButtonActive : {}) }}>
      {label}
      {dot && <span style={{ ...styles.tabDot, background: dot }} />}
    </button>
  );
}

const styles = {
  page: { display: "flex", flexDirection: "column", height: "100vh", background: "var(--bg)" },

  loadingScreen: {
    height: "100vh", width: "100vw", background: "var(--bg)", color: "var(--ink)",
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14,
  },
  loadingMark: {
    width: 44, height: 44, borderRadius: 12, display: "grid", placeItems: "center",
    background: "radial-gradient(120% 120% at 0% 0%, #14a08d 0%, #0d7d6f 100%)", color: "#fff",
    fontWeight: 800, fontSize: 16, boxShadow: "0 4px 20px rgba(20,160,141,0.4)",
  },
  loadingSpinner: {
    width: 26, height: 26, borderRadius: "50%",
    border: "3px solid var(--line)", borderTopColor: "var(--brand)",
    animation: "lv-spin 0.8s linear infinite",
  },
  loadingText: { fontSize: 14, fontWeight: 700, color: "var(--ink)" },
  loadingSubtext: { fontSize: 12, color: "var(--muted)" },

  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20,
    padding: "10px 18px", background: "var(--card)",
    borderBottom: "1px solid var(--line)", flexShrink: 0,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 12, flexShrink: 0 },
  exitBtn: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    border: "1px solid var(--line)", background: "var(--line)",
    color: "var(--ink)", borderRadius: 8, width: 30, height: 30, cursor: "pointer",
  },
  brandMark: {
    width: 32, height: 32, borderRadius: 8, display: "grid", placeItems: "center",
    background: "radial-gradient(120% 120% at 0% 0%, #14a08d 0%, #0d7d6f 100%)", color: "#ffffff", flexShrink: 0,
  },
  brandTitle: { color: "var(--ink)", fontWeight: 800, fontSize: 15 },
  brandSubtitle: { color: "var(--muted)", fontSize: 11, marginTop: 1 },

  headerStats: { display: "flex", alignItems: "center", gap: 22, flex: "1 1 auto", justifyContent: "center", minWidth: 0 },
  headerStat: { display: "flex", alignItems: "center", gap: 8 },
  headerStatValue: { fontFamily: "ui-monospace, monospace", fontWeight: 800, fontSize: 15, color: "var(--ink)", lineHeight: 1.2 },
  headerStatLabel: { fontSize: 10, color: "var(--muted)", whiteSpace: "nowrap" },
  headerDivider: { width: 1, height: 26, background: "var(--line)" },

  phStatGroup: { display: "flex", alignItems: "center", gap: 16 },
  phStatCell: { display: "flex", flexDirection: "column", alignItems: "center", minWidth: 46 },
  phStatValue: { fontFamily: "ui-monospace, monospace", fontWeight: 800, fontSize: 15, color: "var(--ink)", lineHeight: 1.2 },
  phStatLabel: { fontSize: 10, color: "var(--muted)", whiteSpace: "nowrap" },
  phStatMine: { fontSize: 9.5, fontWeight: 700, fontFamily: "ui-monospace, monospace", marginTop: 1 },

  rowFlags: { display: "flex", alignItems: "center", gap: 5 },

  // --- Stop Intelligence (panel groups, card chips, Current Load, drawer) ---
  siGroupHead: { display: "flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--muted)", padding: "8px 4px 3px" },
  siDotRed: { width: 7, height: 7, borderRadius: 999, background: "var(--danger)", display: "inline-block" },
  siDotAmber: { width: 7, height: 7, borderRadius: 999, background: "var(--warn)", display: "inline-block" },
  chipMissed: { fontSize: 9.5, fontWeight: 800, padding: "2px 6px", borderRadius: 6, color: "var(--danger)", background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.4)", whiteSpace: "nowrap" },
  chipFacility: { fontSize: 9.5, fontWeight: 700, padding: "2px 6px", borderRadius: 6, color: "var(--ink-2)", background: "rgba(120,120,120,0.1)", border: "1px solid var(--line)", whiteSpace: "nowrap" },
  chipFacilityEmph: { color: "#a16207", background: "rgba(234,179,8,0.14)", borderColor: "rgba(234,179,8,0.45)" },
  clMissedBox: { border: "1px solid rgba(220,38,38,0.4)", background: "rgba(220,38,38,0.08)", borderRadius: 10, padding: "10px 12px", marginBottom: 12 },
  clMissedTitle: { fontSize: 12.5, fontWeight: 800, color: "var(--danger)", marginBottom: 4 },
  clMissedBody: { fontSize: 12, color: "var(--ink-2)", lineHeight: 1.4 },
  clReviewBtn: { marginTop: 8, fontSize: 12, fontWeight: 700, color: "#fff", background: "var(--danger)", border: "none", borderRadius: 8, padding: "6px 14px", cursor: "pointer" },
  clFacilityBox: { border: "1px solid var(--line)", background: "rgba(120,120,120,0.06)", borderRadius: 10, padding: "10px 12px", marginBottom: 12 },
  clFacilityBoxEmph: { border: "1px solid rgba(234,179,8,0.45)", background: "rgba(234,179,8,0.1)" },
  clFacilityTitle: { fontSize: 11, fontWeight: 800, letterSpacing: ".04em", color: "var(--ink)", marginBottom: 3 },
  clFacilityLine: { fontSize: 12, color: "var(--ink-2)" },
  clFacilitySub: { fontSize: 11, color: "var(--muted)", marginTop: 3 },
  stopDrawer: { position: "absolute", top: 0, right: 0, bottom: 0, width: 420, maxWidth: "92vw", background: "var(--card)", borderLeft: "1px solid var(--line)", boxShadow: "-8px 0 24px rgba(0,0,0,0.18)", padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 },
  stopDrawerHead: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  stopDrawerBadge: { fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--danger)", background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.4)", borderRadius: 6, padding: "3px 8px" },
  stopDrawerTitle: { fontSize: 18, fontWeight: 800, color: "var(--ink)" },
  stopDrawerEvidence: { fontSize: 13.5, color: "var(--ink-2)", lineHeight: 1.5, margin: 0 },
  stopDrawerFacts: { display: "flex", flexDirection: "column", gap: 6, borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", padding: "10px 0" },
  sdRow: { display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "var(--ink)" },
  sdLabel: { color: "var(--muted)" },
  stopDrawerDetails: { fontSize: 12 },
  sdSummary: { cursor: "pointer", color: "var(--muted)", fontSize: 11.5, fontWeight: 600 },
  sdCodes: { marginTop: 6, fontFamily: "ui-monospace, monospace", fontSize: 11, color: "var(--muted)", lineHeight: 1.5 },
  stopDrawerActions: { display: "flex", gap: 10, marginTop: "auto", paddingTop: 12 },
  sdConfirmBtn: { flex: 1, fontSize: 13, fontWeight: 700, color: "#fff", background: "#0d7d6f", border: "none", borderRadius: 8, padding: "10px 14px", cursor: "pointer" },
  sdDismissBtn: { flex: "0 0 auto", fontSize: 13, fontWeight: 700, color: "var(--ink-2)", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: "10px 18px", cursor: "pointer" },

  wrap: { display: "flex", gap: 14, flex: "1 1 auto", minHeight: 0, padding: 14 },

  // ---- list column ----
  listCol: { width: 250, flexShrink: 0, display: "flex", flexDirection: "column", gap: 10, minHeight: 0 },
  fleetPanelTitle: {
    fontSize: 11, fontWeight: 800, color: "var(--muted)", textTransform: "uppercase",
    letterSpacing: ".05em", marginBottom: -4,
  },

  // Single joined pill -- one shared background/border instead of 3-4
  // separate boxes, so the active segment reads as "selected within a
  // control" rather than a row of disconnected buttons.
  statusSegment: {
    display: "flex", background: "var(--card-2)", border: "1px solid var(--line)",
    borderRadius: 9, padding: 3, gap: 2,
  },
  segmentBtn: {
    flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
    background: "transparent", border: "none", borderRadius: 7,
    padding: "6px 2px", cursor: "pointer", color: "var(--muted)",
  },
  segmentDot: { width: 6, height: 6, borderRadius: "50%", marginBottom: 1 },
  segmentCount: { fontSize: 14, fontWeight: 800, fontFamily: "ui-monospace, monospace" },
  segmentLabel: { fontSize: 9, textTransform: "uppercase", letterSpacing: ".04em" },

  extraFilterRow: { display: "flex", flexWrap: "wrap", gap: 5 },
  filterChip: {
    display: "flex", alignItems: "center", gap: 4, background: "var(--card)",
    border: "1px solid var(--line)", borderRadius: 999, padding: "4px 8px 4px 7px",
    fontSize: 10.5, fontWeight: 700, color: "var(--ink-2)", cursor: "pointer",
  },
  filterChipActive: { background: "rgba(224,93,93,0.16)", borderColor: "rgba(224,93,93,0.5)", color: "#f87171" },
  filterChipCount: { fontFamily: "ui-monospace, monospace", opacity: 0.75 },

  reminderPanel: {
    border: "1px solid var(--line)", borderRadius: 11, background: "var(--card-2)", overflow: "hidden",
    // Real bug, confirmed live on prod: this is a flex child of listCol
    // (a height-constrained flex column -- fleetPanelTitle, filter chips,
    // this panel, the search box, then the scrollable truck list). Once
    // that column's content outgrows its available height, flexbox
    // shrinks children to fit -- and per spec, `overflow: hidden` (needed
    // above for the collapsed header's rounded corners) strips this
    // element's automatic min-height-from-content floor, the one thing
    // that would otherwise stop it from being crushed. Every sibling here
    // keeps its natural size because none of them has overflow:hidden;
    // this one got squeezed from ~39px down to ~11px, clipping the bell
    // icon/text/count down to an invisible sliver while still technically
    // being present and non-zero in the DOM -- which is exactly why it
    // read as "hidden" rather than simply absent. flexShrink: 0 opts it
    // out of that shrink pass entirely, same as listCol itself already
    // does relative to ITS OWN parent.
    flexShrink: 0,
  },
  reminderPanelUrgent: { borderColor: "rgba(220,38,38,0.45)", boxShadow: "0 0 0 1px rgba(220,38,38,0.1)" },
  reminderHead: {
    display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", cursor: "pointer",
    userSelect: "none", background: "transparent", border: "none", width: "100%", textAlign: "left",
  },
  reminderBell: {
    width: 20, height: 20, borderRadius: 6, background: "var(--card)", color: "var(--muted)",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  reminderBellUrgent: { background: "rgba(220,38,38,0.14)", color: "var(--danger)" },
  reminderTitle: { fontSize: 12, fontWeight: 800, color: "var(--muted)", flex: 1 },
  reminderTitleUrgent: { color: "var(--ink)" },
  reminderCount: {
    fontFamily: "ui-monospace, monospace", fontSize: 11, fontWeight: 800, color: "var(--muted)",
    background: "var(--card)", borderRadius: 999, padding: "2px 8px",
  },
  reminderCountUrgent: { color: "var(--danger)", background: "rgba(220,38,38,0.12)" },

  reminderList: { display: "flex", flexDirection: "column" },
  reminderRow: {
    display: "flex", alignItems: "center", gap: 9, padding: "9px 11px", borderTop: "1px solid var(--line)", position: "relative",
  },
  reminderStripe: { position: "absolute", left: 0, top: 0, bottom: 0, width: 3 },
  reminderDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  reminderInfo: { flex: 1, minWidth: 0 },
  reminderInfoLine1: { fontSize: 11.5, fontWeight: 700, color: "var(--ink)" },
  reminderTier: { fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".03em" },
  reminderInfoLine2: { fontSize: 10, color: "var(--muted)", marginTop: 1, fontFamily: "ui-monospace, monospace" },
  reminderBtn: {
    fontSize: 10, fontWeight: 700, borderRadius: 6, padding: "5px 8px", whiteSpace: "nowrap",
    border: "1px solid", cursor: "pointer", background: "transparent", flexShrink: 0,
  },

  reminderToast: {
    display: "flex", gap: 9, alignItems: "flex-start", background: "var(--card)", border: "1px solid var(--line)",
    borderLeft: "3px solid var(--warn)", borderRadius: 10, boxShadow: "var(--shadow-lg)", padding: "11px 12px",
    width: 300, color: "var(--ink)", transition: "opacity .15s",
  },
  reminderToastTitle: { fontSize: 12, fontWeight: 700 },
  reminderToastBody: { fontSize: 11, color: "var(--muted)", marginTop: 3, lineHeight: 1.4 },
  reminderToastActions: { display: "flex", gap: 10, marginTop: 7 },
  reminderToastLink: {
    fontSize: 11, fontWeight: 700, color: "var(--brand-2)", cursor: "pointer",
    background: "none", border: "none", padding: 0, font: "inherit", fontWeight: 700,
  },
  reminderToastClose: { color: "var(--muted)", cursor: "pointer", flexShrink: 0 },

  searchWrap: { position: "relative", display: "flex", alignItems: "center" },
  searchIcon: { position: "absolute", left: 10, color: "var(--muted)" },
  searchInput: {
    width: "100%", boxSizing: "border-box", padding: "8px 10px 8px 30px", borderRadius: 7,
    border: "1px solid var(--line)", background: "var(--card)", color: "var(--ink)", fontSize: 12.5, outline: "none",
  },

  truckList: { display: "flex", flexDirection: "column", gap: 8, overflowY: "auto", minHeight: 0, paddingRight: 2 },
  truckRow: {
    background: "var(--card)", borderLeft: "3px solid transparent", border: "1px solid var(--line)",
    borderRadius: 9, padding: "9px 10px 8px", cursor: "pointer", boxShadow: "var(--shadow-xs)",
  },
  truckRowSelected: { background: "rgba(13,125,111,0.08)", boxShadow: "0 0 0 1px rgba(13,125,111,0.4)" },
  truckRowTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 },
  truckIdentity: { display: "flex", alignItems: "center", gap: 7, minWidth: 0 },
  truckAvatar: {
    width: 24, height: 24, borderRadius: 7, border: "1px solid", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  truckUnit: { color: "var(--ink)", fontWeight: 800, fontSize: 13, lineHeight: 1.2, fontFamily: "ui-monospace, monospace" },
  truckMakeModel: { color: "var(--muted)", fontSize: 10, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  statusPillMini: {
    fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".03em",
    padding: "3px 6px", borderRadius: 5, border: "1px solid", whiteSpace: "nowrap", flexShrink: 0,
  },
  driverLine: { display: "flex", alignItems: "center", gap: 6, marginTop: 7 },
  driverAvatar: {
    width: 17, height: 17, borderRadius: "50%", background: "var(--brand-wash)", color: "var(--brand-dark)",
    fontSize: 8.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  driverAvatarEmpty: { background: "var(--card-2)", color: "var(--muted)" },
  driverName: {
    color: "var(--ink-2)", fontSize: 11.5, fontWeight: 600, flex: 1, minWidth: 0,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  speedBadge: { color: "var(--muted)", fontSize: 10.5, fontFamily: "ui-monospace, monospace", display: "inline-flex", alignItems: "center" },
  // hosRow puts the HOS chip and the $ profit chip on one line (HOS left,
  // $ right) -- the same row that used to be inside loadLine, moved below
  // the route/schedule lines per the redesigned card order.
  hosRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginTop: 6 },
  hosChip: {
    display: "inline-flex", alignItems: "center", gap: 4, color: "var(--muted)",
    fontSize: 10, fontWeight: 600, background: "var(--card-2)", borderRadius: 5, padding: "2px 6px", width: "fit-content",
  },
  hosChipLow: { background: "rgba(224,93,93,0.16)", color: "#f87171" },
  loadLine: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginTop: 7 },
  loadDest: {
    color: "var(--ink-2)", fontSize: 11, fontWeight: 600, minWidth: 0,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "flex", alignItems: "center",
  },
  dueDateChip: { fontSize: 9.5, color: "var(--muted)", fontFamily: "ui-monospace, monospace", flexShrink: 0 },
  scheduleChip: {
    display: "inline-block", marginTop: 4, fontSize: 9.5, fontWeight: 700, borderRadius: 5,
    padding: "2px 6px", width: "fit-content",
  },
  scheduleChipOnTime: { background: "rgba(76,174,109,0.16)", color: "#4ade80" },
  scheduleChipLate: { background: "rgba(224,93,93,0.16)", color: "#f87171" },
  profitChip: { fontSize: 10, fontWeight: 800, borderRadius: 5, padding: "2px 5px", fontFamily: "ui-monospace, monospace" },
  profitChipPos: { background: "rgba(76,174,109,0.18)", color: "#4ade80" },
  profitChipNeg: { background: "rgba(224,93,93,0.16)", color: "#f87171" },
  truckRowSub: { color: "var(--muted)", fontSize: 11, marginTop: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },

  emptyState: { color: "var(--muted)", fontSize: 12, padding: "10px 2px" },

  // ---- detail column ----
  detailCol: {
    width: 340, flexShrink: 0, background: "var(--card)", border: "1px solid var(--line)",
    borderRadius: 12, padding: 18, overflowY: "auto", overflowX: "hidden", minHeight: 0,
    transition: "width .18s ease",
  },
  detailColExpanded: { width: 560 },
  detailHeader: { marginBottom: 14, position: "relative" },
  detailExpandBtn: {
    position: "absolute", top: 0, right: 0, width: 26, height: 26,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 7,
    cursor: "pointer", color: "var(--muted)",
  },
  statusPill: {
    display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: ".03em", padding: "3px 9px",
    borderRadius: 999, border: "1px solid", marginBottom: 8,
  },
  detailTitle: { margin: 0, color: "var(--ink)", fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em" },
  detailSubtitle: { color: "var(--muted)", fontSize: 12, marginTop: 2 },
  truckDetailsTitle: {
    fontSize: 11, fontWeight: 800, color: "var(--muted)", textTransform: "uppercase",
    letterSpacing: ".05em", marginBottom: 10,
  },

  tabBar: { display: "flex", gap: 2, borderBottom: "1px solid var(--line)", marginBottom: 14 },
  tabButton: {
    display: "flex", alignItems: "center", gap: 5, background: "transparent", border: "none",
    borderBottom: "2px solid transparent", color: "var(--muted)", fontSize: 12, fontWeight: 700,
    padding: "0 2px 9px", marginRight: 10, cursor: "pointer", whiteSpace: "nowrap",
  },
  tabButtonActive: { color: "var(--brand)", borderBottom: "2px solid var(--brand)" },
  tabDot: { width: 6, height: 6, borderRadius: "50%", display: "inline-block" },
  tabPanel: { minHeight: 0 },

  // Badge/index styles reused by the Next Trips tab's own upcoming-loads
  // list (see loadSwitcherBadge/loadSwitcherIndex usages there).
  loadSwitcherBadge: {
    fontSize: 9, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
    border: "1px solid", borderRadius: 999, padding: "1.5px 7px",
  },
  loadSwitcherIndex: { fontSize: 10, fontWeight: 600, color: "var(--muted)" },

  statRow: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12.5, padding: "5px 0" },
  statLabel: { color: "var(--muted)", display: "flex", alignItems: "center", gap: 6 },
  statIcon: { display: "inline-flex", color: "var(--muted)" },
  // JetBrains Mono instead of the plain system monospace fallback -- reads
  // as a deliberate "data readout" for driver/dispatcher names, dates and
  // numbers in this panel rather than a generic default. tabular-nums keeps
  // digits aligned column-to-column as rows update.
  statValue: {
    fontFamily: "var(--font-mono)", fontWeight: 600, fontSize: 12.5, letterSpacing: "-0.01em",
    fontVariantNumeric: "tabular-nums", textAlign: "right",
  },

  sectionDivider: {
    display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, fontWeight: 700,
    letterSpacing: ".07em", textTransform: "uppercase", color: "var(--muted)",
    borderTop: "1px solid var(--line)", marginTop: 14, paddingTop: 12, marginBottom: 6,
  },

  checkBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%",
    background: "rgba(76,174,109,0.1)", color: "#4cae6d", border: "1px solid rgba(76,174,109,0.35)",
    borderRadius: 7, padding: "9px 10px", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
  },
  checkLoadTriggerBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%",
    background: "rgba(76,174,109,0.1)", color: "#4cae6d", border: "1px solid rgba(76,174,109,0.35)",
    borderRadius: 7, padding: "8px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", marginBottom: 14,
  },
  explainBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%",
    background: "rgba(138,104,201,0.1)", color: "#a78bfa", border: "1px solid rgba(138,104,201,0.4)",
    borderRadius: 7, padding: "9px 10px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", marginBottom: 10,
  },
  excludeStationBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", width: "100%",
    background: "rgba(224,85,61,0.1)", color: "#e0553d", border: "1px solid rgba(224,85,61,0.4)",
    borderRadius: 6, padding: "6px 8px", fontSize: 11.5, fontWeight: 600, cursor: "pointer",
  },
  // Same box model as explainBtn (same height/padding/font/centering) so a
  // Skip/No/Back button sitting next to it in a flex row reads as one
  // matched pair, not two differently-sized controls -- cancelLoadBtn
  // (smaller, non-flex, red) is deliberately for actual destructive
  // "cancel this load" actions elsewhere, not a paired secondary action.
  popupSecondaryBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%",
    background: "var(--bg)", color: "var(--ink-2)", border: "1px solid var(--line)",
    borderRadius: 7, padding: "9px 10px", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
  },
  tripPlanCard: {
    background: "rgba(138,104,201,0.06)", border: "1px solid rgba(138,104,201,0.25)",
    borderRadius: 9, padding: 12, marginBottom: 12,
  },
  tripPlanTitle: {
    fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
    color: "#a78bfa", marginBottom: 6,
  },
  tripPlanNarrative: { fontSize: 12.5, lineHeight: 1.5, color: "var(--ink-2)", marginBottom: 10 },
  narrativeParagraph: { fontSize: 12.5, lineHeight: 1.55, color: "var(--ink-2)", margin: "0 0 10px" },
  narrativeHeading: { fontSize: 12.5, fontWeight: 700, color: "var(--brand)", margin: "0 0 6px" },
  narrativeList: { margin: "0 0 10px", padding: "0 0 0 18px", display: "flex", flexDirection: "column", gap: 4 },
  narrativeListItem: { fontSize: 12.5, lineHeight: 1.5, color: "var(--ink-2)" },
  negotiationStats: {
    display: "flex", flexDirection: "column", gap: 2, background: "var(--bg)",
    border: "1px solid var(--line)", borderRadius: 7, padding: "6px 9px", marginBottom: 6,
  },
  tripPlanStops: { display: "flex", flexDirection: "column", gap: 5 },
  tripPlanStopRow: { display: "flex", alignItems: "center", gap: 8 },
  tripPlanStopDot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  tripPlanStopLabel: { fontSize: 12, color: "var(--ink-2)" },
  tripPlanTollNote: { fontSize: 11.5, color: "var(--muted)", marginTop: 4 },
  tripPlanEta: { fontSize: 11.5, color: "var(--muted)", marginTop: 6, fontWeight: 600 },

  // ---------- broker-call wizard (match -> analysis -> routes) ----------
  wizardStepper: { display: "flex", alignItems: "center", marginBottom: 8 },
  wizardStepItem: { display: "flex", alignItems: "center", gap: 7, cursor: "default" },
  wizardStepDot: {
    width: 21, height: 21, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 10.5, fontWeight: 700, border: "1.5px solid var(--line)", color: "var(--muted)", background: "var(--card)",
    flexShrink: 0,
  },
  wizardStepLabel: { fontSize: 11.5, fontWeight: 600, color: "var(--muted)", whiteSpace: "nowrap" },
  wizardConnector: { flex: 1, height: 1.5, background: "var(--line)", margin: "0 8px", minWidth: 14 },
  wizardBackLink: {
    fontSize: 11.5, color: "var(--muted)", display: "flex", alignItems: "center", gap: 4,
    cursor: "pointer", border: "none", background: "none", padding: 0, marginBottom: 10, fontFamily: "inherit",
  },
  legendPanel: {
    position: "absolute", top: 12, right: 12, zIndex: 1000, width: 232,
    maxWidth: "calc(100% - 24px)", maxHeight: "calc(100% - 24px)", overflowY: "auto",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: 10,
    padding: "10px 12px", boxShadow: "0 4px 16px rgba(0,0,0,.25)", fontSize: 11.5,
  },
  legendPickHeading: { fontSize: 11, fontWeight: 700, color: "var(--ink-2)", marginBottom: 8 },
  legendDetailsToggle: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 5, width: "100%",
    fontSize: 10.5, fontWeight: 600, color: "var(--muted)", background: "var(--bg)",
    border: "1px solid var(--line)", borderRadius: 6, padding: "4px 6px", cursor: "pointer",
    marginBottom: 10,
  },
  legendOfferedRate: {
    fontSize: 11, color: "var(--muted)", marginBottom: 8, paddingBottom: 8,
    borderBottom: "1px dashed var(--line)",
  },
  // Row-by-row spec-sheet table: stat labels down a fixed-width left
  // column, one value column per route so a dispatcher reads a stat
  // straight across ("Fuel: $23 | $45 | ...") instead of hunting the same
  // stat inside N separate stacked cards. gridTemplateColumns set inline
  // per-render since it depends on route count.
  legendTable: { display: "grid", rowGap: 2, columnGap: 4, alignItems: "stretch", overflowX: "auto" },
  legendTableHeaderCell: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 2, textAlign: "center",
    padding: "6px 3px", borderBottom: "2px solid var(--line)", borderRadius: "6px 6px 0 0",
  },
  // The whole column -- header AND every value cell below it -- gets this
  // same tint, so selection reads as one continuous highlighted column
  // when scanning down, not just a badge on the header row (that
  // ambiguity is exactly what let a route go unselected in practice --
  // see cheapestRouteType's own comment). The radio dot in the header is
  // the third, unambiguous signal.
  legendColumnSelected: { background: "rgba(13,125,111,0.1)", borderBottomColor: "var(--brand)" },
  legendRadioOuter: {
    width: 13, height: 13, borderRadius: "50%", border: "2px solid var(--line)",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxSizing: "border-box",
  },
  legendRadioInner: { width: 6, height: 6, borderRadius: "50%", background: "var(--brand)" },
  legendDot: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  legendRouteLabel: { fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.2, lineHeight: 1.2 },
  legendCategoryBadge: {
    fontSize: 9, fontWeight: 700, color: "#4cae6d", background: "rgba(76,174,109,0.14)",
    border: "1px solid rgba(76,174,109,0.3)", borderRadius: 4, padding: "1px 5px",
    textTransform: "uppercase", letterSpacing: 0.2,
  },
  legendTableRowLabel: { color: "var(--muted)", fontSize: 10.5, padding: "3px 4px 3px 0", alignSelf: "center" },
  legendTableValueCell: {
    color: "var(--ink)", fontWeight: 600, fontVariantNumeric: "tabular-nums",
    fontSize: 10.5, textAlign: "center", padding: "3px 4px", alignSelf: "center",
  },
  // Cost/profit are the two numbers a dispatcher needs fastest -- bumped
  // up in size/weight so they read at a glance instead of blending into
  // the same row height as distance/tolls/etc.
  legendTableLabelBig: { fontWeight: 700, color: "var(--ink-2)" },
  legendTableValueBig: { fontSize: 13.5, fontWeight: 800 },
  legendLogSection: { marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--line)" },
  legendLogPrompt: {
    width: "100%", fontSize: 11, fontWeight: 600, color: "var(--muted)", textAlign: "center",
    padding: "8px 10px", border: "1px dashed var(--line)", borderRadius: 7, boxSizing: "border-box",
  },
  legendLogBtn: {
    width: "100%", fontSize: 11.5, fontWeight: 700, color: "#fff", background: "var(--brand)",
    border: "none", borderRadius: 7, padding: "8px 10px", cursor: "pointer", lineHeight: 1.3,
  },
  legendLogBtnDisabled: { opacity: 0.6, cursor: "default" },
  legendLogDone: {
    width: "100%", fontSize: 11.5, fontWeight: 700, color: "#4cae6d", background: "rgba(76,174,109,0.14)",
    border: "1px solid rgba(76,174,109,0.35)", borderRadius: 7, padding: "8px 10px", textAlign: "center",
  },
  legendHeadlineRow: { display: "flex", gap: 14, marginBottom: 6 },
  legendHeadlineValue: { fontSize: 15.5, fontWeight: 800, color: "var(--ink)", lineHeight: 1.2 },
  legendHeadlineLabel: { fontSize: 9.5, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.3, marginTop: 1 },
  tooltipTitle: { fontWeight: 700, fontSize: 12.5, color: "var(--ink)", marginBottom: 5, paddingBottom: 5, borderBottom: "1px solid var(--line)" },
  legendRateRow: { display: "flex", gap: 10, fontSize: 11, fontWeight: 600, color: "var(--ink-2)" },
  legendWeeklyBalance: { fontSize: 11, color: "var(--muted)", marginTop: 6, lineHeight: 1.4 },
  legendCoveredBadge: {
    display: "flex", alignItems: "center", gap: 5, marginTop: 6, padding: "5px 8px",
    fontSize: 10.5, fontWeight: 600, color: "#4ade80", background: "rgba(76,174,109,0.16)",
    border: "1px solid rgba(76,174,109,0.35)", borderRadius: 6, lineHeight: 1.35,
  },
  legendDetails: { marginTop: 8, paddingTop: 8, borderTop: "1px dashed var(--line)" },
  legendDividerLine: { borderTop: "1px solid var(--line)", margin: "6px 0" },
  legendIncompleteWarning: {
    display: "flex", alignItems: "flex-start", gap: 5, padding: "6px 8px",
    fontSize: 10.5, fontWeight: 600, color: "#f2a33d", background: "rgba(242,163,61,0.1)",
    border: "1px solid rgba(242,163,61,0.3)", borderRadius: 6, lineHeight: 1.4,
  },
  matchRow: {
    display: "flex", alignItems: "center", gap: 8, padding: "6px 9px", border: "1px solid var(--line)",
    borderRadius: 7, marginBottom: 4, cursor: "pointer",
  },
  matchRank: {
    width: 20, height: 20, borderRadius: "50%", background: "var(--line)", color: "var(--ink-2)",
    fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  statGrid2: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: "var(--line)",
    border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", marginBottom: 6,
  },
  statCell2: { background: "var(--card)", padding: "5px 8px" },
  statCell2Label: { fontSize: 9.5, color: "var(--muted)", marginBottom: 1 },
  statCell2Value: { fontSize: 12.5, fontWeight: 700, color: "var(--ink)" },
  offerInput: {
    fontSize: 12.5, fontWeight: 700, color: "var(--ink)", background: "transparent", border: "none",
    borderBottom: "1.5px dashed var(--brand)", width: 76, padding: "0 0 1px", outline: "none",
  },
  useCounterBtn: {
    fontSize: 10.5, fontWeight: 700, color: "var(--brand)", background: "rgba(13,125,111,0.09)",
    border: "1px solid rgba(13,125,111,0.2)", borderRadius: 6, padding: "3px 8px", cursor: "pointer",
  },

  routeOptionCard: {
    border: "1px solid var(--line)", borderRadius: 8, padding: "8px 10px", marginBottom: 6,
    background: "var(--line)", transition: "opacity .15s",
  },
  routeOptionCardActive: { border: "1px solid rgba(13,125,111,0.4)", background: "rgba(13,125,111,0.06)" },
  routeOptionCardDimmed: { opacity: 0.55 },
  routeOptionRow: { display: "flex", alignItems: "flex-start", gap: 9 },
  routeSelectDot: {
    width: 15, height: 15, borderRadius: "50%", border: "2px solid", flexShrink: 0,
    marginTop: 3, cursor: "pointer", padding: 0, background: "transparent",
  },
  routeOptionTitle: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, fontSize: 12.5, fontWeight: 700, color: "var(--ink)" },
  routeOptionSwatch: { width: 9, height: 9, borderRadius: "50%", flexShrink: 0, boxShadow: "0 0 0 2px var(--line)" },
  routeOptionBadge: {
    fontSize: 9.5, fontWeight: 700, letterSpacing: ".03em", textTransform: "uppercase",
    color: "#5eead4", background: "rgba(94,234,212,0.14)", border: "1px solid rgba(94,234,212,0.4)",
    borderRadius: 999, padding: "1px 6px",
  },
  routeOptionMeta: { fontSize: 11.5, color: "var(--muted)", marginTop: 3 },
  routeOptionTolls: { fontSize: 11, color: "var(--muted)", marginTop: 2 },
  routePlanBar: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 },
  routePlanSavedBadge: {
    fontSize: 10, fontWeight: 700, letterSpacing: ".03em", textTransform: "uppercase",
    color: "#4cae6d", background: "rgba(76,174,109,0.12)", border: "1px solid rgba(76,174,109,0.35)",
    borderRadius: 999, padding: "2px 8px",
  },
  refreshRoutesBtn: {
    marginLeft: "auto", fontSize: 11, fontWeight: 600, color: "var(--muted)",
    background: "var(--line)", border: "1px solid var(--line)",
    borderRadius: 6, padding: "3px 9px", cursor: "pointer",
  },
  loadLifecycleRow: { display: "flex", gap: 8, marginTop: 10, marginBottom: 4 },
  closeLoadBtn: {
    fontSize: 11, fontWeight: 600, color: "#4cae6d", background: "rgba(76,174,109,0.1)",
    border: "1px solid rgba(76,174,109,0.35)", borderRadius: 6, padding: "5px 10px", cursor: "pointer",
  },
  cancelLoadBtn: {
    fontSize: 11, fontWeight: 600, color: "#e05d5d", background: "rgba(224,93,93,0.08)",
    border: "1px solid rgba(224,93,93,0.3)", borderRadius: 6, padding: "5px 10px", cursor: "pointer",
  },
  loadClosedBadge: {
    fontSize: 11, fontWeight: 700, color: "var(--muted)", background: "var(--line)",
    border: "1px solid var(--line)", borderRadius: 6, padding: "6px 10px",
    marginTop: 10, marginBottom: 4, display: "inline-block",
  },

  recapBox: {
    marginTop: 6, marginBottom: 6, padding: "8px 0",
    borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)",
  },
  recapHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    fontSize: 11.5, color: "var(--muted)", marginBottom: 8,
  },
  recapTotal: { color: "var(--ink)", fontWeight: 700 },
  recapLegend: { display: "flex", gap: 14, fontSize: 10, color: "var(--muted)", marginBottom: 6 },
  recapLegendItem: { display: "inline-flex", alignItems: "center", gap: 4 },
  recapSwatch: { display: "inline-block", width: 8, height: 8, borderRadius: 2 },
  recapBars: { display: "flex", flexDirection: "column", gap: 4 },
  recapBarRow: { display: "flex", alignItems: "center", gap: 8 },
  recapBarLabel: { fontSize: 10.5, width: 28, flexShrink: 0 },
  recapBarTrack: { flex: 1, height: 6, borderRadius: 3, background: "var(--line)", overflow: "hidden", display: "flex" },
  recapBarFillDriving: { height: "100%", background: "#a78bfa" },
  recapBarFillOnDuty: { height: "100%", background: "#5b6472" },
  recapBarValue: { fontSize: 10.5, color: "var(--muted)", width: 42, textAlign: "right", flexShrink: 0, fontVariantNumeric: "tabular-nums" },
  recapCaption: { fontSize: 10.5, color: "var(--muted)", marginTop: 8, lineHeight: 1.4 },

  availabilityBox: {
    marginTop: 6, marginBottom: 6, padding: "8px 0",
    borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)",
  },
  availabilityNote: { fontSize: 11.5, color: "var(--muted)", marginTop: 2, marginBottom: 8 },
  availabilityBtn: {
    fontSize: 11, fontWeight: 600, color: "#a78bfa", background: "rgba(167,139,250,0.1)",
    border: "1px solid rgba(167,139,250,0.35)", borderRadius: 6, padding: "5px 10px", cursor: "pointer",
  },
  availabilityForm: { display: "flex", flexDirection: "column", gap: 6 },
  availabilityFormLabel: { fontSize: 11.5, color: "var(--muted)" },
  availabilityInput: {
    width: "100%", boxSizing: "border-box", padding: "7px 9px", borderRadius: 6,
    border: "1px solid var(--line)", background: "var(--card)", color: "var(--ink)", fontSize: 12, outline: "none",
  },
  routeExplainTurn: { marginBottom: 10 },
  routeExplainQuestion: { fontSize: 11.5, fontWeight: 700, color: "#a78bfa", marginBottom: 3 },
  routeExplainAnswer: { fontSize: 12.5, lineHeight: 1.55, color: "var(--ink-2)" },
  routeExplainAskRow: { display: "flex", gap: 8, marginTop: 8 },
  routeExplainInput: {
    flex: 1, background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 7,
    padding: "7px 10px", color: "var(--ink)", fontSize: 12.5, fontFamily: "inherit",
  },
  routePlanGroup: {
    border: "1px solid var(--line)", borderRadius: 8, padding: "8px 10px", marginBottom: 8,
    background: "var(--line)",
  },
  routePlanHeader: {
    display: "flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 700, color: "var(--ink)", marginBottom: 6,
  },
  routePlanSummary: { fontSize: 10.5, fontWeight: 500, color: "var(--muted)", marginLeft: "auto" },

  // Solid brand fill instead of a translucent ghost outline -- the old
  // rgba(94,234,212,0.1) wash + light-teal text was tuned for reading
  // against this page's own dark chrome, but nearly disappeared against
  // the light theme's white header (both the background wash and the
  // text color were low-contrast against white, not just dim). var(--brand)
  // is already theme-aware (brighter teal in dark, standard teal in
  // light -- see DARK_THEME_VARS/LIGHT_THEME_VARS), so a solid fill reads
  // clearly as the primary header action in either theme.
  brokerCallBtn: {
    display: "flex", alignItems: "center", gap: 6,
    background: "var(--brand)", color: "#fff", border: "1px solid var(--brand)",
    borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer", marginRight: 14,
    boxShadow: "0 2px 8px rgba(13,125,111,0.35)",
  },
  quickCheckBtn: {
    display: "flex", alignItems: "center", gap: 6,
    background: "var(--card-2)", color: "var(--ink)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", marginRight: 10,
  },
  quickCheckSectionLabel: {
    fontSize: 10.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase",
    letterSpacing: 0.4, marginBottom: 6,
  },
  quickCheckCostRow: {
    display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12,
    color: "var(--ink)", padding: "4px 0", borderBottom: "1px solid var(--line)",
  },
  quickCheckMissingList: {
    margin: 0, padding: "0 0 0 16px", fontSize: 11.5, color: "var(--muted)", lineHeight: 1.7,
  },
  bellBtn: {
    position: "relative", display: "flex", alignItems: "center", justifyContent: "center",
    background: "none", border: "none", cursor: "pointer", padding: 6, marginRight: 10,
  },
  bellBadge: {
    position: "absolute", top: 0, right: 0, minWidth: 15, height: 15, borderRadius: 999,
    color: "#fff", fontSize: 9.5, fontWeight: 700, display: "flex", alignItems: "center",
    justifyContent: "center", padding: "0 3px", border: "1.5px solid var(--card)",
  },
  impersonationChip: {
    display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 999,
    background: "rgba(245, 158, 11, 0.95)", color: "#111827", fontSize: 12, fontWeight: 800,
    marginRight: 10, boxShadow: "0 2px 10px rgba(245,158,11,.28)",
  },
  stopImpersonationBtn: {
    border: "none", background: "#111827", color: "#fff", borderRadius: 999,
    padding: "3px 8px", cursor: "pointer", fontSize: 11, fontWeight: 900,
  },
  themeToggleBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 32, height: 32, borderRadius: 999, marginRight: 4,
    background: "var(--card-2)", border: "1px solid var(--line)", color: "var(--ink)",
    cursor: "pointer",
  },
  modalOverlay: {
    position: "fixed", inset: 0, background: "rgba(2,6,23,0.6)", backdropFilter: "blur(2px)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  },
  modalCard: {
    width: 480, maxWidth: "90vw", maxHeight: "92vh", overflowY: "auto",
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: 14, padding: 20,
    boxShadow: "0 20px 60px rgba(0,0,0,.5)",
  },
  modalHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 },
  modalTitle: { display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 700, color: "var(--ink)" },
  modalCloseBtn: {
    background: "transparent", border: "none", color: "var(--muted)",
    fontSize: 22, lineHeight: 1, cursor: "pointer", padding: 0,
  },
  modalBackBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "transparent", border: "none", color: "var(--muted)",
    cursor: "pointer", padding: 4, borderRadius: 6, flexShrink: 0,
  },
  modalTextarea: {
    width: "100%", background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 8,
    padding: 10, color: "var(--ink)", fontSize: 13, fontFamily: "inherit", resize: "vertical", marginBottom: 10,
  },
  modalInput: {
    width: "100%", background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 8,
    padding: "8px 10px", color: "var(--ink)", fontSize: 12.5, fontFamily: "inherit", boxSizing: "border-box",
  },
  checkLoadModeTabs: { display: "flex", gap: 6, marginBottom: 10 },
  checkLoadModeTab: {
    fontSize: 12, fontWeight: 700, color: "var(--muted)", background: "var(--bg)",
    border: "1px solid var(--line)", borderRadius: 7, padding: "6px 12px", cursor: "pointer",
  },
  checkLoadModeTabActive: { color: "var(--ink)", background: "var(--card)", borderColor: "var(--brand)" },
  checkLoadManualGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10,
  },
  checkLoadManualLabel: { fontSize: 11, color: "var(--muted)", marginBottom: 4 },
  laneHistoryBox: {
    background: "rgba(76,174,109,0.06)", border: "1px solid rgba(76,174,109,0.25)",
    borderRadius: 8, padding: 10, marginBottom: 10,
  },
  laneHistoryTitle: { fontSize: 11.5, color: "var(--ink)", fontWeight: 700, marginBottom: 6 },
  laneHistoryLoading: { fontSize: 11.5, color: "var(--muted)" },
  laneHistoryRow: {
    display: "grid", gridTemplateColumns: "62px 1fr auto 90px", gap: 8, alignItems: "center",
    fontSize: 11, padding: "4px 0", borderTop: "1px solid var(--line)",
  },
  laneHistoryDate: { color: "var(--muted)", fontFamily: "ui-monospace, monospace", fontSize: 10.5 },
  laneHistoryRoute: { color: "var(--ink-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  laneHistoryRate: { color: "var(--brand)", fontWeight: 700, whiteSpace: "nowrap" },
  laneHistoryBy: { color: "var(--muted)", fontSize: 10, textAlign: "right", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  laneHistoryExpandBtn: {
    display: "block", width: "100%", textAlign: "center", marginTop: 6, padding: "4px 0",
    background: "none", border: "none", cursor: "pointer", color: "var(--brand)", fontSize: 10.5, fontWeight: 600,
  },
  checkLoadStopsSection: {
    border: "1px dashed var(--line)", borderRadius: 8, padding: 10, marginBottom: 10,
  },
  checkLoadStopsHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8,
  },
  checkLoadStopsTitle: { fontSize: 11.5, color: "var(--muted)", fontWeight: 700 },
  checkLoadStopRow: {
    display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 8, alignItems: "center", marginBottom: 6,
  },
  checkLoadStopFields: { display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 6 },
  checkLoadStopBadge: {
    fontSize: 10, fontWeight: 800, color: "var(--muted)", fontFamily: "ui-monospace, monospace",
    minWidth: 18, textAlign: "center",
  },
  checkLoadStopRemoveBtn: {
    background: "transparent", border: "none", color: "var(--muted)", cursor: "pointer", padding: 4,
  },
  checkLoadStopResolved: { fontSize: 10.5, color: "#3fae6a", marginTop: 2 },
  checkLoadStopError: { fontSize: 10.5, color: "#f2a33d", marginTop: 2 },
  addStopBtn: {
    display: "flex", alignItems: "center", gap: 6, background: "transparent",
    border: "1px dashed var(--line)", borderRadius: 8, color: "var(--muted)",
    fontSize: 11.5, padding: "6px 10px", cursor: "pointer", width: "100%", justifyContent: "center",
  },
  nextLoadStats: { fontSize: 11.5, color: "var(--muted)", marginBottom: 8, lineHeight: 1.5 },
  candidateList: { display: "flex", flexDirection: "column", gap: 6 },
  candidateCard: {
    background: "rgba(138,104,201,0.08)", border: "1px solid rgba(138,104,201,0.28)", borderRadius: 7,
    padding: "8px 10px", cursor: "pointer",
  },
  candidateCardSelected: { boxShadow: "0 0 0 1.5px #a78bfa" },
  candidateTop: { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 },
  candidateName: { fontSize: 12.5, fontWeight: 700, color: "var(--ink)" },
  candidateProfit: { fontSize: 13, fontWeight: 800, color: "#4cae6d", fontFamily: "ui-monospace, monospace" },
  candidateMeta: { fontSize: 10.5, color: "var(--muted)", marginTop: 3 },
  riskBadge: { color: "#f2a33d", fontSize: 11, marginTop: 7, fontWeight: 600 },
  caption: { color: "var(--muted)", fontSize: 10.5, marginTop: 8, lineHeight: 1.4 },

  // ---- map column ----
  mapCol: { flex: "1 1 auto", minHeight: 500, position: "relative", borderRadius: 12, overflow: "hidden", border: "1px solid var(--line)" },
  mapPanelsStack: {
    position: "absolute", top: 12, right: 12, zIndex: 1000, width: 250,
    display: "flex", flexDirection: "column", gap: 10,
  },
  floatingPanel: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: 10,
    boxShadow: "0 6px 20px rgba(0,0,0,.45)", overflow: "hidden",
  },
  layersPanelHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 12px",
    cursor: "pointer", background: "var(--card-2)", color: "var(--ink)", userSelect: "none",
  },
  layersPanelTitle: { display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase", color: "var(--ink)" },
  poiPanelBody: { padding: "10px 12px 12px", display: "flex", flexDirection: "column", gap: 2 },
  // No maxHeight/overflow -- the panel grows to fit however many routes
  // this load has instead of scrolling a fixed-height box. It's a floating
  // overlay on top of the map, not a fixed layout slot, so there's nothing
  // below it that unbounded growth would push around.
  routesPanelBody: { padding: "10px 12px 12px", display: "flex", flexDirection: "column", gap: 6 },
  layerRefreshBtn: {
    background: "transparent", border: "1px solid var(--line)", borderRadius: 6, color: "var(--ink-2)",
    fontSize: 10, fontWeight: 600, padding: "2px 8px", cursor: "pointer",
  },
  layerRouteRow: { display: "flex", alignItems: "center", gap: 8, padding: "5px 0" },
  layerPoiRow: { display: "flex", alignItems: "center", gap: 9, padding: "3px 0" },
  layerPoiIcon: { display: "flex", alignItems: "center", justifyContent: "center", width: 20, color: "var(--muted)", flexShrink: 0 },
  layerPoiLabel: { fontSize: 12, fontWeight: 600, color: "var(--ink)" },
  layerPoiSub: { fontSize: 10, color: "var(--muted)", marginTop: 1 },
  layerPoiSectionLabel: {
    fontSize: 9.5, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase",
    letterSpacing: 0.4, margin: "8px 0 2px", paddingTop: 6, borderTop: "1px solid var(--line)",
  },
  layerPoiSectionLabelClickable: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    cursor: "pointer", userSelect: "none",
  },
  map: { width: "100%", height: "100%", minHeight: 500, background: "var(--bg)" },
  tipInner: { fontSize: 12, lineHeight: 1.5 },
  tipTitle: { fontWeight: 700, marginBottom: 2 },
};
